import { utils as utils$1, BigNumber, ethers, Contract, ContractFactory, Signer, constants } from 'ethers';

var concat = utils$1.concat,
    hexlify = utils$1.hexlify,
    zeroPad = utils$1.zeroPad,
    hexZeroPad = utils$1.hexZeroPad,
    arrayify = utils$1.arrayify,
    parseUnits = utils$1.parseUnits;
var selectLteLogic;

(function (selectLteLogic) {
  selectLteLogic[selectLteLogic["every"] = 0] = "every";
  selectLteLogic[selectLteLogic["any"] = 1] = "any";
})(selectLteLogic || (selectLteLogic = {}));
/**
 * @public An enum for selectLte mode
 */


var selectLteMode;

(function (selectLteMode) {
  selectLteMode[selectLteMode["min"] = 0] = "min";
  selectLteMode[selectLteMode["max"] = 1] = "max";
  selectLteMode[selectLteMode["first"] = 2] = "first";
})(selectLteMode || (selectLteMode = {}));
/**
 * @public
 * Converts an opcode and operand to bytes, and returns their concatenation.
 *
 * @param code - the opcode
 * @param erand - the operand, currently limited to 1 byte (defaults to 0)
 */


var op = function op(code, erand) {
  if (erand === void 0) {
    erand = 0;
  }

  return concat([bytify(code), bytify(erand)]);
};
/**
 * @public
 * Converts a value to raw bytes representation. Assumes `value` is less than or equal to 1 byte,
 * unless a desired `bytesLength` is specified.
 *
 * @param value - value to convert to raw bytes format
 * @param bytesLength - (defaults to 1) number of bytes to left pad if `value` doesn't completely
 * fill the desired amount of memory. Will throw `InvalidArgument` error if value already exceeds
 * bytes length.
 * @returns raw bytes representation as Uint8Array
 */

var bytify = function bytify(value, bytesLength) {
  if (bytesLength === void 0) {
    bytesLength = 1;
  }

  return zeroPad(hexlify(value), bytesLength);
};
/**
 * @public
 * Utility function that transforms a BigNumber from the output of the ITier contract report
 *
 * @param report - report as bignumber from the ITier contract
 * @returns hexadecimal string of the report already padded
 */

var paddedUInt256 = function paddedUInt256(report) {
  if (BigNumber.from(report).gt(ethers.constants.MaxUint256)) {
    throw new Error(report + " exceeds max uint256");
  }

  return "0x" + BigNumber.from(report).toHexString().substring(2).padStart(64, "0");
};
/**
 * @public Utility function to produce 32 bits size hexString
 *
 * @param number - the value to convert into a 32bit size hexString
 *
 * @returns a 8 character hexString
 */

var paddedUInt32 = function paddedUInt32(number) {
  if (BigNumber.from(number).gt('0xffffffff')) {
    throw new Error(number + " exceeds max uint32");
  }

  return BigNumber.from(number).toHexString().substring(2).padStart(8, "0");
};
/**
 * @public function to pack the operand for VAL opcode of ZIPMAP arguments
 *
 * @param valIndex - index of the ZIPMAP arguments
 *
 * @returns a byte size number
 */

function arg(valIndex) {
  var arg = 1;
  arg <<= 7;
  arg += valIndex;
  return arg;
}
/**
 * @public function to pack start/end tier range into a byte size number for the UPDATE_BLOCKS_FOR_TIER_RANGE opcode
 *
 * @param startTier - the start tier of the updating which ranges between 0 to 8 (exclusive)
 * @param endTier - the end tier of the updating which ranges between 0 to 8 (inclusive)
 *
 * @returns a byte size number
 */

function tierRange(startTier, endTier) {
  //   op_.val & 0x0f, //     00001111
  //   op_.val & 0xf0, //     11110000
  if (startTier < 0 || startTier > 8) {
    throw new Error("Invalid startTier " + startTier);
  } else if (endTier < 0 || endTier > 8) {
    throw new Error("Invalid endTier " + endTier);
  }

  var range = endTier;
  range <<= 4;
  range += startTier;
  return range;
}
/**
 * @public
 * Constructs the operand for RainVM's `call` AllStandardOps by packing 3 numbers into a single byte.
 * All parameters use zero-based counting i.e. an `fnSize` of 0 means to allocate one element (32 bytes)
 * on the stack to define your functions, while an `fnSize` of 3 means to allocate all four elements (4 * 32 bytes) on the stack.
 *
 * @param sourceIndex - index of function source in `immutableSourceConfig.sources`
 * @param loopSize - number of times to subdivide vals, reduces uint size but allows for more vals (range 0-7)
 * @param valSize - number of vals in outer stack (range 0-7)
 *
 * @returns a byte size number
 */

function callSize(sourceIndex, loopSize, valSize) {
  // CallSize(
  //   op_.val & 0x07,      // 00000111
  //   op_.val >> 3 & 0x03, // 00011000
  //   op_.val >> 5 & 0x07  // 11100000
  // )
  if (sourceIndex < 0 || sourceIndex > 7) {
    throw new Error('Invalid fnSize');
  } else if (loopSize < 0 || loopSize > 3) {
    throw new Error('Invalid loopSize');
  } else if (valSize < 0 || valSize > 7) {
    throw new Error('Invalid valSize');
  }

  var callSize = valSize;
  callSize <<= 2;
  callSize += loopSize;
  callSize <<= 3;
  callSize += sourceIndex;
  return callSize;
}
/**
 * @public function to set up the operand for a SELECT_LTE opcode
 *
 * @param logic - 0 = every, 1 = any, acts like a logical and/or for the check against BLOCK_NUMBER
 * @param mode - 0 = min, 1 = max, 2 = first, the way to select the reports that pass the check against BLOCK_NUMBER
 * @param length - the number of reports to stack for SELECT_LTE opcode
 *
 * @returns a byte size number
 */

function selectLte(logic, mode, length) {
  var lte = logic;
  lte <<= 2;
  lte += mode;
  lte <<= 5;
  lte += length;
  return lte;
}
/**
 * @public
 * Replace a value in a BytesLike. Set `replacement` in the `index` on the `original` BytesLike value
 *
 * @param original - Value that will be changed
 * @param index - index/position where the new value will be set
 * @param replacement - the new value to replace
 * @returns BytesLike with the value replaced
 */

var replaceAt = function replaceAt(original, index, replacement) {
  var originalParsed = arrayify(original, {
    allowMissingPrefix: true
  });
  originalParsed[index] = parseInt(replacement.toString());
  return originalParsed;
};
/**
 * @public function for the operand of the SKIP opcode
 * @remarks - SKIP is no longer available
 *
 * @returns a byte size number
 */

function skip(places, conditional) {
  if (conditional === void 0) {
    conditional = false;
  }

  var skip = conditional ? 1 : 0;
  skip <<= 7; // JS ints are already signed.

  skip |= places & 0x7f;
  return skip;
}
/**
 * @public Utility function to produce 64 bits size hexString
 *
 * @param number - the value to convert into a 64bit size hexString
 *
 * @returns a 16 character hexString (without 0x prefix)
 */

var paddedUInt64 = function paddedUInt64(number) {
  if (BigNumber.from(number).gt('0xffffffffffffffff')) {
    throw new Error(number + " exceeds max uint64");
  }

  return BigNumber.from(number).toHexString().substring(2).padStart(16, "0");
};
/**
 * @public Utility function to produce 128 bits size hexString
 *
 * @param number - the value to convert into a 128bit size hexString
 *
 * @returns a 32 character hexString (without 0x prefix)
 */

var paddedUInt128 = function paddedUInt128(number) {
  if (BigNumber.from(number).gt('0xffffffffffffffffffffffffffffffff')) {
    throw new Error(number + " exceeds max uint128");
  }

  return BigNumber.from(number).toHexString().substring(2).padStart(32, "0");
};
/**
 * @public
 * Utility function that transforms a BigNumberish to an ether address (40 char length hexString)
 *
 * @param report - value as bignumberish
 * @returns hexadecimal string as an ether address (40 char length hexString)
 */

var paddedUInt160 = function paddedUInt160(address) {
  if (BigNumber.from(address).gt('0xffffffffffffffffffffffffffffffffffffffff')) {
    throw new Error(address + " exceeds max uint160");
  }

  return "0x" + BigNumber.from(address).toHexString().substring(2).padStart(40, "0");
};

var utils = {
  __proto__: null,
  concat: concat,
  hexlify: hexlify,
  zeroPad: zeroPad,
  hexZeroPad: hexZeroPad,
  arrayify: arrayify,
  parseUnits: parseUnits,
  get selectLteLogic () { return selectLteLogic; },
  get selectLteMode () { return selectLteMode; },
  op: op,
  bytify: bytify,
  paddedUInt256: paddedUInt256,
  paddedUInt32: paddedUInt32,
  arg: arg,
  tierRange: tierRange,
  callSize: callSize,
  selectLte: selectLte,
  replaceAt: replaceAt,
  skip: skip,
  paddedUInt64: paddedUInt64,
  paddedUInt128: paddedUInt128,
  paddedUInt160: paddedUInt160
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;

  _setPrototypeOf(subClass, superClass);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var runtime_1 = createCommonjsModule(function (module) {
/**
 * Copyright (c) 2014-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var runtime = (function (exports) {

  var Op = Object.prototype;
  var hasOwn = Op.hasOwnProperty;
  var undefined$1; // More compressible than void 0.
  var $Symbol = typeof Symbol === "function" ? Symbol : {};
  var iteratorSymbol = $Symbol.iterator || "@@iterator";
  var asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator";
  var toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
    return obj[key];
  }
  try {
    // IE 8 has a broken Object.defineProperty that only works on DOM objects.
    define({}, "");
  } catch (err) {
    define = function(obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    // If outerFn provided and outerFn.prototype is a Generator, then outerFn.prototype instanceof Generator.
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator;
    var generator = Object.create(protoGenerator.prototype);
    var context = new Context(tryLocsList || []);

    // The ._invoke method unifies the implementations of the .next,
    // .throw, and .return methods.
    generator._invoke = makeInvokeMethod(innerFn, self, context);

    return generator;
  }
  exports.wrap = wrap;

  // Try/catch helper to minimize deoptimizations. Returns a completion
  // record like context.tryEntries[i].completion. This interface could
  // have been (and was previously) designed to take a closure to be
  // invoked without arguments, but in all the cases we care about we
  // already have an existing method we want to call, so there's no need
  // to create a new function object. We can even get away with assuming
  // the method takes exactly one argument, since that happens to be true
  // in every case, so we don't have to touch the arguments object. The
  // only additional allocation required is the completion record, which
  // has a stable shape and so hopefully should be cheap to allocate.
  function tryCatch(fn, obj, arg) {
    try {
      return { type: "normal", arg: fn.call(obj, arg) };
    } catch (err) {
      return { type: "throw", arg: err };
    }
  }

  var GenStateSuspendedStart = "suspendedStart";
  var GenStateSuspendedYield = "suspendedYield";
  var GenStateExecuting = "executing";
  var GenStateCompleted = "completed";

  // Returning this object from the innerFn has the same effect as
  // breaking out of the dispatch switch statement.
  var ContinueSentinel = {};

  // Dummy constructor functions that we use as the .constructor and
  // .constructor.prototype properties for functions that return Generator
  // objects. For full spec compliance, you may wish to configure your
  // minifier not to mangle the names of these two functions.
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}

  // This is a polyfill for %IteratorPrototype% for environments that
  // don't natively support it.
  var IteratorPrototype = {};
  define(IteratorPrototype, iteratorSymbol, function () {
    return this;
  });

  var getProto = Object.getPrototypeOf;
  var NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  if (NativeIteratorPrototype &&
      NativeIteratorPrototype !== Op &&
      hasOwn.call(NativeIteratorPrototype, iteratorSymbol)) {
    // This environment has a native %IteratorPrototype%; use it instead
    // of the polyfill.
    IteratorPrototype = NativeIteratorPrototype;
  }

  var Gp = GeneratorFunctionPrototype.prototype =
    Generator.prototype = Object.create(IteratorPrototype);
  GeneratorFunction.prototype = GeneratorFunctionPrototype;
  define(Gp, "constructor", GeneratorFunctionPrototype);
  define(GeneratorFunctionPrototype, "constructor", GeneratorFunction);
  GeneratorFunction.displayName = define(
    GeneratorFunctionPrototype,
    toStringTagSymbol,
    "GeneratorFunction"
  );

  // Helper for defining the .next, .throw, and .return methods of the
  // Iterator interface in terms of a single ._invoke method.
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function(method) {
      define(prototype, method, function(arg) {
        return this._invoke(method, arg);
      });
    });
  }

  exports.isGeneratorFunction = function(genFun) {
    var ctor = typeof genFun === "function" && genFun.constructor;
    return ctor
      ? ctor === GeneratorFunction ||
        // For the native GeneratorFunction constructor, the best we can
        // do is to check its .name property.
        (ctor.displayName || ctor.name) === "GeneratorFunction"
      : false;
  };

  exports.mark = function(genFun) {
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(genFun, GeneratorFunctionPrototype);
    } else {
      genFun.__proto__ = GeneratorFunctionPrototype;
      define(genFun, toStringTagSymbol, "GeneratorFunction");
    }
    genFun.prototype = Object.create(Gp);
    return genFun;
  };

  // Within the body of any async function, `await x` is transformed to
  // `yield regeneratorRuntime.awrap(x)`, so that the runtime can test
  // `hasOwn.call(value, "__await")` to determine if the yielded value is
  // meant to be awaited.
  exports.awrap = function(arg) {
    return { __await: arg };
  };

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if (record.type === "throw") {
        reject(record.arg);
      } else {
        var result = record.arg;
        var value = result.value;
        if (value &&
            typeof value === "object" &&
            hasOwn.call(value, "__await")) {
          return PromiseImpl.resolve(value.__await).then(function(value) {
            invoke("next", value, resolve, reject);
          }, function(err) {
            invoke("throw", err, resolve, reject);
          });
        }

        return PromiseImpl.resolve(value).then(function(unwrapped) {
          // When a yielded Promise is resolved, its final value becomes
          // the .value of the Promise<{value,done}> result for the
          // current iteration.
          result.value = unwrapped;
          resolve(result);
        }, function(error) {
          // If a rejected Promise was yielded, throw the rejection back
          // into the async generator function so it can be handled there.
          return invoke("throw", error, resolve, reject);
        });
      }
    }

    var previousPromise;

    function enqueue(method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function(resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise =
        // If enqueue has been called before, then we want to wait until
        // all previous Promises have been resolved before calling invoke,
        // so that results are always delivered in the correct order. If
        // enqueue has not been called before, then it is important to
        // call invoke immediately, without waiting on a callback to fire,
        // so that the async generator function has the opportunity to do
        // any necessary setup in a predictable way. This predictability
        // is why the Promise constructor synchronously invokes its
        // executor callback, and why async functions synchronously
        // execute code before the first await. Since we implement simple
        // async functions in terms of async generators, it is especially
        // important to get this right, even though it requires care.
        previousPromise ? previousPromise.then(
          callInvokeWithMethodAndArg,
          // Avoid propagating failures to Promises returned by later
          // invocations of the iterator.
          callInvokeWithMethodAndArg
        ) : callInvokeWithMethodAndArg();
    }

    // Define the unified helper method that is used to implement .next,
    // .throw, and .return (see defineIteratorMethods).
    this._invoke = enqueue;
  }

  defineIteratorMethods(AsyncIterator.prototype);
  define(AsyncIterator.prototype, asyncIteratorSymbol, function () {
    return this;
  });
  exports.AsyncIterator = AsyncIterator;

  // Note that simple async functions are implemented on top of
  // AsyncIterator objects; they just return a Promise for the value of
  // the final result produced by the iterator.
  exports.async = function(innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    if (PromiseImpl === void 0) PromiseImpl = Promise;

    var iter = new AsyncIterator(
      wrap(innerFn, outerFn, self, tryLocsList),
      PromiseImpl
    );

    return exports.isGeneratorFunction(outerFn)
      ? iter // If outerFn is a generator, return the full iterator.
      : iter.next().then(function(result) {
          return result.done ? result.value : iter.next();
        });
  };

  function makeInvokeMethod(innerFn, self, context) {
    var state = GenStateSuspendedStart;

    return function invoke(method, arg) {
      if (state === GenStateExecuting) {
        throw new Error("Generator is already running");
      }

      if (state === GenStateCompleted) {
        if (method === "throw") {
          throw arg;
        }

        // Be forgiving, per 25.3.3.3.3 of the spec:
        // https://people.mozilla.org/~jorendorff/es6-draft.html#sec-generatorresume
        return doneResult();
      }

      context.method = method;
      context.arg = arg;

      while (true) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }

        if (context.method === "next") {
          // Setting context._sent for legacy support of Babel's
          // function.sent implementation.
          context.sent = context._sent = context.arg;

        } else if (context.method === "throw") {
          if (state === GenStateSuspendedStart) {
            state = GenStateCompleted;
            throw context.arg;
          }

          context.dispatchException(context.arg);

        } else if (context.method === "return") {
          context.abrupt("return", context.arg);
        }

        state = GenStateExecuting;

        var record = tryCatch(innerFn, self, context);
        if (record.type === "normal") {
          // If an exception is thrown from innerFn, we leave state ===
          // GenStateExecuting and loop back for another invocation.
          state = context.done
            ? GenStateCompleted
            : GenStateSuspendedYield;

          if (record.arg === ContinueSentinel) {
            continue;
          }

          return {
            value: record.arg,
            done: context.done
          };

        } else if (record.type === "throw") {
          state = GenStateCompleted;
          // Dispatch the exception by looping back around to the
          // context.dispatchException(context.arg) call above.
          context.method = "throw";
          context.arg = record.arg;
        }
      }
    };
  }

  // Call delegate.iterator[context.method](context.arg) and handle the
  // result, either by returning a { value, done } result from the
  // delegate iterator, or by modifying context.method and context.arg,
  // setting context.delegate to null, and returning the ContinueSentinel.
  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];
    if (method === undefined$1) {
      // A .throw or .return when the delegate iterator has no .throw
      // method always terminates the yield* loop.
      context.delegate = null;

      if (context.method === "throw") {
        // Note: ["return"] must be used for ES3 parsing compatibility.
        if (delegate.iterator["return"]) {
          // If the delegate iterator has a return method, give it a
          // chance to clean up.
          context.method = "return";
          context.arg = undefined$1;
          maybeInvokeDelegate(delegate, context);

          if (context.method === "throw") {
            // If maybeInvokeDelegate(context) changed context.method from
            // "return" to "throw", let that override the TypeError below.
            return ContinueSentinel;
          }
        }

        context.method = "throw";
        context.arg = new TypeError(
          "The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);

    if (record.type === "throw") {
      context.method = "throw";
      context.arg = record.arg;
      context.delegate = null;
      return ContinueSentinel;
    }

    var info = record.arg;

    if (! info) {
      context.method = "throw";
      context.arg = new TypeError("iterator result is not an object");
      context.delegate = null;
      return ContinueSentinel;
    }

    if (info.done) {
      // Assign the result of the finished delegate to the temporary
      // variable specified by delegate.resultName (see delegateYield).
      context[delegate.resultName] = info.value;

      // Resume execution at the desired location (see delegateYield).
      context.next = delegate.nextLoc;

      // If context.method was "throw" but the delegate handled the
      // exception, let the outer generator proceed normally. If
      // context.method was "next", forget context.arg since it has been
      // "consumed" by the delegate iterator. If context.method was
      // "return", allow the original .return call to continue in the
      // outer generator.
      if (context.method !== "return") {
        context.method = "next";
        context.arg = undefined$1;
      }

    } else {
      // Re-yield the result returned by the delegate method.
      return info;
    }

    // The delegate iterator is finished, so forget it and continue with
    // the outer generator.
    context.delegate = null;
    return ContinueSentinel;
  }

  // Define Generator.prototype.{next,throw,return} in terms of the
  // unified ._invoke helper method.
  defineIteratorMethods(Gp);

  define(Gp, toStringTagSymbol, "Generator");

  // A Generator should always return itself as the iterator object when the
  // @@iterator function is called on it. Some browsers' implementations of the
  // iterator prototype chain incorrectly implement this, causing the Generator
  // object to not be returned from this call. This ensures that doesn't happen.
  // See https://github.com/facebook/regenerator/issues/274 for more details.
  define(Gp, iteratorSymbol, function() {
    return this;
  });

  define(Gp, "toString", function() {
    return "[object Generator]";
  });

  function pushTryEntry(locs) {
    var entry = { tryLoc: locs[0] };

    if (1 in locs) {
      entry.catchLoc = locs[1];
    }

    if (2 in locs) {
      entry.finallyLoc = locs[2];
      entry.afterLoc = locs[3];
    }

    this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal";
    delete record.arg;
    entry.completion = record;
  }

  function Context(tryLocsList) {
    // The root entry object (effectively a try statement without a catch
    // or a finally block) gives us a place to store values thrown from
    // locations where there is no enclosing try statement.
    this.tryEntries = [{ tryLoc: "root" }];
    tryLocsList.forEach(pushTryEntry, this);
    this.reset(true);
  }

  exports.keys = function(object) {
    var keys = [];
    for (var key in object) {
      keys.push(key);
    }
    keys.reverse();

    // Rather than returning an object with a next method, we keep
    // things simple and return the next function itself.
    return function next() {
      while (keys.length) {
        var key = keys.pop();
        if (key in object) {
          next.value = key;
          next.done = false;
          return next;
        }
      }

      // To avoid creating an additional object, we just hang the .value
      // and .done properties off the next function object itself. This
      // also ensures that the minifier will not anonymize the function.
      next.done = true;
      return next;
    };
  };

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) {
        return iteratorMethod.call(iterable);
      }

      if (typeof iterable.next === "function") {
        return iterable;
      }

      if (!isNaN(iterable.length)) {
        var i = -1, next = function next() {
          while (++i < iterable.length) {
            if (hasOwn.call(iterable, i)) {
              next.value = iterable[i];
              next.done = false;
              return next;
            }
          }

          next.value = undefined$1;
          next.done = true;

          return next;
        };

        return next.next = next;
      }
    }

    // Return an iterator with no values.
    return { next: doneResult };
  }
  exports.values = values;

  function doneResult() {
    return { value: undefined$1, done: true };
  }

  Context.prototype = {
    constructor: Context,

    reset: function(skipTempReset) {
      this.prev = 0;
      this.next = 0;
      // Resetting context._sent for legacy support of Babel's
      // function.sent implementation.
      this.sent = this._sent = undefined$1;
      this.done = false;
      this.delegate = null;

      this.method = "next";
      this.arg = undefined$1;

      this.tryEntries.forEach(resetTryEntry);

      if (!skipTempReset) {
        for (var name in this) {
          // Not sure about the optimal order of these conditions:
          if (name.charAt(0) === "t" &&
              hasOwn.call(this, name) &&
              !isNaN(+name.slice(1))) {
            this[name] = undefined$1;
          }
        }
      }
    },

    stop: function() {
      this.done = true;

      var rootEntry = this.tryEntries[0];
      var rootRecord = rootEntry.completion;
      if (rootRecord.type === "throw") {
        throw rootRecord.arg;
      }

      return this.rval;
    },

    dispatchException: function(exception) {
      if (this.done) {
        throw exception;
      }

      var context = this;
      function handle(loc, caught) {
        record.type = "throw";
        record.arg = exception;
        context.next = loc;

        if (caught) {
          // If the dispatched exception was caught by a catch block,
          // then let that catch block handle the exception normally.
          context.method = "next";
          context.arg = undefined$1;
        }

        return !! caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        var record = entry.completion;

        if (entry.tryLoc === "root") {
          // Exception thrown outside of any try block that could handle
          // it, so set the completion value of the entire function to
          // throw the exception.
          return handle("end");
        }

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc");
          var hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            } else if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            }

          } else if (hasFinally) {
            if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else {
            throw new Error("try statement without catch or finally");
          }
        }
      }
    },

    abrupt: function(type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev &&
            hasOwn.call(entry, "finallyLoc") &&
            this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      if (finallyEntry &&
          (type === "break" ||
           type === "continue") &&
          finallyEntry.tryLoc <= arg &&
          arg <= finallyEntry.finallyLoc) {
        // Ignore the finally entry if control is not jumping to a
        // location outside the try/catch block.
        finallyEntry = null;
      }

      var record = finallyEntry ? finallyEntry.completion : {};
      record.type = type;
      record.arg = arg;

      if (finallyEntry) {
        this.method = "next";
        this.next = finallyEntry.finallyLoc;
        return ContinueSentinel;
      }

      return this.complete(record);
    },

    complete: function(record, afterLoc) {
      if (record.type === "throw") {
        throw record.arg;
      }

      if (record.type === "break" ||
          record.type === "continue") {
        this.next = record.arg;
      } else if (record.type === "return") {
        this.rval = this.arg = record.arg;
        this.method = "return";
        this.next = "end";
      } else if (record.type === "normal" && afterLoc) {
        this.next = afterLoc;
      }

      return ContinueSentinel;
    },

    finish: function(finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) {
          this.complete(entry.completion, entry.afterLoc);
          resetTryEntry(entry);
          return ContinueSentinel;
        }
      }
    },

    "catch": function(tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if (record.type === "throw") {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }

      // The context.catch method must only be called with a location
      // argument that corresponds to a known catch block.
      throw new Error("illegal catch attempt");
    },

    delegateYield: function(iterable, resultName, nextLoc) {
      this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      };

      if (this.method === "next") {
        // Deliberately forget the last sent value so that we don't
        // accidentally pass it on to the delegate.
        this.arg = undefined$1;
      }

      return ContinueSentinel;
    }
  };

  // Regardless of whether this script is executing as a CommonJS module
  // or not, return the runtime object so that we can declare the variable
  // regeneratorRuntime in the outer scope, which allows this module to be
  // injected easily by `bin/regenerator --include-runtime script.js`.
  return exports;

}(
  // If this script is executing as a CommonJS module, use module.exports
  // as the regeneratorRuntime namespace. Otherwise create a new empty
  // object. Either way, the resulting object will be used to initialize
  // the regeneratorRuntime variable at the top of this file.
   module.exports 
));

try {
  regeneratorRuntime = runtime;
} catch (accidentalStrictMode) {
  // This module should not be running in strict mode, so the above
  // assignment should always work unless something is misconfigured. Just
  // in case runtime.js accidentally runs in strict mode, in modern engines
  // we can explicitly access globalThis. In older engines we can escape
  // strict mode using a global Function call. This could conceivably fail
  // if a Content Security Policy forbids using Function, but in that case
  // the proper solution is to fix the accidental strict mode problem. If
  // you've misconfigured your bundler to force strict mode and applied a
  // CSP to forbid Function, and you're not willing to fix either of those
  // problems, please detail your unique predicament in a GitHub issue.
  if (typeof globalThis === "object") {
    globalThis.regeneratorRuntime = runtime;
  } else {
    Function("r", "regeneratorRuntime = r")(runtime);
  }
}
});

/* Autogenerated file. Do not edit manually. */
var _abi = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "Approval",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "Transfer",
  type: "event"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    internalType: "address",
    name: "spender",
    type: "address"
  }],
  name: "allowance",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "approve",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }],
  name: "balanceOf",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "totalSupply",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "transfer",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "transferFrom",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}];
var IERC20__factory = /*#__PURE__*/function () {
  function IERC20__factory() {}

  IERC20__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi);
  };

  IERC20__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi, signerOrProvider);
  };

  return IERC20__factory;
}();
IERC20__factory.abi = _abi;

/* Autogenerated file. Do not edit manually. */
var _abi$1 = [{
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var Factory__factory = /*#__PURE__*/function () {
  function Factory__factory() {}

  Factory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$1);
  };

  Factory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$1, signerOrProvider);
  };

  return Factory__factory;
}();
Factory__factory.abi = _abi$1;

/* Autogenerated file. Do not edit manually. */
var _abi$2 = [{
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "startTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "endTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "TierChange",
  type: "event"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }],
  name: "report",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    internalType: "uint256",
    name: "endTier",
    type: "uint256"
  }, {
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "setTier",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}];
var ITier__factory = /*#__PURE__*/function () {
  function ITier__factory() {}

  ITier__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$2);
  };

  ITier__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$2, signerOrProvider);
  };

  return ITier__factory;
}();
ITier__factory.abi = _abi$2;

/* Autogenerated file. Do not edit manually. */
var _abi$3 = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    indexed: false,
    internalType: "bool",
    name: "approved",
    type: "bool"
  }],
  name: "ApprovalForAll",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256[]",
    name: "ids",
    type: "uint256[]"
  }, {
    indexed: false,
    internalType: "uint256[]",
    name: "values",
    type: "uint256[]"
  }],
  name: "TransferBatch",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "id",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "TransferSingle",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "string",
    name: "value",
    type: "string"
  }, {
    indexed: true,
    internalType: "uint256",
    name: "id",
    type: "uint256"
  }],
  name: "URI",
  type: "event"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    internalType: "uint256",
    name: "id",
    type: "uint256"
  }],
  name: "balanceOf",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address[]",
    name: "accounts",
    type: "address[]"
  }, {
    internalType: "uint256[]",
    name: "ids",
    type: "uint256[]"
  }],
  name: "balanceOfBatch",
  outputs: [{
    internalType: "uint256[]",
    name: "",
    type: "uint256[]"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    internalType: "uint256",
    name: "id",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "burn",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    internalType: "uint256[]",
    name: "ids",
    type: "uint256[]"
  }, {
    internalType: "uint256[]",
    name: "values",
    type: "uint256[]"
  }],
  name: "burnBatch",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    internalType: "address",
    name: "operator",
    type: "address"
  }],
  name: "isApprovedForAll",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256[]",
    name: "ids",
    type: "uint256[]"
  }, {
    internalType: "uint256[]",
    name: "amounts",
    type: "uint256[]"
  }, {
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "safeBatchTransferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "id",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }, {
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "safeTransferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    internalType: "bool",
    name: "approved",
    type: "bool"
  }],
  name: "setApprovalForAll",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes4",
    name: "interfaceId",
    type: "bytes4"
  }],
  name: "supportsInterface",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  name: "uri",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}];
var ERC1155Burnable__factory = /*#__PURE__*/function () {
  function ERC1155Burnable__factory() {}

  ERC1155Burnable__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$3);
  };

  ERC1155Burnable__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$3, signerOrProvider);
  };

  return ERC1155Burnable__factory;
}();
ERC1155Burnable__factory.abi = _abi$3;

/* Autogenerated file. Do not edit manually. */
var _abi$4 = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "Approval",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "Transfer",
  type: "event"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    internalType: "address",
    name: "spender",
    type: "address"
  }],
  name: "allowance",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "approve",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }],
  name: "balanceOf",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "burn",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "burnFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "decimals",
  outputs: [{
    internalType: "uint8",
    name: "",
    type: "uint8"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "subtractedValue",
    type: "uint256"
  }],
  name: "decreaseAllowance",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "addedValue",
    type: "uint256"
  }],
  name: "increaseAllowance",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "name",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "symbol",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "totalSupply",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "transfer",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "transferFrom",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}];
var ERC20Burnable__factory = /*#__PURE__*/function () {
  function ERC20Burnable__factory() {}

  ERC20Burnable__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$4);
  };

  ERC20Burnable__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$4, signerOrProvider);
  };

  return ERC20Burnable__factory;
}();
ERC20Burnable__factory.abi = _abi$4;

/* Autogenerated file. Do not edit manually. */
var _abi$5 = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "approved",
    type: "address"
  }, {
    indexed: true,
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "Approval",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    indexed: false,
    internalType: "bool",
    name: "approved",
    type: "bool"
  }],
  name: "ApprovalForAll",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    indexed: true,
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "Transfer",
  type: "event"
}, {
  inputs: [{
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "approve",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }],
  name: "balanceOf",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "burn",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "getApproved",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    internalType: "address",
    name: "operator",
    type: "address"
  }],
  name: "isApprovedForAll",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "name",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "ownerOf",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "safeTransferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }, {
    internalType: "bytes",
    name: "_data",
    type: "bytes"
  }],
  name: "safeTransferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    internalType: "bool",
    name: "approved",
    type: "bool"
  }],
  name: "setApprovalForAll",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes4",
    name: "interfaceId",
    type: "bytes4"
  }],
  name: "supportsInterface",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "symbol",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "tokenURI",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "transferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}];
var ERC721Burnable__factory = /*#__PURE__*/function () {
  function ERC721Burnable__factory() {}

  ERC721Burnable__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$5);
  };

  ERC721Burnable__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$5, signerOrProvider);
  };

  return ERC721Burnable__factory;
}();
ERC721Burnable__factory.abi = _abi$5;

/* Autogenerated file. Do not edit manually. */
var _abi$6 = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "approved",
    type: "address"
  }, {
    indexed: true,
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "Approval",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    indexed: false,
    internalType: "bool",
    name: "approved",
    type: "bool"
  }],
  name: "ApprovalForAll",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    indexed: true,
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "Transfer",
  type: "event"
}, {
  inputs: [{
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "approve",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }],
  name: "balanceOf",
  outputs: [{
    internalType: "uint256",
    name: "balance",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "getApproved",
  outputs: [{
    internalType: "address",
    name: "operator",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    internalType: "address",
    name: "operator",
    type: "address"
  }],
  name: "isApprovedForAll",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "ownerOf",
  outputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "safeTransferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }, {
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "safeTransferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    internalType: "bool",
    name: "_approved",
    type: "bool"
  }],
  name: "setApprovalForAll",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes4",
    name: "interfaceId",
    type: "bytes4"
  }],
  name: "supportsInterface",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "transferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}];
var IERC721__factory = /*#__PURE__*/function () {
  function IERC721__factory() {}

  IERC721__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$6);
  };

  IERC721__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$6, signerOrProvider);
  };

  return IERC721__factory;
}();
IERC721__factory.abi = _abi$6;

var _abi$7 = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  inputs: [{
    internalType: "uint256",
    name: "_size",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_start",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_end",
    type: "uint256"
  }],
  name: "InvalidCodeAtRange",
  type: "error"
}, {
  inputs: [],
  name: "WriteError",
  type: "error"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "Approval",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "claimant",
    type: "address"
  }, {
    indexed: false,
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "Claim",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "bool",
    name: "allowDelegatedClaims",
    type: "bool"
  }],
  name: "Initialize",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "pointer",
    type: "address"
  }, {
    components: [{
      internalType: "uint256",
      name: "stackIndex",
      type: "uint256"
    }, {
      internalType: "uint256[]",
      name: "stack",
      type: "uint256[]"
    }, {
      internalType: "bytes[]",
      name: "sources",
      type: "bytes[]"
    }, {
      internalType: "uint256[]",
      name: "constants",
      type: "uint256[]"
    }, {
      internalType: "uint256[]",
      name: "arguments",
      type: "uint256[]"
    }],
    indexed: false,
    internalType: "struct State",
    name: "state",
    type: "tuple"
  }],
  name: "Snapshot",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "startTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "endTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "TierChange",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "Transfer",
  type: "event"
}, {
  inputs: [],
  name: "allowDelegatedClaims",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    internalType: "address",
    name: "spender",
    type: "address"
  }],
  name: "allowance",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "approve",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }],
  name: "balanceOf",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "claimant_",
    type: "address"
  }],
  name: "calculateClaim",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "claimant_",
    type: "address"
  }, {
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "claim",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "decimals",
  outputs: [{
    internalType: "uint8",
    name: "",
    type: "uint8"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "subtractedValue",
    type: "uint256"
  }],
  name: "decreaseAllowance",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "addedValue",
    type: "uint256"
  }],
  name: "increaseAllowance",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "bool",
      name: "allowDelegatedClaims",
      type: "bool"
    }, {
      components: [{
        internalType: "string",
        name: "name",
        type: "string"
      }, {
        internalType: "string",
        name: "symbol",
        type: "string"
      }, {
        internalType: "address",
        name: "distributor",
        type: "address"
      }, {
        internalType: "uint256",
        name: "initialSupply",
        type: "uint256"
      }],
      internalType: "struct ERC20Config",
      name: "erc20Config",
      type: "tuple"
    }, {
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "vmStateConfig",
      type: "tuple"
    }],
    internalType: "struct EmissionsERC20Config",
    name: "config_",
    type: "tuple"
  }],
  name: "initialize",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "name",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account_",
    type: "address"
  }],
  name: "report",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }, {
    internalType: "uint256",
    name: "",
    type: "uint256"
  }, {
    internalType: "bytes",
    name: "",
    type: "bytes"
  }],
  name: "setTier",
  outputs: [],
  stateMutability: "pure",
  type: "function"
}, {
  inputs: [],
  name: "symbol",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "totalSupply",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "transfer",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "transferFrom",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}];
var _bytecode = "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";

var isSuperArgs = function isSuperArgs(xs) {
  return xs.length > 1;
};

var EmissionsERC20__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(EmissionsERC20__factory, _ContractFactory);

  function EmissionsERC20__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$7, _bytecode, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = EmissionsERC20__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  EmissionsERC20__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$7);
  };

  EmissionsERC20__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$7, signerOrProvider);
  };

  return EmissionsERC20__factory;
}(ContractFactory);
EmissionsERC20__factory.bytecode = _bytecode;
EmissionsERC20__factory.abi = _abi$7;

var _abi$8 = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "bool",
      name: "allowDelegatedClaims",
      type: "bool"
    }, {
      components: [{
        internalType: "string",
        name: "name",
        type: "string"
      }, {
        internalType: "string",
        name: "symbol",
        type: "string"
      }, {
        internalType: "address",
        name: "distributor",
        type: "address"
      }, {
        internalType: "uint256",
        name: "initialSupply",
        type: "uint256"
      }],
      internalType: "struct ERC20Config",
      name: "erc20Config",
      type: "tuple"
    }, {
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "vmStateConfig",
      type: "tuple"
    }],
    internalType: "struct EmissionsERC20Config",
    name: "config_",
    type: "tuple"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract EmissionsERC20",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "implementation",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$1 = "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";

var isSuperArgs$1 = function isSuperArgs(xs) {
  return xs.length > 1;
};

var EmissionsERC20Factory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(EmissionsERC20Factory__factory, _ContractFactory);

  function EmissionsERC20Factory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$1(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$8, _bytecode$1, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = EmissionsERC20Factory__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  EmissionsERC20Factory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$8);
  };

  EmissionsERC20Factory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$8, signerOrProvider);
  };

  return EmissionsERC20Factory__factory;
}(ContractFactory);
EmissionsERC20Factory__factory.bytecode = _bytecode$1;
EmissionsERC20Factory__factory.abi = _abi$8;

var _abi$9 = [{
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "depositor",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "sale",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "redeemable",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "token",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "supply",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "Deposit",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "sale",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "redeemable",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "token",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "PendingDeposit",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "depositor",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "sale",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "redeemable",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "token",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "Sweep",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "sale",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "redeemable",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "token",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "supply",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "Undeposit",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "withdrawer",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "sale",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "redeemable",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "token",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "supply",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "Withdraw",
  type: "event"
}, {
  inputs: [{
    internalType: "address",
    name: "sale_",
    type: "address"
  }, {
    internalType: "address",
    name: "token_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount_",
    type: "uint256"
  }],
  name: "deposit",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "sale_",
    type: "address"
  }, {
    internalType: "address",
    name: "token_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount_",
    type: "uint256"
  }],
  name: "depositPending",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "sale_",
    type: "address"
  }, {
    internalType: "address",
    name: "token_",
    type: "address"
  }, {
    internalType: "address",
    name: "depositor_",
    type: "address"
  }],
  name: "sweepPending",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "sale_",
    type: "address"
  }, {
    internalType: "address",
    name: "token_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "supply_",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "amount_",
    type: "uint256"
  }],
  name: "undeposit",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "sale_",
    type: "address"
  }, {
    internalType: "address",
    name: "token_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "supply_",
    type: "uint256"
  }],
  name: "withdraw",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}];
var _bytecode$2 = "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";

var isSuperArgs$2 = function isSuperArgs(xs) {
  return xs.length > 1;
};

var RedeemableERC20ClaimEscrow__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(RedeemableERC20ClaimEscrow__factory, _ContractFactory);

  function RedeemableERC20ClaimEscrow__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$2(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$9, _bytecode$2, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = RedeemableERC20ClaimEscrow__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  RedeemableERC20ClaimEscrow__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$9);
  };

  RedeemableERC20ClaimEscrow__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$9, signerOrProvider);
  };

  return RedeemableERC20ClaimEscrow__factory;
}(ContractFactory);
RedeemableERC20ClaimEscrow__factory.bytecode = _bytecode$2;
RedeemableERC20ClaimEscrow__factory.abi = _abi$9;

var _abi$a = [{
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "subject",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    indexed: false,
    internalType: "struct Notice",
    name: "notice",
    type: "tuple"
  }],
  name: "NewNotice",
  type: "event"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "subject",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    internalType: "struct Notice[]",
    name: "notices_",
    type: "tuple[]"
  }],
  name: "createNotices",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}];
var _bytecode$3 = "0x608060405234801561001057600080fd5b50610340806100206000396000f3fe608060405234801561001057600080fd5b506004361061002b5760003560e01c806376d6500014610030575b600080fd5b61004361003e3660046100c3565b610045565b005b60005b818110156100be577faa4518b627c102261dfb05cfe2e978716392d85d3ba1a5fbeb7a0da1ca9d0c3f3384848481811061008457610084610138565b90506020028101906100969190610167565b6040516100a49291906101ee565b60405180910390a1806100b6816102aa565b915050610048565b505050565b600080602083850312156100d657600080fd5b823567ffffffffffffffff808211156100ee57600080fd5b818501915085601f83011261010257600080fd5b81358181111561011157600080fd5b8660208260051b850101111561012657600080fd5b60209290920196919550909350505050565b7f4e487b7100000000000000000000000000000000000000000000000000000000600052603260045260246000fd5b600082357fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffc183360301811261019b57600080fd5b9190910192915050565b8183528181602085013750600060208284010152600060207fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0601f840116840101905092915050565b600073ffffffffffffffffffffffffffffffffffffffff8085168352604060208401528335818116915081811461022457600080fd5b5060408301526020830135368490037fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe101811261026057600080fd5b8301803567ffffffffffffffff81111561027957600080fd5b80360385131561028857600080fd5b604060608501526102a06080850182602085016101a5565b9695505050505050565b60007fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff821415610303577f4e487b7100000000000000000000000000000000000000000000000000000000600052601160045260246000fd5b506001019056fea26469706673582212206c5b7ca6e17320f19aba7d8057d9d9d29ecce801ad8c8949c8029809b5df685d64736f6c634300080a0033";

var isSuperArgs$3 = function isSuperArgs(xs) {
  return xs.length > 1;
};

var NoticeBoard__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(NoticeBoard__factory, _ContractFactory);

  function NoticeBoard__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$3(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$a, _bytecode$3, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = NoticeBoard__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  NoticeBoard__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$a);
  };

  NoticeBoard__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$a, signerOrProvider);
  };

  return NoticeBoard__factory;
}(ContractFactory);
NoticeBoard__factory.bytecode = _bytecode$3;
NoticeBoard__factory.abi = _abi$a;

var _abi$b = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "approved",
    type: "address"
  }, {
    indexed: true,
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "Approval",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    indexed: false,
    internalType: "bool",
    name: "approved",
    type: "bool"
  }],
  name: "ApprovalForAll",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "contractAddress",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "creator",
    type: "address"
  }, {
    components: [{
      internalType: "string",
      name: "name",
      type: "string"
    }, {
      internalType: "string",
      name: "symbol",
      type: "string"
    }, {
      internalType: "string",
      name: "description",
      type: "string"
    }, {
      internalType: "string",
      name: "animationUrl",
      type: "string"
    }, {
      internalType: "string",
      name: "imageUrl",
      type: "string"
    }, {
      internalType: "bytes32",
      name: "animationHash",
      type: "bytes32"
    }, {
      internalType: "bytes32",
      name: "imageHash",
      type: "bytes32"
    }],
    indexed: false,
    internalType: "struct GatedNFT.Config",
    name: "config",
    type: "tuple"
  }, {
    indexed: false,
    internalType: "contract ITier",
    name: "tier",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "minimumStatus",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "maxPerAddress",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "enum GatedNFT.Transferrable",
    name: "transferrable",
    type: "uint8"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "maxMintable",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "address",
    name: "royaltyRecipient",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "royaltyBPS",
    type: "uint256"
  }],
  name: "CreatedGatedNFT",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "previousOwner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "newOwner",
    type: "address"
  }],
  name: "OwnershipTransferred",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    indexed: true,
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "Transfer",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "royaltyRecipient",
    type: "address"
  }],
  name: "UpdatedRoyaltyRecipient",
  type: "event"
}, {
  inputs: [{
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "approve",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }],
  name: "balanceOf",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "getApproved",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "owner_",
    type: "address"
  }, {
    components: [{
      internalType: "string",
      name: "name",
      type: "string"
    }, {
      internalType: "string",
      name: "symbol",
      type: "string"
    }, {
      internalType: "string",
      name: "description",
      type: "string"
    }, {
      internalType: "string",
      name: "animationUrl",
      type: "string"
    }, {
      internalType: "string",
      name: "imageUrl",
      type: "string"
    }, {
      internalType: "bytes32",
      name: "animationHash",
      type: "bytes32"
    }, {
      internalType: "bytes32",
      name: "imageHash",
      type: "bytes32"
    }],
    internalType: "struct GatedNFT.Config",
    name: "config_",
    type: "tuple"
  }, {
    internalType: "contract ITier",
    name: "tier_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "minimumStatus_",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "maxPerAddress_",
    type: "uint256"
  }, {
    internalType: "enum GatedNFT.Transferrable",
    name: "transferrable_",
    type: "uint8"
  }, {
    internalType: "uint256",
    name: "maxMintable_",
    type: "uint256"
  }, {
    internalType: "address",
    name: "royaltyRecipient_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "royaltyBPS_",
    type: "uint256"
  }],
  name: "initialize",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    internalType: "address",
    name: "operator",
    type: "address"
  }],
  name: "isApprovedForAll",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "to",
    type: "address"
  }],
  name: "mint",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "name",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "owner",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "ownerOf",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "renounceOwnership",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "salePrice_",
    type: "uint256"
  }],
  name: "royaltyInfo",
  outputs: [{
    internalType: "address",
    name: "receiver",
    type: "address"
  }, {
    internalType: "uint256",
    name: "royaltyAmount",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "safeTransferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }, {
    internalType: "bytes",
    name: "_data",
    type: "bytes"
  }],
  name: "safeTransferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "operator",
    type: "address"
  }, {
    internalType: "bool",
    name: "approved",
    type: "bool"
  }],
  name: "setApprovalForAll",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes4",
    name: "interfaceId",
    type: "bytes4"
  }],
  name: "supportsInterface",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "symbol",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "tier",
  outputs: [{
    internalType: "contract ITier",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "tokenURI",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "totalSupply",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "tokenId",
    type: "uint256"
  }],
  name: "transferFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "newOwner",
    type: "address"
  }],
  name: "transferOwnership",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "royaltyRecipient_",
    type: "address"
  }],
  name: "updateRoyaltyRecipient",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}];
var _bytecode$4 = "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";

var isSuperArgs$4 = function isSuperArgs(xs) {
  return xs.length > 1;
};

var GatedNFT__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(GatedNFT__factory, _ContractFactory);

  function GatedNFT__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$4(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$b, _bytecode$4, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = GatedNFT__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  GatedNFT__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$b);
  };

  GatedNFT__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$b, signerOrProvider);
  };

  return GatedNFT__factory;
}(ContractFactory);
GatedNFT__factory.bytecode = _bytecode$4;
GatedNFT__factory.abi = _abi$b;

var _abi$c = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "string",
      name: "name",
      type: "string"
    }, {
      internalType: "string",
      name: "symbol",
      type: "string"
    }, {
      internalType: "string",
      name: "description",
      type: "string"
    }, {
      internalType: "string",
      name: "animationUrl",
      type: "string"
    }, {
      internalType: "string",
      name: "imageUrl",
      type: "string"
    }, {
      internalType: "bytes32",
      name: "animationHash",
      type: "bytes32"
    }, {
      internalType: "bytes32",
      name: "imageHash",
      type: "bytes32"
    }],
    internalType: "struct GatedNFT.Config",
    name: "config_",
    type: "tuple"
  }, {
    internalType: "contract ITier",
    name: "tier_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "minimumStatus_",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "maxPerAddress_",
    type: "uint256"
  }, {
    internalType: "enum GatedNFT.Transferrable",
    name: "transferrable_",
    type: "uint8"
  }, {
    internalType: "uint256",
    name: "maxMintable_",
    type: "uint256"
  }, {
    internalType: "address",
    name: "royaltyRecipient_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "royaltyBPS",
    type: "uint256"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract GatedNFT",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$5 = "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";

var isSuperArgs$5 = function isSuperArgs(xs) {
  return xs.length > 1;
};

var GatedNFTFactory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(GatedNFTFactory__factory, _ContractFactory);

  function GatedNFTFactory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$5(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$c, _bytecode$5, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = GatedNFTFactory__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  GatedNFTFactory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$c);
  };

  GatedNFTFactory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$c, signerOrProvider);
  };

  return GatedNFTFactory__factory;
}(ContractFactory);
GatedNFTFactory__factory.bytecode = _bytecode$5;
GatedNFTFactory__factory.abi = _abi$c;

var _abi$d = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "Approval",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "tokenSender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "token",
    type: "address"
  }],
  name: "ERC20PullInitialize",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "reserve",
      type: "address"
    }, {
      components: [{
        internalType: "string",
        name: "name",
        type: "string"
      }, {
        internalType: "string",
        name: "symbol",
        type: "string"
      }, {
        internalType: "address",
        name: "distributor",
        type: "address"
      }, {
        internalType: "uint256",
        name: "initialSupply",
        type: "uint256"
      }],
      internalType: "struct ERC20Config",
      name: "erc20Config",
      type: "tuple"
    }, {
      internalType: "address",
      name: "tier",
      type: "address"
    }, {
      internalType: "uint256",
      name: "minimumTier",
      type: "uint256"
    }, {
      internalType: "address",
      name: "distributionEndForwardingAddress",
      type: "address"
    }],
    indexed: false,
    internalType: "struct RedeemableERC20Config",
    name: "config",
    type: "tuple"
  }],
  name: "Initialize",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "newPhase",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "scheduledBlock",
    type: "uint256"
  }],
  name: "PhaseScheduled",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "grantedReceiver",
    type: "address"
  }],
  name: "Receiver",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "treasuryAsset",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "redeemAmount",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "assetAmount",
    type: "uint256"
  }],
  name: "Redeem",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "grantedSender",
    type: "address"
  }],
  name: "Sender",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "value",
    type: "uint256"
  }],
  name: "Transfer",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "asset",
    type: "address"
  }],
  name: "TreasuryAsset",
  type: "event"
}, {
  inputs: [{
    internalType: "address",
    name: "owner",
    type: "address"
  }, {
    internalType: "address",
    name: "spender",
    type: "address"
  }],
  name: "allowance",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "approve",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }],
  name: "balanceOf",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint32[8]",
    name: "phaseBlocks_",
    type: "uint32[8]"
  }, {
    internalType: "uint256",
    name: "phase_",
    type: "uint256"
  }],
  name: "blockNumberForPhase",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "pure",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "burn",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "burnFrom",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "currentPhase",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "decimals",
  outputs: [{
    internalType: "uint8",
    name: "",
    type: "uint8"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "subtractedValue",
    type: "uint256"
  }],
  name: "decreaseAllowance",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "distributor_",
    type: "address"
  }],
  name: "endDistribution",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "newReceiver_",
    type: "address"
  }],
  name: "grantReceiver",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "newSender_",
    type: "address"
  }],
  name: "grantSender",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "spender",
    type: "address"
  }, {
    internalType: "uint256",
    name: "addedValue",
    type: "uint256"
  }],
  name: "increaseAllowance",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "reserve",
      type: "address"
    }, {
      components: [{
        internalType: "string",
        name: "name",
        type: "string"
      }, {
        internalType: "string",
        name: "symbol",
        type: "string"
      }, {
        internalType: "address",
        name: "distributor",
        type: "address"
      }, {
        internalType: "uint256",
        name: "initialSupply",
        type: "uint256"
      }],
      internalType: "struct ERC20Config",
      name: "erc20Config",
      type: "tuple"
    }, {
      internalType: "address",
      name: "tier",
      type: "address"
    }, {
      internalType: "uint256",
      name: "minimumTier",
      type: "uint256"
    }, {
      internalType: "address",
      name: "distributionEndForwardingAddress",
      type: "address"
    }],
    internalType: "struct RedeemableERC20Config",
    name: "config_",
    type: "tuple"
  }],
  name: "initialize",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeReceiver_",
    type: "address"
  }],
  name: "isReceiver",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeSender_",
    type: "address"
  }],
  name: "isSender",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "minimumTier",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "name",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "newTreasuryAsset_",
    type: "address"
  }],
  name: "newTreasuryAsset",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "uint32[8]",
    name: "phaseBlocks_",
    type: "uint32[8]"
  }, {
    internalType: "uint256",
    name: "blockNumber_",
    type: "uint256"
  }],
  name: "phaseAtBlockNumber",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "pure",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  name: "phaseBlocks",
  outputs: [{
    internalType: "uint32",
    name: "",
    type: "uint32"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "amount_",
    type: "uint256"
  }],
  name: "pullERC20",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "contract IERC20[]",
    name: "treasuryAssets_",
    type: "address[]"
  }, {
    internalType: "uint256",
    name: "redeemAmount_",
    type: "uint256"
  }],
  name: "redeem",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "symbol",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "tier",
  outputs: [{
    internalType: "contract ITier",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "totalSupply",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "transfer",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "from",
    type: "address"
  }, {
    internalType: "address",
    name: "to",
    type: "address"
  }, {
    internalType: "uint256",
    name: "amount",
    type: "uint256"
  }],
  name: "transferFrom",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "nonpayable",
  type: "function"
}];
var _bytecode$6 = "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";

var isSuperArgs$6 = function isSuperArgs(xs) {
  return xs.length > 1;
};

var RedeemableERC20__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(RedeemableERC20__factory, _ContractFactory);

  function RedeemableERC20__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$6(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$d, _bytecode$6, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = RedeemableERC20__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  RedeemableERC20__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$d);
  };

  RedeemableERC20__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$d, signerOrProvider);
  };

  return RedeemableERC20__factory;
}(ContractFactory);
RedeemableERC20__factory.bytecode = _bytecode$6;
RedeemableERC20__factory.abi = _abi$d;

var _abi$e = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "reserve",
      type: "address"
    }, {
      components: [{
        internalType: "string",
        name: "name",
        type: "string"
      }, {
        internalType: "string",
        name: "symbol",
        type: "string"
      }, {
        internalType: "address",
        name: "distributor",
        type: "address"
      }, {
        internalType: "uint256",
        name: "initialSupply",
        type: "uint256"
      }],
      internalType: "struct ERC20Config",
      name: "erc20Config",
      type: "tuple"
    }, {
      internalType: "address",
      name: "tier",
      type: "address"
    }, {
      internalType: "uint256",
      name: "minimumTier",
      type: "uint256"
    }, {
      internalType: "address",
      name: "distributionEndForwardingAddress",
      type: "address"
    }],
    internalType: "struct RedeemableERC20Config",
    name: "config_",
    type: "tuple"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract RedeemableERC20",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "implementation",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$7 = "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";

var isSuperArgs$7 = function isSuperArgs(xs) {
  return xs.length > 1;
};

var RedeemableERC20Factory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(RedeemableERC20Factory__factory, _ContractFactory);

  function RedeemableERC20Factory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$7(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$e, _bytecode$7, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = RedeemableERC20Factory__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  RedeemableERC20Factory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$e);
  };

  RedeemableERC20Factory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$e, signerOrProvider);
  };

  return RedeemableERC20Factory__factory;
}(ContractFactory);
RedeemableERC20Factory__factory.bytecode = _bytecode$7;
RedeemableERC20Factory__factory.abi = _abi$e;

var _abi$f = [{
  inputs: [{
    components: [{
      internalType: "uint256",
      name: "maximumSaleTimeout",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "maximumCooldownDuration",
      type: "uint256"
    }, {
      internalType: "contract RedeemableERC20Factory",
      name: "redeemableERC20Factory",
      type: "address"
    }],
    internalType: "struct SaleConstructorConfig",
    name: "config_",
    type: "tuple"
  }],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  inputs: [{
    internalType: "uint256",
    name: "_size",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_start",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_end",
    type: "uint256"
  }],
  name: "InvalidCodeAtRange",
  type: "error"
}, {
  inputs: [],
  name: "WriteError",
  type: "error"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "feeRecipient",
      type: "address"
    }, {
      internalType: "uint256",
      name: "fee",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "minimumUnits",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "desiredUnits",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "maximumPrice",
      type: "uint256"
    }],
    indexed: false,
    internalType: "struct BuyConfig",
    name: "config",
    type: "tuple"
  }, {
    components: [{
      internalType: "uint256",
      name: "id",
      type: "uint256"
    }, {
      internalType: "address",
      name: "feeRecipient",
      type: "address"
    }, {
      internalType: "uint256",
      name: "fee",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "units",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "price",
      type: "uint256"
    }],
    indexed: false,
    internalType: "struct Receipt",
    name: "receipt",
    type: "tuple"
  }],
  name: "Buy",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "uint256",
      name: "maximumSaleTimeout",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "maximumCooldownDuration",
      type: "uint256"
    }, {
      internalType: "contract RedeemableERC20Factory",
      name: "redeemableERC20Factory",
      type: "address"
    }],
    indexed: false,
    internalType: "struct SaleConstructorConfig",
    name: "config",
    type: "tuple"
  }],
  name: "Construct",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "cooldownDuration",
    type: "uint256"
  }],
  name: "CooldownInitialize",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "caller",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "cooldown",
    type: "uint256"
  }],
  name: "CooldownTriggered",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "enum SaleStatus",
    name: "saleStatus",
    type: "uint8"
  }],
  name: "End",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "canStartStateConfig",
      type: "tuple"
    }, {
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "canEndStateConfig",
      type: "tuple"
    }, {
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "calculatePriceStateConfig",
      type: "tuple"
    }, {
      internalType: "address",
      name: "recipient",
      type: "address"
    }, {
      internalType: "contract IERC20",
      name: "reserve",
      type: "address"
    }, {
      internalType: "uint256",
      name: "saleTimeout",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "cooldownDuration",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "minimumRaise",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "dustSize",
      type: "uint256"
    }],
    indexed: false,
    internalType: "struct SaleConfig",
    name: "config",
    type: "tuple"
  }, {
    indexed: false,
    internalType: "address",
    name: "token",
    type: "address"
  }],
  name: "Initialize",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "uint256",
      name: "id",
      type: "uint256"
    }, {
      internalType: "address",
      name: "feeRecipient",
      type: "address"
    }, {
      internalType: "uint256",
      name: "fee",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "units",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "price",
      type: "uint256"
    }],
    indexed: false,
    internalType: "struct Receipt",
    name: "receipt",
    type: "tuple"
  }],
  name: "Refund",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "pointer",
    type: "address"
  }, {
    components: [{
      internalType: "uint256",
      name: "stackIndex",
      type: "uint256"
    }, {
      internalType: "uint256[]",
      name: "stack",
      type: "uint256[]"
    }, {
      internalType: "bytes[]",
      name: "sources",
      type: "bytes[]"
    }, {
      internalType: "uint256[]",
      name: "constants",
      type: "uint256[]"
    }, {
      internalType: "uint256[]",
      name: "arguments",
      type: "uint256[]"
    }],
    indexed: false,
    internalType: "struct State",
    name: "state",
    type: "tuple"
  }],
  name: "Snapshot",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }],
  name: "Start",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }],
  name: "Timeout",
  type: "event"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "feeRecipient",
      type: "address"
    }, {
      internalType: "uint256",
      name: "fee",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "minimumUnits",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "desiredUnits",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "maximumPrice",
      type: "uint256"
    }],
    internalType: "struct BuyConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "buy",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "units_",
    type: "uint256"
  }],
  name: "calculatePrice",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "canEnd",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "canStart",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "recipient_",
    type: "address"
  }],
  name: "claimFees",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "end",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "canStartStateConfig",
      type: "tuple"
    }, {
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "canEndStateConfig",
      type: "tuple"
    }, {
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "calculatePriceStateConfig",
      type: "tuple"
    }, {
      internalType: "address",
      name: "recipient",
      type: "address"
    }, {
      internalType: "contract IERC20",
      name: "reserve",
      type: "address"
    }, {
      internalType: "uint256",
      name: "saleTimeout",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "cooldownDuration",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "minimumRaise",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "dustSize",
      type: "uint256"
    }],
    internalType: "struct SaleConfig",
    name: "config_",
    type: "tuple"
  }, {
    components: [{
      components: [{
        internalType: "string",
        name: "name",
        type: "string"
      }, {
        internalType: "string",
        name: "symbol",
        type: "string"
      }, {
        internalType: "address",
        name: "distributor",
        type: "address"
      }, {
        internalType: "uint256",
        name: "initialSupply",
        type: "uint256"
      }],
      internalType: "struct ERC20Config",
      name: "erc20Config",
      type: "tuple"
    }, {
      internalType: "address",
      name: "tier",
      type: "address"
    }, {
      internalType: "uint256",
      name: "minimumTier",
      type: "uint256"
    }, {
      internalType: "address",
      name: "distributionEndForwardingAddress",
      type: "address"
    }],
    internalType: "struct SaleRedeemableERC20Config",
    name: "saleRedeemableERC20Config_",
    type: "tuple"
  }],
  name: "initialize",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "uint256",
      name: "id",
      type: "uint256"
    }, {
      internalType: "address",
      name: "feeRecipient",
      type: "address"
    }, {
      internalType: "uint256",
      name: "fee",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "units",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "price",
      type: "uint256"
    }],
    internalType: "struct Receipt",
    name: "receipt_",
    type: "tuple"
  }],
  name: "refund",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "reserve",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "saleStatus",
  outputs: [{
    internalType: "enum SaleStatus",
    name: "",
    type: "uint8"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "start",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "timeout",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "token",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$8 = "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";

var isSuperArgs$8 = function isSuperArgs(xs) {
  return xs.length > 1;
};

var Sale__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(Sale__factory, _ContractFactory);

  function Sale__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$8(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$f, _bytecode$8, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = Sale__factory.prototype;

  _proto.deploy = function deploy(config_, overrides) {
    return _ContractFactory.prototype.deploy.call(this, config_, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(config_, overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, config_, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  Sale__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$f);
  };

  Sale__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$f, signerOrProvider);
  };

  return Sale__factory;
}(ContractFactory);
Sale__factory.bytecode = _bytecode$8;
Sale__factory.abi = _abi$f;

var _abi$g = [{
  inputs: [{
    components: [{
      internalType: "uint256",
      name: "maximumSaleTimeout",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "maximumCooldownDuration",
      type: "uint256"
    }, {
      internalType: "contract RedeemableERC20Factory",
      name: "redeemableERC20Factory",
      type: "address"
    }],
    internalType: "struct SaleConstructorConfig",
    name: "config_",
    type: "tuple"
  }],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "canStartStateConfig",
      type: "tuple"
    }, {
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "canEndStateConfig",
      type: "tuple"
    }, {
      components: [{
        internalType: "bytes[]",
        name: "sources",
        type: "bytes[]"
      }, {
        internalType: "uint256[]",
        name: "constants",
        type: "uint256[]"
      }, {
        internalType: "uint256",
        name: "stackLength",
        type: "uint256"
      }, {
        internalType: "uint256",
        name: "argumentsLength",
        type: "uint256"
      }],
      internalType: "struct StateConfig",
      name: "calculatePriceStateConfig",
      type: "tuple"
    }, {
      internalType: "address",
      name: "recipient",
      type: "address"
    }, {
      internalType: "contract IERC20",
      name: "reserve",
      type: "address"
    }, {
      internalType: "uint256",
      name: "saleTimeout",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "cooldownDuration",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "minimumRaise",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "dustSize",
      type: "uint256"
    }],
    internalType: "struct SaleConfig",
    name: "config_",
    type: "tuple"
  }, {
    components: [{
      components: [{
        internalType: "string",
        name: "name",
        type: "string"
      }, {
        internalType: "string",
        name: "symbol",
        type: "string"
      }, {
        internalType: "address",
        name: "distributor",
        type: "address"
      }, {
        internalType: "uint256",
        name: "initialSupply",
        type: "uint256"
      }],
      internalType: "struct ERC20Config",
      name: "erc20Config",
      type: "tuple"
    }, {
      internalType: "address",
      name: "tier",
      type: "address"
    }, {
      internalType: "uint256",
      name: "minimumTier",
      type: "uint256"
    }, {
      internalType: "address",
      name: "distributionEndForwardingAddress",
      type: "address"
    }],
    internalType: "struct SaleRedeemableERC20Config",
    name: "saleRedeemableERC20Config_",
    type: "tuple"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract Sale",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$9 = "0x60a060405234801561001057600080fd5b50604051620074703803806200747083398101604081905261003191610137565b60016000908155604051829061004690610129565b81518152602080830151908201526040918201516001600160a01b031682820152905190819003606001906000f080158015610086573d6000803e3d6000fd5b5090506001600160a01b0381166100d65760405162461bcd60e51b815260206004820152601060248201526f182fa4a6a82622a6a2a72a20aa24a7a760811b604482015260640160405180910390fd5b604080513381526001600160a01b03831660208201527f7fac34ecf0b1b374cd9659e3d17db602659d53f1162b1f20142e6c02694aed70910160405180910390a16001600160a01b0316608052506101b0565b615f3d806200153383390190565b60006060828403121561014957600080fd5b604051606081016001600160401b038111828210171561017957634e487b7160e01b600052604160045260246000fd5b604090815283518252602080850151908301528301516001600160a01b03811681146101a457600080fd5b60408201529392505050565b608051611367620001cc600039600061034e01526113676000f3fe608060405234801561001057600080fd5b50600436106100415760003560e01c8063079fb762146100465780632ea72a4914610083578063fc91a89714610096575b600080fd5b6100596100543660046104e7565b6100df565b60405173ffffffffffffffffffffffffffffffffffffffff90911681526020015b60405180910390f35b61005961009136600461055b565b610185565b6100cf6100a43660046105fd565b73ffffffffffffffffffffffffffffffffffffffff1660009081526001602052604090205460ff1690565b604051901515815260200161007a565b60003073ffffffffffffffffffffffffffffffffffffffff16632ea72a4984846040516020016101109291906109a2565b6040516020818303038152906040526040518263ffffffff1660e01b815260040161013b9190610b37565b6020604051808303816000875af115801561015a573d6000803e3d6000fd5b505050506040513d601f19601f8201168201806040525081019061017e9190610b4a565b9392505050565b6000600260005414156101f9576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152601f60248201527f5265656e7472616e637947756172643a207265656e7472616e742063616c6c0060448201526064015b60405180910390fd5b6002600090815561020a8484610333565b73ffffffffffffffffffffffffffffffffffffffff811660009081526001602052604090205490915060ff161561029d576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600f60248201527f4455504c49434154455f4348494c44000000000000000000000000000000000060448201526064016101f0565b73ffffffffffffffffffffffffffffffffffffffff811660008181526001602081815260409283902080547fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff00169092179091558151338152908101929092527f7da70c4e5387d7038610b79ca7d304caaef815826e51e67cf247135387a79bce910160405180910390a160016000559392505050565b6000808061034384860186610f4a565b9150915060006103727f0000000000000000000000000000000000000000000000000000000000000000610405565b6040517f7939b0fe00000000000000000000000000000000000000000000000000000000815290915073ffffffffffffffffffffffffffffffffffffffff821690637939b0fe906103c9908690869060040161122c565b600060405180830381600087803b1580156103e357600080fd5b505af11580156103f7573d6000803e3d6000fd5b509298975050505050505050565b60006040517f3d602d80600a3d3981f3363d3d373d3d3d363d7300000000000000000000000081528260601b60148201527f5af43d82803e903d91602b57fd5bf3000000000000000000000000000000000060288201526037816000f091505073ffffffffffffffffffffffffffffffffffffffff81166104e2576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152601660248201527f455243313136373a20637265617465206661696c65640000000000000000000060448201526064016101f0565b919050565b600080604083850312156104fa57600080fd5b823567ffffffffffffffff8082111561051257600080fd5b90840190610120828703121561052757600080fd5b9092506020840135908082111561053d57600080fd5b5083016080818603121561055057600080fd5b809150509250929050565b6000806020838503121561056e57600080fd5b823567ffffffffffffffff8082111561058657600080fd5b818501915085601f83011261059a57600080fd5b8135818111156105a957600080fd5b8660208285010111156105bb57600080fd5b60209290920196919550909350505050565b73ffffffffffffffffffffffffffffffffffffffff811681146105ef57600080fd5b50565b80356104e2816105cd565b60006020828403121561060f57600080fd5b813561017e816105cd565b600082357fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff8183360301811261064e57600080fd5b90910192915050565b60008083357fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe184360301811261068c57600080fd5b830160208101925035905067ffffffffffffffff8111156106ac57600080fd5b8060051b36038313156106be57600080fd5b9250929050565b8183528181602085013750600060208284010152600060207fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0601f840116840101905092915050565b60008083357fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe184360301811261074357600080fd5b830160208101925035905067ffffffffffffffff81111561076357600080fd5b8036038313156106be57600080fd5b81835260007f07ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff8311156107a457600080fd5b8260051b8083602087013760009401602001938452509192915050565b6000608083016107d18384610657565b608086528281845260a08701905060a08260051b88010193508260005b83811015610849577fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff60898703018352610827828661070e565b6108328882846106c5565b9750505060209283019291909101906001016107ee565b505050505061085b6020840184610657565b858303602087015261086e838284610772565b9250505060408301356040850152606083013560608501528091505092915050565b600061089c828361061a565b608084526108aa818261070e565b6080808701526108bf610100870182846106c5565b9150506108cf602083018361070e565b7fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff808784030160a08801526109048382846106c5565b925050506040820135610916816105cd565b73ffffffffffffffffffffffffffffffffffffffff1660c0860152606082013560e0860152610947602085016105f2565b73ffffffffffffffffffffffffffffffffffffffff8116602087015291506040840135604086015261097b606085016105f2565b73ffffffffffffffffffffffffffffffffffffffff8116606087015291505b949350505050565b6040815260006109b2848561061a565b6101208060408501526109c96101608501836107c1565b91506109d8602087018761061a565b7fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffc080868503016060870152610a0d84836107c1565b9350610a1c604089018961061a565b91508086850301608087015250610a3383826107c1565b925050610a42606087016105f2565b73ffffffffffffffffffffffffffffffffffffffff1660a0850152610a69608087016105f2565b73ffffffffffffffffffffffffffffffffffffffff811660c08601525060a086013560e085015261010060c08701358186015260e0870135828601528087013561014086015250508281036020840152610ac38185610890565b95945050505050565b6000815180845260005b81811015610af257602081850181015186830182015201610ad6565b81811115610b04576000602083870101525b50601f017fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0169290920160200192915050565b60208152600061017e6020830184610acc565b600060208284031215610b5c57600080fd5b815161017e816105cd565b7f4e487b7100000000000000000000000000000000000000000000000000000000600052604160045260246000fd5b6040516080810167ffffffffffffffff81118282101715610bb957610bb9610b67565b60405290565b604051610120810167ffffffffffffffff81118282101715610bb957610bb9610b67565b604051601f82017fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe016810167ffffffffffffffff81118282101715610c2a57610c2a610b67565b604052919050565b600067ffffffffffffffff821115610c4c57610c4c610b67565b5060051b60200190565b600067ffffffffffffffff831115610c7057610c70610b67565b610ca160207fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0601f86011601610be3565b9050828152838383011115610cb557600080fd5b828260208301376000602084830101529392505050565b600082601f830112610cdd57600080fd5b81356020610cf2610ced83610c32565b610be3565b82815260059290921b84018101918181019086841115610d1157600080fd5b8286015b84811015610d2c5780358352918301918301610d15565b509695505050505050565b600060808284031215610d4957600080fd5b610d51610b96565b9050813567ffffffffffffffff80821115610d6b57600080fd5b818401915084601f830112610d7f57600080fd5b81356020610d8f610ced83610c32565b82815260059290921b84018101918181019088841115610dae57600080fd5b8286015b84811015610dfb57803586811115610dca5760008081fd5b8701603f81018b13610ddc5760008081fd5b610ded8b8683013560408401610c56565b845250918301918301610db2565b5086525085810135935082841115610e1257600080fd5b610e1e87858801610ccc565b8186015250505050604082013560408201526060820135606082015292915050565b600082601f830112610e5157600080fd5b61017e83833560208501610c56565b600060808284031215610e7257600080fd5b610e7a610b96565b9050813567ffffffffffffffff80821115610e9457600080fd5b9083019060808286031215610ea857600080fd5b610eb0610b96565b823582811115610ebf57600080fd5b610ecb87828601610e40565b825250602083013582811115610ee057600080fd5b610eec87828601610e40565b60208301525060408301359150610f02826105cd565b81604082015260608301356060820152808452505050610f24602083016105f2565b602082015260408201356040820152610f3f606083016105f2565b606082015292915050565b60008060408385031215610f5d57600080fd5b823567ffffffffffffffff80821115610f7557600080fd5b908401906101208287031215610f8a57600080fd5b610f92610bbf565b823582811115610fa157600080fd5b610fad88828601610d37565b825250602083013582811115610fc257600080fd5b610fce88828601610d37565b602083015250604083013582811115610fe657600080fd5b610ff288828601610d37565b604083015250611004606084016105f2565b6060820152611015608084016105f2565b608082015260a083013560a082015260c083013560c082015260e083013560e082015261010080840135818301525080945050602085013591508082111561105c57600080fd5b5061106985828601610e60565b9150509250929050565b600081518084526020808501945080840160005b838110156110a357815187529582019590820190600101611087565b509495945050505050565b60006080830182516080855281815180845260a08701915060a08160051b88010193506020808401935060005b82811015611127577fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff60898703018452611115868651610acc565b955093810193928101926001016110db565b50808701519350878503818901525050506111428282611073565b91505060408301516040850152606083015160608501528091505092915050565b60008151608084528051608080860152611181610100860182610acc565b905060208201517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff808683030160a08701526111bc8282610acc565b915050604082015173ffffffffffffffffffffffffffffffffffffffff80821660c0880152606084015160e08801528060208701511660208801525050604084015160408601526060840151915061099a606086018373ffffffffffffffffffffffffffffffffffffffff169052565b604081526000835161012080604085015261124b6101608501836110ae565b915060208601517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffc08086850301606087015261128784836110ae565b93506040880151915080868503016080870152506112a583826110ae565b92505060608601516112cf60a086018273ffffffffffffffffffffffffffffffffffffffff169052565b50608086015173ffffffffffffffffffffffffffffffffffffffff811660c08601525060a086015160e085015260c0860151610100818187015260e088015183870152808801516101408701525050508281036020840152610ac3818561116356fea2646970667358221220c60ef6184818a000b5ca8e3725035d37948d0d2d683ea6ffdf6ddb8afb0d3fe164736f6c634300080a00336101006040523480156200001257600080fd5b5060405162005f3d38038062005f3d833981016040819052620000359162000126565b6001600481905550600280600260066007600b60076004620000589190620001a2565b620000649190620001a2565b620000709190620001a2565b6200007c9190620001a2565b620000889190620001a2565b620000949190620001a2565b620000a09190620001a2565b620000ad906005620001a2565b6080908152815160a0526020808301805160c052604080850180516001600160a01b0390811660e0528251338152875195810195909552925191840191909152511660608201527febb7bbe68143e887fe137da12458b32f7aec9b8aa0030b338fb7758a798daf9a910160405180910390a150620001c9565b6000606082840312156200013957600080fd5b604051606081016001600160401b03811182821017156200016a57634e487b7160e01b600052604160045260246000fd5b604090815283518252602080850151908301528301516001600160a01b03811681146200019657600080fd5b60408201529392505050565b60008219821115620001c457634e487b7160e01b600052601160045260246000fd5b500190565b60805160a05160c05160e051615d336200020a6000396000610c87015260006108bb01526000610956015260008181612552015261258d0152615d336000f3fe608060405234801561001057600080fd5b50600436106100df5760003560e01c8063be9a65551161008c578063ec30903811610066578063ec309038146101bf578063efbe1c1c146101c7578063f9020e33146101cf578063fc0c546a146101fa57600080fd5b8063be9a655514610165578063cd3293de1461016d578063d48827e9146101ac57600080fd5b80637939b0fe116100bd5780637939b0fe14610114578063abf3969e14610127578063ae1042651461014457600080fd5b806315a0ea6a146100e4578063504d14f8146100f957806370dea79a1461010c575b600080fd5b6100f76100f23660046149d3565b610218565b005b6100f76101073660046149f0565b61031b565b6100f7610597565b6100f7610122366004614e11565b6107cf565b61012f610ddc565b60405190151581526020015b60405180910390f35b610157610152366004614f3a565b610ea5565b60405190815260200161013b565b6100f7610f2e565b600c5473ffffffffffffffffffffffffffffffffffffffff165b60405173ffffffffffffffffffffffffffffffffffffffff909116815260200161013b565b6100f76101ba366004614f53565b611010565b61012f611700565b6100f761175d565b600c5474010000000000000000000000000000000000000000900460ff1660405161013b9190615036565b60055473ffffffffffffffffffffffffffffffffffffffff16610187565b6002600c5474010000000000000000000000000000000000000000900460ff16600381111561024957610249614fcc565b146102b5576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600b60248201527f4e4f545f5355434345535300000000000000000000000000000000000000000060448201526064015b60405180910390fd5b73ffffffffffffffffffffffffffffffffffffffff811660009081526012602052604090205480156103175773ffffffffffffffffffffffffffffffffffffffff808316600090815260126020526040812055600c546103179116838361192d565b5050565b6002600c5474010000000000000000000000000000000000000000900460ff16600381111561034c5761034c614fcc565b14156103b4576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600e60248201527f524546554e445f5355434345535300000000000000000000000000000000000060448201526064016102ac565b6003600c5474010000000000000000000000000000000000000000900460ff1660038111156103e5576103e5614fcc565b146103f2576103f2611a01565b336000908152601060209081526040808320905190929161041591859101615090565b604051602081830303815290604052805190602001208152602001908152602001600020600081548092919061044a906150cd565b909155506000905061046460808301356060840135611ba0565b905080600f60008282546104789190615102565b925050819055508160600135600e60008282546104959190615119565b9091555050604082018035906012906000906104b490602087016149d3565b73ffffffffffffffffffffffffffffffffffffffff1673ffffffffffffffffffffffffffffffffffffffff16815260200190815260200160002060008282546104fd9190615102565b90915550506040517f92f85a40e83c8d882fe8d6e484c07c775950072962f3b22dd739834849a5a324906105349033908590615131565b60405180910390a16005546105659073ffffffffffffffffffffffffffffffffffffffff1633306060860135611bc8565b61031733610577604085013584615119565b600c5473ffffffffffffffffffffffffffffffffffffffff16919061192d565b43600d5410610602576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600d60248201527f4541524c595f54494d454f55540000000000000000000000000000000000000060448201526064016102ac565b6000600c5474010000000000000000000000000000000000000000900460ff16600381111561063357610633614fcc565b148061066d57506001600c5474010000000000000000000000000000000000000000900460ff16600381111561066b5761066b614fcc565b145b6106d3576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600d60248201527f414c52454144595f454e4445440000000000000000000000000000000000000060448201526064016102ac565b6000600e55600c80547fffffffffffffffffffffff00ffffffffffffffffffffffffffffffffffffffff16740300000000000000000000000000000000000000001790556040805133815290517f71b8d288b6b795cd40378095d6a9c6466eb4a081c0238dfd691b0b1f5868f1809181900360200190a16005546040517f8b24259b00000000000000000000000000000000000000000000000000000000815230600482015273ffffffffffffffffffffffffffffffffffffffff90911690638b24259b90602401600060405180830381600087803b1580156107b557600080fd5b505af11580156107c9573d6000803e3d6000fd5b50505050565b600054610100900460ff166107ea5760005460ff16156107ee565b303b155b61087a576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152602e60248201527f496e697469616c697a61626c653a20636f6e747261637420697320616c72656160448201527f647920696e697469616c697a656400000000000000000000000000000000000060648201526084016102ac565b600054610100900460ff161580156108b957600080547fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff0000166101011790555b7f00000000000000000000000000000000000000000000000000000000000000008360c001511115610947576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600c60248201527f4d41585f434f4f4c444f574e000000000000000000000000000000000000000060448201526064016102ac565b6109548360c00151611c26565b7f00000000000000000000000000000000000000000000000000000000000000008360a0015111156109e2576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600b60248201527f4d41585f54494d454f555400000000000000000000000000000000000000000060448201526064016102ac565b60a08301516109f19043615119565b600d5560e0830151610a5f576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600b60248201527f4d494e5f52414953455f3000000000000000000000000000000000000000000060448201526064016102ac565b60e0830151600a558251610a7b90610a7690611ce1565b611e3d565b600760006101000a81548173ffffffffffffffffffffffffffffffffffffffff021916908373ffffffffffffffffffffffffffffffffffffffff160217905550610acb610a768460200151611ce1565b600860006101000a81548173ffffffffffffffffffffffffffffffffffffffff021916908373ffffffffffffffffffffffffffffffffffffffff160217905550610b1b610a768460400151611ce1565b600980547fffffffffffffffffffffffff000000000000000000000000000000000000000090811673ffffffffffffffffffffffffffffffffffffffff93841617909155606085015160068054909216908316179055610100840151600b55600c8054608086015183167fffffffffffffffffffffff0000000000000000000000000000000000000000009091161790558251604001511615610c1a576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600f60248201527f4449535452494255544f525f534554000000000000000000000000000000000060448201526064016102ac565b8151306040918201528251606090810151600e55815160a08101835260808087015173ffffffffffffffffffffffffffffffffffffffff908116835286516020808501919091528088015182168487015285880151848601529387015181169183019190915292516000937f00000000000000000000000000000000000000000000000000000000000000001692632ea72a4992610cba929091016151d1565b6040516020818303038152906040526040518263ffffffff1660e01b8152600401610ce591906152d3565b6020604051808303816000875af1158015610d04573d6000803e3d6000fd5b505050506040513d601f19601f82011682018060405250810190610d2891906152e6565b600580547fffffffffffffffffffffffff00000000000000000000000000000000000000001673ffffffffffffffffffffffffffffffffffffffff83161790556040519091507f887b6fed5d41b64af6f50c6d1010798acb0e93f4adea8213a06574ea968e5d2590610d9f903390879085906153df565b60405180910390a1508015610dd757600080547fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff00ff1690555b505050565b60006001600c5474010000000000000000000000000000000000000000900460ff166003811115610e0f57610e0f614fcc565b1415610e9f576001600e541015610e265750600190565b600854600090610e4b9073ffffffffffffffffffffffffffffffffffffffff16611eab565b9050610e6860405180602001604052806000815250826000611ef9565b6000816020015160018360000151610e809190615102565b81518110610e9057610e90615513565b60200260200101511191505090565b50600090565b6009546000908190610ecc9073ffffffffffffffffffffffffffffffffffffffff16611eab565b9050610efb83604051602001610ee491815260200190565b604051602081830303815290604052826000611ef9565b60208101518151610f0e90600190615102565b81518110610f1e57610f1e615513565b6020026020010151915050919050565b610f36611700565b610f9c576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f43414e545f53544152540000000000000000000000000000000000000000000060448201526064016102ac565b600c80547fffffffffffffffffffffff00ffffffffffffffffffffffffffffffffffffffff16740100000000000000000000000000000000000000001790556040805133815290517f84d0447ca38875fa61115673259a210915bc1dd53a3c112d6f0790f15956a9659181900360200190a1565b60035460009073ffffffffffffffffffffffffffffffffffffffff161561104f5760035473ffffffffffffffffffffffffffffffffffffffff1661107d565b600380547fffffffffffffffffffffffff000000000000000000000000000000000000000016339081179091555b73ffffffffffffffffffffffffffffffffffffffff811660009081526002602052604090205490915043101561110f576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600860248201527f434f4f4c444f574e00000000000000000000000000000000000000000000000060448201526064016102ac565b60006001544361111f9190615119565b73ffffffffffffffffffffffffffffffffffffffff8316600081815260026020908152604091829020849055815192835282018390529192507fdd9ec76fd04aab7ee6b385c04a7bdf966620500e72b134bfc3f19654fae20f25910160405180910390a1600260045414156111f0576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152601f60248201527f5265656e7472616e637947756172643a207265656e7472616e742063616c6c0060448201526064016102ac565b60026004556040830151611260576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600960248201527f305f4d494e494d554d000000000000000000000000000000000000000000000060448201526064016102ac565b8260600151836040015111156112d2576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152601460248201527f4d494e494d554d5f4f5645525f4445534952454400000000000000000000000060448201526064016102ac565b6001600c5474010000000000000000000000000000000000000000900460ff16600381111561130357611303614fcc565b1461136a576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4e4f545f4143544956450000000000000000000000000000000000000000000060448201526064016102ac565b6000611385600e54856060015161217d90919063ffffffff16565b905083604001518110156113f5576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152601260248201527f494e53554646494349454e545f53544f434b000000000000000000000000000060448201526064016102ac565b600061140082610ea5565b90508460800151811115611470576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600d60248201527f4d4158494d554d5f50524943450000000000000000000000000000000000000060448201526064016102ac565b600061147c8284611ba0565b6040805160a081018252601180548083528a5173ffffffffffffffffffffffffffffffffffffffff166020808501919091528b01519383019390935260608201879052608082018690529293509160006114d583615542565b909155505033600090815260106020908152604080832090519092916114fd9185910161557b565b604051602081830303815290604052805190602001208152602001908152602001600020600081548092919061153290615542565b9091555050602080880151885173ffffffffffffffffffffffffffffffffffffffff166000908152601290925260408220805491929091611574908490615119565b9250508190555083600e600082825461158d9190615102565b9250508190555081600f60008282546115a69190615119565b925050819055506115e333308960200151856115c29190615119565b600c5473ffffffffffffffffffffffffffffffffffffffff16929190611bc8565b6005546116079073ffffffffffffffffffffffffffffffffffffffff16338661192d565b6001600e54101561161f5761161a61175d565b61168f565b600b54600e54101561168f576040517f08c379a00000000000000000000000000000000000000000000000000000000081526004016102ac9060208082526004908201527f4455535400000000000000000000000000000000000000000000000000000000604082015260600190565b7fde28bbdf409354cf811d2e1f52147a0a2a1b89be5848c4b5a41587f63b7cfa6a3388836040516116c2939291906155ca565b60405180910390a1505060016004555050600380547fffffffffffffffffffffffff0000000000000000000000000000000000000000169055505050565b600080600c5474010000000000000000000000000000000000000000900460ff16600381111561173257611732614fcc565b1415610e9f57600754600090610e4b9073ffffffffffffffffffffffffffffffffffffffff16611eab565b611765610ddc565b6117cb576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600860248201527f43414e545f454e4400000000000000000000000000000000000000000000000060448201526064016102ac565b600a54600f5410156000816117e15760036117e4565b60025b6000600e55600c805491925082917fffffffffffffffffffffff00ffffffffffffffffffffffffffffffffffffffff167401000000000000000000000000000000000000000083600381111561183c5761183c614fcc565b02179055507f9aef4e6c636914c533cde859f632ab5e2b68cfd8f06b6a2e18f89783f00072cd338260405161187292919061566d565b60405180910390a16005546040517f8b24259b00000000000000000000000000000000000000000000000000000000815230600482015273ffffffffffffffffffffffffffffffffffffffff90911690638b24259b90602401600060405180830381600087803b1580156118e557600080fd5b505af11580156118f9573d6000803e3d6000fd5b50505050811561031757600654600f54600c546103179273ffffffffffffffffffffffffffffffffffffffff918216929116905b60405173ffffffffffffffffffffffffffffffffffffffff8316602482015260448101829052610dd79084907fa9059cbb00000000000000000000000000000000000000000000000000000000906064015b604080517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe08184030181529190526020810180517bffffffffffffffffffffffffffffffffffffffffffffffffffffffff167fffffffff0000000000000000000000000000000000000000000000000000000090931692909217909152612193565b60035460009073ffffffffffffffffffffffffffffffffffffffff1615611a405760035473ffffffffffffffffffffffffffffffffffffffff16611a6e565b600380547fffffffffffffffffffffffff000000000000000000000000000000000000000016339081179091555b73ffffffffffffffffffffffffffffffffffffffff8116600090815260026020526040902054909150431015611b00576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600860248201527f434f4f4c444f574e00000000000000000000000000000000000000000000000060448201526064016102ac565b600060015443611b109190615119565b73ffffffffffffffffffffffffffffffffffffffff8316600081815260026020908152604091829020849055815192835282018390529192507fdd9ec76fd04aab7ee6b385c04a7bdf966620500e72b134bfc3f19654fae20f25910160405180910390a15050600380547fffffffffffffffffffffffff0000000000000000000000000000000000000000169055565b6000670de0b6b3a7640000611bb58385615697565b611bbf9190615703565b90505b92915050565b60405173ffffffffffffffffffffffffffffffffffffffff808516602483015283166044820152606481018290526107c99085907f23b872dd000000000000000000000000000000000000000000000000000000009060840161197f565b60008111611c90576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f434f4f4c444f574e5f300000000000000000000000000000000000000000000060448201526064016102ac565b60015415611ca057611ca0615717565b600181905560408051338152602081018390527f6153539ba1c54ed4ef5b6f1fda4b7183eeaa1b5707c152abed56f61e09452521910160405180910390a150565b611d136040518060a0016040528060008152602001606081526020016060815260200160608152602001606081525090565b815151611d7c576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600960248201527f305f534f5552434553000000000000000000000000000000000000000000000060448201526064016102ac565b6040518060a0016040528060008152602001836040015167ffffffffffffffff811115611dab57611dab614a02565b604051908082528060200260200182016040528015611dd4578160200160208202803683370190505b5081526020018360000151815260200183602001518152602001836060015167ffffffffffffffff811115611e0b57611e0b614a02565b604051908082528060200260200182016040528015611e34578160200160208202803683370190505b50905292915050565b600080611e6883604051602001611e5491906157b2565b60405160208183030381529060405261229f565b90507ff95eed63f55d96a35306669cc3b5f652f8d44552e3e4b17b2f78f13571114abd338285604051611e9d939291906157c5565b60405180910390a192915050565b611edd6040518060a0016040528060008152602001606081526020016060815260200160608152602001606081525090565b611ee68261232a565b806020019051810190611bc29190615915565b60208201515182511115611f69576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600e60248201527f535441434b5f4f564552464c4f5700000000000000000000000000000000000060448201526064016102ac565b602082810151604084015183830201909101516060840151608085015182516000948594859492939092915b84881015612170576002880197508784015180601e1a975080601f1a96505060058710156120ef576001871415612007578160808716611fd25750825b607f871681518110611fe357600080fd5b8b516020820260200183015160208202602001850152600181018d525050506120fb565b600287141561203e57895180871061201e57600080fd5b6020870260200182015160208202602001830152600181018b52506120fb565b6003871415612057576120528b8b88612358565b6120fb565b6004871415612088576120528a60405160200161207491906157b2565b6040516020818303038152906040526124be565b86612052576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600c60248201527f534b49505f52454d4f564544000000000000000000000000000000000000000060448201526064016102ac565b6120fb8b8b8989612550565b60208a0151518a51111561216b576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600e60248201527f535441434b5f4f564552464c4f5700000000000000000000000000000000000060448201526064016102ac565b611f95565b5050505050505050505050565b600081831061218c5781611bbf565b5090919050565b60006121f5826040518060400160405280602081526020017f5361666545524332303a206c6f772d6c6576656c2063616c6c206661696c65648152508573ffffffffffffffffffffffffffffffffffffffff1661274c9092919063ffffffff16565b805190915015610dd7578080602001905181019061221391906159f8565b610dd7576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152602a60248201527f5361666545524332303a204552433230206f7065726174696f6e20646964206e60448201527f6f7420737563636565640000000000000000000000000000000000000000000060648201526084016102ac565b6000806122ca836040516020016122b69190615a1a565b604051602081830303815290604052612765565b90508051602082016000f0915073ffffffffffffffffffffffffffffffffffffffff8216612324576040517f08d4abb600000000000000000000000000000000000000000000000000000000815260040160405180910390fd5b50919050565b6060611bc28260017fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff612791565b81517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff600583901c918290030183526007821690610100600384811c1681901c91908290039060010160008167ffffffffffffffff8111156123bc576123bc614a02565b6040519080825280602002602001820160405280156123e5578160200160208202803683370190505b50905060005b8281101561243c578760200151818960000151018151811061240f5761240f615513565b602002602001015182828151811061242957612429615513565b60209081029190910101526001016123eb565b5060005b6101008110156124b35760005b838110156124a0578482860384838151811061246b5761246b615513565b6020026020010151901b901c8960800151828151811061248d5761248d615513565b602090810291909101015260010161244d565b506124ac898988611ef9565b8401612440565b505050505050505050565b61254d816040516024016124d291906152d3565b604080517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe08184030181529190526020810180517bffffffffffffffffffffffffffffffffffffffffffffffffffffffff167f0be77f5600000000000000000000000000000000000000000000000000000000179052612879565b50565b7f000000000000000000000000000000000000000000000000000000000000000082101561258b5761258683600584038361289a565b6107c9565b7f0000000000000000000000000000000000000000000000000000000000000000820391506005821061261a576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4d41585f4f50434f44450000000000000000000000000000000000000000000060448201526064016102ac565b8161264a57600e54602084015184518151811061263957612639615513565b602002602001018181525050612740565b600182141561266d57600f54602084015184518151811061263957612639615513565b60028214156126bb5760008480602001905181019061268c9190615a40565b90508084602001518560000151815181106126a9576126a9615513565b60200260200101818152505050612740565b60038214156126f75760055460208401518451815173ffffffffffffffffffffffffffffffffffffffff90931692811061263957612639615513565b600482141561274057600c5460208401518451815173ffffffffffffffffffffffffffffffffffffffff90931692811061273357612733615513565b6020026020010181815250505b50508051600101905250565b606061275b8484600085612a00565b90505b9392505050565b606081518260405160200161277b929190615a59565b6040516020818303038152906040529050919050565b6060833b806127b057505060408051602081019091526000815261275e565b808411156127ce57505060408051602081019091526000815261275e565b83831015612819576040517f2c4a89fa0000000000000000000000000000000000000000000000000000000081526004810182905260248101859052604481018490526064016102ac565b838303848203600082821061282e5782612830565b815b60408051603f83017fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0168101909152818152955090508087602087018a3c505050509392505050565b80516a636f6e736f6c652e6c6f67602083016000808483855afa5050505050565b60048210156128ae57610dd7838383612b98565b601d82101561293157600b8210156128ce57610dd7836004840383612cae565b601682101561290457610dd7837ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff5840183612fdc565b610dd7837fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffea840183613475565b602382101561296757610dd7837fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe38401836136e5565b602582101561299d57610dd7837fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffdd840183613bb9565b60278210156129d357610dd7837fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffdb840183613de2565b610dd7837fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffd9840183613fdc565b606082471015612a92576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152602660248201527f416464726573733a20696e73756666696369656e742062616c616e636520666f60448201527f722063616c6c000000000000000000000000000000000000000000000000000060648201526084016102ac565b73ffffffffffffffffffffffffffffffffffffffff85163b612b10576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152601d60248201527f416464726573733a2063616c6c20746f206e6f6e2d636f6e747261637400000060448201526064016102ac565b6000808673ffffffffffffffffffffffffffffffffffffffff168587604051612b399190615af0565b60006040518083038185875af1925050503d8060008114612b76576040519150601f19603f3d011682016040523d82523d6000602084013e612b7b565b606091505b5091509150612b8b828286614408565b925050505b949350505050565b60048210612c02576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4d41585f4f50434f44450000000000000000000000000000000000000000000060448201526064016102ac565b81612c3357438360200151846000015181518110612c2257612c22615513565b602002602001018181525050612ca3565b6001821415612c5757428360200151846000015181518110612c2257612c22615513565b6002821415612c7b57602083015183518151339291908110612c2257612c22615513565b602083015183518151309291908110612c9657612c96615513565b6020026020010181815250505b505080516001019052565b60078210612d18576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4d41585f4f50434f44450000000000000000000000000000000000000000000060448201526064016102ac565b6002821015612e645782517ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0182612dc35783602001518160010181518110612d6357612d63615513565b6020026020010151612d9b8386602001518481518110612d8557612d85615513565b602002602001015161445b90919063ffffffff16565b0284602001518281518110612db257612db2615513565b602002602001018181525050612e38565b6001831415612e385783602001518160010181518110612de557612de5615513565b6020026020010151612e078386602001518481518110612d8557612d85615513565b81612e1457612e146156d4565b0484602001518281518110612e2b57612e2b615513565b6020026020010181815250505b5082517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff018352505050565b6005821015612f755782517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff016002831415612edb57612eb48285602001518381518110612d8557612d85615513565b84602001518281518110612eca57612eca615513565b6020026020010181815250506107c9565b6003831415612f1457612eb48285602001518381518110612efe57612efe615513565b60200260200101516144dc90919063ffffffff16565b60048314156107c957612f4d8285602001518381518110612f3757612f37615513565b602002602001015161453a90919063ffffffff16565b84602001518281518110612f6357612f63615513565b60200260200101818152505050505050565b6005821415612fb757670de0b6b3a76400008360200151846000015181518110612fa157612fa1615513565b6020908102919091010152505080516001019052565b6006821415610dd75760128360200151846000015181518110612c9657612c96615513565b600b8210613046576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4d41585f4f50434f44450000000000000000000000000000000000000000000060448201526064016102ac565b825160208401518051838303927fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff01918391600091908390811061308c5761308c615513565b6020026020010151905060008614156130e4575b828210156130df5760208701518051600190930192839081106130c5576130c5615513565b6020026020010151816130d89190615119565b90506130a0565b613446565b6001861415613160575b828210801561311c57507fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff81105b156130df5781806001019250506131598760200151838151811061314257613142615513565b60200260200101518261458590919063ffffffff16565b90506130ee565b60028614156131a9575b828210156130df57602087015180516001909301928390811061318f5761318f615513565b6020026020010151816131a29190615102565b905061316a565b6003861415613206575b82821080156131c25750806000105b156130df5781806001019250506131ff876020015183815181106131e8576131e8615513565b6020026020010151826145c090919063ffffffff16565b90506131b3565b600486141561324f575b828210156130df57602087015180516001909301928390811061323557613235615513565b6020026020010151816132489190615697565b9050613210565b60058614156132cb575b828210801561328757507fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff81105b156130df5781806001019250506132c4876020015183815181106132ad576132ad615513565b6020026020010151826145d690919063ffffffff16565b9050613259565b6006861415613314575b828210156130df5760208701518051600190930192839081106132fa576132fa615513565b60200260200101518161330d9190615703565b90506132d5565b600786141561335d575b828210156130df57602087015180516001909301928390811061334357613343615513565b6020026020010151816133569190615b0c565b905061331e565b60088614156133a6575b828210156130df57602087015180516001909301928390811061338c5761338c615513565b60200260200101518161339f9190615c40565b9050613367565b60098614156133f85760005b838310156133f25760208801518051600190940193849081106133d7576133d7615513565b60200260200101519050818110156133ed578091505b6133b2565b50613446565b600a8614156134465760005b8383101561344457602088015180516001909401938490811061342957613429615513565b602002602001015190508181111561343f578091505b613404565b505b808760200151858151811061345d5761345d615513565b60209081029190910101525050506001019092525050565b600782106134df576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4d41585f4f50434f44450000000000000000000000000000000000000000000060448201526064016102ac565b82516020808501518183020101838015613528576001811461355f57600281146135d85760038114613600576004811461362857600581146136505760068114613697576136dd565b8261353257600080fd5b7fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0820180511590526136dd565b600383101561356d57600080fd5b7ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe830186527fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffa08201805180156135c957602082015182526135d1565b604082015182525b50506136dd565b60028310156135e657600080fd5b6001830386526040820360208101518151148152506136dd565b600283101561360e57600080fd5b6001830386526040820360208101518151108152506136dd565b600283101561363657600080fd5b6001830386526040820360208101518151118152506136dd565b8383101561365d57600080fd5b8315613692576001840383038652602084028203805b838110156135d157805161368a57600082526135d1565b602001613673565b6136dd565b838310156136a457600080fd5b83156136dd576001840383038652602084028203805b838110156136da57805180156136d15782526136da565b506020016136ba565b50505b505050505050565b6006821061374f576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4d41585f4f50434f44450000000000000000000000000000000000000000000060448201526064016102ac565b600082613877575082517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff81018452602084015180517ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe90920191829081106137ba576137ba615513565b602002602001015173ffffffffffffffffffffffffffffffffffffffff1663e053ea31856020015183600101815181106137f6576137f6615513565b60200260200101516040518263ffffffff1660e01b8152600401613836919073ffffffffffffffffffffffffffffffffffffffff91909116815260200190565b602060405180830381865afa158015613853573d6000803e3d6000fd5b505050506040513d601f19601f82011682018060405250810190612eb49190615a40565b60018314156138d2577fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff84602001518560000151815181106138bb576138bb615513565b6020908102919091010152835160010184526107c9565b60028314156138f757600084602001518560000151815181106138bb576138bb615513565b60038314156139ab575082517ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0180845260208401518051600091908390811061394357613943615513565b6020026020010151905060008560200151836001018151811061396857613968615513565b6020026020010151905061397c8282614605565b8660200151848151811061399257613992615513565b60209081029190910101525050835160010184526107c9565b6004831415613a73575082517ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0180845260208401518051600f80851692600486901c909116916000919085908110613a0657613a06615513565b60200260200101519050600087602001518560010181518110613a2b57613a2b615513565b60200260200101519050613a4182858584614664565b88602001518681518110613a5757613a57615513565b6020908102919091010152505085516001018652506107c99050565b60058314156107c9575082517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff601f8316918290030180855290600783901c906003600585901c16908360008267ffffffffffffffff811115613ad857613ad8614a02565b604051908082528060200260200182016040528015613b01578160200160208202803683370190505b50905060005b83811015613b565789602001518381518110613b2557613b25615513565b6020026020010151828281518110613b3f57613b3f615513565b602090810291909101015260019283019201613b07565b50600089602001518381518110613b6f57613b6f615513565b60200260200101519050613b858282888861470a565b8a602001518881518110613b9b57613b9b615513565b60209081029190910101525050875160010188525050505050505050565b60028210613c23576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4d41585f4f50434f44450000000000000000000000000000000000000000000060448201526064016102ac565b81613d495782517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff81018452602084015180517ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe9092019182908110613c8b57613c8b615513565b602002602001015173ffffffffffffffffffffffffffffffffffffffff166370a082318560200151866000015181518110613cc857613cc8615513565b60200260200101516040518263ffffffff1660e01b8152600401613d08919073ffffffffffffffffffffffffffffffffffffffff91909116815260200190565b602060405180830381865afa158015613d25573d6000803e3d6000fd5b505050506040513d601f19601f82011682018060405250810190612f4d9190615a40565b6001821415610dd7578251602084015180517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff9092019182908110613d9057613d90615513565b602002602001015173ffffffffffffffffffffffffffffffffffffffff166318160ddd6040518163ffffffff1660e01b8152600401602060405180830381865afa158015613d25573d6000803e3d6000fd5b60028210613e4c576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4d41585f4f50434f44450000000000000000000000000000000000000000000060448201526064016102ac565b82517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff810184527ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0182613eed5783602001518181518110613eb057613eb0615513565b602002602001015173ffffffffffffffffffffffffffffffffffffffff166370a0823185602001518660000151815181106137f6576137f6615513565b60018314156107c95783602001518181518110613f0c57613f0c615513565b602002602001015173ffffffffffffffffffffffffffffffffffffffff16636352211e8560200151866000015181518110613f4957613f49615513565b60200260200101516040518263ffffffff1660e01b8152600401613f6f91815260200190565b602060405180830381865afa158015613f8c573d6000803e3d6000fd5b505050506040513d601f19601f82011682018060405250810190613fb091906152e6565b73ffffffffffffffffffffffffffffffffffffffff1684602001518281518110612f6357612f63615513565b60028210614046576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600a60248201527f4d41585f4f50434f44450000000000000000000000000000000000000000000060448201526064016102ac565b816141505782517ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe81018452602084015180517ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffd90920191829081106140ae576140ae615513565b602002602001015173ffffffffffffffffffffffffffffffffffffffff1662fdd58e856020015183600101815181106140e9576140e9615513565b60200260200101518660200151846002018151811061410a5761410a615513565b60200260200101516040518363ffffffff1660e01b8152600401613d0892919073ffffffffffffffffffffffffffffffffffffffff929092168252602082015260400190565b6001821415610dd7576001810160008167ffffffffffffffff81111561417857614178614a02565b6040519080825280602002602001820160405280156141a1578160200160208202803683370190505b50905060008267ffffffffffffffff8111156141bf576141bf614a02565b6040519080825280602002602001820160405280156141e8578160200160208202803683370190505b5086518490037fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff818101895260208901518051939450918690030191600091908390811061423857614238615513565b6020026020010151905060005b858110156142ef5788602001518184016001018151811061426857614268615513565b602002602001015185828151811061428257614282615513565b73ffffffffffffffffffffffffffffffffffffffff909216602092830291909101820152890151805160018589018401019081106142c2576142c2615513565b60200260200101518482815181106142dc576142dc615513565b6020908102919091010152600101614245565b506040517f4e1273f400000000000000000000000000000000000000000000000000000000815260009073ffffffffffffffffffffffffffffffffffffffff831690634e1273f4906143479088908890600401615c4c565b600060405180830381865afa158015614364573d6000803e3d6000fd5b505050506040513d6000823e601f3d9081017fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe01682016040526143aa9190810190615cb9565b905060005b868110156143fc578181815181106143c9576143c9615513565b60200260200101518a60200151828601815181106143e9576143e9615513565b60209081029190910101526001016143af565b50505050505050505050565b6060831561441757508161275e565b8251156144275782518084602001fd5b816040517f08c379a00000000000000000000000000000000000000000000000000000000081526004016102ac91906152d3565b60008082601214156144705783915050611bc2565b826012111561449c5750601282900361448a81600a615c40565b6144949085615697565b915050611bc2565b507fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffee82016144cb81600a615c40565b6144949085615703565b5092915050565b60008060128314156144f15783915050611bc2565b826012111561450b575060128290036144cb81600a615c40565b507fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffee820161448a81600a615c40565b600081810b61454a575081611bc2565b60008260000b13156145725761456182600a615cee565b61456b9084615697565b9050611bc2565b60ff6000839003166144cb81600a615c40565b60008282018381106145975780612b90565b7fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff949350505050565b60008183116145d0576000611bbf565b50900390565b6000826145e557506000611bc2565b828202828482816145f8576145f86156d4565b0414156145975780612b90565b60008060015b6008811161465c57600061461f868361485a565b9050600061462d868461485a565b9050600061463b83836145c0565b905061464b856001860383614907565b9450506001909201915061460b9050565b509392505050565b60008260088111156146d2576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600860248201527f4d41585f5449455200000000000000000000000000000000000000000000000060448201526064016102ac565b6000855b858110156146fe5763ffffffff6020820290811b199890981685891b179791506001016146d6565b50959695505050505050565b835160009081908190819060015b6008811161484c5760009250828761475157507fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff614755565b5060005b60005b8381101561480a576147838c828151811061477557614775615513565b60200260200101518461485a565b95508a86116147d557886147a25761479b868361217d565b91506147cc565b60018914156147b55761479b8683614991565b6002891480156147c3575084155b156147cc578591505b60019450614802565b89614802577fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff915061480a565b600101614758565b508361483357507fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff5b614841866001840383614907565b955050600101614718565b509298975050505050505050565b60008160088111156148c8576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600860248201527f4d41585f5449455200000000000000000000000000000000000000000000000060448201526064016102ac565b826148d657600091506144d5565b50507fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff016020021c63ffffffff1690565b6000826008811115614975576040517f08c379a000000000000000000000000000000000000000000000000000000000815260206004820152600860248201527f4d41585f5449455200000000000000000000000000000000000000000000000060448201526064016102ac565b505060209190910290811b63ffffffff90911b19919091161790565b60008183101561218c5781611bbf565b73ffffffffffffffffffffffffffffffffffffffff8116811461254d57600080fd5b80356149ce816149a1565b919050565b6000602082840312156149e557600080fd5b813561275e816149a1565b600060a0828403121561232457600080fd5b7f4e487b7100000000000000000000000000000000000000000000000000000000600052604160045260246000fd5b6040516080810167ffffffffffffffff81118282101715614a5457614a54614a02565b60405290565b604051610120810167ffffffffffffffff81118282101715614a5457614a54614a02565b60405160a0810167ffffffffffffffff81118282101715614a5457614a54614a02565b604051601f82017fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe016810167ffffffffffffffff81118282101715614ae857614ae8614a02565b604052919050565b600067ffffffffffffffff821115614b0a57614b0a614a02565b5060051b60200190565b600067ffffffffffffffff821115614b2e57614b2e614a02565b50601f017fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe01660200190565b6000614b6d614b6884614b14565b614aa1565b9050828152838383011115614b8157600080fd5b828260208301376000602084830101529392505050565b600082601f830112614ba957600080fd5b81356020614bb9614b6883614af0565b82815260059290921b84018101918181019086841115614bd857600080fd5b8286015b84811015614bf35780358352918301918301614bdc565b509695505050505050565b600060808284031215614c1057600080fd5b614c18614a31565b9050813567ffffffffffffffff80821115614c3257600080fd5b818401915084601f830112614c4657600080fd5b81356020614c56614b6883614af0565b82815260059290921b84018101918181019088841115614c7557600080fd5b8286015b84811015614cc257803586811115614c915760008081fd5b8701603f81018b13614ca35760008081fd5b614cb48b8683013560408401614b5a565b845250918301918301614c79565b5086525085810135935082841115614cd957600080fd5b614ce587858801614b98565b8186015250505050604082013560408201526060820135606082015292915050565b600082601f830112614d1857600080fd5b611bbf83833560208501614b5a565b600060808284031215614d3957600080fd5b614d41614a31565b9050813567ffffffffffffffff80821115614d5b57600080fd5b9083019060808286031215614d6f57600080fd5b614d77614a31565b823582811115614d8657600080fd5b614d9287828601614d07565b825250602083013582811115614da757600080fd5b614db387828601614d07565b60208301525060408301359150614dc9826149a1565b81604082015260608301356060820152808452505050614deb602083016149c3565b602082015260408201356040820152614e06606083016149c3565b606082015292915050565b60008060408385031215614e2457600080fd5b823567ffffffffffffffff80821115614e3c57600080fd5b908401906101208287031215614e5157600080fd5b614e59614a5a565b823582811115614e6857600080fd5b614e7488828601614bfe565b825250602083013582811115614e8957600080fd5b614e9588828601614bfe565b602083015250604083013582811115614ead57600080fd5b614eb988828601614bfe565b604083015250614ecb606084016149c3565b6060820152614edc608084016149c3565b608082015260a083013560a082015260c083013560c082015260e083013560e0820152610100808401358183015250809450506020850135915080821115614f2357600080fd5b50614f3085828601614d27565b9150509250929050565b600060208284031215614f4c57600080fd5b5035919050565b600060a08284031215614f6557600080fd5b60405160a0810181811067ffffffffffffffff82111715614f8857614f88614a02565b6040528235614f96816149a1565b80825250602083013560208201526040830135604082015260608301356060820152608083013560808201528091505092915050565b7f4e487b7100000000000000000000000000000000000000000000000000000000600052602160045260246000fd5b60048110615032577f4e487b7100000000000000000000000000000000000000000000000000000000600052602160045260246000fd5b9052565b60208101611bc28284614ffb565b803582526020810135615056816149a1565b73ffffffffffffffffffffffffffffffffffffffff1660208301526040818101359083015260608082013590830152608090810135910152565b60a08101611bc28284615044565b7f4e487b7100000000000000000000000000000000000000000000000000000000600052601160045260246000fd5b6000816150dc576150dc61509e565b507fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff0190565b6000828210156151145761511461509e565b500390565b6000821982111561512c5761512c61509e565b500190565b73ffffffffffffffffffffffffffffffffffffffff8316815260c0810161275e6020830184615044565b60005b8381101561517657818101518382015260200161515e565b838111156107c95750506000910152565b6000815180845261519f81602086016020860161515b565b601f017fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffe0169290920160200192915050565b60208152600073ffffffffffffffffffffffffffffffffffffffff808451166020840152602084015160a060408501528051608060c0860152615218610140860182615187565b905060208201517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff408683030160e08701526152538282615187565b91505082604083015116610100860152606082015161012086015260408601519250615297606086018473ffffffffffffffffffffffffffffffffffffffff169052565b60608601516080860152608086015192506152ca60a086018473ffffffffffffffffffffffffffffffffffffffff169052565b95945050505050565b602081526000611bbf6020830184615187565b6000602082840312156152f857600080fd5b815161275e816149a1565b6000815180845260208085019450848260051b860182860160005b85811015615348578383038952615336838351615187565b9885019892509084019060010161531e565b5090979650505050505050565b600081518084526020808501945080840160005b8381101561538557815187529582019590820190600101615369565b509495945050505050565b60008151608084526153a56080850182615303565b9050602083015184820360208601526153be8282615355565b91505060408301516040850152606083015160608501528091505092915050565b73ffffffffffffffffffffffffffffffffffffffff84168152606060208201526000835161012080606085015261541a610180850183615390565b915060208601517fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffa0808685030160808701526154568483615390565b935060408801519150808685030160a0870152506154748382615390565b925050606086015161549e60c086018273ffffffffffffffffffffffffffffffffffffffff169052565b50608086015173ffffffffffffffffffffffffffffffffffffffff811660e08601525060a08601516101008581019190915260c08701519185019190915260e086015161014085015285015161016084015273ffffffffffffffffffffffffffffffffffffffff841660408401529050612b90565b7f4e487b7100000000000000000000000000000000000000000000000000000000600052603260045260246000fd5b60007fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff8214156155745761557461509e565b5060010190565b60a08101611bc282848051825273ffffffffffffffffffffffffffffffffffffffff60208201511660208301526040810151604083015260608101516060830152608081015160808301525050565b60006101608201905073ffffffffffffffffffffffffffffffffffffffff808616835280855116602084015250602084015160408301526040840151606083015260608401516080830152608084015160a0830152612b9060c08301848051825273ffffffffffffffffffffffffffffffffffffffff60208201511660208301526040810151604083015260608101516060830152608081015160808301525050565b73ffffffffffffffffffffffffffffffffffffffff831681526040810161275e6020830184614ffb565b6000817fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff04831182151516156156cf576156cf61509e565b500290565b7f4e487b7100000000000000000000000000000000000000000000000000000000600052601260045260246000fd5b600082615712576157126156d4565b500490565b7f4e487b7100000000000000000000000000000000000000000000000000000000600052600160045260246000fd5b805182526000602082015160a0602085015261576560a0850182615355565b90506040830151848203604086015261577e8282615303565b915050606083015184820360608601526157988282615355565b915050608083015184820360808601526152ca8282615355565b602081526000611bbf6020830184615746565b600073ffffffffffffffffffffffffffffffffffffffff8086168352808516602084015250606060408301526152ca6060830184615746565b600082601f83011261580f57600080fd5b8151602061581f614b6883614af0565b82815260059290921b8401810191818101908684111561583e57600080fd5b8286015b84811015614bf35780518352918301918301615842565b600082601f83011261586a57600080fd5b8151602061587a614b6883614af0565b82815260059290921b8401810191818101908684111561589957600080fd5b8286015b84811015614bf357805167ffffffffffffffff8111156158bd5760008081fd5b8701603f810189136158cf5760008081fd5b8481015160406158e1614b6883614b14565b8281528b828486010111156158f65760008081fd5b6159058389830184870161515b565b865250505091830191830161589d565b60006020828403121561592757600080fd5b815167ffffffffffffffff8082111561593f57600080fd5b9083019060a0828603121561595357600080fd5b61595b614a7e565b8251815260208301518281111561597157600080fd5b61597d878286016157fe565b60208301525060408301518281111561599557600080fd5b6159a187828601615859565b6040830152506060830151828111156159b957600080fd5b6159c5878286016157fe565b6060830152506080830151828111156159dd57600080fd5b6159e9878286016157fe565b60808301525095945050505050565b600060208284031215615a0a57600080fd5b8151801515811461275e57600080fd5b6000815260008251615a3381600185016020870161515b565b9190910160010192915050565b600060208284031215615a5257600080fd5b5051919050565b7f630000000000000000000000000000000000000000000000000000000000000081527fffffffff000000000000000000000000000000000000000000000000000000008360e01b1660018201527f80600e6000396000f30000000000000000000000000000000000000000000000600582015260008251615ae281600e85016020870161515b565b91909101600e019392505050565b60008251615b0281846020870161515b565b9190910192915050565b600082615b1b57615b1b6156d4565b500690565b600181815b80851115615b7957817fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff04821115615b5f57615b5f61509e565b80851615615b6c57918102915b93841c9390800290615b25565b509250929050565b600082615b9057506001611bc2565b81615b9d57506000611bc2565b8160018114615bb35760028114615bbd57615bd9565b6001915050611bc2565b60ff841115615bce57615bce61509e565b50506001821b611bc2565b5060208310610133831016604e8410600b8410161715615bfc575081810a611bc2565b615c068383615b20565b807fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff04821115615c3857615c3861509e565b029392505050565b6000611bbf8383615b81565b604080825283519082018190526000906020906060840190828701845b82811015615c9b57815173ffffffffffffffffffffffffffffffffffffffff1684529284019290840190600101615c69565b50505083810382850152615caf8186615355565b9695505050505050565b600060208284031215615ccb57600080fd5b815167ffffffffffffffff811115615ce257600080fd5b612b90848285016157fe565b6000611bbf60ff841683615b8156fea26469706673582212205f1b2889f19d9f0dbf5f50f5bad76f88df9741b85bcc16bf86c818cf593e961864736f6c634300080a0033";

var isSuperArgs$9 = function isSuperArgs(xs) {
  return xs.length > 1;
};

var SaleFactory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(SaleFactory__factory, _ContractFactory);

  function SaleFactory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$9(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$g, _bytecode$9, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = SaleFactory__factory.prototype;

  _proto.deploy = function deploy(config_, overrides) {
    return _ContractFactory.prototype.deploy.call(this, config_, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(config_, overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, config_, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  SaleFactory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$g);
  };

  SaleFactory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$g, signerOrProvider);
  };

  return SaleFactory__factory;
}(ContractFactory);
SaleFactory__factory.bytecode = _bytecode$9;
SaleFactory__factory.abi = _abi$g;

var _abi$h = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "bytes[]",
      name: "sources",
      type: "bytes[]"
    }, {
      internalType: "uint256[]",
      name: "constants",
      type: "uint256[]"
    }, {
      internalType: "uint256",
      name: "stackLength",
      type: "uint256"
    }, {
      internalType: "uint256",
      name: "argumentsLength",
      type: "uint256"
    }],
    internalType: "struct StateConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract CombineTier",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "implementation",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$a = "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";

var isSuperArgs$a = function isSuperArgs(xs) {
  return xs.length > 1;
};

var CombineTierFactory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(CombineTierFactory__factory, _ContractFactory);

  function CombineTierFactory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$a(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$h, _bytecode$a, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = CombineTierFactory__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  CombineTierFactory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$h);
  };

  CombineTierFactory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$h, signerOrProvider);
  };

  return CombineTierFactory__factory;
}(ContractFactory);
CombineTierFactory__factory.bytecode = _bytecode$a;
CombineTierFactory__factory.abi = _abi$h;

var _abi$i = [{
  inputs: [{
    internalType: "uint256",
    name: "_size",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_start",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_end",
    type: "uint256"
  }],
  name: "InvalidCodeAtRange",
  type: "error"
}, {
  inputs: [],
  name: "WriteError",
  type: "error"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "erc20",
    type: "address"
  }],
  name: "Initialize",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "pointer",
    type: "address"
  }],
  name: "InitializeValueTier",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "startTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "endTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "TierChange",
  type: "event"
}, {
  inputs: [{
    components: [{
      internalType: "contract IERC20",
      name: "erc20",
      type: "address"
    }, {
      internalType: "uint256[8]",
      name: "tierValues",
      type: "uint256[8]"
    }],
    internalType: "struct ERC20BalanceTierConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "initialize",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account_",
    type: "address"
  }],
  name: "report",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }, {
    internalType: "uint256",
    name: "",
    type: "uint256"
  }, {
    internalType: "bytes",
    name: "",
    type: "bytes"
  }],
  name: "setTier",
  outputs: [],
  stateMutability: "pure",
  type: "function"
}, {
  inputs: [],
  name: "tierValues",
  outputs: [{
    internalType: "uint256[8]",
    name: "tierValues_",
    type: "uint256[8]"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$b = "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";

var isSuperArgs$b = function isSuperArgs(xs) {
  return xs.length > 1;
};

var ERC20BalanceTier__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(ERC20BalanceTier__factory, _ContractFactory);

  function ERC20BalanceTier__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$b(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$i, _bytecode$b, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = ERC20BalanceTier__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  ERC20BalanceTier__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$i);
  };

  ERC20BalanceTier__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$i, signerOrProvider);
  };

  return ERC20BalanceTier__factory;
}(ContractFactory);
ERC20BalanceTier__factory.bytecode = _bytecode$b;
ERC20BalanceTier__factory.abi = _abi$i;

var _abi$j = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "contract IERC20",
      name: "erc20",
      type: "address"
    }, {
      internalType: "uint256[8]",
      name: "tierValues",
      type: "uint256[8]"
    }],
    internalType: "struct ERC20BalanceTierConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract ERC20BalanceTier",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "implementation",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$c = "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";

var isSuperArgs$c = function isSuperArgs(xs) {
  return xs.length > 1;
};

var ERC20BalanceTierFactory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(ERC20BalanceTierFactory__factory, _ContractFactory);

  function ERC20BalanceTierFactory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$c(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$j, _bytecode$c, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = ERC20BalanceTierFactory__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  ERC20BalanceTierFactory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$j);
  };

  ERC20BalanceTierFactory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$j, signerOrProvider);
  };

  return ERC20BalanceTierFactory__factory;
}(ContractFactory);
ERC20BalanceTierFactory__factory.bytecode = _bytecode$c;
ERC20BalanceTierFactory__factory.abi = _abi$j;

var _abi$k = [{
  inputs: [{
    internalType: "uint256",
    name: "_size",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_start",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_end",
    type: "uint256"
  }],
  name: "InvalidCodeAtRange",
  type: "error"
}, {
  inputs: [],
  name: "WriteError",
  type: "error"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "erc20",
    type: "address"
  }],
  name: "Initialize",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "pointer",
    type: "address"
  }],
  name: "InitializeValueTier",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "startTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "endTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "TierChange",
  type: "event"
}, {
  inputs: [{
    components: [{
      internalType: "contract IERC20",
      name: "erc20",
      type: "address"
    }, {
      internalType: "uint256[8]",
      name: "tierValues",
      type: "uint256[8]"
    }],
    internalType: "struct ERC20TransferTierConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "initialize",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account_",
    type: "address"
  }],
  name: "report",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "endTier_",
    type: "uint256"
  }, {
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "setTier",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "tierValues",
  outputs: [{
    internalType: "uint256[8]",
    name: "tierValues_",
    type: "uint256[8]"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$d = "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";

var isSuperArgs$d = function isSuperArgs(xs) {
  return xs.length > 1;
};

var ERC20TransferTier__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(ERC20TransferTier__factory, _ContractFactory);

  function ERC20TransferTier__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$d(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$k, _bytecode$d, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = ERC20TransferTier__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  ERC20TransferTier__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$k);
  };

  ERC20TransferTier__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$k, signerOrProvider);
  };

  return ERC20TransferTier__factory;
}(ContractFactory);
ERC20TransferTier__factory.bytecode = _bytecode$d;
ERC20TransferTier__factory.abi = _abi$k;

var _abi$l = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "contract IERC20",
      name: "erc20",
      type: "address"
    }, {
      internalType: "uint256[8]",
      name: "tierValues",
      type: "uint256[8]"
    }],
    internalType: "struct ERC20TransferTierConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract ERC20TransferTier",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "implementation",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$e = "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";

var isSuperArgs$e = function isSuperArgs(xs) {
  return xs.length > 1;
};

var ERC20TransferTierFactory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(ERC20TransferTierFactory__factory, _ContractFactory);

  function ERC20TransferTierFactory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$e(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$l, _bytecode$e, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = ERC20TransferTierFactory__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  ERC20TransferTierFactory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$l);
  };

  ERC20TransferTierFactory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$l, signerOrProvider);
  };

  return ERC20TransferTierFactory__factory;
}(ContractFactory);
ERC20TransferTierFactory__factory.bytecode = _bytecode$e;
ERC20TransferTierFactory__factory.abi = _abi$l;

var _abi$m = [{
  inputs: [{
    internalType: "uint256",
    name: "_size",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_start",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "_end",
    type: "uint256"
  }],
  name: "InvalidCodeAtRange",
  type: "error"
}, {
  inputs: [],
  name: "WriteError",
  type: "error"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "erc721",
    type: "address"
  }],
  name: "Initialize",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "pointer",
    type: "address"
  }],
  name: "InitializeValueTier",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "startTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "uint256",
    name: "endTier",
    type: "uint256"
  }, {
    indexed: false,
    internalType: "bytes",
    name: "data",
    type: "bytes"
  }],
  name: "TierChange",
  type: "event"
}, {
  inputs: [],
  name: "erc721",
  outputs: [{
    internalType: "contract IERC721",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "contract IERC721",
      name: "erc721",
      type: "address"
    }, {
      internalType: "uint256[8]",
      name: "tierValues",
      type: "uint256[8]"
    }],
    internalType: "struct ERC721BalanceTierConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "initialize",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account_",
    type: "address"
  }],
  name: "report",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }, {
    internalType: "uint256",
    name: "",
    type: "uint256"
  }, {
    internalType: "bytes",
    name: "",
    type: "bytes"
  }],
  name: "setTier",
  outputs: [],
  stateMutability: "pure",
  type: "function"
}, {
  inputs: [],
  name: "tierValues",
  outputs: [{
    internalType: "uint256[8]",
    name: "tierValues_",
    type: "uint256[8]"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$f = "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";

var isSuperArgs$f = function isSuperArgs(xs) {
  return xs.length > 1;
};

var ERC721BalanceTier__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(ERC721BalanceTier__factory, _ContractFactory);

  function ERC721BalanceTier__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$f(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$m, _bytecode$f, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = ERC721BalanceTier__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  ERC721BalanceTier__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$m);
  };

  ERC721BalanceTier__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$m, signerOrProvider);
  };

  return ERC721BalanceTier__factory;
}(ContractFactory);
ERC721BalanceTier__factory.bytecode = _bytecode$f;
ERC721BalanceTier__factory.abi = _abi$m;

var _abi$n = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "contract IERC721",
      name: "erc721",
      type: "address"
    }, {
      internalType: "uint256[8]",
      name: "tierValues",
      type: "uint256[8]"
    }],
    internalType: "struct ERC721BalanceTierConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract ERC721BalanceTier",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$g = "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";

var isSuperArgs$g = function isSuperArgs(xs) {
  return xs.length > 1;
};

var ERC721BalanceTierFactory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(ERC721BalanceTierFactory__factory, _ContractFactory);

  function ERC721BalanceTierFactory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$g(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$n, _bytecode$g, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = ERC721BalanceTierFactory__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  ERC721BalanceTierFactory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$n);
  };

  ERC721BalanceTierFactory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$n, signerOrProvider);
  };

  return ERC721BalanceTierFactory__factory;
}(ContractFactory);
ERC721BalanceTierFactory__factory.bytecode = _bytecode$g;
ERC721BalanceTierFactory__factory.abi = _abi$n;

var _abi$o = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "verify_",
    type: "address"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract VerifyTier",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "implementation",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$h = "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";

var isSuperArgs$h = function isSuperArgs(xs) {
  return xs.length > 1;
};

var VerifyTierFactory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(VerifyTierFactory__factory, _ContractFactory);

  function VerifyTierFactory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$h(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$o, _bytecode$h, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = VerifyTierFactory__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  VerifyTierFactory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$o);
  };

  VerifyTierFactory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$o, signerOrProvider);
  };

  return VerifyTierFactory__factory;
}(ContractFactory);
VerifyTierFactory__factory.bytecode = _bytecode$h;
VerifyTierFactory__factory.abi = _abi$o;

var _abi$p = [{
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    indexed: false,
    internalType: "struct Evidence",
    name: "evidence",
    type: "tuple"
  }],
  name: "Approve",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    indexed: false,
    internalType: "struct Evidence",
    name: "evidence",
    type: "tuple"
  }],
  name: "Ban",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "admin",
      type: "address"
    }, {
      internalType: "address",
      name: "callback",
      type: "address"
    }],
    indexed: false,
    internalType: "struct VerifyConfig",
    name: "config",
    type: "tuple"
  }],
  name: "Initialize",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    indexed: false,
    internalType: "struct Evidence",
    name: "evidence",
    type: "tuple"
  }],
  name: "Remove",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    indexed: false,
    internalType: "struct Evidence",
    name: "evidence",
    type: "tuple"
  }],
  name: "RequestApprove",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    indexed: false,
    internalType: "struct Evidence",
    name: "evidence",
    type: "tuple"
  }],
  name: "RequestBan",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    indexed: false,
    internalType: "struct Evidence",
    name: "evidence",
    type: "tuple"
  }],
  name: "RequestRemove",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "bytes32",
    name: "role",
    type: "bytes32"
  }, {
    indexed: true,
    internalType: "bytes32",
    name: "previousAdminRole",
    type: "bytes32"
  }, {
    indexed: true,
    internalType: "bytes32",
    name: "newAdminRole",
    type: "bytes32"
  }],
  name: "RoleAdminChanged",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "bytes32",
    name: "role",
    type: "bytes32"
  }, {
    indexed: true,
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "sender",
    type: "address"
  }],
  name: "RoleGranted",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: "bytes32",
    name: "role",
    type: "bytes32"
  }, {
    indexed: true,
    internalType: "address",
    name: "account",
    type: "address"
  }, {
    indexed: true,
    internalType: "address",
    name: "sender",
    type: "address"
  }],
  name: "RoleRevoked",
  type: "event"
}, {
  inputs: [],
  name: "APPROVER",
  outputs: [{
    internalType: "bytes32",
    name: "",
    type: "bytes32"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "APPROVER_ADMIN",
  outputs: [{
    internalType: "bytes32",
    name: "",
    type: "bytes32"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "BANNER",
  outputs: [{
    internalType: "bytes32",
    name: "",
    type: "bytes32"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "BANNER_ADMIN",
  outputs: [{
    internalType: "bytes32",
    name: "",
    type: "bytes32"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "DEFAULT_ADMIN_ROLE",
  outputs: [{
    internalType: "bytes32",
    name: "",
    type: "bytes32"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "REMOVER",
  outputs: [{
    internalType: "bytes32",
    name: "",
    type: "bytes32"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "REMOVER_ADMIN",
  outputs: [{
    internalType: "bytes32",
    name: "",
    type: "bytes32"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "add",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    internalType: "struct Evidence[]",
    name: "evidences_",
    type: "tuple[]"
  }],
  name: "approve",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    internalType: "struct Evidence[]",
    name: "evidences_",
    type: "tuple[]"
  }],
  name: "ban",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "callback",
  outputs: [{
    internalType: "contract IVerifyCallback",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes32",
    name: "role",
    type: "bytes32"
  }],
  name: "getRoleAdmin",
  outputs: [{
    internalType: "bytes32",
    name: "",
    type: "bytes32"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes32",
    name: "role",
    type: "bytes32"
  }, {
    internalType: "address",
    name: "account",
    type: "address"
  }],
  name: "grantRole",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes32",
    name: "role",
    type: "bytes32"
  }, {
    internalType: "address",
    name: "account",
    type: "address"
  }],
  name: "hasRole",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "admin",
      type: "address"
    }, {
      internalType: "address",
      name: "callback",
      type: "address"
    }],
    internalType: "struct VerifyConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "initialize",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    internalType: "struct Evidence[]",
    name: "evidences_",
    type: "tuple[]"
  }],
  name: "remove",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes32",
    name: "role",
    type: "bytes32"
  }, {
    internalType: "address",
    name: "account",
    type: "address"
  }],
  name: "renounceRole",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    internalType: "struct Evidence[]",
    name: "evidences_",
    type: "tuple[]"
  }],
  name: "requestApprove",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    internalType: "struct Evidence[]",
    name: "evidences_",
    type: "tuple[]"
  }],
  name: "requestBan",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "account",
      type: "address"
    }, {
      internalType: "bytes",
      name: "data",
      type: "bytes"
    }],
    internalType: "struct Evidence[]",
    name: "evidences_",
    type: "tuple[]"
  }],
  name: "requestRemove",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes32",
    name: "role",
    type: "bytes32"
  }, {
    internalType: "address",
    name: "account",
    type: "address"
  }],
  name: "revokeRole",
  outputs: [],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "account_",
    type: "address"
  }],
  name: "state",
  outputs: [{
    components: [{
      internalType: "uint32",
      name: "addedSince",
      type: "uint32"
    }, {
      internalType: "uint32",
      name: "approvedSince",
      type: "uint32"
    }, {
      internalType: "uint32",
      name: "bannedSince",
      type: "uint32"
    }],
    internalType: "struct State",
    name: "",
    type: "tuple"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "uint32",
      name: "addedSince",
      type: "uint32"
    }, {
      internalType: "uint32",
      name: "approvedSince",
      type: "uint32"
    }, {
      internalType: "uint32",
      name: "bannedSince",
      type: "uint32"
    }],
    internalType: "struct State",
    name: "state_",
    type: "tuple"
  }, {
    internalType: "uint256",
    name: "blockNumber_",
    type: "uint256"
  }],
  name: "statusAtBlock",
  outputs: [{
    internalType: "uint256",
    name: "",
    type: "uint256"
  }],
  stateMutability: "pure",
  type: "function"
}, {
  inputs: [{
    internalType: "bytes4",
    name: "interfaceId",
    type: "bytes4"
  }],
  name: "supportsInterface",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$i = "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";

var isSuperArgs$i = function isSuperArgs(xs) {
  return xs.length > 1;
};

var Verify__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(Verify__factory, _ContractFactory);

  function Verify__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$i(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$p, _bytecode$i, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = Verify__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  Verify__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$p);
  };

  Verify__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$p, signerOrProvider);
  };

  return Verify__factory;
}(ContractFactory);
Verify__factory.bytecode = _bytecode$i;
Verify__factory.abi = _abi$p;

var _abi$q = [{
  inputs: [],
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "implementation",
    type: "address"
  }],
  name: "Implementation",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: "address",
    name: "sender",
    type: "address"
  }, {
    indexed: false,
    internalType: "address",
    name: "child",
    type: "address"
  }],
  name: "NewChild",
  type: "event"
}, {
  inputs: [{
    internalType: "bytes",
    name: "data_",
    type: "bytes"
  }],
  name: "createChild",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [{
    components: [{
      internalType: "address",
      name: "admin",
      type: "address"
    }, {
      internalType: "address",
      name: "callback",
      type: "address"
    }],
    internalType: "struct VerifyConfig",
    name: "config_",
    type: "tuple"
  }],
  name: "createChildTyped",
  outputs: [{
    internalType: "contract Verify",
    name: "",
    type: "address"
  }],
  stateMutability: "nonpayable",
  type: "function"
}, {
  inputs: [],
  name: "implementation",
  outputs: [{
    internalType: "address",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "maybeChild_",
    type: "address"
  }],
  name: "isChild",
  outputs: [{
    internalType: "bool",
    name: "",
    type: "bool"
  }],
  stateMutability: "view",
  type: "function"
}];
var _bytecode$j = "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";

var isSuperArgs$j = function isSuperArgs(xs) {
  return xs.length > 1;
};

var VerifyFactory__factory = /*#__PURE__*/function (_ContractFactory) {
  _inheritsLoose(VerifyFactory__factory, _ContractFactory);

  function VerifyFactory__factory() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isSuperArgs$j(args)) {
      _this = _ContractFactory.call.apply(_ContractFactory, [this].concat(args)) || this;
    } else {
      _this = _ContractFactory.call(this, _abi$q, _bytecode$j, args[0]) || this;
    }

    return _assertThisInitialized(_this);
  }

  var _proto = VerifyFactory__factory.prototype;

  _proto.deploy = function deploy(overrides) {
    return _ContractFactory.prototype.deploy.call(this, overrides || {});
  };

  _proto.getDeployTransaction = function getDeployTransaction(overrides) {
    return _ContractFactory.prototype.getDeployTransaction.call(this, overrides || {});
  };

  _proto.attach = function attach(address) {
    return _ContractFactory.prototype.attach.call(this, address);
  };

  _proto.connect = function connect(signer) {
    return _ContractFactory.prototype.connect.call(this, signer);
  };

  VerifyFactory__factory.createInterface = function createInterface() {
    return new utils$1.Interface(_abi$q);
  };

  VerifyFactory__factory.connect = function connect(address, signerOrProvider) {
    return new Contract(address, _abi$q, signerOrProvider);
  };

  return VerifyFactory__factory;
}(ContractFactory);
VerifyFactory__factory.bytecode = _bytecode$j;
VerifyFactory__factory.abi = _abi$q;

var addressBook = [{
  /**
   * Ethereum chain
   */
  chainId: 1,
  addresses: {
    alwaysTier: '0x735ECdF9a79e62531BAB9240696a450F94AB79f8',
    combineTierFactory: '0x36983711f9C4869F0B9BEb2Cf677814bb40d41c5',
    erc20BalanceTierFactory: '0x50B2921A8c915A8228dA39e93AD9E9AA43fBc901',
    erc20TransferTierFactory: '0xB681C35244a224Fcf8c4eeC0b139980705EdF4fE',
    erc721BalanceTierFactory: '0x4Dd6BF4a433265D6B01Ae18E77Ef069Cb29a0790',
    emissionsERC20Factory: '0xe7CE1aF1e174f1526EE3f10C0cb0E01E0e47FD4E',
    gatedNFTFactory: '0x53f9810AF32AC48b04DEbc6e8f32fC245eB88cE5',
    noticeBoard: '0x1C432FBb8cd10749F3A93a91AE01f1d08FA7569a',
    redeemableERC20ClaimEscrow: '0x10DB4a5BB73150B1CEF945e90E4336b594D64DFb',
    redeemableERC20Factory: '0xD2c0729dc1A7BbeF0a770bA7F9f2DbAE9A9e27D0',
    saleFactory: '0x4dce23a63ec3f27193df8ba26141b25417815c62',
    verifyFactory: '0x039Ab991616b3B473f3096D385b6660c846F646A',
    verifyTierFactory: '0x4B2C45c3F393B803C57106C81e4E64Ce71914b8e'
  }
}, {
  /**
   * Goerli chain
   */
  chainId: 5,
  addresses: {
    alwaysTier: '0x1Fcf1f92919F0E0023463234c78296E9517b56Ea',
    combineTierFactory: '0x34A0946CEAD3417E629B2679D05C0A24d49a1eB4',
    erc20BalanceTierFactory: '0xbA9Ce92a65e172ADd70A71B6E280D9941fb9185C',
    erc20TransferTierFactory: '0xcF75a4bc22AD40D6DA6f57b5174D39D28bC85a96',
    erc721BalanceTierFactory: '0xa76F06F09346141977cF652A1EDFD09D779AA22a',
    emissionsERC20Factory: '0x586Fa008Ae3C1228B9E42452DfE04c99D7E97C35',
    gatedNFTFactory: '0xE09f5c0bA8A3C59f3c7aFFB458058881413878a2',
    noticeBoard: '0x25ACA096cbEF597Aa3B99CDD747805e58d1aea81',
    redeemableERC20ClaimEscrow: '0x00DDE5505E0075C4bF7536d3d2014895D9cbE546',
    redeemableERC20Factory: '0x16D33236D7Af17ca55E60EcE64e9F6CeD32Ba416',
    saleFactory: '0xA54f23A7B650Ea510fCA651EAfc39BFA5a04010D',
    verifyFactory: '0xa018eAB945De7e8073d1995284b78FC0cfE45B76',
    verifyTierFactory: '0x333F9355Bc47A22f64ac5C5F3d7cC5408544851f'
  }
}, {
  /**
   * BNB Mainnet
   */
  chainId: 56,
  addresses: {
    alwaysTier: '0x735ECdF9a79e62531BAB9240696a450F94AB79f8',
    combineTierFactory: '0x36983711f9C4869F0B9BEb2Cf677814bb40d41c5',
    erc20BalanceTierFactory: '0x50B2921A8c915A8228dA39e93AD9E9AA43fBc901',
    erc20TransferTierFactory: '0xB681C35244a224Fcf8c4eeC0b139980705EdF4fE',
    erc721BalanceTierFactory: '0x4Dd6BF4a433265D6B01Ae18E77Ef069Cb29a0790',
    emissionsERC20Factory: '0xe7CE1aF1e174f1526EE3f10C0cb0E01E0e47FD4E',
    gatedNFTFactory: '0x53f9810AF32AC48b04DEbc6e8f32fC245eB88cE5',
    noticeBoard: '0x1C432FBb8cd10749F3A93a91AE01f1d08FA7569a',
    redeemableERC20ClaimEscrow: '0x10db4a5bb73150b1cef945e90e4336b594d64dfb',
    redeemableERC20Factory: '0xD2c0729dc1A7BbeF0a770bA7F9f2DbAE9A9e27D0',
    saleFactory: '0x4dce23A63ec3f27193Df8bA26141b25417815C62',
    verifyFactory: '0x039Ab991616b3B473f3096D385b6660c846F646A',
    verifyTierFactory: '0x4B2C45c3F393B803C57106C81e4E64Ce71914b8e'
  }
}, {
  /**
   * BNB Testnet
   */
  chainId: 97,
  addresses: {
    alwaysTier: '0x735ECdF9a79e62531BAB9240696a450F94AB79f8',
    combineTierFactory: '0x36983711f9C4869F0B9BEb2Cf677814bb40d41c5',
    erc20BalanceTierFactory: '0x50B2921A8c915A8228dA39e93AD9E9AA43fBc901',
    erc20TransferTierFactory: '0xB681C35244a224Fcf8c4eeC0b139980705EdF4fE',
    erc721BalanceTierFactory: '0x4Dd6BF4a433265D6B01Ae18E77Ef069Cb29a0790',
    emissionsERC20Factory: '0xe7CE1aF1e174f1526EE3f10C0cb0E01E0e47FD4E',
    gatedNFTFactory: '0x53f9810AF32AC48b04DEbc6e8f32fC245eB88cE5',
    noticeBoard: '0x1C432FBb8cd10749F3A93a91AE01f1d08FA7569a',
    redeemableERC20ClaimEscrow: '0x10DB4a5BB73150B1CEF945e90E4336b594D64DFb',
    redeemableERC20Factory: '0xD2c0729dc1A7BbeF0a770bA7F9f2DbAE9A9e27D0',
    saleFactory: '0x4dce23A63ec3f27193Df8bA26141b25417815C62',
    verifyFactory: '0x039Ab991616b3B473f3096D385b6660c846F646A',
    verifyTierFactory: '0x4B2C45c3F393B803C57106C81e4E64Ce71914b8e'
  }
}, {
  /**
   * Polygon chain
   */
  chainId: 137,
  addresses: {
    alwaysTier: '0xF81572101B3bD379ADFddCBF4d46f40268475A14',
    combineTierFactory: '0xaB9D00F59A8C45b8b20F060D5F7d97FdDbA2De19',
    erc20BalanceTierFactory: '0xcCFa697D85771B67826cB7E1F7535576fA91b6b1',
    erc20TransferTierFactory: '0x807FBB1515fcFD5574b8d5FfA6e4b831d7897987',
    erc721BalanceTierFactory: '0x095Cc7BcA08F2A9c1059DE30631aA94fF9fca623',
    emissionsERC20Factory: '0xED4f245C7Afc8b52295d4045929bbdd6ba722Eaa',
    gatedNFTFactory: '0x1965F5943F8a6C18C444fad09094aB6cFfBe3F4a',
    noticeBoard: '0x790Bfb7C3E07805F9880ece979ee2f3E19f524C2',
    redeemableERC20ClaimEscrow: '0x588BFaF31410240A1A780dbd7078485A7A65305A',
    redeemableERC20Factory: '0x84A35D90D5181473662e67eFE6e2de350F9918d7',
    saleFactory: '0x6ab3Dde1e33F64F294a8b55321d345724C85E0A4',
    verifyFactory: '0xe68B2ce23d959bEe80d92b25271B767AE7A6A79D',
    verifyTierFactory: '0xB827ae1cD6D27abd56070d196A7175E02e531A5d'
  }
}, {
  /**
   * Fantom mainnet chain
   */
  chainId: 250,
  addresses: {
    alwaysTier: '0x552E9bDA51Ca6f669277dB900FC2b05fAf7A79B5',
    combineTierFactory: '0x16D33236D7Af17ca55E60EcE64e9F6CeD32Ba416',
    erc20BalanceTierFactory: '0xc50aAf61BAE0b5c69DAf59aED1Fe8dC793C44595',
    erc20TransferTierFactory: '0xFB7Cd2084f0C745193DF635413dFbc1a682bD494',
    erc721BalanceTierFactory: '0xa018eAB945De7e8073d1995284b78FC0cfE45B76',
    emissionsERC20Factory: '0xa76F06F09346141977cF652A1EDFD09D779AA22a',
    gatedNFTFactory: '0xbA9Ce92a65e172ADd70A71B6E280D9941fb9185C',
    noticeBoard: '0x34A0946CEAD3417E629B2679D05C0A24d49a1eB4',
    redeemableERC20ClaimEscrow: '0xcF75a4bc22AD40D6DA6f57b5174D39D28bC85a96',
    redeemableERC20Factory: '0xdcE20fE4E286912ec457536Ae0b3Df5fDE21a40A',
    saleFactory: '0x333F9355Bc47A22f64ac5C5F3d7cC5408544851f',
    verifyFactory: '0xc1E7CA4D153Ac0919f34198c1d029ed3391b9f29',
    verifyTierFactory: '0x3F9276cE9DDCaaB2b558DeFEA6DE1a8ee321536b'
  }
}, {
  /**
   * Fantom testnet chain
   */
  chainId: 4002,
  addresses: {
    alwaysTier: '0x01c289870AC24F51A95CBe70Cf79F43C9B6181F0',
    combineTierFactory: '0x1F19450C9a42bb831D0cD09666b6cd1094102b70',
    erc20BalanceTierFactory: '0x0109BDCFe01d46057f33658dcaE8193C7F9e631e',
    erc20TransferTierFactory: '0x29e25ED6363D93C350205424B468A9E403D65707',
    erc721BalanceTierFactory: '0x7433Be51094D9e21129a11AF1baC4F7adE4ad5ab',
    emissionsERC20Factory: '0xf66c32C3E6f4c592CF7BA42146552A9523739310',
    gatedNFTFactory: '0x2f3387650B50853Ff41A91410b4cBDAB257bf4d7',
    noticeBoard: '0xbeF822DE1fD0688BbDC569f2e13aAAf178104e12',
    redeemableERC20ClaimEscrow: '0xA53aF19D81c12E17f4fB35Efd102745d2b2FEDE8',
    redeemableERC20Factory: '0x31515Bdd855C0f105E7A0D0DD6A66bf8AEFB1f7D',
    saleFactory: '0x2381523Dac2D060F73A117a14BF6077A7C1688A9',
    verifyFactory: '0x87f5Ff1BaCb125A7d3151B673484069A47e4BF55',
    verifyTierFactory: '0xaC1f7973258b17C6fE0EDECe44Dcac1B7B610D98'
  }
}, {
  /**
   * Hardhat chain
   */
  chainId: 31337,
  addresses: {
    alwaysTier: '0x23dB4a08f2272df049a4932a4Cc3A6Dc1002B33E',
    redeemableERC20Factory: '0x5FbDB2315678afecb367f032d93F642f64180aa3',
    verifyFactory: '0xe7f1725E7734CE288F8367e1Bb143E90bb3F0512',
    verifyTierFactory: '0x9fE46736679d2D9a65F0992F2272dE9f3c7fa6e0',
    erc20BalanceTierFactory: '0xCf7Ed3AccA5a467e9e704C703E8D87F634fB0Fc9',
    erc20TransferTierFactory: '0xDc64a140Aa3E981100a9becA4E685f962f0cF6C9',
    combineTierFactory: '0x5FC8d32690cc91D4c39d9d3abcBD16989F875707',
    erc721BalanceTierFactory: '0x0165878A594ca255338adfa4d48449f69242Eb8F',
    gatedNFTFactory: '0xa513E6E4b8f2a923D98304ec87F64353C4D5C853',
    redeemableERC20ClaimEscrow: '0x2279B7A0a67DB372996a5FaB50D91eAA73d2eBe6',
    noticeBoard: '0x8A791620dd6260079BF849Dc5567aDC3F2FdC318',
    emissionsERC20Factory: '0x610178dA211FEF7D417bC0e6FeD39F05609AD788',
    saleFactory: '0xB7f8BC63BbcaD18155201308C8f3540b07f84F5e'
  }
}, {
  /**
   * Arbitrum mainnet chain
   */
  chainId: 42161,
  addresses: {
    alwaysTier: '0x552E9bDA51Ca6f669277dB900FC2b05fAf7A79B5',
    combineTierFactory: '0x16D33236D7Af17ca55E60EcE64e9F6CeD32Ba416',
    erc20BalanceTierFactory: '0xc50aAf61BAE0b5c69DAf59aED1Fe8dC793C44595',
    erc20TransferTierFactory: '0xFB7Cd2084f0C745193DF635413dFbc1a682bD494',
    erc721BalanceTierFactory: '0xa018eAB945De7e8073d1995284b78FC0cfE45B76',
    emissionsERC20Factory: '0xa76F06F09346141977cF652A1EDFD09D779AA22a',
    gatedNFTFactory: '0xbA9Ce92a65e172ADd70A71B6E280D9941fb9185C',
    noticeBoard: '0x34A0946CEAD3417E629B2679D05C0A24d49a1eB4',
    redeemableERC20ClaimEscrow: '0xcF75a4bc22AD40D6DA6f57b5174D39D28bC85a96',
    redeemableERC20Factory: '0xdcE20fE4E286912ec457536Ae0b3Df5fDE21a40A',
    saleFactory: '0x333F9355Bc47A22f64ac5C5F3d7cC5408544851f',
    verifyFactory: '0xc1E7CA4D153Ac0919f34198c1d029ed3391b9f29',
    verifyTierFactory: '0x3F9276cE9DDCaaB2b558DeFEA6DE1a8ee321536b'
  }
}, {
  /**
   * Celo mainnet chain
   */
  chainId: 42220,
  addresses: {
    alwaysTier: '0x552E9bDA51Ca6f669277dB900FC2b05fAf7A79B5',
    combineTierFactory: '0x16D33236D7Af17ca55E60EcE64e9F6CeD32Ba416',
    erc20BalanceTierFactory: '0xc50aAf61BAE0b5c69DAf59aED1Fe8dC793C44595',
    erc20TransferTierFactory: '0xFB7Cd2084f0C745193DF635413dFbc1a682bD494',
    erc721BalanceTierFactory: '0xa018eAB945De7e8073d1995284b78FC0cfE45B76',
    emissionsERC20Factory: '0xa76F06F09346141977cF652A1EDFD09D779AA22a',
    gatedNFTFactory: '0xbA9Ce92a65e172ADd70A71B6E280D9941fb9185C',
    noticeBoard: '0x34A0946CEAD3417E629B2679D05C0A24d49a1eB4',
    redeemableERC20ClaimEscrow: '0xcF75a4bc22AD40D6DA6f57b5174D39D28bC85a96',
    redeemableERC20Factory: '0xdcE20fE4E286912ec457536Ae0b3Df5fDE21a40A',
    saleFactory: '0x333F9355Bc47A22f64ac5C5F3d7cC5408544851f',
    verifyFactory: '0xc1E7CA4D153Ac0919f34198c1d029ed3391b9f29',
    verifyTierFactory: '0x3F9276cE9DDCaaB2b558DeFEA6DE1a8ee321536b'
  }
}, {
  /**
   * Fuji chain - Avalanche Testnet
   */
  chainId: 43113,
  addresses: {
    alwaysTier: '0x3f16F26C6844be3d0934EC0543039d874D45da9e',
    combineTierFactory: '0x4B8FC6f1f3f62099f4fb7151548b9c5226ceD76E',
    erc20BalanceTierFactory: '0x724007CDD1689e43c6ef55b7032b93A1cC8C02C7',
    erc20TransferTierFactory: '0xB28A191150D901cCFF849983E714d689Eb25e7eC',
    erc721BalanceTierFactory: '0xFC870c970b34926ecf224e981caCdF21e963a084',
    emissionsERC20Factory: '0x060974dd46c2dbB22Aa7a7AFE685880A4A7B34d2',
    gatedNFTFactory: '0x1dBB0186c4805238FcA1c94e38479D3a5546722D',
    noticeBoard: '0xCD2A879a770Df2Bbcc0f7496AA5105a4068Aa1e6',
    redeemableERC20ClaimEscrow: '0x8a9B7B490Feb339640dA303c3602B6786061Da30',
    redeemableERC20Factory: '0xff56cc9B3aC11Dbe1620865DC29FA2CcC4213C28',
    saleFactory: '0x4E1742656F066470cbd68B50864E168D8054E90d',
    verifyFactory: '0xBd9C59Cf73C0446341E8b1B532E9534A1E36501C',
    verifyTierFactory: '0x2a72d7fcDD6C4640EF612a6523217Df67d34cf4b'
  }
}, {
  /**
   * Avalanche chain
   */
  chainId: 43114,
  addresses: {
    alwaysTier: '0x552E9bDA51Ca6f669277dB900FC2b05fAf7A79B5',
    combineTierFactory: '0x16D33236D7Af17ca55E60EcE64e9F6CeD32Ba416',
    erc20BalanceTierFactory: '0xc50aAf61BAE0b5c69DAf59aED1Fe8dC793C44595',
    erc20TransferTierFactory: '0xFB7Cd2084f0C745193DF635413dFbc1a682bD494',
    erc721BalanceTierFactory: '0xa018eAB945De7e8073d1995284b78FC0cfE45B76',
    emissionsERC20Factory: '0xa76F06F09346141977cF652A1EDFD09D779AA22a',
    gatedNFTFactory: '0xbA9Ce92a65e172ADd70A71B6E280D9941fb9185C',
    noticeBoard: '0x34A0946CEAD3417E629B2679D05C0A24d49a1eB4',
    redeemableERC20ClaimEscrow: '0xcF75a4bc22AD40D6DA6f57b5174D39D28bC85a96',
    redeemableERC20Factory: '0xdcE20fE4E286912ec457536Ae0b3Df5fDE21a40A',
    saleFactory: '0x333F9355Bc47A22f64ac5C5F3d7cC5408544851f',
    verifyFactory: '0xc1E7CA4D153Ac0919f34198c1d029ed3391b9f29',
    verifyTierFactory: '0x3F9276cE9DDCaaB2b558DeFEA6DE1a8ee321536b'
  }
}, {
  /**
   * Celo alfajores chain - Celo testnet
   */
  chainId: 44787,
  addresses: {
    alwaysTier: '0x61904ed7643c7Bc12aaBfc6B29B830Be93231d8b',
    combineTierFactory: '0xa76F06F09346141977cF652A1EDFD09D779AA22a',
    erc20BalanceTierFactory: '0xcF75a4bc22AD40D6DA6f57b5174D39D28bC85a96',
    erc20TransferTierFactory: '0x34A0946CEAD3417E629B2679D05C0A24d49a1eB4',
    erc721BalanceTierFactory: '0xA54f23A7B650Ea510fCA651EAfc39BFA5a04010D',
    emissionsERC20Factory: '0x31aa5951100e11b8A34Bc9D98a44De152Dc5d3a2',
    gatedNFTFactory: '0x00DDE5505E0075C4bF7536d3d2014895D9cbE546',
    noticeBoard: '0x586Fa008Ae3C1228B9E42452DfE04c99D7E97C35',
    redeemableERC20ClaimEscrow: '0x25ACA096cbEF597Aa3B99CDD747805e58d1aea81',
    redeemableERC20Factory: '0xa018eAB945De7e8073d1995284b78FC0cfE45B76',
    saleFactory: '0xE09f5c0bA8A3C59f3c7aFFB458058881413878a2',
    verifyFactory: '0x333F9355Bc47A22f64ac5C5F3d7cC5408544851f',
    verifyTierFactory: '0xbA9Ce92a65e172ADd70A71B6E280D9941fb9185C'
  }
}, {
  /**
   * Mumbai chain
   */
  chainId: 80001,
  addresses: {
    alwaysTier: '0x051dC03ba4d33eCC59Fb23F506Fe1e67004F142C',
    combineTierFactory: '0x21fC48631F0efA5EFe790b5c05929cEdc271dB43',
    erc20BalanceTierFactory: '0xb1C8EA6E410a71290D5C21404D3324e61912e3c6',
    erc20TransferTierFactory: '0x2f1554BF57a234828ca3D210bA65cF80d8e5073c',
    erc721BalanceTierFactory: '0xC0E50AD884EBb7C2582677978d48338D46930a08',
    emissionsERC20Factory: '0xE51BeE9adccBafc20507e01EAA4F5aA966306669',
    gatedNFTFactory: '0xbfe6E65daB36FbdB14bC7979D5DE244628F4eD3a',
    noticeBoard: '0x4B02C8ceF32db81D4918e1C5b4b16EBA6830872a',
    redeemableERC20ClaimEscrow: '0xCd67b4ef5659B776e996Dd4Be371DCac4ABba6c8',
    redeemableERC20Factory: '0x4127ea3daf008043a3783094064670d1330d53f0',
    saleFactory: '0xE152e59e644e49AE5D73Df70658084DFc8b5CBb6',
    verifyFactory: '0x6e55b00ecfc718d202e57709a747fe3fc7f6e61e',
    verifyTierFactory: '0x2aAA6104B099B2d1DF1D984F67da042555506B71'
  }
}, {
  /**
   * Arbitrum rinkeby chain - Arbitrum testnet
   */
  chainId: 421611,
  addresses: {
    alwaysTier: '0x552E9bDA51Ca6f669277dB900FC2b05fAf7A79B5',
    combineTierFactory: '0x16D33236D7Af17ca55E60EcE64e9F6CeD32Ba416',
    erc20BalanceTierFactory: '0xc50aAf61BAE0b5c69DAf59aED1Fe8dC793C44595',
    erc20TransferTierFactory: '0xFB7Cd2084f0C745193DF635413dFbc1a682bD494',
    erc721BalanceTierFactory: '0xa018eAB945De7e8073d1995284b78FC0cfE45B76',
    emissionsERC20Factory: '0xa76F06F09346141977cF652A1EDFD09D779AA22a',
    gatedNFTFactory: '0xbA9Ce92a65e172ADd70A71B6E280D9941fb9185C',
    noticeBoard: '0x34A0946CEAD3417E629B2679D05C0A24d49a1eB4',
    redeemableERC20ClaimEscrow: '0xcF75a4bc22AD40D6DA6f57b5174D39D28bC85a96',
    redeemableERC20Factory: '0xdcE20fE4E286912ec457536Ae0b3Df5fDE21a40A',
    saleFactory: '0x333F9355Bc47A22f64ac5C5F3d7cC5408544851f',
    verifyFactory: '0xc1E7CA4D153Ac0919f34198c1d029ed3391b9f29',
    verifyTierFactory: '0x3F9276cE9DDCaaB2b558DeFEA6DE1a8ee321536b'
  }
}, {
  /**
   * Aurora mainnet chain
   */
  chainId: 1313161554,
  addresses: {
    alwaysTier: '0x552E9bDA51Ca6f669277dB900FC2b05fAf7A79B5',
    combineTierFactory: '0x16D33236D7Af17ca55E60EcE64e9F6CeD32Ba416',
    erc20BalanceTierFactory: '0xc50aAf61BAE0b5c69DAf59aED1Fe8dC793C44595',
    erc20TransferTierFactory: '0xFB7Cd2084f0C745193DF635413dFbc1a682bD494',
    erc721BalanceTierFactory: '0xa018eAB945De7e8073d1995284b78FC0cfE45B76',
    emissionsERC20Factory: '0xa76F06F09346141977cF652A1EDFD09D779AA22a',
    gatedNFTFactory: '0xbA9Ce92a65e172ADd70A71B6E280D9941fb9185C',
    noticeBoard: '0x34A0946CEAD3417E629B2679D05C0A24d49a1eB4',
    redeemableERC20ClaimEscrow: '0xcF75a4bc22AD40D6DA6f57b5174D39D28bC85a96',
    redeemableERC20Factory: '0xdcE20fE4E286912ec457536Ae0b3Df5fDE21a40A',
    saleFactory: '0x333F9355Bc47A22f64ac5C5F3d7cC5408544851f',
    verifyFactory: '0xc1E7CA4D153Ac0919f34198c1d029ed3391b9f29',
    verifyTierFactory: '0x3F9276cE9DDCaaB2b558DeFEA6DE1a8ee321536b'
  }
}, {
  /**
   * Aurora testnet chain
   */
  chainId: 1313161555,
  addresses: {
    alwaysTier: '0xd51AAeCC45bfAeC2e5bc50d5d647D2B7FF48f807',
    combineTierFactory: '0x1E4432F8d3f16A41c79640a93dF221dEAa022f50',
    erc20BalanceTierFactory: '0xbBEB09Fe86536d66C43f76BFdc30e8802Fb7e6C5',
    erc20TransferTierFactory: '0xd1db393d3dEdE96ECF48CFED9De5f5ebE2df7d6F',
    erc721BalanceTierFactory: '0xd385De5A3f0e56fC44F9726BF27ff9aD5a11B4dF',
    emissionsERC20Factory: '0x40A5EB6E16C8a61d9992a9d46FA51F1840460Fa8',
    gatedNFTFactory: '0xeA4faDB2edcC7e362C7dbCdC1A83e44E1009661A',
    noticeBoard: '0x1298E8154BF05D8701bD0d3F9504B69C46d7f4A1',
    redeemableERC20ClaimEscrow: '0xBFC5Ae6E8A111C630Bef9B76BE5a4D0c82cB2ef4',
    redeemableERC20Factory: '0xa6703bAC5A591fa7f59B1aF76060D4c34c7DaAaB',
    saleFactory: '0x7364cBbd1248573fcd7caD73a978BD7E436Effe5',
    verifyFactory: '0x125102a6Fa1f8E83Cc5329F03f179E165eF623d5',
    verifyTierFactory: '0xCB265d72a2669F1fA587327Ddb809dc5A4a6E45a'
  }
}];
var subgraphBook = {
  /**
   * Ethereum chain
   */
  1: 'https://api.thegraph.com/subgraphs/name/beehive-innovation/rain-protocol-mainnet-e590ce',

  /**
   * Goerli chain
   */
  5: 'https://api.thegraph.com/subgraphs/name/beehive-innovation/rain-protocol-goerli-e590ce',

  /**
   * BNB Mainnet
   */
  56: 'https://api.thegraph.com/subgraphs/name/beehive-innovation/rain-protocol-bnb-e590ce',

  /**
   * BNB Testnet
   */
  97: 'https://api.thegraph.com/subgraphs/name/beehive-innovation/rain-protocol-bnb-chapel-e590ce',

  /**
   * Polygon chain
   */
  137: 'https://api.thegraph.com/subgraphs/name/beehive-innovation/rain-protocol-polygon-e590ce',

  /**
   * Fantom mainnet chain
   */
  250: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-fantom-e590ce',

  /**
   * Fantom testnet chain
   */
  4002: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-fantom-testnet-e590ce',

  /**
   * Hardhat chain
   */
  31337: 'https://api.url-test',

  /**
   * Arbitrum mainnet chain
   */
  42161: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-arbitrum-e590ce',

  /**
   * Celo mainnet chain
   */
  42220: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-celo-mainnet-e590ce',

  /**
   * Fuji chain - Avalanche Testnet
   */
  43113: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-fuji-e590ce',

  /**
   * Avalanche chain
   */
  43114: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-avalanche-e590ce',

  /**
   * Celo alfajores testnet chain
   */
  44787: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-celo-alfajores-e590ce',

  /**
   * Mumbai chain
   */
  80001: 'https://api.thegraph.com/subgraphs/name/beehive-innovation/rain-protocol-mumbai-e590ce',

  /**
   * Arbitrum rinkeby testnet chain
   */
  421611: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-arbitrum-rinkeby-e590ce',

  /**
   * Aurora mainnet chain
   */
  1313161554: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-aurora-e590ce',

  /**
   * Aurora testnet chain
   */
  1313161555: 'https://api.thegraph.com/subgraphs/name/nanezx/rain-protocol-aurora-testnet-e590ce'
};
/**
 *  @public
 * Class related to handle and obtain all the addresses and tools (like Subgraph) deployed by chain and stored in the SDK.
 *
 * @remarks
 * It will get all the addresses deployed and added at the time of the current version. If the class and methods are used directly, the chain ID should
 * be provided directly too.
 */

var AddressBook = function AddressBook() {};

AddressBook.getAddressesForChainId = function (chainId) {
  var network = addressBook.find(function (n) {
    return n.chainId === chainId;
  });

  if (!(network != null && network.addresses)) {
    throw new Error('No deployed contracts for this chain.');
  }

  return network.addresses;
};

AddressBook.getSubgraphEndpoint = function (chainId) {
  var endpoint = subgraphBook[chainId];

  if (!endpoint) {
    throw new Error('No subgraph endpoint found for this chain.');
  }

  return endpoint;
};

/**
 * @public
 * Abstract class that contain all the general methods that are requirement for all the Rain contracts to
 * be able to interact correctly.
 */

var RainContract = /*#__PURE__*/function (_AddressBook) {
  _inheritsLoose(RainContract, _AddressBook);

  /**
   * The ethers signer that is connected to the instance.
   *
   * @remarks
   * This signer will be used to call and sign the tranasctions.
   */

  /**
   * The contract address of the instance.
   */

  /**
   * Name reference to find the address of the contract in the book address.
   *
   * @remarks
   * Should be implemented in each class to find the factory or main address in the book.
   */
  function RainContract(address, signer) {
    var _this;

    _this = _AddressBook.call(this) || this;
    _this.signer = void 0;
    _this.address = void 0;

    if (!utils$1.isAddress(address.toLowerCase())) {
      throw new Error('NOT A VALID FORMAT ADDRESS');
    }

    _this.signer = signer;
    _this.address = address;
    return _this;
  }
  /**
   * @public
   * Check if an address is correctly formatted and throw an error if it is not an valid address
   *
   * @param address - address to be evaluated
   * @param message - optional message to throw in case if it's not
   */


  RainContract.checkAddress = function checkAddress(address, message) {
    this._isAddress(address, message);
  }
  /**
   * @public
   * Check if an address is correctly formatted and throw an error if it is not an valid address
   *
   * @param address - address to be evaluated
   * @param message - optional message to throw in case if it's not
   */
  ;

  var _proto = RainContract.prototype;

  _proto.checkAddress = function checkAddress(address, message) {
    RainContract._isAddress(address, message);
  };

  RainContract._isAddress = function _isAddress(address, message) {
    if (message === void 0) {
      message = 'TOKEN: NOT A VALID FORMAT ADDRESS';
    }

    if (!utils$1.isAddress(address)) {
      throw new Error(message);
    }
  }
  /**
   * Get the address stored in the book for a determined chain if it is available.
   *
   * @remarks
   * If any address is deployed to the determined chain, an error will be throwed with
   * `No deployed contracts for this chain.`
   *
   * @param chainId - The chain ID where is deployed the contract
   * @returns The address for this contract
   */
  ;

  RainContract.getBookAddress = function getBookAddress(chainId) {
    return this.getAddressesForChainId(chainId)[this.nameBookReference];
  }
  /**
   * Get the chain ID from a valid ethers provider.
   *
   * Request to the provider stored in the signer which is the chain ID.
   *
   * @param signerOrProvider - An ethers signer or ethers provider
   * @returns The chain ID
   */
  ;

  return RainContract;
}(AddressBook);
RainContract.nameBookReference = void 0;

RainContract.getChainId = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(signerOrProvider) {
    var id, _yield$signerOrProvid, _signerOrProvider$pro;

    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (!Signer.isSigner(signerOrProvider)) {
              _context.next = 12;
              break;
            }

            _context.next = 3;
            return (_signerOrProvider$pro = signerOrProvider.provider) == null ? void 0 : _signerOrProvider$pro.getNetwork();

          case 3:
            _context.t0 = _yield$signerOrProvid = _context.sent;

            if (!(_context.t0 == null)) {
              _context.next = 8;
              break;
            }

            _context.t1 = void 0;
            _context.next = 9;
            break;

          case 8:
            _context.t1 = _yield$signerOrProvid.chainId;

          case 9:
            id = _context.t1;
            _context.next = 15;
            break;

          case 12:
            _context.next = 14;
            return signerOrProvider.getNetwork();

          case 14:
            id = _context.sent.chainId;

          case 15:
            if (!id) {
              _context.next = 19;
              break;
            }

            return _context.abrupt("return", id);

          case 19:
            throw new Error('Cannot get the chain ID');

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function (_x) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @public
 * All contract factory should use this instead of directly Rain contract to take advantage
 * of specific method to factories
 */

var FactoryContract = /*#__PURE__*/function (_RainContract) {
  _inheritsLoose(FactoryContract, _RainContract);

  function FactoryContract() {
    return _RainContract.apply(this, arguments) || this;
  }

  /**
   * Get the child from a receipt obtain from a Factory transaction
   *
   * @param receipt - The receipt of the transaction
   * @param parentContract - Contract factory/parent that create the child. Can be the instance or the address
   * @returns The address of the child
   */
  FactoryContract.getNewChildFromReceipt = function getNewChildFromReceipt(receipt, parentContract) {
    var _receipt$events, _receipt$events$find;

    var parentAddress = parentContract instanceof Contract ? parentContract.address : parentContract;
    var data = (_receipt$events = receipt.events) == null ? void 0 : (_receipt$events$find = _receipt$events.find(function (x) {
      return x.event === 'NewChild' && x.address.toLowerCase() === parentAddress.toLowerCase();
    })) == null ? void 0 : _receipt$events$find.data;

    if (data) {
      return utils$1.defaultAbiCoder.decode(['address', 'address'], data)[1];
    } else {
      throw new Error('NewChild not found in the receipt');
    }
  }
  /**
   * Checks if address is registered as a child contract of the factory in the chain.
   *
   * @param signer - An ethers.js Signer
   * @param maybeChild - Address to check registration for.
   * @returns `true` if address was deployed by this contract factory, otherwise `false`
   */
  ;

  FactoryContract._isChild =
  /*#__PURE__*/
  function () {
    var _isChild2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(signer, maybeChild) {
      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.t0 = Factory__factory;
              _context.t1 = this;
              _context.next = 4;
              return this.getChainId(signer);

            case 4:
              _context.t2 = _context.sent;
              _context.t3 = _context.t1.getBookAddress.call(_context.t1, _context.t2);
              _context.t4 = signer;
              _context.next = 9;
              return _context.t0.connect.call(_context.t0, _context.t3, _context.t4).isChild(maybeChild);

            case 9:
              return _context.abrupt("return", _context.sent);

            case 10:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    }));

    function _isChild(_x, _x2) {
      return _isChild2.apply(this, arguments);
    }

    return _isChild;
  }();

  return FactoryContract;
}(RainContract);

/**
 * @public
 * All the contract tier levels availables in all ITier contracts.
 */

var Tier;

(function (Tier) {
  /**
   * Contract tier level 0. This represent that the uset never has been
   * interacted with the Tier contract.
   */
  Tier[Tier["ZERO"] = 0] = "ZERO";
  /**
   * Contract tier level 1.
   */

  Tier[Tier["ONE"] = 1] = "ONE";
  /**
   * Contract tier level 2.
   */

  Tier[Tier["TWO"] = 2] = "TWO";
  /**
   * Contract tier level 3.
   */

  Tier[Tier["THREE"] = 3] = "THREE";
  /**
   * Contract tier level 4.
   */

  Tier[Tier["FOUR"] = 4] = "FOUR";
  /**
   * Contract tier level 5.
   */

  Tier[Tier["FIVE"] = 5] = "FIVE";
  /**
   * Contract tier level 6.
   */

  Tier[Tier["SIX"] = 6] = "SIX";
  /**
   * Contract tier level 7.
   */

  Tier[Tier["SEVEN"] = 7] = "SEVEN";
  /**
   * Contract tier level 8.
   */

  Tier[Tier["EIGHT"] = 8] = "EIGHT";
})(Tier || (Tier = {}));
/**
 * @public
 * Combine the static methods that are present in factories with the ITier instance methods.
 * Should be use to the TierFactories.
 */


var TierContract = /*#__PURE__*/function (_FactoryContract) {
  _inheritsLoose(TierContract, _FactoryContract);

  function TierContract(address, signer) {
    var _this;

    _this = _FactoryContract.call(this, address, signer) || this;
    _this.levels = Tier;
    _this.report = void 0;
    _this.setTier = void 0;
    var tier = ITier__factory.connect(address, signer);
    _this.report = tier.report;
    _this.setTier = tier.setTier;
    return _this;
  }
  /** {@inheritDoc Tier} */


  var _proto = TierContract.prototype;

  /**
   * Get the current tier of an `account` in the Tier as an expression between `[0 - 8]`.
   * Tier 0 is that a address has never interact with the Tier Contract.
   *
   * @param account - address to check the current tier
   * @param block - (optional) check the level tier of an account with respect to a specific block
   * @returns current tier level of the account
   */
  _proto.currentTier =
  /*#__PURE__*/
  function () {
    var _currentTier = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(account, block) {
      var _this$signer$provider, _paddedUInt256$substr;

      var currentTier, againstBlock, parsedReport, eligibleStatus, i;
      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return this.report(account);

            case 2:
              currentTier = _context.sent;

              if (!block) {
                _context.next = 7;
                break;
              }

              _context.t0 = block;
              _context.next = 10;
              break;

            case 7:
              _context.next = 9;
              return (_this$signer$provider = this.signer.provider) == null ? void 0 : _this$signer$provider.getBlockNumber();

            case 9:
              _context.t0 = _context.sent;

            case 10:
              againstBlock = _context.t0;
              parsedReport = (_paddedUInt256$substr = paddedUInt256(currentTier).substring(2).match(/.{1,8}/g)) == null ? void 0 : _paddedUInt256$substr.reverse().map(function (x) {
                return parseInt('0x' + x);
              });
              eligibleStatus = 0;

              if (!(parsedReport && againstBlock)) {
                _context.next = 24;
                break;
              }

              i = 0;

            case 15:
              if (!(i < 8)) {
                _context.next = 24;
                break;
              }

              if (!(parsedReport[i] <= againstBlock)) {
                _context.next = 20;
                break;
              }

              eligibleStatus = i + 1;
              _context.next = 21;
              break;

            case 20:
              return _context.abrupt("break", 24);

            case 21:
              i++;
              _context.next = 15;
              break;

            case 24:
              return _context.abrupt("return", eligibleStatus);

            case 25:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    }));

    function currentTier(_x, _x2) {
      return _currentTier.apply(this, arguments);
    }

    return currentTier;
  }();

  return TierContract;
}(FactoryContract);

/**
 * @public
 *
 * All the standard Op Codes
 */

var AllStandardOps;

(function (AllStandardOps) {
  /**
   * @deprecated **DONT USE SKIP!**
   *
   * It is a skip as this is the fallback value for unset solidity bytes.
   * Any additional "whitespace" in rain scripts will be noops as `0 0` is
   * "skip self". The val can be used to skip additional opcodes but take
   * care to not underflow the source itself.
   */
  AllStandardOps[AllStandardOps["SKIP"] = 0] = "SKIP";
  /**
   * Copies a value either off `constants` or `arguments` to the top of
   * the stack. The high bit of the operand specifies which, `0` for
   * `constants` and `1` for `arguments`.
   */

  AllStandardOps[AllStandardOps["VAL"] = 1] = "VAL";
  /**
   * Duplicates the value atoptions.index `operand_` to the top of the stack
   */

  AllStandardOps[AllStandardOps["DUP"] = 2] = "DUP";
  /**
   * Takes N values off the stack, interprets them as an array then zips
   * and maps a source from `sources` over them. The source has access to
   * the original constants using `1 0` and to zipped arguments as `1 1`.
   */

  AllStandardOps[AllStandardOps["ZIPMAP"] = 3] = "ZIPMAP";
  /**
   * ABI encodes the entire stack and logs it to the hardhat console.
   */

  AllStandardOps[AllStandardOps["DEBUG"] = 4] = "DEBUG";
  /**
   * Opcode for the block number.
   */

  AllStandardOps[AllStandardOps["BLOCK_NUMBER"] = 5] = "BLOCK_NUMBER";
  /**
   * Opcode for the block timestamp.
   */

  AllStandardOps[AllStandardOps["BLOCK_TIMESTAMP"] = 6] = "BLOCK_TIMESTAMP";
  /**
   * Opcode for the `msg.sender`.
   */

  AllStandardOps[AllStandardOps["SENDER"] = 7] = "SENDER";
  /**
   * Opcode for `this` address of the current contract.
   */

  AllStandardOps[AllStandardOps["THIS_ADDRESS"] = 8] = "THIS_ADDRESS";
  /**
   * Opcode for multiplication.
   */

  AllStandardOps[AllStandardOps["SCALE18_MUL"] = 9] = "SCALE18_MUL";
  /**
   * Opcode for division.
   */

  AllStandardOps[AllStandardOps["SCALE18_DIV"] = 10] = "SCALE18_DIV";
  /**
   * Opcode to rescale some fixed point number to 18 OOMs in situ.
   */

  AllStandardOps[AllStandardOps["SCALE18"] = 11] = "SCALE18";
  /**
   * Opcode to rescale an 18 OOMs fixed point number to scale N.
   */

  AllStandardOps[AllStandardOps["SCALEN"] = 12] = "SCALEN";
  /**
   * Opcode to rescale an arbitrary fixed point number by some OOMs.
   */

  AllStandardOps[AllStandardOps["SCALE_BY"] = 13] = "SCALE_BY";
  /**
   * Opcode for stacking the definition of one.
   */

  AllStandardOps[AllStandardOps["SCALE18_ONE"] = 14] = "SCALE18_ONE";
  /**
   * Opcode for stacking number of fixed point decimals used.
   */

  AllStandardOps[AllStandardOps["SCALE18_DECIMALS"] = 15] = "SCALE18_DECIMALS";
  /**
   * Opcode for addition.
   */

  AllStandardOps[AllStandardOps["ADD"] = 16] = "ADD";
  /**
   * Opcode for saturating addition.
   */

  AllStandardOps[AllStandardOps["SATURATING_ADD"] = 17] = "SATURATING_ADD";
  /**
   * Opcode for subtraction.
   */

  AllStandardOps[AllStandardOps["SUB"] = 18] = "SUB";
  /**
   * Opcode for saturating subtraction.
   */

  AllStandardOps[AllStandardOps["SATURATING_SUB"] = 19] = "SATURATING_SUB";
  /**
   * Opcode for multiplication.
   */

  AllStandardOps[AllStandardOps["MUL"] = 20] = "MUL";
  /**
   * Opcode for saturating multiplication.
   */

  AllStandardOps[AllStandardOps["SATURATING_MUL"] = 21] = "SATURATING_MUL";
  /**
   * Opcode for division
   */

  AllStandardOps[AllStandardOps["DIV"] = 22] = "DIV";
  /**
   * Opcode for modulo.
   */

  AllStandardOps[AllStandardOps["MOD"] = 23] = "MOD";
  /**
   * Opcode for exponentiation.
   */

  AllStandardOps[AllStandardOps["EXP"] = 24] = "EXP";
  /**
   * Opcode for minimum.
   */

  AllStandardOps[AllStandardOps["MIN"] = 25] = "MIN";
  /**
   * Opcode for maximum.
   */

  AllStandardOps[AllStandardOps["MAX"] = 26] = "MAX";
  /**
   * Opcode for ISZERO.
   */

  AllStandardOps[AllStandardOps["ISZERO"] = 27] = "ISZERO";
  /**
   * Eager because BOTH x_ and y_ must be eagerly evaluated
   * before EAGER_IF will select one of them. If both x_ and y_
   * are cheap (e.g. constant values) then this may also be the
   * simplest and cheapest way to select one of them. If either
   * x_ or y_ is expensive consider using the conditional form
   * of OP_SKIP to carefully avoid it instead.
   */

  AllStandardOps[AllStandardOps["EAGER_IF"] = 28] = "EAGER_IF";
  /**
   * Opcode for EQUAL_TO.
   */

  AllStandardOps[AllStandardOps["EQUAL_TO"] = 29] = "EQUAL_TO";
  /**
   * Opcode for LESS_THAN.
   */

  AllStandardOps[AllStandardOps["LESS_THAN"] = 30] = "LESS_THAN";
  /**
   * Opcode for GREATER_THAN.
   */

  AllStandardOps[AllStandardOps["GREATER_THAN"] = 31] = "GREATER_THAN";
  /**
   * Opcode for EVERY.
   */

  AllStandardOps[AllStandardOps["EVERY"] = 32] = "EVERY";
  /**
   * Opcode for ANY.
   */

  AllStandardOps[AllStandardOps["ANY"] = 33] = "ANY";
  /**
   * Opcode to call `report` on an `ITier` contract.
   */

  AllStandardOps[AllStandardOps["REPORT"] = 34] = "REPORT";
  /**
   * Opcode to stack a report that has never been held for all tiers.
   */

  AllStandardOps[AllStandardOps["NEVER"] = 35] = "NEVER";
  /**
   * Opcode to stack a report that has always been held for all tiers.
   */

  AllStandardOps[AllStandardOps["ALWAYS"] = 36] = "ALWAYS";
  /**
   * Opcode to calculate the tierwise diff of two reports.
   */

  AllStandardOps[AllStandardOps["SATURATING_DIFF"] = 37] = "SATURATING_DIFF";
  /**
   * Opcode to update the blocks over a range of tiers for a report.
   */

  AllStandardOps[AllStandardOps["UPDATE_BLOCKS_FOR_TIER_RANGE"] = 38] = "UPDATE_BLOCKS_FOR_TIER_RANGE";
  /**
   * Opcode to tierwise select the best block lte a reference block.
   */

  AllStandardOps[AllStandardOps["SELECT_LTE"] = 39] = "SELECT_LTE";
  /**
   * Opcode for `IERC20` `balanceOf`.
   */

  AllStandardOps[AllStandardOps["IERC20_BALANCE_OF"] = 40] = "IERC20_BALANCE_OF";
  /**
   * Opcode for `IERC20` `totalSupply`.
   */

  AllStandardOps[AllStandardOps["IERC20_TOTAL_SUPPLY"] = 41] = "IERC20_TOTAL_SUPPLY";
  /**
   * Opcode for `IERC721` `balanceOf`.
   */

  AllStandardOps[AllStandardOps["IERC721_BALANCE_OF"] = 42] = "IERC721_BALANCE_OF";
  /**
   * Number of provided opcodes for `IERC721Ops`.
   */

  AllStandardOps[AllStandardOps["IERC721_OWNER_OF"] = 43] = "IERC721_OWNER_OF";
  /**
   * Opcode for `IERC1155` `balanceOf`.
   */

  AllStandardOps[AllStandardOps["IERC1155_BALANCE_OF"] = 44] = "IERC1155_BALANCE_OF";
  /**
   * Number of provided opcodes for `IERC1155Ops`.
   */

  AllStandardOps[AllStandardOps["IERC1155_BALANCE_OF_BATCH"] = 45] = "IERC1155_BALANCE_OF_BATCH";
  /**
   * Length of the Standard Opcodes
   */

  AllStandardOps[AllStandardOps["length"] = 46] = "length";
})(AllStandardOps || (AllStandardOps = {}));
/**
 * @public
 * Class related to hold the some of the common patterns around the Rain VM that are required to interact
 * with it.
 *
 * @remarks
 * The class is intented to contain general methods that can be used in a lot of scenarios and ways.
 */


var VM = /*#__PURE__*/function () {
  function VM() {}

  /**
   * All the standard Op Codes
   */

  /**
   * Create a VM sources to be ready to use in any call just providing the combination desired.
   *
   * @param OPerands - All the configuration with the opcodes and operands. If any combination
   * does not have an operand with an opcode, a 0 (zero) will be use with the opcode as the
   * operand. Please @see OPerand
   * @returns A source
   */
  VM.createVMSources = function createVMSources(OPerands) {
    // return [concat(OPerands.map((x) => op(x[0], x[1] || 0)))];
    return [concat(OPerands.map(function (x) {
      if (x.constructor === Uint8Array) {
        return x;
      } else {
        return op(x[0], x[1] || 0);
      }
    }))];
  }
  /**
   * Combines 2 individual VM scripts
   *
   * @remarks - please be aware if your script has DUP opcode, as DUP is relative to script and cannot be handled by this method
   * and needs to be dealt with manualy before calling this method.
   *
   * @param config1 - the first VM script that will be combined. (default sits at top)
   * @param config2 - the second VM script that will be combined. (default sits at bottom)
   * @param options - used for additional configuraions:
   *    - (param) index - to identify which sources item in config1.sources the combination starts at, if not specified, it will be 0.
   *    - (param) numberOfSource - for specifying how many sources item to combine.
   *    - (param) position - An array representing the positions of config1 script where config2 sources
   *       will be merged at; position, array length must be equal to 'numberOfSources' or else it will be ignored.
   *
   * @returns combined VM script. @see StateConfig
   */
  ;

  VM.combiner = function combiner(config1, config2, options) {
    var Index = options != null && options.index ? options.index : 0;
    var NumberOfSources = options != null && options.numberOfSources ? options.numberOfSources : 1;
    var constants = [].concat(config1.constants, config2.constants);

    for (var i = 0; i < config2.sources.length; i++) {
      var sourceModify = arrayify(config2.sources[i], {
        allowMissingPrefix: true
      });

      for (var j = 0; j < sourceModify.length; j++) {
        if (sourceModify[j] == 1) {
          var argCheck = sourceModify[j + 1] >> 7;

          if (!argCheck) {
            sourceModify[j + 1] += config1.constants.length;
          }
        }

        if (sourceModify[j] == 3) {
          var srcIndexIncrement = config1.sources.length - NumberOfSources;
          var srcIndex = sourceModify[j + 1] & 7;
          sourceModify[j + 1] = srcIndex < NumberOfSources ? sourceModify[j + 1] + Index : sourceModify[j + 1] + srcIndexIncrement;
        }

        j++;
      }

      config2.sources[i] = sourceModify;
    }

    if (options != null && options.position && options.position.length == NumberOfSources) {
      for (var _i = 0; _i < NumberOfSources; _i++) {
        var _sourceModify = arrayify(config1.sources[Index + _i], {
          allowMissingPrefix: true
        });

        config1.sources[Index + _i] = concat([_sourceModify.subarray(0, options.position[_i] * 2), config2.sources[_i], _sourceModify.subarray(options.position[_i] * 2)]);
      }
    } else {
      for (var _i2 = 0; _i2 < NumberOfSources; _i2++) {
        config1.sources[Index + _i2] = concat([config1.sources[Index + _i2], config2.sources[_i2]]);
      }
    }

    config2.sources.splice(0, NumberOfSources);
    var sources = [].concat(config1.sources, config2.sources);
    var finalStackLength = BigNumber.from(config1.stackLength).add(config2.stackLength);
    var finalArgumentsLength = BigNumber.from(config1.argumentsLength).add(config2.argumentsLength);
    return {
      constants: constants,
      sources: sources,
      stackLength: finalStackLength,
      argumentsLength: finalArgumentsLength
    };
  }
  /**
   * Make an address the owner of a VM Script - checks the sender address against the owner address and if it passes the final
   * result will be determined by the main VM script and if it fails it will be 0 by default.
   *
   * @remarks - please be aware if your script has DUP opcode, as DUP is relative to script and cannot be handled by this method
   * and needs to be dealt with manualy before calling this method.
   *
   * @param config - the main VM script
   * @param ownerAddress - the address that is going to be the owner of the main VM script.
   * @param options - used for additional configuraions:
   *    - (param) index - to identify which sources item in config.sources the combination starts at, if not specified, it will be 0.
   *    - (param) position - An array representing the positions of config script where notOwnerVar sources (if exists)
   *       will be merged at; position, array length must be equal to 'numberOfSources' or else it will be ignored.
   *    - (param) notOwnerVar - the value or the script that will be executed if the owner check fails, if not specified 0 will be applied.
   *
   * @returns a VM script. @see StateConfig
   */
  ;

  VM.toOwnerMaker = function toOwnerMaker(config, ownerAddress, options) {
    var Index = options != null && options.index ? options.index : 0;

    var MAKE_OWNER = function MAKE_OWNER(i) {
      return concat([op(VM.Opcodes.VAL, i), op(VM.Opcodes.SENDER), op(VM.Opcodes.EQUAL_TO)]);
    };

    if (options != null && options.notOwnerVar && typeof options.notOwnerVar === 'object') {
      var _result = this.combiner(config, options.notOwnerVar, {
        index: options.index,
        position: options.position
      });

      _result.constants.push(ownerAddress);

      _result.sources[Index] = concat([MAKE_OWNER(_result.constants.length - 1), _result.sources[Index], op(VM.Opcodes.EAGER_IF)]);
      _result.stackLength = BigNumber.from(_result.stackLength).add(4);
      return _result;
    } else {
      var NotOwnerVar = options != null && options.notOwnerVar && typeof (options == null ? void 0 : options.notOwnerVar) == 'number' ? options.notOwnerVar : 0;
      var constants = [].concat(config.constants, [ownerAddress, NotOwnerVar]);
      config.sources[Index] = concat([MAKE_OWNER(constants.length - 2), config.sources[Index], op(VM.Opcodes.VAL, constants.length - 1), op(VM.Opcodes.EAGER_IF)]);
      var sources = config.sources;
      return {
        constants: constants,
        sources: sources,
        stackLength: Number(config.stackLength) + 5,
        argumentsLength: config.argumentsLength
      };
    }
  }
  /**
   * Deducts percentage off of the result of a VM script based on the holding tier of a tier contract.
   *
   * @param config - the main VM script
   * @param tierAddress - the contract address of the tier contract.
   * @param tierDiscount - an array of 8 items - the discount value (range 0 - 99) of each tier are the 8 items of the array.
   * @param options - used for additional configuraions:
   *    - (param) index to identify which sources item in config.sources the tierMultiplier applies to, if not specified, it will be 0.
   *    - (param) tierActivation An array of numbers, representing the amount of blocks each tier must hold in order to get the discount,
   *       e.g. the first item in array is 100 mean tier 1 needs to be held at least 100 blocks to get the discount.
   *
   * @returns a VM script @see StateConfig
   */
  ;

  VM.toTierDiscounter = function toTierDiscounter(config, tierAddress, tierDiscount, options) {
    var Index = options != null && options.index ? options.index : 0;
    var TierDiscount = paddedUInt256(BigNumber.from('0x' + paddedUInt32(100 - tierDiscount[7]) + paddedUInt32(100 - tierDiscount[6]) + paddedUInt32(100 - tierDiscount[5]) + paddedUInt32(100 - tierDiscount[4]) + paddedUInt32(100 - tierDiscount[3]) + paddedUInt32(100 - tierDiscount[2]) + paddedUInt32(100 - tierDiscount[1]) + paddedUInt32(100 - tierDiscount[0])));

    var TIER_BASED_DIS = function TIER_BASED_DIS(i) {
      return concat([op(VM.Opcodes.NEVER), op(VM.Opcodes.VAL, i - 1), op(VM.Opcodes.UPDATE_BLOCKS_FOR_TIER_RANGE, tierRange(0, 8)), op(VM.Opcodes.VAL, i - 2), op(VM.Opcodes.VAL, i - 3), op(VM.Opcodes.SENDER), op(VM.Opcodes.REPORT), op(VM.Opcodes.BLOCK_NUMBER), op(VM.Opcodes.SELECT_LTE, selectLte(selectLteLogic.every, selectLteMode.first, 2)), op(VM.Opcodes.SATURATING_DIFF)]);
    };

    var TIER_BASED_DIS_ZIMAP = function TIER_BASED_DIS_ZIMAP(i, sourceIndex, valSize) {
      return concat([op(VM.Opcodes.ZIPMAP, callSize(sourceIndex, 3, valSize)), op(VM.Opcodes.MIN, 8), op(VM.Opcodes.MUL, 2), op(VM.Opcodes.VAL, i - 1), op(VM.Opcodes.DIV, 2)]);
    };

    var ACTIVATION_TIME = function ACTIVATION_TIME(i) {
      return concat([op(VM.Opcodes.NEVER), op(VM.Opcodes.BLOCK_NUMBER), op(VM.Opcodes.UPDATE_BLOCKS_FOR_TIER_RANGE, tierRange(0, 8)), op(VM.Opcodes.VAL, i - 3), op(VM.Opcodes.SENDER), op(VM.Opcodes.REPORT), op(VM.Opcodes.SATURATING_DIFF), op(VM.Opcodes.VAL, i - 4)]);
    };

    var TIER_BASED_DIS_FN = function TIER_BASED_DIS_FN(i) {
      return concat([op(VM.Opcodes.VAL, i - 1), op(VM.Opcodes.VAL, arg(0)), op(VM.Opcodes.SUB, 2)]);
    };

    var ACTIVATION_TIME_FN = function ACTIVATION_TIME_FN(i) {
      return concat([op(VM.Opcodes.VAL, arg(1)), op(VM.Opcodes.VAL, arg(2)), op(VM.Opcodes.LESS_THAN), op(VM.Opcodes.VAL, i - 1)]);
    };

    if (options != null && options.tierActivation && options.tierActivation.length > 7) {
      var TierDiscountActivation = paddedUInt256(BigNumber.from('0x' + paddedUInt32(options.tierActivation[7]) + paddedUInt32(options.tierActivation[6]) + paddedUInt32(options.tierActivation[5]) + paddedUInt32(options.tierActivation[4]) + paddedUInt32(options.tierActivation[3]) + paddedUInt32(options.tierActivation[2]) + paddedUInt32(options.tierActivation[1]) + paddedUInt32(options.tierActivation[0])));
      var constants = [].concat(config.constants, [TierDiscountActivation, tierAddress, TierDiscount, '100']);
      config.sources[Index] = concat([config.sources[Index], TIER_BASED_DIS(constants.length), ACTIVATION_TIME(constants.length), TIER_BASED_DIS_ZIMAP(constants.length, config.sources.length, 2)]);
      var sources = [].concat(config.sources, [concat([ACTIVATION_TIME_FN(constants.length), TIER_BASED_DIS_FN(constants.length), op(VM.Opcodes.EAGER_IF)])]);
      return {
        constants: constants,
        sources: sources,
        stackLength: Number(config.stackLength) + 30,
        argumentsLength: Number(config.argumentsLength) + 3
      };
    } else {
      var _constants = [].concat(config.constants, [tierAddress, TierDiscount, '100']);

      config.sources[Index] = concat([config.sources[Index], TIER_BASED_DIS(_constants.length), TIER_BASED_DIS_ZIMAP(_constants.length, config.sources.length, 0)]);

      var _sources = [].concat(config.sources, [TIER_BASED_DIS_FN(_constants.length)]);

      return {
        constants: _constants,
        sources: _sources,
        stackLength: Number(config.stackLength) + 20,
        argumentsLength: Number(config.argumentsLength) + 1
      };
    }
  }
  /**
   * Multiply the result of a VM script based on the holding tier of a tier contract.
   *
   * @param config - the main VM script
   * @param tierAddress - the contract address of the tier contract.
   * @param tierMultiplier - an array of 8 items - the multiplier value (2 decimals max) of each tier are the 8 items of the array.
   * @param options - used for additional configuraions:
   *    - (param) index to identify which sources item in config.sources the tierMultiplier applies to, if not specified, it will be 0.
   *    - (param) tierActivation An array of numbers, representing the amount of blocks each tier must hold in order to get the multiplier,
   *       e.g. the first item in array is 100 mean tier 1 needs to be held at least 100 blocks to get the multiplier.
   *
   * @returns a VM script @see StateConfig
   */
  ;

  VM.toTierMultiplier = function toTierMultiplier(config, tierAddress, tierMultiplier, options) {
    var Index = options != null && options.index ? options.index : 0;
    var TierMultiplier = paddedUInt256(BigNumber.from('0x' + paddedUInt32(Math.floor(tierMultiplier[7] * 100)) + paddedUInt32(Math.floor(tierMultiplier[6] * 100)) + paddedUInt32(Math.floor(tierMultiplier[5] * 100)) + paddedUInt32(Math.floor(tierMultiplier[4] * 100)) + paddedUInt32(Math.floor(tierMultiplier[3] * 100)) + paddedUInt32(Math.floor(tierMultiplier[2] * 100)) + paddedUInt32(Math.floor(tierMultiplier[1] * 100)) + paddedUInt32(Math.floor(tierMultiplier[0] * 100))));

    var TIER_BASED_MUL = function TIER_BASED_MUL(i) {
      return concat([op(VM.Opcodes.VAL, i - 3), op(VM.Opcodes.VAL, i - 4), op(VM.Opcodes.SENDER), op(VM.Opcodes.REPORT), op(VM.Opcodes.BLOCK_NUMBER), op(VM.Opcodes.SELECT_LTE, selectLte(selectLteLogic.every, selectLteMode.first, 2))]);
    };

    var TIER_BASED_MUL_ZIPMAP = function TIER_BASED_MUL_ZIPMAP(i, sourceIndex, valSize) {
      return concat([op(VM.Opcodes.ZIPMAP, callSize(sourceIndex, 3, valSize)), op(VM.Opcodes.MAX, 8), op(VM.Opcodes.MUL, 2), op(VM.Opcodes.VAL, i - 2), op(VM.Opcodes.DIV, 2)]);
    };

    var ACTIVATION_TIME = function ACTIVATION_TIME(i) {
      return concat([op(VM.Opcodes.NEVER), op(VM.Opcodes.BLOCK_NUMBER), op(VM.Opcodes.UPDATE_BLOCKS_FOR_TIER_RANGE, tierRange(0, 8)), op(VM.Opcodes.VAL, i - 4), op(VM.Opcodes.SENDER), op(VM.Opcodes.REPORT), op(VM.Opcodes.SATURATING_DIFF), op(VM.Opcodes.VAL, i - 5)]);
    };

    var TIER_BASED_MUL_FN = function TIER_BASED_MUL_FN(i) {
      return concat([op(VM.Opcodes.VAL, arg(0)), op(VM.Opcodes.VAL, i - 1), op(VM.Opcodes.LESS_THAN), op(VM.Opcodes.VAL, arg(0)), op(VM.Opcodes.VAL, i - 2), op(VM.Opcodes.EAGER_IF)]);
    };

    var ACTIVATION_TIME_FN = function ACTIVATION_TIME_FN(i) {
      return concat([op(VM.Opcodes.VAL, arg(1)), op(VM.Opcodes.VAL, arg(2)), op(VM.Opcodes.LESS_THAN), op(VM.Opcodes.VAL, i - 2)]);
    };

    if (options != null && options.tierActivation && options.tierActivation.length > 7) {
      var TierMultiplierActivation = paddedUInt256(BigNumber.from('0x' + paddedUInt32(options.tierActivation[7]) + paddedUInt32(options.tierActivation[6]) + paddedUInt32(options.tierActivation[5]) + paddedUInt32(options.tierActivation[4]) + paddedUInt32(options.tierActivation[3]) + paddedUInt32(options.tierActivation[2]) + paddedUInt32(options.tierActivation[1]) + paddedUInt32(options.tierActivation[0])));
      var constants = [].concat(config.constants, [TierMultiplierActivation, tierAddress, TierMultiplier, '100', '0xffffffff']);
      config.sources[Index] = concat([config.sources[Index], TIER_BASED_MUL(constants.length), ACTIVATION_TIME(constants.length), TIER_BASED_MUL_ZIPMAP(constants.length, config.sources.length, 2)]);
      var sources = [].concat(config.sources, [concat([ACTIVATION_TIME_FN(constants.length), TIER_BASED_MUL_FN(constants.length), op(VM.Opcodes.EAGER_IF)])]);
      return {
        constants: constants,
        sources: sources,
        stackLength: Number(config.stackLength) + 30,
        argumentsLength: Number(config.argumentsLength) + 3
      };
    } else {
      var _constants2 = [].concat(config.constants, [tierAddress, TierMultiplier, '100', '0xffffffff']);

      config.sources[Index] = concat([config.sources[Index], TIER_BASED_MUL(_constants2.length), TIER_BASED_MUL_ZIPMAP(_constants2.length, config.sources.length, 0)]);

      var _sources2 = [].concat(config.sources, [TIER_BASED_MUL_FN(_constants2.length)]);

      return {
        constants: _constants2,
        sources: _sources2,
        stackLength: Number(config.stackLength) + 20,
        argumentsLength: Number(config.argumentsLength) + 1
      };
    }
  }
  /**
   * A method to merge multiple (more than 1) scripts to be executed based on time slices.
   *
   * @param configs - An array of StateConfigs that will be merged and executed at runtime in order by time slices
   * @param times - An array of numbers representing either BLOCK_NUMBER or TIMESTAMP that time slices will be between each of the 2 items in the array
   * its length should be number of configs - 1.
   * @param inBlockNumber - (optional) false by default which means the time slices will be based on TIMESTAMP, pass true to base it on BLOCK_NUMBER
   *
   * @returns a VM script @see StateConfig
   */
  ;

  VM.toTimeSlicer = function toTimeSlicer(configs, times, inBlockNumber) {
    if (inBlockNumber === void 0) {
      inBlockNumber = false;
    }

    if (configs.length == times.length + 1) {
      var _result;

      var SLICER = function SLICER(i) {
        return {
          constants: [times[i]],
          sources: [concat([inBlockNumber ? op(VM.Opcodes.BLOCK_NUMBER) : op(VM.Opcodes.BLOCK_TIMESTAMP), op(VM.Opcodes.VAL, 0), op(VM.Opcodes.LESS_THAN)])],
          stackLength: 3,
          argumentsLength: 0
        };
      };

      _result = VM.combiner(SLICER(0), configs[0]);

      for (var i = 1; i < configs.length; i++) {
        if (i + 1 == configs.length) {
          _result = VM.combiner(_result, configs[i]);
        } else {
          _result = VM.combiner(_result, SLICER(i));
          _result = VM.combiner(_result, configs[i]);
        }
      }

      for (var _i3 = 1; _i3 < configs.length; _i3++) {
        _result.sources[0] = concat([_result.sources[0], op(VM.Opcodes.EAGER_IF)]);
      }

      return _result;
    } else throw new Error('invalid number of times or configs arguments');
  };

  return VM;
}();
VM.Opcodes = AllStandardOps;

/**
 * @public
 * A class for calling methods on a EmissionsERC20.
 *
 * @remarks
 *   This class provides an easy way to deploy and interact with EmissionsERC20's.
 *
 * @example
 * ```typescript
 * import { EmissionsERC20 } from 'rain-sdk';
 *
 * // To deploy a new EmissionsERC20, pass an ethers.js Signer and the config for the EmissionsERC20.
 * const newEmission = EmissionsERC20.deploy(signer, args);
 *
 * // Check if an address is child from the factory deployed in the current chain.
 * await EmissionsERC20.isChild(signer, newEmission.address);
 *```
 */

var EmissionsERC20 = /*#__PURE__*/function (_TierContract) {
  _inheritsLoose(EmissionsERC20, _TierContract);

  /**
   * Constructs a new EmissionsERC20 from a known address.
   *
   * @param address - The address of the EmissionsERC20 contract
   * @param signer - An ethers.js Signer
   * @returns A new EmissionsERC20 instance
   *
   */
  function EmissionsERC20(address, _signer) {
    var _this;

    EmissionsERC20.checkAddress(address);
    _this = _TierContract.call(this, address, _signer) || this;

    _this.connect = function (signer) {
      return new EmissionsERC20(_this.address, signer);
    };

    _this.setTier = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(account, endTier, data, overrides) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                throw new Error('SET TIER: NOT IMPLEMENTED');

              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3, _x4) {
        return _ref.apply(this, arguments);
      };
    }();

    _this.allowDelegatedClaims = void 0;
    _this.allowance = void 0;
    _this.approve = void 0;
    _this.balanceOf = void 0;
    _this.calculateClaim = void 0;
    _this.claim = void 0;
    _this.decimals = void 0;
    _this.decreaseAllowance = void 0;
    _this.increaseAllowance = void 0;
    _this.name = void 0;
    _this.symbol = void 0;
    _this.totalSupply = void 0;
    _this.transfer = void 0;
    _this.transferFrom = void 0;

    var _emission = EmissionsERC20__factory.connect(address, _signer);

    _this.allowDelegatedClaims = _emission.allowDelegatedClaims;
    _this.allowance = _emission.allowance;
    _this.approve = _emission.approve;
    _this.balanceOf = _emission.balanceOf;
    _this.calculateClaim = _emission.calculateClaim;
    _this.claim = _emission.claim;
    _this.decimals = _emission.decimals;
    _this.decreaseAllowance = _emission.decreaseAllowance;
    _this.increaseAllowance = _emission.increaseAllowance;
    _this.name = _emission.name;
    _this.symbol = _emission.symbol;
    _this.totalSupply = _emission.totalSupply;
    _this.transfer = _emission.transfer;
    _this.transferFrom = _emission.transferFrom;
    return _this;
  }
  /**
   * All the opcodes avaialbles in the EmissionsERC20 contract.
   *
   * @remarks
   * This expose all the standard opcodes along with the specific opcodes of the EmissionsERC20.
   */


  return EmissionsERC20;
}(TierContract);
EmissionsERC20.nameBookReference = 'emissionsERC20Factory';
EmissionsERC20.Opcodes = /*#__PURE__*/_extends({}, AllStandardOps, {
  CLAIMANT_ACCOUNT: 0 + AllStandardOps.length
});

EmissionsERC20.deploy = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(signer, args, overrides) {
    var emissionsERC20Factory, tx, receipt, address;
    return runtime_1.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            if (overrides === void 0) {
              overrides = {};
            }

            _context2.t0 = EmissionsERC20Factory__factory;
            _context2.t1 = EmissionsERC20;
            _context2.next = 5;
            return EmissionsERC20.getChainId(signer);

          case 5:
            _context2.t2 = _context2.sent;
            _context2.t3 = _context2.t1.getBookAddress.call(_context2.t1, _context2.t2);
            _context2.t4 = signer;
            emissionsERC20Factory = _context2.t0.connect.call(_context2.t0, _context2.t3, _context2.t4);
            _context2.next = 11;
            return emissionsERC20Factory.createChildTyped(args, overrides);

          case 11:
            tx = _context2.sent;
            _context2.next = 14;
            return tx.wait();

          case 14:
            receipt = _context2.sent;
            address = EmissionsERC20.getNewChildFromReceipt(receipt, emissionsERC20Factory);
            return _context2.abrupt("return", new EmissionsERC20(address, signer));

          case 17:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function (_x5, _x6, _x7) {
    return _ref2.apply(this, arguments);
  };
}();

EmissionsERC20.isChild = /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(signer, maybeChild) {
    return runtime_1.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _context3.next = 2;
            return EmissionsERC20._isChild(signer, maybeChild);

          case 2:
            return _context3.abrupt("return", _context3.sent);

          case 3:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function (_x8, _x9) {
    return _ref3.apply(this, arguments);
  };
}();

/**
 * @public
 * A class for calling methods on a GatedNFT.
 *
 * @remarks
 * This class provides an easy way to interact with GatedNFT contracts.
 *
 * @example
 * ```typescript
 * import { GatedNFT } from 'rain-sdk';
 *
 * // To get a instance of an already deployed gatedNFT contract, pass the contract address and an ethers.js Signer
 * const gated = new GatedNFT(address, signer);
 *
 * // Check if an address is child from the factory deployed in the current chain.
 * await GatedNFT.isChild(signer, maybeChildAddress);
 *```
 */

var GatedNFT = /*#__PURE__*/function (_FactoryContract) {
  _inheritsLoose(GatedNFT, _FactoryContract);

  /**
   * Constructs a new GatedNFT from a known address.
   *
   * @param address - The address of the GatedNFT contract
   * @param signer - An ethers.js Signer
   * @returns A new GatedNFT instance
   *
   */
  function GatedNFT(address, _signer) {
    var _this;

    GatedNFT.checkAddress(address);
    _this = _FactoryContract.call(this, address, _signer) || this;

    _this.connect = function (signer) {
      return new GatedNFT(_this.address, signer);
    };

    _this.tier = void 0;
    _this.tokenURI = void 0;

    _this.royaltyInfo = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(salePrice, overrides) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (overrides === void 0) {
                  overrides = {};
                }

                return _context.abrupt("return", _this._royaltyInfo(0, salePrice, overrides));

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2) {
        return _ref.apply(this, arguments);
      };
    }();

    _this._royaltyInfo = void 0;
    _this.updateRoyaltyRecipient = void 0;
    _this.name = void 0;
    _this.symbol = void 0;
    _this.totalSupply = void 0;
    _this.mint = void 0;
    _this.ownerOf = void 0;
    _this.balanceOf = void 0;
    _this.approve = void 0;
    _this.getApproved = void 0;
    _this.isApprovedForAll = void 0;
    _this.setApprovalForAll = void 0;
    _this.transferFrom = void 0;
    _this.safeTransferFrom = void 0;
    _this.safeTransferFromWithData = void 0;
    _this.owner = void 0;
    _this.renounceOwnership = void 0;
    _this.transferOwnership = void 0;
    _this.supportsInterface = void 0;

    var _gatedNFT = GatedNFT__factory.connect(address, _signer);

    _this.tier = _gatedNFT.tier;
    _this.tokenURI = _gatedNFT.tokenURI;
    _this._royaltyInfo = _gatedNFT.royaltyInfo;
    _this.updateRoyaltyRecipient = _gatedNFT.updateRoyaltyRecipient;
    _this.name = _gatedNFT.name;
    _this.symbol = _gatedNFT.symbol;
    _this.totalSupply = _gatedNFT.totalSupply;
    _this.mint = _gatedNFT.mint;
    _this.ownerOf = _gatedNFT.ownerOf;
    _this.balanceOf = _gatedNFT.balanceOf;
    _this.approve = _gatedNFT.approve;
    _this.getApproved = _gatedNFT.getApproved;
    _this.isApprovedForAll = _gatedNFT.isApprovedForAll;
    _this.setApprovalForAll = _gatedNFT.setApprovalForAll;
    _this.transferFrom = _gatedNFT.transferFrom;
    _this.safeTransferFrom = _gatedNFT['safeTransferFrom(address,address,uint256)'];
    _this.safeTransferFromWithData = _gatedNFT['safeTransferFrom(address,address,uint256,bytes)'];
    _this.owner = _gatedNFT.owner;
    _this.renounceOwnership = _gatedNFT.renounceOwnership;
    _this.transferOwnership = _gatedNFT.transferOwnership;
    _this.supportsInterface = _gatedNFT.supportsInterface;
    return _this;
  }
  /**
   * Deploys a new GatedNFT.
   *
   * @param signer - An ethers.js Signer
   * @param args - Arguments for deploying a GatedNFT @see GatedNFTDeployArguments
   * @param overrides - @see TxOverrides
   * @returns A new GatedNFT instance
   *
   */


  return GatedNFT;
}(FactoryContract);
/**
 * @public
 * Determine the status about how the GatedNFT contract will handle the transfers
 */

GatedNFT.nameBookReference = 'gatedNFTFactory';

GatedNFT.deploy = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(signer, args, overrides) {
    var gatedNFTFactory, config, tier, minimumStatus, maxPerAddress, transferrable, maxMintable, royaltyRecipient, royaltyBPS, tx, receipt, address;
    return runtime_1.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            if (overrides === void 0) {
              overrides = {};
            }

            _context2.t0 = GatedNFTFactory__factory;
            _context2.t1 = GatedNFT;
            _context2.next = 5;
            return GatedNFT.getChainId(signer);

          case 5:
            _context2.t2 = _context2.sent;
            _context2.t3 = _context2.t1.getBookAddress.call(_context2.t1, _context2.t2);
            _context2.t4 = signer;
            gatedNFTFactory = _context2.t0.connect.call(_context2.t0, _context2.t3, _context2.t4);
            config = args.config, tier = args.tier, minimumStatus = args.minimumStatus, maxPerAddress = args.maxPerAddress, transferrable = args.transferrable, maxMintable = args.maxMintable, royaltyRecipient = args.royaltyRecipient, royaltyBPS = args.royaltyBPS;
            _context2.next = 12;
            return gatedNFTFactory.createChildTyped(config, tier, minimumStatus, maxPerAddress, transferrable, maxMintable, royaltyRecipient, royaltyBPS, overrides);

          case 12:
            tx = _context2.sent;
            _context2.next = 15;
            return tx.wait();

          case 15:
            receipt = _context2.sent;
            address = GatedNFT.getNewChildFromReceipt(receipt, gatedNFTFactory);
            return _context2.abrupt("return", new GatedNFT(address, signer));

          case 18:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function (_x3, _x4, _x5) {
    return _ref2.apply(this, arguments);
  };
}();

GatedNFT.isChild = /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(signer, maybeChild) {
    return runtime_1.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _context3.next = 2;
            return GatedNFT._isChild(signer, maybeChild);

          case 2:
            return _context3.abrupt("return", _context3.sent);

          case 3:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function (_x6, _x7) {
    return _ref3.apply(this, arguments);
  };
}();

var Transferrable;

(function (Transferrable) {
  /**
   * Transfer not supported
   */
  Transferrable[Transferrable["NonTransferrable"] = 0] = "NonTransferrable";
  /**
   * Transfer supported
   */

  Transferrable[Transferrable["Transferrable"] = 1] = "Transferrable";
  /**
   * Address required a specific tier
   */

  Transferrable[Transferrable["TierGatedTransferrable"] = 2] = "TierGatedTransferrable";
})(Transferrable || (Transferrable = {}));

/**
 * @public
 * A class for calling method on a NoticeBoard.
 *
 * @remarks
 * Anyone can emit a `Notice`.
 *
 * This is open ended content related to the subject.
 * Some examples:
 * - Raise descriptions/promises
 * - Reviews/comments from token holders
 * - Simple onchain voting/signalling
 *
 * GUIs/tooling/indexers reading this data are expected to know how to
 * interpret it in context because the `NoticeBoard` contract does not.
 *
 * @example
 * ```typescript
 * import { NoticeBoard } from 'rain-sdk'
 *
 * const notice = new NoticeBoard(address, signer);
 *
 * const noticeArg = {
 *   subject: signer.address; // Subject can be any address
 *   data: "0x00"; // Can be any data
 * }
 *
 * const tx = await notice.createNotices([noticeArg]);
 * ```
 *
 */

var NoticeBoard = /*#__PURE__*/function (_RainContract) {
  _inheritsLoose(NoticeBoard, _RainContract);

  /**
   * Constructs a new NoticeBoard from a known address.
   *
   * @param address - The address of the NoticeBoard contract
   * @param signer - An ethers.js Signer
   * @returns A new NoticeBoard instance
   *
   */
  function NoticeBoard(address, _signer) {
    var _this;

    NoticeBoard.checkAddress(address);
    _this = _RainContract.call(this, address, _signer) || this;

    _this.connect = function (signer) {
      return new NoticeBoard(_this.address, signer);
    };

    _this.createNotices = void 0;

    var _noticeBoard = NoticeBoard__factory.connect(address, _signer);

    _this.createNotices = _noticeBoard.createNotices;
    return _this;
  }
  /**
   * Create the NoticeBoard instance.
   *
   * The function ask to the provider inside of the ethers signer what is the chain
   * identifier to get the address in this chain.
   *
   * @param signer - ethers signer connected to the instance
   * @returns A NoticeBoard instance
   */


  return NoticeBoard;
}(RainContract);
NoticeBoard.nameBookReference = 'noticeBoard';

NoticeBoard.get = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(signer) {
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.t0 = NoticeBoard;
            _context.t1 = NoticeBoard;
            _context.next = 4;
            return NoticeBoard.getChainId(signer);

          case 4:
            _context.t2 = _context.sent;
            _context.t3 = _context.t1.getBookAddress.call(_context.t1, _context.t2);
            _context.t4 = signer;
            return _context.abrupt("return", new _context.t0(_context.t3, _context.t4));

          case 8:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function (_x) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @public
 * A class for calling methods on a RedeemableERC20.
 *
 * @remarks
 * This class provides an easy way to interact with RedeemableERC20 contracts.
 *
 * An RedeemableERC20 is the ERC20 token that is minted and distributed in the ISale contract.
 *
 * During `Phase.ZERO` the token can be traded and so compatible with the Balancer pool mechanics.
 * During `Phase.ONE` the token is frozen and no longer able to be traded on any AMM or transferred
 * directly.
 *
 * The token can be redeemed during `Phase.ONE` which burns the token in exchange for pro-rata erc20
 * tokens held by the `RedeemableERC20` contract itself.
 *
 * The token balances can be used indirectly for other claims, promotions and events as a proof of
 * participation in the original distribution by token holders.
 *
 * The token can optionally be restricted by the `ITier` contract to only allow receipients with a
 * specified membership status.
 *
 * @example
 * ```typescript
 * import { RedeemableERC20 } from 'rain-sdk';
 *
 * // To get a instance of an already deployed redeemable, pass the contract address and an ethers.js Signer
 * const redeemable = new RedeemableERC20(address, signer);
 *
 * // Check if an address is child from the factory deployed in the current chain.
 * await RedeemableERC20.isChild(signer, maybeChildAddress);
 *```
 */

var RedeemableERC20 = /*#__PURE__*/function (_FactoryContract) {
  _inheritsLoose(RedeemableERC20, _FactoryContract);

  /**
   * Constructs a new RedeemableERC20 from a known address.
   *
   * @param address - The address of the RedeemableERC20 contract
   * @param signer - An ethers.js Signer
   * @returns A new RedeemableERC20 instance
   *
   */
  function RedeemableERC20(address, _signer) {
    var _this;

    RedeemableERC20.checkAddress(address);
    _this = _FactoryContract.call(this, address, _signer) || this;

    _this.connect = function (signer) {
      return new RedeemableERC20(_this.address, signer);
    };

    _this.allowance = void 0;
    _this.approve = void 0;
    _this.balanceOf = void 0;
    _this.blockNumberForPhase = void 0;
    _this.burn = void 0;
    _this.burnFrom = void 0;
    _this.currentPhase = void 0;
    _this.decimals = void 0;
    _this.decreaseAllowance = void 0;
    _this.endDistribution = void 0;
    _this.grantReceiver = void 0;
    _this.grantSender = void 0;
    _this.increaseAllowance = void 0;
    _this.isReceiver = void 0;
    _this.isSender = void 0;
    _this.minimumTier = void 0;
    _this.name = void 0;
    _this.newTreasuryAsset = void 0;
    _this.phaseAtBlockNumber = void 0;
    _this.phaseBlocks = void 0;
    _this.redeem = void 0;
    _this.symbol = void 0;
    _this.tier = void 0;
    _this.totalSupply = void 0;
    _this.transfer = void 0;
    _this.transferFrom = void 0;

    var _redeemable = RedeemableERC20__factory.connect(address, _signer);

    _this.allowance = _redeemable.allowance;
    _this.approve = _redeemable.approve;
    _this.balanceOf = _redeemable.balanceOf;
    _this.blockNumberForPhase = _redeemable.blockNumberForPhase;
    _this.burn = _redeemable.burn;
    _this.burnFrom = _redeemable.burnFrom;
    _this.currentPhase = _redeemable.currentPhase;
    _this.decimals = _redeemable.decimals;
    _this.decreaseAllowance = _redeemable.decreaseAllowance;
    _this.endDistribution = _redeemable.endDistribution;
    _this.grantReceiver = _redeemable.grantReceiver;
    _this.grantSender = _redeemable.grantSender;
    _this.increaseAllowance = _redeemable.increaseAllowance;
    _this.isReceiver = _redeemable.isReceiver;
    _this.isSender = _redeemable.isSender;
    _this.name = _redeemable.name;
    _this.minimumTier = _redeemable.minimumTier;
    _this.newTreasuryAsset = _redeemable.newTreasuryAsset;
    _this.phaseAtBlockNumber = _redeemable.phaseAtBlockNumber;
    _this.phaseBlocks = _redeemable.phaseBlocks;
    _this.redeem = _redeemable.redeem;
    _this.symbol = _redeemable.symbol;
    _this.tier = _redeemable.tier;
    _this.totalSupply = _redeemable.totalSupply;
    _this.transfer = _redeemable.transfer;
    _this.transferFrom = _redeemable.transferFrom;
    return _this;
  }
  /**
   * Deploys a new RedeemableERC20.
   *
   * @param signer - An ethers.js Signer
   * @param args - Arguments for deploying a RedeemableERC20 @see RedeemableERC20DeployArgs
   * @param overrides - @see TxOverrides
   * @returns A new RedeemableERC20 instance
   *
   */


  return RedeemableERC20;
}(FactoryContract);
RedeemableERC20.nameBookReference = 'redeemableERC20Factory';

RedeemableERC20.deploy = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(signer, args, overrides) {
    var redeemableFactory, tx, receipt, address;
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (overrides === void 0) {
              overrides = {};
            }

            _context.t0 = RedeemableERC20Factory__factory;
            _context.t1 = RedeemableERC20;
            _context.next = 5;
            return RedeemableERC20.getChainId(signer);

          case 5:
            _context.t2 = _context.sent;
            _context.t3 = _context.t1.getBookAddress.call(_context.t1, _context.t2);
            _context.t4 = signer;
            redeemableFactory = _context.t0.connect.call(_context.t0, _context.t3, _context.t4);
            _context.next = 11;
            return redeemableFactory.createChildTyped(args, overrides);

          case 11:
            tx = _context.sent;
            _context.next = 14;
            return tx.wait();

          case 14:
            receipt = _context.sent;
            address = RedeemableERC20.getNewChildFromReceipt(receipt, redeemableFactory);
            return _context.abrupt("return", new RedeemableERC20(address, signer));

          case 17:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function (_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

RedeemableERC20.isChild = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(signer, maybeChild) {
    return runtime_1.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return RedeemableERC20._isChild(signer, maybeChild);

          case 2:
            return _context2.abrupt("return", _context2.sent);

          case 3:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function (_x4, _x5) {
    return _ref2.apply(this, arguments);
  };
}();

/**
 * @public
 * A class for calling methods on a RedeemableERC20ClaimEscrow.
 *
 * @remarks
 * This class provides an easy way to interact with the Escrow Rain
 *
 * An Escrow contract for ERC20 tokens to be deposited and withdrawn against
 * redeemableERC20 tokens from a specific `Sale`.
 *
 * When some token is deposited the running total of that token against the
 * trust is incremented by the deposited amount. When some `redeemableERC20`
 * token holder calls `withdraw` they are sent the full balance they have not
 * previously claimed, multiplied by their fraction of the redeemable token
 * supply that they currently hold. As redeemable tokens are frozen after
 * distribution there are no issues with holders manipulating withdrawals by
 * transferring tokens to claim multiple times.
 *
 * @example
 * ```typescript
 * import { RedeemableERC20ClaimEscrow } from 'rain-sdk'
 *
 * // If the escrow address is unkwon or want to simplify the process, use get() to create the instance.
 * // This use the provider inside the signer to get the chainId and search the correct address.
 * const escrow = RedeemableERC20ClaimEscrow(saleAddress, tokenAddress, ethersSigner);
 *
 * const tx = escrow.deposit(amount);
 * ```
 */

var RedeemableERC20ClaimEscrow = /*#__PURE__*/function (_RainContract) {
  _inheritsLoose(RedeemableERC20ClaimEscrow, _RainContract);

  /**
   * The `ISale` contract address that is used to interact with the Escrow contract.
   */

  /**
   * The `IERC20` token being deposited and that will be use to interact with the Escrow contract
   */

  /**
   * Constructs a new RedeemableERC20ClaimEscrow from a known address.
   *
   * @param address - The address of the RedeemableERC20ClaimEscrow contract
   * @param saleAddress - The address of the Sale contract that will be use to interact
   * @param tokenAddress - The address of the tokenAddress contract that will be use to interact
   * @param signer - An ethers.js Signer
   * @returns A new RedeemableERC20ClaimEscrow instance
   *
   */
  function RedeemableERC20ClaimEscrow(address, saleAddress, tokenAddress, _signer) {
    var _this;

    RedeemableERC20ClaimEscrow.checkAddress(address);
    _this = _RainContract.call(this, address, _signer) || this;
    _this.sale = void 0;
    _this.token = void 0;

    _this.connect = function (signer) {
      return new RedeemableERC20ClaimEscrow(_this.address, _this.sale, _this.token, signer);
    };

    _this.changeSale = function (newSale) {
      if (!utils$1.isAddress(newSale)) {
        throw new Error('SALE: NOT A VALID FORMAT ADDRESS');
      }

      return new RedeemableERC20ClaimEscrow(_this.address, newSale, _this.token, _this.signer);
    };

    _this.changeToken = function (newToken) {
      if (!utils$1.isAddress(newToken)) {
        throw new Error('TOKEN: NOT A VALID FORMAT ADDRESS');
      }

      return new RedeemableERC20ClaimEscrow(_this.address, _this.sale, newToken, _this.signer);
    };

    _this.deposit = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(amount, overrides) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (overrides === void 0) {
                  overrides = {};
                }

                _context.next = 3;
                return _this._deposit(_this.sale, _this.token, amount, overrides);

              case 3:
                return _context.abrupt("return", _context.sent);

              case 4:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2) {
        return _ref.apply(this, arguments);
      };
    }();

    _this.depositPending = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(amount, overrides) {
        var tx;
        return runtime_1.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (overrides === void 0) {
                  overrides = {};
                }

                _context2.next = 3;
                return _this._depositPending(_this.sale, _this.token, amount, overrides);

              case 3:
                tx = _context2.sent;
                return _context2.abrupt("return", tx);

              case 5:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      return function (_x3, _x4) {
        return _ref2.apply(this, arguments);
      };
    }();

    _this.sweepPending = /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(depositor, overrides) {
        var tx;
        return runtime_1.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                if (overrides === void 0) {
                  overrides = {};
                }

                _context3.next = 3;
                return _this._sweepPending(_this.sale, _this.token, depositor, overrides);

              case 3:
                tx = _context3.sent;
                return _context3.abrupt("return", tx);

              case 5:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }));

      return function (_x5, _x6) {
        return _ref3.apply(this, arguments);
      };
    }();

    _this.undeposit = /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(supply, amount, overrides) {
        var tx;
        return runtime_1.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                if (overrides === void 0) {
                  overrides = {};
                }

                _context4.next = 3;
                return _this._undeposit(_this.sale, _this.token, supply, amount, overrides);

              case 3:
                tx = _context4.sent;
                return _context4.abrupt("return", tx);

              case 5:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }));

      return function (_x7, _x8, _x9) {
        return _ref4.apply(this, arguments);
      };
    }();

    _this.withdraw = /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee5(supply, overrides) {
        var tx;
        return runtime_1.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (overrides === void 0) {
                  overrides = {};
                }

                _context5.next = 3;
                return _this._withdraw(_this.sale, _this.token, supply, overrides);

              case 3:
                tx = _context5.sent;
                return _context5.abrupt("return", tx);

              case 5:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }));

      return function (_x10, _x11) {
        return _ref5.apply(this, arguments);
      };
    }();

    _this._deposit = void 0;
    _this._depositPending = void 0;
    _this._sweepPending = void 0;
    _this._undeposit = void 0;
    _this._withdraw = void 0;

    var _claimEsrow = RedeemableERC20ClaimEscrow__factory.connect(address, _signer);

    _this.checkAddress(saleAddress, 'SALE: NOT A VALID FORMAT ADDRESS');

    _this.checkAddress(tokenAddress, 'TOKEN: NOT A VALID FORMAT ADDRESS');

    _this.sale = saleAddress;
    _this.token = tokenAddress;
    _this._deposit = _claimEsrow.deposit;
    _this._depositPending = _claimEsrow.depositPending;
    _this._sweepPending = _claimEsrow.sweepPending;
    _this._undeposit = _claimEsrow.undeposit;
    _this._withdraw = _claimEsrow.withdraw;
    return _this;
  }
  /**
   * Create the RedeemableERC20ClaimEscrow instance.
   *
   * @remarks
   * The function ask to the provider inside of the ethers signer what is the chain
   * identifier to get the address in this chain.
   *
   * @param saleAddress - sale address
   * @param tokenAddress - token ERC20 address
   * @param signer - ethers signer connected to the instance
   * @returns A RedeemableERC20ClaimEscrow instance
   */


  return RedeemableERC20ClaimEscrow;
}(RainContract);
RedeemableERC20ClaimEscrow.nameBookReference = 'redeemableERC20ClaimEscrow';

RedeemableERC20ClaimEscrow.get = /*#__PURE__*/function () {
  var _ref6 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee6(saleAddress, tokenAddress, signer) {
    return runtime_1.wrap(function _callee6$(_context6) {
      while (1) {
        switch (_context6.prev = _context6.next) {
          case 0:
            _context6.t0 = RedeemableERC20ClaimEscrow;
            _context6.t1 = RedeemableERC20ClaimEscrow;
            _context6.next = 4;
            return RedeemableERC20ClaimEscrow.getChainId(signer);

          case 4:
            _context6.t2 = _context6.sent;
            _context6.t3 = _context6.t1.getBookAddress.call(_context6.t1, _context6.t2);
            _context6.t4 = saleAddress;
            _context6.t5 = tokenAddress;
            _context6.t6 = signer;
            return _context6.abrupt("return", new _context6.t0(_context6.t3, _context6.t4, _context6.t5, _context6.t6));

          case 10:
          case "end":
            return _context6.stop();
        }
      }
    }, _callee6);
  }));

  return function (_x12, _x13, _x14) {
    return _ref6.apply(this, arguments);
  };
}();

/**
 * @public
 *
 * A generic ERC20 interface to get connected to any ERC20 address and make transactions.
 *
 * @remarks
 * The interface only have and provide generic and common methods calls. Remember that any specific
 * method implemented in the contract will NOT be available in this interface.
 */

var ERC20 =
/**
 * Constructs a new ERC20 from a known address.
 *
 * @param address - The address of the ERC20 contract
 * @param signer - An ethers.js Signer
 * @returns A new ERC20 instance
 *
 */
function ERC20(_address, _signer) {
  var _this = this;

  this.signer = void 0;
  this.address = void 0;

  this.connect = function (signer) {
    return new ERC20(_this.address, signer);
  };

  this.attach = function (address) {
    return new ERC20(address, _this.signer);
  };

  this.allowance = void 0;
  this.approve = void 0;
  this.balanceOf = void 0;
  this.burn = void 0;
  this.burnFrom = void 0;
  this.decimals = void 0;
  this.decreaseAllowance = void 0;
  this.increaseAllowance = void 0;
  this.name = void 0;
  this.symbol = void 0;
  this.totalSupply = void 0;
  this.transfer = void 0;
  this.transferFrom = void 0;
  this.address = _address;
  this.signer = _signer;

  var _erc20 = ERC20Burnable__factory.connect(_address, _signer);

  this.allowance = _erc20.allowance;
  this.approve = _erc20.approve;
  this.balanceOf = _erc20.balanceOf;
  this.burn = _erc20.burn;
  this.burnFrom = _erc20.burnFrom;
  this.decimals = _erc20.decimals;
  this.decreaseAllowance = _erc20.decreaseAllowance;
  this.increaseAllowance = _erc20.increaseAllowance;
  this.name = _erc20.name;
  this.symbol = _erc20.symbol;
  this.totalSupply = _erc20.totalSupply;
  this.transfer = _erc20.transfer;
  this.transferFrom = _erc20.transferFrom;
}
/**
 * Check if the address is an IERC20.
 *
 * @remarks
 * A valid IERC20 are those contracts that have generics methods in their interface with same signature.
 *
 * @param address - Address to check if is the IERC20
 * @param signer - Signer necessary to valid the IERC20
 * @returns True if the address is a valid IERC20
 */
;

ERC20.isERC20 = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(address, signer) {
    var erc20, balance;
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            erc20 = new ERC20(address, signer);
            _context.prev = 1;
            _context.next = 4;
            return erc20.name();

          case 4:
            _context.next = 6;
            return erc20.symbol();

          case 6:
            _context.next = 8;
            return erc20.decimals();

          case 8:
            _context.t0 = erc20;
            _context.next = 11;
            return signer.getAddress();

          case 11:
            _context.t1 = _context.sent;
            _context.next = 14;
            return _context.t0.balanceOf.call(_context.t0, _context.t1);

          case 14:
            balance = _context.sent;

            if (balance) {
              _context.next = 17;
              break;
            }

            return _context.abrupt("return", Promise.resolve(false));

          case 17:
            return _context.abrupt("return", Promise.resolve(true));

          case 20:
            _context.prev = 20;
            _context.t2 = _context["catch"](1);
            return _context.abrupt("return", Promise.resolve(false));

          case 23:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 20]]);
  }));

  return function (_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @public
 * A class for deploying and calling methods on a Sale.
 *
 * @remarks
 *
 * This class provides an easy way to deploy Sales using Rain's canonical factories, and methods for interacting with an already deployed Sale.
 * @example
 * ```typescript
 * import { Sale } from 'rain-sdk'
 * // To deploy a new Sale, pass an ethers.js Signer and the config for the Sale.
 * const newSale = await Sale.deploy(signer, SaleArgs)
 *
 * // To connect to an existing Sale just pass the address and an ethers.js Signer.
 * const existingSale = new Sale(address, signer)
 *
 * // Once you have a Sale, you can call the smart contract methods:
 * await existingSale.start()
 * await existingSale.buy(config_)
 * ```
 *
 */

var Sale = /*#__PURE__*/function (_FactoryContract) {
  _inheritsLoose(Sale, _FactoryContract);

  /**
   * Constructs a new Sale from a known address.
   *
   * @param address - The address of the Sale contract
   * @param signer - An ethers.js Signer
   * @returns A new Sale instance
   *
   */
  function Sale(address, _signer) {
    var _this;

    Sale.checkAddress(address);
    _this = _FactoryContract.call(this, address, _signer) || this;

    _this.connect = function (signer) {
      return new Sale(_this.address, signer);
    };

    _this.getRedeemable = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(signer) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (signer === void 0) {
                  signer = _this.signer;
                }

                _context.t0 = RedeemableERC20;
                _context.next = 4;
                return _this.token();

              case 4:
                _context.t1 = _context.sent;
                _context.t2 = signer;
                return _context.abrupt("return", new _context.t0(_context.t1, _context.t2));

              case 7:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }();

    _this.getReserve = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(signer) {
        return runtime_1.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (signer === void 0) {
                  signer = _this.signer;
                }

                _context2.t0 = ERC20;
                _context2.next = 4;
                return _this.reserve();

              case 4:
                _context2.t1 = _context2.sent;
                _context2.t2 = signer;
                return _context2.abrupt("return", new _context2.t0(_context2.t1, _context2.t2));

              case 7:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }();

    _this.buy = void 0;
    _this.refund = void 0;
    _this.calculatePrice = void 0;
    _this.claimFees = void 0;
    _this.start = void 0;
    _this.end = void 0;
    _this.canStart = void 0;
    _this.canEnd = void 0;
    _this.saleStatus = void 0;
    _this.timeout = void 0;
    _this.reserve = void 0;
    _this.token = void 0;

    var _sale = Sale__factory.connect(address, _signer);

    _this.buy = _sale.buy;
    _this.refund = _sale.refund;
    _this.calculatePrice = _sale.calculatePrice;
    _this.claimFees = _sale.claimFees;
    _this.start = _sale.start;
    _this.end = _sale.end;
    _this.canStart = _sale.canStart;
    _this.canEnd = _sale.canEnd;
    _this.saleStatus = _sale.saleStatus;
    _this.timeout = _sale.timeout;
    _this.reserve = _sale.reserve;
    _this.token = _sale.token;
    return _this;
  }
  /**
   * All the standard and Sale Opcodes
   */


  return Sale;
}(FactoryContract);
Sale.nameBookReference = 'saleFactory';
Sale.Opcodes = /*#__PURE__*/_extends({}, VM.Opcodes, {
  REMAINING_UNITS: 0 + VM.Opcodes.length,
  TOTAL_RESERVE_IN: 1 + VM.Opcodes.length,
  CURRENT_BUY_UNITS: 2 + VM.Opcodes.length,
  TOKEN_ADDRESS: 3 + VM.Opcodes.length,
  RESERVE_ADDRESS: 4 + VM.Opcodes.length
});

Sale.deploy = /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(signer, saleConfig, saleRedeemableERC20Config, overrides) {
    var saleFactory, tx, receipt, address;
    return runtime_1.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            if (overrides === void 0) {
              overrides = {};
            }

            _context3.t0 = SaleFactory__factory;
            _context3.t1 = Sale;
            _context3.next = 5;
            return Sale.getChainId(signer);

          case 5:
            _context3.t2 = _context3.sent;
            _context3.t3 = _context3.t1.getBookAddress.call(_context3.t1, _context3.t2);
            _context3.t4 = signer;
            saleFactory = _context3.t0.connect.call(_context3.t0, _context3.t3, _context3.t4);
            _context3.next = 11;
            return saleFactory.createChildTyped(saleConfig, saleRedeemableERC20Config, overrides);

          case 11:
            tx = _context3.sent;
            _context3.next = 14;
            return tx.wait();

          case 14:
            receipt = _context3.sent;
            address = Sale.getNewChildFromReceipt(receipt, saleFactory);
            return _context3.abrupt("return", new Sale(address, signer));

          case 17:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function (_x3, _x4, _x5, _x6) {
    return _ref3.apply(this, arguments);
  };
}();

Sale.isChild = /*#__PURE__*/function () {
  var _ref4 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(signer, maybeChild) {
    return runtime_1.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            _context4.next = 2;
            return Sale._isChild(signer, maybeChild);

          case 2:
            return _context4.abrupt("return", _context4.sent);

          case 3:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));

  return function (_x7, _x8) {
    return _ref4.apply(this, arguments);
  };
}();

/**
 * @public
 * Summary statuses derived from a `State` by comparing the `Since` times
 * against a specific block number.
 */

var VerifyStatus;

(function (VerifyStatus) {
  /**
   * Account has not interacted with the system yet or was removed.
   */
  VerifyStatus[VerifyStatus["NIL"] = 0] = "NIL";
  /**
   * Account has added evidence for themselves.
   */

  VerifyStatus[VerifyStatus["ADDED"] = 1] = "ADDED";
  /**
   * Approver has reviewed added/approve evidence and approved the account.
   */

  VerifyStatus[VerifyStatus["APPROVED"] = 2] = "APPROVED";
  /**
   * Banner has reviewed a request to ban an account and banned it.
   */

  VerifyStatus[VerifyStatus["BANNED"] = 3] = "BANNED";
})(VerifyStatus || (VerifyStatus = {}));
/**
 * @public
 * A class for deploying and calling methods on a Verify.
 *
 * @remarks
 * This class provides an easy way to deploy Verifies using Rain's canonical factories, and methods for interacting
 * with an already deployed Verify.
 *
 * Trust-minimised contract to record the state of some verification process. When some off-chain
 * identity is to be reified on chain there is inherently some multi-party, multi-faceted trust
 * relationship. For example, the DID (Decentralized Identifiers) specification from W3C outlines
 * that the controller and the subject of an identity are two different entities.
 *
 * @example
 * ```typescript
 * import { Verify } from 'rain-sdk'
 *
 * // To deploy a new Verify, pass an ethers.js Signer, the chainId and the config for the Verify.
 * const newVerify = await Verify.deploy(signer, chainId, VerifyConfigArgs)
 *
 * // To connect to an existing Verify just pass the address and an ethers.js Signer.
 * const existingVerify = new Verify(address, signer)
 *
 * // Once you have a Verify, you can call the smart contract methods:
 * ```
 *
 */


var Verify = /*#__PURE__*/function (_FactoryContract) {
  _inheritsLoose(Verify, _FactoryContract);

  /** {@inheritDoc VerifyStatus} */

  /** {@inheritDoc VerifyStatus} */

  /**
   * Constructs a new Verify from a known address.
   *
   * @param address - The address of the Verify contract
   * @param signer - An ethers.js Signer
   * @returns A new Verify instance
   *
   */
  function Verify(address, _signer) {
    var _this;

    Verify.checkAddress(address);
    _this = _FactoryContract.call(this, address, _signer) || this;
    _this.status = VerifyStatus;

    _this.connect = function (signer) {
      return new Verify(_this.address, signer);
    };

    _this.DEFAULT_ADMIN_ROLE = void 0;
    _this.APPROVER_ADMIN = void 0;
    _this.REMOVER_ADMIN = void 0;
    _this.BANNER_ADMIN = void 0;
    _this.APPROVER = void 0;
    _this.REMOVER = void 0;
    _this.BANNER = void 0;
    _this.callback = void 0;
    _this.add = void 0;
    _this.approve = void 0;
    _this.ban = void 0;
    _this.remove = void 0;
    _this.requestApprove = void 0;
    _this.requestBan = void 0;
    _this.requestRemove = void 0;
    _this.state = void 0;
    _this.statusAtBlock = void 0;
    _this.hasRole = void 0;
    _this.getRoleAdmin = void 0;
    _this.grantRole = void 0;
    _this.revokeRole = void 0;
    _this.renounceRole = void 0;
    _this.supportsInterface = void 0;

    var _verify = Verify__factory.connect(address, _signer);

    _this.callback = _verify.callback;
    _this.add = _verify.add;
    _this.approve = _verify.approve;
    _this.ban = _verify.ban;
    _this.remove = _verify.remove;
    _this.requestApprove = _verify.requestApprove;
    _this.requestBan = _verify.requestBan;
    _this.requestRemove = _verify.requestRemove;
    _this.state = _verify.state;
    _this.statusAtBlock = _verify.statusAtBlock;
    _this.hasRole = _verify.hasRole;
    _this.getRoleAdmin = _verify.getRoleAdmin;
    _this.grantRole = _verify.grantRole;
    _this.revokeRole = _verify.revokeRole;
    _this.renounceRole = _verify.renounceRole;
    _this.supportsInterface = _verify.supportsInterface;
    _this.DEFAULT_ADMIN_ROLE = _verify.DEFAULT_ADMIN_ROLE;
    _this.APPROVER_ADMIN = _verify.APPROVER_ADMIN;
    _this.REMOVER_ADMIN = _verify.REMOVER_ADMIN;
    _this.BANNER_ADMIN = _verify.BANNER_ADMIN;
    _this.APPROVER = _verify.APPROVER;
    _this.REMOVER = _verify.REMOVER;
    _this.BANNER = _verify.BANNER;
    return _this;
  }
  /**
   * Deploys a new Verify.
   *
   * @param signer - An ethers.js Signer
   * @param args - Arguments for deploying a Verify @see VerifyDeployArgs
   * @param overrides - Specific transaction values to send it (e.g gasLimit, nonce or gasPrice)
   * @returns A new Verify instance
   *
   */


  return Verify;
}(FactoryContract);
Verify.nameBookReference = 'verifyFactory';
Verify.status = VerifyStatus;

Verify.deploy = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(signer, args, overrides) {
    var verifyFactory, admin, callback, tx, receipt, address;
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (overrides === void 0) {
              overrides = {};
            }

            _context.t0 = VerifyFactory__factory;
            _context.t1 = Verify;
            _context.next = 5;
            return Verify.getChainId(signer);

          case 5:
            _context.t2 = _context.sent;
            _context.t3 = _context.t1.getBookAddress.call(_context.t1, _context.t2);
            _context.t4 = signer;
            verifyFactory = _context.t0.connect.call(_context.t0, _context.t3, _context.t4);
            admin = args.admin, callback = args.callback;

            if (!callback) {
              callback = constants.AddressZero;
            }

            _context.next = 13;
            return verifyFactory.createChildTyped({
              admin: admin,
              callback: callback
            }, overrides);

          case 13:
            tx = _context.sent;
            _context.next = 16;
            return tx.wait();

          case 16:
            receipt = _context.sent;
            address = Verify.getNewChildFromReceipt(receipt, verifyFactory);
            return _context.abrupt("return", new Verify(address, signer));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function (_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

Verify.isChild = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(signer, maybeChild) {
    return runtime_1.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return Verify._isChild(signer, maybeChild);

          case 2:
            return _context2.abrupt("return", _context2.sent);

          case 3:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function (_x4, _x5) {
    return _ref2.apply(this, arguments);
  };
}();

/**
 * @public
 * Standard cap per wallet modes
 */

var WalletCapMode;

(function (WalletCapMode) {
  WalletCapMode[WalletCapMode["min"] = 0] = "min";
  WalletCapMode[WalletCapMode["max"] = 1] = "max";
  WalletCapMode[WalletCapMode["both"] = 2] = "both";
})(WalletCapMode || (WalletCapMode = {}));
/**
 * @public - PriceCurve is an class that all the other sale types (sub-classes) will inherit from.
 *
 * @remarks - It holds all the global methods for generating a sale script with different features for a sale
 * such as tier discount which makes depolying a new sale contracts with different features easy.
 *
 * @remarks - the order of calling the methods of this class is important, meaning in order to get the
 * desired result for the sale, mthods should be called in correct order, although it is worth saying
 * that even if the order is not followed, the result will still be reliable if that is been done by intention.
 * For example if we call 'applyExtraTime' method after the the 'applyTierDiscount' method, the extra
 * time discount will be applied to the result of 'applyTierDiscount' and if before that, it will be vice versa.
 * The general order for calling these methods is:
 *    1.applyExtraTimeDiscount
 *    2.applyTierDiscount
 *    3.applyWalletCap
 *
 */


var PriceCurve = /*#__PURE__*/function () {
  // StateConfig Properties of this class

  /**
   * Constructor of PriceCurve class.
   *
   * @param stateConfig - A StatecConfig
   */
  function PriceCurve(stateConfig) {
    this.constants = void 0;
    this.sources = void 0;
    this.stackLength = void 0;
    this.argumentsLength = void 0;
    this.constants = stateConfig.constants;
    this.sources = stateConfig.sources;
    this.stackLength = stateConfig.stackLength;
    this.argumentsLength = stateConfig.argumentsLength;
  }
  /**
   * Method to apply extra time discount to the sale. if sale's continues into extra time
   * then those addresses that have met the critera of extra time discount which is already
   * purchased a certain amount of rTKN will get some discount on price for their next purchase.
   *
   * @remarks - Sale should have extra time feature in order for extra time discount to be effective.
   * @see SaleDuration.applyExtraTime - to deploy a sale that has extra time feature
   *
   * @param endTimestamp - Usual end time of the sale.
   * @param extraTimeDiscountThreshold - the criteria for extra time discount, if the address has already
   * purchased more than this amount then it will recieve the discount for its next purchases once the sale
   * is gone into extra time.
   * @param extraTimeDiscount - The amount of discount the address will receive.
   *
   * @returns this
   *
   */


  var _proto = PriceCurve.prototype;

  _proto.applyExtraTimeDiscount = function applyExtraTimeDiscount(endTimestamp, extraTimeDiscountThreshold, extraTimeDiscount) {
    var EXTRA_TIME_DISCOUNT = function EXTRA_TIME_DISCOUNT(i) {
      return concat([op(Sale.Opcodes.VAL, i - 4), op(Sale.Opcodes.BLOCK_TIMESTAMP), op(Sale.Opcodes.GREATER_THAN), op(Sale.Opcodes.VAL, i - 3), op(Sale.Opcodes.TOKEN_ADDRESS), op(Sale.Opcodes.SENDER), op(Sale.Opcodes.IERC20_BALANCE_OF), op(Sale.Opcodes.GREATER_THAN), op(Sale.Opcodes.ANY, 2)]);
    };

    var DISCOUNT_CONDITION_SOURCES = function DISCOUNT_CONDITION_SOURCES(i) {
      return concat([op(Sale.Opcodes.VAL, i - 2), op(Sale.Opcodes.MUL, 2), op(Sale.Opcodes.VAL, i - 1), op(Sale.Opcodes.DIV, 2), op(Sale.Opcodes.EAGER_IF)]);
    };

    this.constants.push(endTimestamp, parseUnits(extraTimeDiscountThreshold.toString()), 100 - extraTimeDiscount, 100);
    this.sources[0] = concat([EXTRA_TIME_DISCOUNT(this.constants.length), this.sources[0], op(Sale.Opcodes.DUP, 1), DISCOUNT_CONDITION_SOURCES(this.constants.length)]);
    this.stackLength = Number(this.stackLength) + 20;
    return this;
  }
  /**
   * Method to apply tiers' discounts to the sale. Tiered addresses will get discount based on the tier they hold.
   *
   * @param tierAddress - The Tier contract address.
   * @param tierDiscount - An array of each tiers' discount ranging between 0 - 99.
   * @param tierActivation - (optional) An array of number of blocks for each tier that will be the required period of time for that tiered
   * address to hold the tier's in order to be eligible for that tier's discount.
   *
   * @returns this
   *
   */
  ;

  _proto.applyTierDiscount = function applyTierDiscount(tierAddress, tierDiscount, tierActivation) {
    var stateConfig = VM.toTierDiscounter(this, tierAddress, tierDiscount, {
      tierActivation: tierActivation
    });
    this.constants = stateConfig.constants;
    this.sources = stateConfig.sources;
    this.stackLength = stateConfig.stackLength;
    this.argumentsLength = stateConfig.argumentsLength;
    return this;
  }
  /**
   * Method to apply cap per wallet to the sale. addresses can only buy that certain amount of rTKNs.
   * With the option of applying multiplier for max cap per wallet.
   *
   * @param mode - The mode that determines if there is max or min cap per wallet or both.
   * @param options - (optional) Additional arguments to configure the cap per wallet behaviour:
   *    - (param) minWalletCap - The number for min cap per wallet, addresses cannot buy less number of rTKNs than this amount.
   *    - (param) maxWalletCap - The number for max cap per wallet, addresses cannot buy more number of rTKNs than this amount.
   *    - (param) tierMultiplierMode - Set true in order to apply Multiplier for max cap per wallet.
   *    - (param) tierAddress - The Tier contract address for tiers' max cap per wallet multiplier.
   *    - (param) tierMultiplier - An array of each tiers' Multiplier value.
   *    - (param) tierActivation - An array of number of blocks for each tier that will be the required period of time for that tiered
   *       address to hold the tier's in order to be eligible for that tier's multiplier.
   *
   * @returns this
   *
   */
  ;

  _proto.applyWalletCap = function applyWalletCap(mode, options) {
    var MIN_CAP_SOURCES = function MIN_CAP_SOURCES(i) {
      return concat([op(Sale.Opcodes.VAL, i), op(Sale.Opcodes.CURRENT_BUY_UNITS), op(Sale.Opcodes.TOKEN_ADDRESS), op(Sale.Opcodes.SENDER), op(Sale.Opcodes.IERC20_BALANCE_OF), op(Sale.Opcodes.ADD, 2), op(Sale.Opcodes.LESS_THAN)]);
    };

    var MAX_CAP_SOURCES = function MAX_CAP_SOURCES() {
      return concat([op(Sale.Opcodes.CURRENT_BUY_UNITS), op(Sale.Opcodes.TOKEN_ADDRESS), op(Sale.Opcodes.SENDER), op(Sale.Opcodes.IERC20_BALANCE_OF), op(Sale.Opcodes.ADD, 2), op(Sale.Opcodes.GREATER_THAN)]);
    };

    if (mode == WalletCapMode.min && (options == null ? void 0 : options.minWalletCap) !== undefined) {
      options.minWalletCap = options.minWalletCap === 0 ? 1 : options.minWalletCap;
      this.constants.push(parseUnits(options.minWalletCap.toString()).sub(1), ethers.constants.MaxUint256);
      this.sources[0] = concat([this.sources[0], MIN_CAP_SOURCES(this.constants.length - 2), op(Sale.Opcodes.DUP, 0), op(Sale.Opcodes.VAL, this.constants.length - 1), op(Sale.Opcodes.EAGER_IF)]);
      this.stackLength = Number(this.stackLength) + 15;
      return this;
    } else if (mode == WalletCapMode.max && (options == null ? void 0 : options.maxWalletCap) !== undefined) {
      if (options.tierMultiplierMode && options.tierAddress && options.tierMultiplier) {
        var maxCapConfig = {
          constants: [parseUnits(options.maxWalletCap.toString())],
          sources: [concat([op(Sale.Opcodes.VAL, 0)])],
          stackLength: 1,
          argumentsLength: 0
        };
        maxCapConfig = VM.toTierMultiplier(maxCapConfig, options.tierAddress, options.tierMultiplier, {
          tierActivation: options.tierActivation
        });
        maxCapConfig.constants.push(1, ethers.constants.MaxUint256);
        maxCapConfig.sources[0] = concat([maxCapConfig.sources[0], op(Sale.Opcodes.VAL, maxCapConfig.constants.length - 2), op(Sale.Opcodes.ADD, 2), MAX_CAP_SOURCES(), op(Sale.Opcodes.DUP, 0), op(Sale.Opcodes.VAL, maxCapConfig.constants.length - 1), op(Sale.Opcodes.EAGER_IF)]);
        var stateConfig = VM.combiner(this, maxCapConfig);
        this.constants = stateConfig.constants;
        this.sources = stateConfig.sources;
        this.stackLength = stateConfig.stackLength;
        this.argumentsLength = stateConfig.argumentsLength;
        return this;
      } else {
        this.constants.push(parseUnits(options.maxWalletCap.toString()).add(1), ethers.constants.MaxUint256);
        this.sources[0] = concat([this.sources[0], op(Sale.Opcodes.VAL, this.constants.length - 2), MAX_CAP_SOURCES(), op(Sale.Opcodes.DUP, 0), op(Sale.Opcodes.VAL, this.constants.length - 1), op(Sale.Opcodes.EAGER_IF)]);
        this.stackLength = Number(this.stackLength) + 10;
        return this;
      }
    } else if (mode == WalletCapMode.both && (options == null ? void 0 : options.minWalletCap) !== undefined && (options == null ? void 0 : options.maxWalletCap) !== undefined) {
      options.minWalletCap = options.minWalletCap === 0 ? 1 : options.minWalletCap;

      if (options.tierMultiplierMode && options.tierAddress && options.tierMultiplier) {
        var bothCapConfig = {
          constants: [parseUnits(options.maxWalletCap.toString())],
          sources: [concat([op(Sale.Opcodes.VAL, 0)])],
          stackLength: 1,
          argumentsLength: 0
        };
        bothCapConfig = VM.toTierMultiplier(bothCapConfig, options.tierAddress, options.tierMultiplier, {
          tierActivation: options.tierActivation
        });
        bothCapConfig.constants.push(1, parseUnits(options.minWalletCap.toString()).sub(1), ethers.constants.MaxUint256);
        bothCapConfig.sources[0] = concat([bothCapConfig.sources[0], op(Sale.Opcodes.VAL, bothCapConfig.constants.length - 3), op(Sale.Opcodes.ADD, 2), MAX_CAP_SOURCES(), MIN_CAP_SOURCES(bothCapConfig.constants.length - 2), op(Sale.Opcodes.EVERY, 2), op(Sale.Opcodes.DUP, 0), op(Sale.Opcodes.VAL, bothCapConfig.constants.length - 1), op(Sale.Opcodes.EAGER_IF)]);

        var _stateConfig = VM.combiner(this, bothCapConfig);

        this.constants = _stateConfig.constants;
        this.sources = _stateConfig.sources;
        this.stackLength = _stateConfig.stackLength;
        this.argumentsLength = _stateConfig.argumentsLength;
        return this;
      } else {
        this.constants.push(parseUnits(options.maxWalletCap.toString()).add(1), parseUnits(options.minWalletCap.toString()).sub(1), ethers.constants.MaxUint256);
        this.sources[0] = concat([this.sources[0], op(Sale.Opcodes.VAL, this.constants.length - 3), MAX_CAP_SOURCES(), MIN_CAP_SOURCES(this.constants.length - 2), op(Sale.Opcodes.EVERY, 2), op(Sale.Opcodes.DUP, 0), op(Sale.Opcodes.VAL, this.constants.length - 1), op(Sale.Opcodes.EAGER_IF)]);
        this.stackLength = Number(this.stackLength) + 25;
        return this;
      }
    } else {
      throw new Error('Invalid arguments');
    }
  };

  return PriceCurve;
}();
/**
 * @public - A sub-class of PriceCurve for creating a Fixed Price sale type.
 * The price is a constant value over the span of the sale.
 *
 * @example
 * ```typescript
 * //For generating a Fixed Price sale type pass in the required arguments to the constructor.
 * const saleType = new FixedPrice(price)
 * ```
 */

var FixedPrice = /*#__PURE__*/function (_PriceCurve) {
  _inheritsLoose(FixedPrice, _PriceCurve);

  /**
   * Constructs a new raw FixedPrice sale type to be used in a Sale contract.
   *
   * @param price - The constant price of the rTKN.
   * @param erc20decimals - (optional) Number of decimals of the reserve asset (default value 18).
   *
   * @returns a VM StateConfig
   *
   */
  function FixedPrice(price, erc20decimals) {
    if (erc20decimals === void 0) {
      erc20decimals = 18;
    }

    return _PriceCurve.call(this, {
      constants: [parseUnits(price.toString(), erc20decimals)],
      sources: [FixedPrice.FIXED_PRICE_SOURCES()],
      stackLength: 1,
      argumentsLength: 0
    }) || this;
  } // fixed price script


  return FixedPrice;
}(PriceCurve);
/**
 * @public - A sub-class of PriceCurve for creating an vLBP i.e virtual LBP sale type.
 *
 * @remarks - It is called virtual FLO or LBP because there is no actual seeding required.
 * Price starts at 'startPrice' and goes to down over the span of the sale's duration
 * if no buys happen. If buys happen then price will go up (exactly like the real LBP)
 *
 * @example
 * ```typescript
 * //For generating a vLBP sale type pass in the required arguments to the constructor.
 * const saleType = new vLBP(startPrice, startTimestamp, endTimestamp, minimumRaise, initialSupply)
 * ```
 */

FixedPrice.FIXED_PRICE_SOURCES = function () {
  return concat([op(Sale.Opcodes.VAL, 0)]);
};

var vLBP = /*#__PURE__*/function (_PriceCurve2) {
  _inheritsLoose(vLBP, _PriceCurve2);

  /**
   * Constructs a new raw vLBP sale type to be used in a Sale contract.
   *
   * @param startPrice - The starting price of the sale for rTKN.
   * @param startTimestamp - Start timestamp of the sale for rTKN.
   * @param endTimestamp - End timestamp of the sale.
   * @param minimumRaise - Used for virtualizing the seed amount and calculating the assets' weights.
   * @param initialSupply - Used for calculating the assets' weights.
   * @param erc20decimals - (optional) Number of decimals of the reserve asset. (default value 18)
   *
   * @returns a VM StateConfig
   *
   */
  function vLBP(startPrice, startTimestamp, endTimestamp, minimumRaise, initialSupply, erc20decimals) {
    if (erc20decimals === void 0) {
      erc20decimals = 18;
    }

    var raiseDuration = endTimestamp - startTimestamp;
    var balanceReserve = minimumRaise * 5;
    var initWeight = initialSupply * startPrice / balanceReserve;
    var weightChange = (initWeight - 1) / raiseDuration;
    return _PriceCurve2.call(this, {
      constants: [parseUnits(balanceReserve.toString(), erc20decimals), parseUnits(initWeight.toString()), parseUnits(weightChange.toFixed(17).toString()), startTimestamp, parseUnits(1 .toString())],
      sources: [vLBP.vLBP_SOURCES()],
      stackLength: 15,
      argumentsLength: 0
    }) || this;
  } //vLBP script


  return vLBP;
}(PriceCurve);
/**
 * @public - A sub-class of PriceCurve for creating an linear Increasing sale type.
 *
 * @remarks - Price starts at 'startPrice' and goes to 'endPrice' over the span of the sale's duration.
 *
 * @example
 * ```typescript
 * //For generating a Increasing Price sale type pass in the required arguments to the constructor.
 * const saleType = new IncreasingPrice(startPrice, endPrice, startTimestamp, endTimestamp)
 * ```
 */

vLBP.vLBP_SOURCES = function () {
  return concat([op(Sale.Opcodes.TOTAL_RESERVE_IN), op(Sale.Opcodes.VAL, 0), op(Sale.Opcodes.ADD, 2), op(Sale.Opcodes.VAL, 1), op(Sale.Opcodes.BLOCK_TIMESTAMP), op(Sale.Opcodes.VAL, 3), op(Sale.Opcodes.SATURATING_SUB, 2), op(Sale.Opcodes.VAL, 2), op(Sale.Opcodes.MUL, 2), op(Sale.Opcodes.SATURATING_SUB, 2), op(Sale.Opcodes.VAL, 4), op(Sale.Opcodes.MAX, 2), op(Sale.Opcodes.MUL, 2), op(Sale.Opcodes.REMAINING_UNITS), op(Sale.Opcodes.DIV, 2)]);
};

var IncreasingPrice = /*#__PURE__*/function (_PriceCurve3) {
  _inheritsLoose(IncreasingPrice, _PriceCurve3);

  /**
   * Constructs a new raw linear Increasing Price sale type to be used in a Sale contract.
   *
   * @param startPrice - The starting price of the sale for rTKN.
   * @param endPrice - The ending price of the sale for rTKN.
   * @param startTimestamp - Start timestamp of the sale.
   * @param endTimestamp - End timestamp of the sale
   * @param erc20decimals - (optional) Number of decimals of the reserve asset. (default value 18)
   *
   * @returns a VM StateConfig
   *
   */
  function IncreasingPrice(startPrice, endPrice, startTimestamp, endTimestamp, erc20decimals) {
    if (erc20decimals === void 0) {
      erc20decimals = 18;
    }

    var raiseDuration = endTimestamp - startTimestamp;
    var priceChange = (endPrice - startPrice) / raiseDuration;
    return _PriceCurve3.call(this, {
      constants: [parseUnits(startPrice.toString(), erc20decimals), parseUnits(endPrice.toString(), erc20decimals), parseUnits(priceChange.toFixed(erc20decimals != 18 ? erc20decimals : 17).toString(), erc20decimals), startTimestamp],
      sources: [IncreasingPrice.INC_PRICE_SOURCES()],
      stackLength: 10,
      argumentsLength: 0
    }) || this;
  } // linear increasing price script


  return IncreasingPrice;
}(PriceCurve);
/**
 * @public - A class used for creating a VM state for Sale's canEnd/StartStateConfig based on timestamp.
 *
 * @remarks - If the VM result is greater than '0' then sale can start/end and if it is '0' it simply
 * cannot. The basic constructed object is a simple timestamp based condition for sale's
 * canStart/EndStateConfig, but with using the methods in the class more complex conditions
 * can be created for how the sale's duration will work.
 *
 * @remarks - Like all the method calls, order of calling methods in this class is important in order to produce
 * the desired result, although calling in any order will produce a reliable result, that depends on what the
 * intention is. For example 'applyOwner' should be called at last in order to apply the ownership over the whole script.
 * The general methods calling order in this class is:
 *    1.applyExtarTime or afterMinimumRaise (one of which only)
 *    2.applyOwner
 *
 * @example
 * ```typescript
 * //For generating a canStart/End StateConfig for the sale pass in the required arguments to the constructor.
 * const saleType = new SaleDuration(timestamp)
 * ```
 */

IncreasingPrice.INC_PRICE_SOURCES = function () {
  return concat([op(Sale.Opcodes.BLOCK_TIMESTAMP), op(Sale.Opcodes.VAL, 3), op(Sale.Opcodes.SUB, 2), op(Sale.Opcodes.VAL, 2), op(Sale.Opcodes.MUL, 2), op(Sale.Opcodes.VAL, 0), op(Sale.Opcodes.ADD, 2), op(Sale.Opcodes.VAL, 1), op(Sale.Opcodes.MIN, 2)]);
};

var SaleDurationInTimestamp = /*#__PURE__*/function () {
  // StateConfig Properties of this class

  /**
   * Constructs a new canStart/End config to be used in a Sale contract's canStart/End functions.
   *
   * @param timestamp - Timestamp that will be used in constructor to create a simple time based canStart/End condition.
   * If the current timestamp is greater than 'timestamp' the sale can start/end and if not the sale cannot start/end.
   *
   */
  function SaleDurationInTimestamp(timestamp) {
    this.timestamp = void 0;
    this.constants = void 0;
    this.sources = void 0;
    this.stackLength = void 0;
    this.argumentsLength = void 0;
    this.timestamp = timestamp;
    this.constants = [timestamp];
    this.sources = [concat([op(Sale.Opcodes.BLOCK_TIMESTAMP), op(Sale.Opcodes.VAL, 0), op(Sale.Opcodes.GREATER_THAN)])];
    this.stackLength = 3;
    this.argumentsLength = 0;
  }
  /**
   * Method to apply extra time to the sale duration. if the extra time criteria which is raising more
   * than 'extraTimeAmount' has been met the sale continue for longer (for 'extraTime' more minutes).
   *
   * @remarks - If the sale has extra time discount, it is important that this method to be applied for canEndStateConfig of the sale.
   * @remarks - This method is designed for sale's canEndStateConfig and should 'not' be used for canStart
   * @see PriceCurve.applyExtraTimeDiscount - to deploy a sale that has discount when it goes into extra time.
   *
   * @param extraTime - The amount of time (in minutes) that sale can continue for, if the extra time criteria has been met.
   * @param extraTimeAmount - The criteria for extra time, if the raised amount exceeds this amount then the raise can continue into extra time.
   *
   * @returns this
   *
   */


  var _proto2 = SaleDurationInTimestamp.prototype;

  _proto2.applyExtraTime = function applyExtraTime(extraTime, extraTimeAmount, ecr20decimals) {
    if (ecr20decimals === void 0) {
      ecr20decimals = 18;
    }

    var EXTRA_TIME = function EXTRA_TIME() {
      return concat([op(Sale.Opcodes.TOTAL_RESERVE_IN), op(Sale.Opcodes.VAL, 2), op(Sale.Opcodes.LESS_THAN), op(Sale.Opcodes.EVERY, 2), op(Sale.Opcodes.BLOCK_TIMESTAMP), op(Sale.Opcodes.VAL, 1), op(Sale.Opcodes.GREATER_THAN), op(Sale.Opcodes.ANY, 2)]);
    };

    var ExtraTimeAmount = parseUnits(extraTimeAmount.toString(), ecr20decimals);
    var ExtraTime = extraTime * 60 + this.timestamp;
    this.constants.push(ExtraTime, ExtraTimeAmount);
    this.sources[0] = concat([this.sources[0], EXTRA_TIME()]);
    this.stackLength = Number(this.stackLength) + 10;
    return this;
  }
  /**
   * Method to apply owner to the sale's canStart and/or canEnd function.
   * Sale's canStart/End functions are public and can be triggered by anyone when the criteria is met, but with using this method for sale's
   * canStart/EndStateConfig, it can configured in a way that only a certain address can actually trigger the sale's start/end functions.
   *
   * @remarks - applyOwnership will apply the ownership over the StateConfig it is been called for, so the order of call is important to get
   * the desired result.
   *
   * @param ownerAddress - The address that will be the owner, only this wallet address can start or end a raise if this method is applied.
   *
   * @returns this
   *
   */
  ;

  _proto2.applyOwnership = function applyOwnership(ownerAddress) {
    var stateConfig = VM.toOwnerMaker(this, ownerAddress);
    this.constants = stateConfig.constants;
    this.sources = stateConfig.sources;
    this.stackLength = stateConfig.stackLength;
    this.argumentsLength = stateConfig.argumentsLength;
    return this;
  }
  /**
   * A method for the sale to be able to end once the sale hits minimumRaise i.e. the minimum amount
   * that needs to be raiseed so the raises status becomes "success" after raise ends.
   *
   * @remarks please note that this method should not be used with applyExtraTime as they are opossit
   * of eachother and also the order of using this method along with other methods of this class is
   * important
   *
   * @param minimumRaise - the minimumRaise parameter of the raise which is passed at the time of
   * sale's deployment as part of the SaleConfig
   *
   * @returns this
   */
  ;

  _proto2.afterMinimumRaise = function afterMinimumRaise(minimumRaise, erc20decimals) {
    if (erc20decimals === void 0) {
      erc20decimals = 18;
    }

    var MinimumRaise = parseUnits(minimumRaise.toString(), erc20decimals);
    var _minimumRaise = {
      constants: [MinimumRaise.sub(1)],
      sources: [concat([op(Sale.Opcodes.VAL, 0), op(Sale.Opcodes.TOTAL_RESERVE_IN), op(Sale.Opcodes.LESS_THAN), op(Sale.Opcodes.ANY, 2)])],
      stackLength: 4,
      argumentsLength: 0
    };
    _minimumRaise = VM.combiner(this, _minimumRaise);
    this.constants = _minimumRaise.constants;
    this.sources = _minimumRaise.sources;
    this.stackLength = _minimumRaise.stackLength;
    this.argumentsLength = _minimumRaise.argumentsLength;
    return this;
  };

  return SaleDurationInTimestamp;
}();
/**
 * @public - A class used for creating a VM state for Sale's canEnd/StartStateConfig based on block number.
 *
 * @remarks - If the VM result is greater than '0' then sale can start/end and if it is '0' it simply
 * cannot. The basic constructed object is a simple block number based condition for sale's
 * canStart/EndStateConfig, but with using the methods in the class more complex conditions
 * can be created for how the sale's duration will work.
 * It is worth mentioning that using a timestamp based canStart/EndStateConfig is much more desireable as most f the
 * PriceCurve configs are designed with timestamp, so if you want to use the block number based canStart/EndStateConfig,
 * please make sure to to match with timestamp used in PriceCurve configs.
 *
 * @remarks - Like all the method calls, order of calling methods in this class is important in order to produce
 * the desired result, although calling in any order will produce a reliable result, that depends on what the
 * intention is. For example 'applyOwner' should be called at last in order to apply the ownership over the whole script.
 * The general methods calling order in this class is:
 *    1.applyExtarTime or afterMinimumRaise (one of which only)
 *    2.applyOwner
 *
 * @example
 * ```typescript
 * //For generating a canStart/End StateConfig for the sale pass in the required arguments to the constructor.
 * const saleType = new SaleDuration(blockNumber)
 * ```
 */

var SaleDurationInBlocks = /*#__PURE__*/function () {
  // StateConfig Properties of this class

  /**
   * Constructs a new canStart/End config to be used in a Sale contract's canStart/End functions.
   *
   * @param blockNumber - blockNumber that will be used in constructor to create a simple block number based canStart/End condition.
   * If the current BlockNumber is greater than 'blockNumber' the sale can start/end and if not the sale cannot start/end.
   *
   */
  function SaleDurationInBlocks(blockNumber) {
    this.blockNumber = void 0;
    this.constants = void 0;
    this.sources = void 0;
    this.stackLength = void 0;
    this.argumentsLength = void 0;
    this.blockNumber = blockNumber;
    this.constants = [blockNumber - 1];
    this.sources = [concat([op(Sale.Opcodes.BLOCK_NUMBER), op(Sale.Opcodes.VAL, 0), op(Sale.Opcodes.GREATER_THAN)])];
    this.stackLength = 3;
    this.argumentsLength = 0;
  }
  /**
   * Method to apply extra time to the sale duration. if the extra time criteria which is raising more
   * than 'extraTimeAmount' has been met the sale continue for longer (for 'extraTimeBlocks' more blocks).
   *
   * @remarks - If the sale has extra time discount, it is important that this method to be applied for canEndStateConfig of the sale.
   * @remarks - This method is designed for sale's canEndStateConfig and should 'not' be used for canStart
   * @see PriceCurve.applyExtraTimeDiscount - to deploy a sale that has discount when it goes into extra time.
   *
   * @param extraTimeBlocks - The amount of time (in blocks) that sale can continue for, if the extra time criteria has been met.
   * @param extraTimeAmount - The criteria for extra time, if the raised amount exceeds this amount then the raise can continue into extra time.
   *
   * @returns this
   *
   */


  var _proto3 = SaleDurationInBlocks.prototype;

  _proto3.applyExtraTime = function applyExtraTime(extraTimeBlocks, extraTimeAmount, erc20decimals) {
    if (erc20decimals === void 0) {
      erc20decimals = 18;
    }

    var EXTRA_TIME = function EXTRA_TIME() {
      return concat([op(Sale.Opcodes.TOTAL_RESERVE_IN), op(Sale.Opcodes.VAL, 2), op(Sale.Opcodes.LESS_THAN), op(Sale.Opcodes.EVERY, 2), op(Sale.Opcodes.BLOCK_NUMBER), op(Sale.Opcodes.VAL, 1), op(Sale.Opcodes.GREATER_THAN), op(Sale.Opcodes.ANY, 2)]);
    };

    var ExtraTimeAmount = parseUnits(extraTimeAmount.toString(), erc20decimals);
    var ExtraTime = extraTimeBlocks + this.blockNumber;
    this.constants.push(ExtraTime, ExtraTimeAmount);
    this.sources[0] = concat([this.sources[0], EXTRA_TIME()]);
    this.stackLength = Number(this.stackLength) + 10;
    return this;
  }
  /**
   * Method to apply owner to the sale's canStart and/or canEnd function.
   * Sale's canStart/End functions are public and can be triggered by anyone when the criteria is met, but with using this method for sale's
   * canStart/EndStateConfig, it can configured in a way that only a certain address can actually trigger the sale's start/end functions.
   *
   * @remarks - applyOwnership will apply the ownership over the StateConfig it is been called for, so the order of call is important to get
   * the desired result.
   *
   * @param ownerAddress - The address that will be the owner, only this wallet address can start or end a raise if this method is applied.
   *
   * @returns this
   *
   */
  ;

  _proto3.applyOwnership = function applyOwnership(ownerAddress) {
    var stateConfig = VM.toOwnerMaker(this, ownerAddress);
    this.constants = stateConfig.constants;
    this.sources = stateConfig.sources;
    this.stackLength = stateConfig.stackLength;
    this.argumentsLength = stateConfig.argumentsLength;
    return this;
  }
  /**
   * A method for the sale to be able to end once the sale hits minimumRaise i.e. the minimum amount
   * that needs to be raiseed so the raises status becomes "success" after raise ends.
   *
   * @remarks please note that this method should not be used with applyExtraTime as they are opossit
   * of eachother and also the order of using this method along with other methods of this class is
   * important
   *
   * @param minimumRaise - the minimumRaise parameter of the raise which is passed at the time of
   * sale's deployment as part of the SaleConfig
   *
   * @returns this
   */
  ;

  _proto3.afterMinimumRaise = function afterMinimumRaise(minimumRaise, erc20decimals) {
    if (erc20decimals === void 0) {
      erc20decimals = 18;
    }

    var MinimumRaise = parseUnits(minimumRaise.toString(), erc20decimals);
    var _minimumRaise = {
      constants: [MinimumRaise.sub(1)],
      sources: [concat([op(Sale.Opcodes.VAL, 0), op(Sale.Opcodes.TOTAL_RESERVE_IN), op(Sale.Opcodes.LESS_THAN), op(Sale.Opcodes.ANY, 2)])],
      stackLength: 4,
      argumentsLength: 0
    };
    _minimumRaise = VM.combiner(this, _minimumRaise);
    this.constants = _minimumRaise.constants;
    this.sources = _minimumRaise.sources;
    this.stackLength = _minimumRaise.stackLength;
    this.argumentsLength = _minimumRaise.argumentsLength;
    return this;
  };

  return SaleDurationInBlocks;
}();

/**
 * @public
 * Class for deploying and calling methods on a CombineTier contract, providin easy way to interact
 * with deployed CombineTiers.
 *
 * @remarks
 * Since provide an easy way to deploy CombineTiers using Rain's canonical factories and methods for
 * interacting with an already deployed CombineTier, will reduce the code use to instanciate contract.
 *
 * The combine tiers implements the `ReadOnlyTier` over RainVM. Allows combining the reports from any other `ITier`
 * contracts referenced in the `ImmutableSource` set at construction.  value at the top of the
 * stack after executing the rain script will be used as the return of `report`.
 *
 * @example
 * ```typescript
 * import { CombineTier } from 'rain-sdk'
 *
 * // Deploy a new CombineTier using the existing factory. Require an ethers signer and the config desired for the CombineTier.
 * const newCombineTier = await CombineTier.deploy(signer, CombineTierConfigArgs);
 *
 * // Connect to an existing CombineTier. Required the address and an ethers signer.
 * const existingCombineTier  = new CombineTier(address, signer)
 *
 * // With a CombineTier instance, you can call the smart contract methods:
 * const report = await existingTier.report(address)
 *
 * // Access to static methods in CombineTier
 * const accountOP = CombineTier.Opcodes.ACCOUNT;
 *
 * // Obtaian default AlwaysTier instance with an ethers signer
 * const alwaysTier = await CombineTier.getAlwaysTier(signer);
 * ```
 */

var CombineTier = /*#__PURE__*/function (_TierContract) {
  _inheritsLoose(CombineTier, _TierContract);

  /**
   * Constructs a new CombineTier from a known address.
   *
   * @param address - A combine tier address
   * @param signer - An ethers Signer
   * @returns A new combineTier instance
   */
  function CombineTier(address, _signer) {
    var _this;

    CombineTier.checkAddress(address);
    _this = _TierContract.call(this, address, _signer) || this;

    _this.connect = function (signer) {
      return new CombineTier(_this.address, signer);
    };

    _this.setTier = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(account, endTier, data, overrides) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                throw new Error('SET TIER: NOT IMPLEMENTED');

              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3, _x4) {
        return _ref.apply(this, arguments);
      };
    }();

    return _this;
  }

  return CombineTier;
}(TierContract);
CombineTier.nameBookReference = 'combineTierFactory';
CombineTier.Opcodes = /*#__PURE__*/_extends({}, AllStandardOps, {
  ACCOUNT: 0 + AllStandardOps.length
});

CombineTier.isChild = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(signer, maybeChild) {
    return runtime_1.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return CombineTier._isChild(signer, maybeChild);

          case 2:
            return _context2.abrupt("return", _context2.sent);

          case 3:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function (_x5, _x6) {
    return _ref2.apply(this, arguments);
  };
}();

CombineTier.getAlwaysTier = /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(signer) {
    return runtime_1.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _context3.t0 = CombineTier;
            _context3.t1 = CombineTier;
            _context3.next = 4;
            return CombineTier.getChainId(signer);

          case 4:
            _context3.t2 = _context3.sent;
            _context3.t3 = _context3.t1.getAddressesForChainId.call(_context3.t1, _context3.t2).alwaysTier;
            _context3.t4 = signer;
            return _context3.abrupt("return", new _context3.t0(_context3.t3, _context3.t4));

          case 8:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function (_x7) {
    return _ref3.apply(this, arguments);
  };
}();

CombineTier.deploy = /*#__PURE__*/function () {
  var _ref4 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(signer, args, overrides) {
    var combineTierFactory, tx, receipt, address;
    return runtime_1.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            if (overrides === void 0) {
              overrides = {};
            }

            _context4.t0 = CombineTierFactory__factory;
            _context4.t1 = CombineTier;
            _context4.next = 5;
            return CombineTier.getChainId(signer);

          case 5:
            _context4.t2 = _context4.sent;
            _context4.t3 = _context4.t1.getBookAddress.call(_context4.t1, _context4.t2);
            _context4.t4 = signer;
            combineTierFactory = _context4.t0.connect.call(_context4.t0, _context4.t3, _context4.t4);
            _context4.next = 11;
            return combineTierFactory.createChildTyped(args, overrides);

          case 11:
            tx = _context4.sent;
            _context4.next = 14;
            return tx.wait();

          case 14:
            receipt = _context4.sent;
            address = CombineTier.getNewChildFromReceipt(receipt, combineTierFactory);
            return _context4.abrupt("return", new CombineTier(address, signer));

          case 17:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));

  return function (_x8, _x9, _x10) {
    return _ref4.apply(this, arguments);
  };
}();

/**
 * @public The script generator for generating CombineTier scripts although it is worth mentioning that
 * the usecases would not be only limited to CombineTier contract and can be used for any script.
 *
 * @example new CombineTier(a tierAddress or a StateConfig)
 */

var CombineTierGenerator = /*#__PURE__*/function () {
  // StateConfig Properties of this class

  /**
   * Constructor for this class
   *
   * @param reportVar - either a tier contract address or a StateConfig of REPROT script (or any other form of StateConfig desired)
   */
  function CombineTierGenerator(reportVar) {
    this.constants = void 0;
    this.sources = void 0;
    this.stackLength = void 0;
    this.argumentsLength = void 0;

    if (typeof reportVar == "string") {
      this.constants = [reportVar];
      this.sources = [concat([op(CombineTier.Opcodes.VAL, 0), op(CombineTier.Opcodes.ACCOUNT), op(CombineTier.Opcodes.REPORT)])];
      this.stackLength = 3;
      this.argumentsLength = 0;
    } else {
      this.constants = reportVar.constants;
      this.sources = reportVar.sources;
      this.stackLength = reportVar.stackLength;
      this.argumentsLength = reportVar.argumentsLength;
    }
  }
  /**
   * Combines 2 tier report with selectLte with its logic and mode, and can be chained for multiple reports each with their own logic and mode
   *
   * @param reportVar - either a tier contract address or a StateConfig of REPROT script (or any other form of StateConfig desired)
   * @param logic - selectLte logic
   * @param mode - selectLte mode
   * @param number - (optional) if passed it would be the number to compare reports against, if not passed reports will be compared against BLOCK_NUMBER
   *
   * @returns this
   */


  var _proto = CombineTierGenerator.prototype;

  _proto.combineWith = function combineWith(reportVar, logic, mode, number) {
    var _buttom = typeof reportVar == "string" ? new CombineTierGenerator(reportVar) : reportVar;

    var _combiner = {
      constants: number ? [number] : [],
      sources: [concat([number ? op(CombineTier.Opcodes.VAL, 0) : op(CombineTier.Opcodes.BLOCK_NUMBER), op(CombineTier.Opcodes.SELECT_LTE, selectLte(logic, mode, 2))])],
      stackLength: 2,
      argumentsLength: 0
    };

    var _result = VM.combiner(_buttom, _combiner);

    _result = VM.combiner(this, _result);
    this.constants = _result.constants;
    this.sources = _result.sources;
    this.stackLength = _result.stackLength;
    this.argumentsLength = _result.argumentsLength;
    return this;
  }
  /**
   * Method to update a report at given tier range (can be any range between 0 to 8)
   *
   * @param startTier - start of the report updating range (exclusive)
   * @param endTier - end of the report updating range (inclusive)
   * @param number - (optional) if passed it would be the number to compare reports against, if not passed reports will be compared against BLOCK_NUMBER
   *
   * @returns this
   */
  ;

  _proto.updateReport = function updateReport(startTier, endTier, number) {
    var _updater = {
      constants: number ? [number] : [],
      sources: [concat([number ? op(CombineTier.Opcodes.VAL, 0) : op(CombineTier.Opcodes.BLOCK_NUMBER), op(CombineTier.Opcodes.UPDATE_BLOCKS_FOR_TIER_RANGE, tierRange(startTier, endTier))])],
      stackLength: 2,
      argumentsLength: 0
    };

    var _result = VM.combiner(this, _updater);

    this.constants = _result.constants;
    this.sources = _result.sources;
    this.stackLength = _result.stackLength;
    this.argumentsLength = _result.argumentsLength;
    return this;
  }
  /**
   * Saturating difference between 2 reports
   *
   * @param reportVar - either a tier contract address or a StateConfig of REPROT script (or any other form of StateConfig desired)
   *
   * @returns this
   */
  ;

  _proto.differenceFrom = function differenceFrom(reportVar) {
    var _buttom = typeof reportVar == "string" ? new CombineTierGenerator(reportVar) : reportVar;

    var _differ = {
      constants: [],
      sources: [concat([op(CombineTier.Opcodes.SATURATING_DIFF)])],
      stackLength: 1,
      argumentsLength: 0
    };

    var _result = VM.combiner(_buttom, _differ);

    _result = VM.combiner(this, _result);
    this.constants = _result.constants;
    this.sources = _result.sources;
    this.stackLength = _result.stackLength;
    this.argumentsLength = _result.argumentsLength;
    return this;
  }
  /**
   * Creats a holding time ALWAYS/NEVER tier script for a CombineTier contract out of a TransferTier.
   *
   * @param reportVar - either a TransferTier contract address or a StateConfig of TransferTier REPORT script (or can be any other form of report-like StateConfig desired)
   * @param numberOfBlocks - A number or an array of numbers represting the number of blocks a given tier must be held to get ALWAYS report or else it gets NEVER report.
   *
   * @returns this
   */
  ;

  _proto.isTierHeldFor = function isTierHeldFor(reportVar, numberOfBlocks) {
    var _report = typeof reportVar == "string" ? new CombineTierGenerator(reportVar) : reportVar;

    var _shifter = paddedUInt256(BigNumber.from("0x" + paddedUInt32("7") + paddedUInt32("6") + paddedUInt32("5") + paddedUInt32("4") + paddedUInt32("3") + paddedUInt32("2") + paddedUInt32("1") + paddedUInt32("0")));

    var _blocks = paddedUInt256(BigNumber.from("0x" + paddedUInt32(typeof numberOfBlocks == "number" ? numberOfBlocks : numberOfBlocks[7]) + paddedUInt32(typeof numberOfBlocks == "number" ? numberOfBlocks : numberOfBlocks[6]) + paddedUInt32(typeof numberOfBlocks == "number" ? numberOfBlocks : numberOfBlocks[5]) + paddedUInt32(typeof numberOfBlocks == "number" ? numberOfBlocks : numberOfBlocks[4]) + paddedUInt32(typeof numberOfBlocks == "number" ? numberOfBlocks : numberOfBlocks[3]) + paddedUInt32(typeof numberOfBlocks == "number" ? numberOfBlocks : numberOfBlocks[2]) + paddedUInt32(typeof numberOfBlocks == "number" ? numberOfBlocks : numberOfBlocks[1]) + paddedUInt32(typeof numberOfBlocks == "number" ? numberOfBlocks : numberOfBlocks[0])));

    var _result = {
      constants: [_blocks, _shifter, "0x100000000", "0", "0xffffffff"],
      sources: [concat([op(CombineTier.Opcodes.VAL, 0), op(CombineTier.Opcodes.VAL, 1), op(CombineTier.Opcodes.ZIPMAP, callSize(1, 3, 2)), op(CombineTier.Opcodes.ADD, 8)]), concat([op(CombineTier.Opcodes.BLOCK_NUMBER), op(CombineTier.Opcodes.VAL, arg(0)), op(CombineTier.Opcodes.SATURATING_SUB, 2), op(CombineTier.Opcodes.VAL, arg(1)), op(CombineTier.Opcodes.LESS_THAN), op(CombineTier.Opcodes.VAL, 4), op(CombineTier.Opcodes.VAL, 3), op(CombineTier.Opcodes.EAGER_IF), op(CombineTier.Opcodes.VAL, 2), op(CombineTier.Opcodes.VAL, arg(2)), op(CombineTier.Opcodes.EXP, 2), op(CombineTier.Opcodes.MUL, 2)])],
      stackLength: 15,
      argumentsLength: 3
    };
    _result = VM.combiner(_report, _result);
    this.constants = _result.constants;
    this.sources = _result.sources;
    this.stackLength = _result.stackLength;
    this.argumentsLength = _result.argumentsLength;
    return this;
  };

  return CombineTierGenerator;
}();
/**
 * @public A class for creating a report-like script which inherits from CombineTierGenerator
 */

var BuildReport = /*#__PURE__*/function (_CombineTierGenerator) {
  _inheritsLoose(BuildReport, _CombineTierGenerator);

  /**
   * Contructor of this class
   *
   * @param number - (optional) A number or an array of numbers represting the report at each tier,
   * if not passed, BLOCK_NUMBER will be used to creat the report of each tier
   */
  function BuildReport(number) {
    var _result;

    if (number != undefined) {
      _result = {
        constants: [paddedUInt256(BigNumber.from("0x" + paddedUInt32(typeof number == "number" ? number : number[7] != undefined ? number[7] : 0) + paddedUInt32(typeof number == "number" ? number : number[6] != undefined ? number[6] : 0) + paddedUInt32(typeof number == "number" ? number : number[5] != undefined ? number[5] : 0) + paddedUInt32(typeof number == "number" ? number : number[4] != undefined ? number[4] : 0) + paddedUInt32(typeof number == "number" ? number : number[3] != undefined ? number[3] : 0) + paddedUInt32(typeof number == "number" ? number : number[2] != undefined ? number[2] : 0) + paddedUInt32(typeof number == "number" ? number : number[1] != undefined ? number[1] : 0) + paddedUInt32(typeof number == "number" ? number : number[0] != undefined ? number[0] : 0)))],
        sources: [concat([op(CombineTier.Opcodes.VAL, 0)])],
        stackLength: 1,
        argumentsLength: 0
      };
    } else {
      _result = {
        constants: [],
        sources: [concat([op(CombineTier.Opcodes.NEVER), op(CombineTier.Opcodes.BLOCK_NUMBER), op(CombineTier.Opcodes.UPDATE_BLOCKS_FOR_TIER_RANGE, tierRange(Tier.ZERO, Tier.EIGHT))])],
        stackLength: 3,
        argumentsLength: 0
      };
    }

    return _CombineTierGenerator.call(this, _result) || this;
  }

  return BuildReport;
}(CombineTierGenerator);

/**
 * @public A linear minting emissions over a period of time. holding more before claiming would result in a more reward.
 *
 */

var LinearEmissions = // StateConfig Properties of this class

/**
 * Constructor for this class
 *
 * @param config - An EmissionsConfig
 */
function LinearEmissions(config) {
  this.constants = void 0;
  this.sources = void 0;
  this.stackLength = void 0;
  this.argumentsLength = void 0;
  var eighteenZeros = "000000000000000000";
  var sixZeros = "000000";
  var BN_ONE = BigNumber.from("1" + eighteenZeros); // We're using uints, so we need to scale reward per block up to get out of the decimal places, but a precision of 18 zeros is 
  // too much to fit within a uint32 (since we store block rewards per tier in a report-like format). Six zeros should be enough.

  var BN_ONE_REWARD = BigNumber.from("1" + sixZeros);
  var BLOCKS_PER_PERIOD = Math.floor(config.period / config.blockTime);
  var PERIODIC_REWARD_TIER1 = BigNumber.from(config.periodicRewards.tier1).mul(BN_ONE_REWARD);
  var PERIODIC_REWARD_TIER2 = BigNumber.from(config.periodicRewards.tier2).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER1);
  var PERIODIC_REWARD_TIER3 = BigNumber.from(config.periodicRewards.tier3).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER2.add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER4 = BigNumber.from(config.periodicRewards.tier4).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER3.add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER5 = BigNumber.from(config.periodicRewards.tier4).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER4.add(PERIODIC_REWARD_TIER3).add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER6 = BigNumber.from(config.periodicRewards.tier4).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER5.add(PERIODIC_REWARD_TIER4).add(PERIODIC_REWARD_TIER3).add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER7 = BigNumber.from(config.periodicRewards.tier4).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER6.add(PERIODIC_REWARD_TIER5).add(PERIODIC_REWARD_TIER4).add(PERIODIC_REWARD_TIER3).add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER8 = BigNumber.from(config.periodicRewards.tier4).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER7.add(PERIODIC_REWARD_TIER6).add(PERIODIC_REWARD_TIER5).add(PERIODIC_REWARD_TIER4).add(PERIODIC_REWARD_TIER3).add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var REWARD_PER_BLOCK_TIER1 = PERIODIC_REWARD_TIER1.div(BLOCKS_PER_PERIOD);
  var REWARD_PER_BLOCK_TIER2 = PERIODIC_REWARD_TIER2.div(BLOCKS_PER_PERIOD);
  var REWARD_PER_BLOCK_TIER3 = PERIODIC_REWARD_TIER3.div(BLOCKS_PER_PERIOD);
  var REWARD_PER_BLOCK_TIER4 = PERIODIC_REWARD_TIER4.div(BLOCKS_PER_PERIOD);
  var REWARD_PER_BLOCK_TIER5 = PERIODIC_REWARD_TIER5.div(BLOCKS_PER_PERIOD);
  var REWARD_PER_BLOCK_TIER6 = PERIODIC_REWARD_TIER6.div(BLOCKS_PER_PERIOD);
  var REWARD_PER_BLOCK_TIER7 = PERIODIC_REWARD_TIER7.div(BLOCKS_PER_PERIOD);
  var REWARD_PER_BLOCK_TIER8 = PERIODIC_REWARD_TIER8.div(BLOCKS_PER_PERIOD);
  var BASE_REWARD_PER_TIER = paddedUInt256(ethers.BigNumber.from("0x" + paddedUInt32(REWARD_PER_BLOCK_TIER8) + paddedUInt32(REWARD_PER_BLOCK_TIER7) + paddedUInt32(REWARD_PER_BLOCK_TIER6) + paddedUInt32(REWARD_PER_BLOCK_TIER5) + paddedUInt32(REWARD_PER_BLOCK_TIER4) + paddedUInt32(REWARD_PER_BLOCK_TIER3) + paddedUInt32(REWARD_PER_BLOCK_TIER2) + paddedUInt32(REWARD_PER_BLOCK_TIER1))); // prettier-ignore

  var REWARD = function REWARD() {
    return concat([op(EmissionsERC20.Opcodes.VAL, arg(0)), op(EmissionsERC20.Opcodes.VAL, arg(1)), op(EmissionsERC20.Opcodes.MUL, 2)]);
  }; // prettier-ignore


  var FN = function FN() {
    return concat([REWARD(), op(EmissionsERC20.Opcodes.VAL, 2), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2)]);
  }; // prettier-ignore


  var CURRENT_BLOCK_AS_REPORT = function CURRENT_BLOCK_AS_REPORT() {
    return concat([op(EmissionsERC20.Opcodes.NEVER), op(EmissionsERC20.Opcodes.BLOCK_NUMBER), op(EmissionsERC20.Opcodes.UPDATE_BLOCKS_FOR_TIER_RANGE, tierRange(Tier.ZERO, Tier.EIGHT))]);
  }; // prettier-ignore


  var LAST_CLAIM_REPORT = function LAST_CLAIM_REPORT() {
    return concat([op(EmissionsERC20.Opcodes.THIS_ADDRESS), op(EmissionsERC20.Opcodes.CLAIMANT_ACCOUNT), op(EmissionsERC20.Opcodes.REPORT)]);
  }; // prettier-ignore


  var TIER_REPORT = function TIER_REPORT() {
    return concat([op(EmissionsERC20.Opcodes.VAL, 0), op(EmissionsERC20.Opcodes.CLAIMANT_ACCOUNT), op(EmissionsERC20.Opcodes.REPORT)]);
  }; // prettier-ignore


  var TIERWISE_DIFF = function TIERWISE_DIFF() {
    return concat([CURRENT_BLOCK_AS_REPORT(), TIER_REPORT(), LAST_CLAIM_REPORT(), op(EmissionsERC20.Opcodes.BLOCK_NUMBER), op(EmissionsERC20.Opcodes.SELECT_LTE, selectLte(selectLteLogic.every, selectLteMode.max, 2)), op(EmissionsERC20.Opcodes.SATURATING_DIFF)]);
  }; // prettier-ignore


  var SOURCE = function SOURCE() {
    return concat([TIERWISE_DIFF(), op(EmissionsERC20.Opcodes.VAL, 1), op(EmissionsERC20.Opcodes.ZIPMAP, callSize(1, 3, 1)), op(EmissionsERC20.Opcodes.ADD, 8)]);
  };

  this.constants = [config.tierAddress, BASE_REWARD_PER_TIER, BN_ONE, BN_ONE_REWARD];
  this.sources = [SOURCE(), FN()];
  this.stackLength = (SOURCE().length + FN().length) / 2;
  this.argumentsLength = 2;
};
/**
 * @public
 * A sequential minting emission, minting can only be done once in every period of time,
 * also can set a max reward with increment over the span of several periods.
 */

var SequentialEmissions = // StateConfig Properties of this class

/**
 * Constructor for this class
 *
 * @param config - An EmissionsConfig
 */
function SequentialEmissions(config) {
  this.constants = void 0;
  this.sources = void 0;
  this.stackLength = void 0;
  this.argumentsLength = void 0;
  var eighteenZeros = "000000000000000000";
  var sixZeros = "000000";
  var BN_ONE = BigNumber.from("1" + eighteenZeros); // We're using uints, so we need to scale reward per block up to get out of the decimal places, but a precision of 18 zeros is 
  // too much to fit within a uint32 (since we store block rewards per tier in a report-like format). Six zeros should be enough.

  var BN_ONE_REWARD = BigNumber.from("1" + sixZeros);
  var BLOCKS_PER_PERIOD = Math.floor(config.period / config.blockTime);
  var PERIODIC_REWARD_TIER1 = BigNumber.from(config.periodicRewards.tier1).mul(BN_ONE_REWARD);
  var PERIODIC_REWARD_TIER2 = BigNumber.from(config.periodicRewards.tier2).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER1);
  var PERIODIC_REWARD_TIER3 = BigNumber.from(config.periodicRewards.tier3).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER2.add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER4 = BigNumber.from(config.periodicRewards.tier4).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER3.add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER5 = BigNumber.from(config.periodicRewards.tier5).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER4.add(PERIODIC_REWARD_TIER3).add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER6 = BigNumber.from(config.periodicRewards.tier6).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER5.add(PERIODIC_REWARD_TIER4).add(PERIODIC_REWARD_TIER3).add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER7 = BigNumber.from(config.periodicRewards.tier7).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER6.add(PERIODIC_REWARD_TIER5).add(PERIODIC_REWARD_TIER4).add(PERIODIC_REWARD_TIER3).add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_TIER8 = BigNumber.from(config.periodicRewards.tier8).mul(BN_ONE_REWARD).sub(PERIODIC_REWARD_TIER7.add(PERIODIC_REWARD_TIER6).add(PERIODIC_REWARD_TIER5).add(PERIODIC_REWARD_TIER4).add(PERIODIC_REWARD_TIER3).add(PERIODIC_REWARD_TIER2).add(PERIODIC_REWARD_TIER1));
  var PERIODIC_REWARD_PER_TIER = paddedUInt256(ethers.BigNumber.from("0x" + paddedUInt32(PERIODIC_REWARD_TIER8) + paddedUInt32(PERIODIC_REWARD_TIER7) + paddedUInt32(PERIODIC_REWARD_TIER6) + paddedUInt32(PERIODIC_REWARD_TIER5) + paddedUInt32(PERIODIC_REWARD_TIER4) + paddedUInt32(PERIODIC_REWARD_TIER3) + paddedUInt32(PERIODIC_REWARD_TIER2) + paddedUInt32(PERIODIC_REWARD_TIER1)));
  var PERIODIC_MAX_TIER1 = config.maxPeriodicRewards ? BigNumber.from(config.maxPeriodicRewards.tier1 - config.periodicRewards.tier1).mul(BN_ONE_REWARD) : ethers.constants.Zero;
  var PERIODIC_MAX_TIER2 = config.maxPeriodicRewards ? BigNumber.from(config.maxPeriodicRewards.tier2 - config.periodicRewards.tier2).mul(BN_ONE_REWARD).sub(PERIODIC_MAX_TIER1) : ethers.constants.Zero;
  var PERIODIC_MAX_TIER3 = config.maxPeriodicRewards ? BigNumber.from(config.maxPeriodicRewards.tier3 - config.periodicRewards.tier3).mul(BN_ONE_REWARD).sub(PERIODIC_MAX_TIER2.add(PERIODIC_MAX_TIER1)) : ethers.constants.Zero;
  var PERIODIC_MAX_TIER4 = config.maxPeriodicRewards ? BigNumber.from(config.maxPeriodicRewards.tier4 - config.periodicRewards.tier4).mul(BN_ONE_REWARD).sub(PERIODIC_MAX_TIER3.add(PERIODIC_MAX_TIER2).add(PERIODIC_MAX_TIER1)) : ethers.constants.Zero;
  var PERIODIC_MAX_TIER5 = config.maxPeriodicRewards ? BigNumber.from(config.maxPeriodicRewards.tier5 - config.periodicRewards.tier5).mul(BN_ONE_REWARD).sub(PERIODIC_MAX_TIER4.add(PERIODIC_MAX_TIER3).add(PERIODIC_MAX_TIER2).add(PERIODIC_MAX_TIER1)) : ethers.constants.Zero;
  var PERIODIC_MAX_TIER6 = config.maxPeriodicRewards ? BigNumber.from(config.maxPeriodicRewards.tier6 - config.periodicRewards.tier6).mul(BN_ONE_REWARD).sub(PERIODIC_MAX_TIER5.add(PERIODIC_MAX_TIER4).add(PERIODIC_MAX_TIER3).add(PERIODIC_MAX_TIER2).add(PERIODIC_MAX_TIER1)) : ethers.constants.Zero;
  var PERIODIC_MAX_TIER7 = config.maxPeriodicRewards ? BigNumber.from(config.maxPeriodicRewards.tier7 - config.periodicRewards.tier7).mul(BN_ONE_REWARD).sub(PERIODIC_MAX_TIER6.add(PERIODIC_MAX_TIER5).add(PERIODIC_MAX_TIER4).add(PERIODIC_MAX_TIER3).add(PERIODIC_MAX_TIER2).add(PERIODIC_MAX_TIER1)) : ethers.constants.Zero;
  var PERIODIC_MAX_TIER8 = config.maxPeriodicRewards ? BigNumber.from(config.maxPeriodicRewards.tier8 - config.periodicRewards.tier8).mul(BN_ONE_REWARD).sub(PERIODIC_MAX_TIER7.add(PERIODIC_MAX_TIER6).add(PERIODIC_MAX_TIER5).add(PERIODIC_MAX_TIER4).add(PERIODIC_MAX_TIER3).add(PERIODIC_MAX_TIER2).add(PERIODIC_MAX_TIER1)) : ethers.constants.Zero;
  var PERIODIC_INC_TIER1 = PERIODIC_MAX_TIER1.div(config.numberOfIncrements ? config.numberOfIncrements - 1 : 1);
  var PERIODIC_INC_TIER2 = PERIODIC_MAX_TIER2.div(config.numberOfIncrements ? config.numberOfIncrements - 1 : 1);
  var PERIODIC_INC_TIER3 = PERIODIC_MAX_TIER3.div(config.numberOfIncrements ? config.numberOfIncrements - 1 : 1);
  var PERIODIC_INC_TIER4 = PERIODIC_MAX_TIER4.div(config.numberOfIncrements ? config.numberOfIncrements - 1 : 1);
  var PERIODIC_INC_TIER5 = PERIODIC_MAX_TIER5.div(config.numberOfIncrements ? config.numberOfIncrements - 1 : 1);
  var PERIODIC_INC_TIER6 = PERIODIC_MAX_TIER6.div(config.numberOfIncrements ? config.numberOfIncrements - 1 : 1);
  var PERIODIC_INC_TIER7 = PERIODIC_MAX_TIER7.div(config.numberOfIncrements ? config.numberOfIncrements - 1 : 1);
  var PERIODIC_INC_TIER8 = PERIODIC_MAX_TIER8.div(config.numberOfIncrements ? config.numberOfIncrements - 1 : 1);
  var PERIODIC_INC_PER_TIER = paddedUInt256(ethers.BigNumber.from("0x" + paddedUInt32(PERIODIC_INC_TIER8) + paddedUInt32(PERIODIC_INC_TIER7) + paddedUInt32(PERIODIC_INC_TIER6) + paddedUInt32(PERIODIC_INC_TIER5) + paddedUInt32(PERIODIC_INC_TIER4) + paddedUInt32(PERIODIC_INC_TIER3) + paddedUInt32(PERIODIC_INC_TIER2) + paddedUInt32(PERIODIC_INC_TIER1)));
  this.constants = [config.tierAddress, PERIODIC_REWARD_PER_TIER, PERIODIC_INC_PER_TIER, BLOCKS_PER_PERIOD, BN_ONE, BN_ONE_REWARD, config.numberOfIncrements ? config.numberOfIncrements : 0, 1, "10", 2];
  this.sources = [concat([op(EmissionsERC20.Opcodes.NEVER), op(EmissionsERC20.Opcodes.BLOCK_NUMBER), op(EmissionsERC20.Opcodes.UPDATE_BLOCKS_FOR_TIER_RANGE, tierRange(Tier.ZERO, Tier.EIGHT)), op(EmissionsERC20.Opcodes.VAL, 0), op(EmissionsERC20.Opcodes.CLAIMANT_ACCOUNT), op(EmissionsERC20.Opcodes.REPORT), op(EmissionsERC20.Opcodes.SATURATING_DIFF), op(EmissionsERC20.Opcodes.THIS_ADDRESS), op(EmissionsERC20.Opcodes.CLAIMANT_ACCOUNT), op(EmissionsERC20.Opcodes.REPORT), op(EmissionsERC20.Opcodes.VAL, 0), op(EmissionsERC20.Opcodes.CLAIMANT_ACCOUNT), op(EmissionsERC20.Opcodes.REPORT), op(EmissionsERC20.Opcodes.SATURATING_DIFF), op(EmissionsERC20.Opcodes.VAL, 1), op(EmissionsERC20.Opcodes.VAL, 2), op(EmissionsERC20.Opcodes.ZIPMAP, callSize(1, 3, 3)), op(EmissionsERC20.Opcodes.ADD, 8), op(EmissionsERC20.Opcodes.VAL, 4), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.VAL, 5), op(EmissionsERC20.Opcodes.DIV, 2)]), concat([op(EmissionsERC20.Opcodes.VAL, arg(0)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.VAL, 6), op(EmissionsERC20.Opcodes.GREATER_THAN), op(EmissionsERC20.Opcodes.VAL, 6), op(EmissionsERC20.Opcodes.VAL, arg(1)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.VAL, arg(1)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.VAL, 6), op(EmissionsERC20.Opcodes.VAL, 7), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.ADD, 2), op(EmissionsERC20.Opcodes.VAL, 8), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.VAL, 9), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.VAL, 8), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.VAL, arg(0)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.VAL, arg(1)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.VAL, 6), op(EmissionsERC20.Opcodes.VAL, arg(1)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.VAL, 6), op(EmissionsERC20.Opcodes.VAL, 7), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.ADD, 2), op(EmissionsERC20.Opcodes.VAL, arg(3)), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.VAL, arg(0)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.VAL, arg(1)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.VAL, arg(0)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.VAL, 7), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.VAL, arg(1)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.ADD, 2), op(EmissionsERC20.Opcodes.VAL, 8), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.VAL, 9), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.VAL, 8), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.VAL, arg(3)), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.EAGER_IF), op(EmissionsERC20.Opcodes.VAL, arg(2)), op(EmissionsERC20.Opcodes.VAL, arg(0)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.VAL, arg(1)), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.MUL, 2), op(EmissionsERC20.Opcodes.ADD, 2)])];
  this.stackLength = (this.sources[0].length + this.sources[1].length) / 2 + 25;
  this.argumentsLength = 4;
};
/**
 * @public
 * A class to creat a an ERC20 token with ability to be a faucet. owner can mint extar token if i=the token is not fixed supply.
 * It can be claimed x number of tokens once every number of blocks passed which is defined by the faucet at the time of deployment.
 */

var CreateERC20 = // StateConfig Properties of this class

/**
 * constructor of this class
 *
 * @param ownerAddress - address of the owner of the token
 * @param mintUnits - amount of tokens to be able to mint each time by calling the contract's "claim" function by the owner, pass zero to get fixed supply token
 * @param faucet - argument to make a faucet for the token with ability to be minted x number of tokens each x number of blocks passed.
 *    (param) - blocks - number of blocks needs to pass before being able to do another faucet claim - cannot be zero
 *    (param) - units - number of token to be transfered by the fuacet each time it is triggered (at each claim)
 */
function CreateERC20(ownerAddress, mintUnits, faucet) {
  this.constants = void 0;
  this.sources = void 0;
  this.stackLength = void 0;
  this.argumentsLength = void 0;

  if (faucet) {
    this.constants = [ownerAddress, parseUnits(mintUnits.toString(), 18), faucet.blocks ? faucet.blocks : 1, parseUnits(faucet.units.toString(), 18), "0x1" + paddedUInt32(0).repeat(7)];
    this.sources = [concat([op(EmissionsERC20.Opcodes.VAL, 0), op(EmissionsERC20.Opcodes.CLAIMANT_ACCOUNT), op(EmissionsERC20.Opcodes.EQUAL_TO), op(EmissionsERC20.Opcodes.VAL, 1), op(EmissionsERC20.Opcodes.VAL, 2), op(EmissionsERC20.Opcodes.BLOCK_NUMBER), op(EmissionsERC20.Opcodes.THIS_ADDRESS), op(EmissionsERC20.Opcodes.CLAIMANT_ACCOUNT), op(EmissionsERC20.Opcodes.REPORT), op(EmissionsERC20.Opcodes.VAL, 4), op(EmissionsERC20.Opcodes.DIV, 2), op(EmissionsERC20.Opcodes.SATURATING_SUB, 2), op(EmissionsERC20.Opcodes.GREATER_THAN), op(EmissionsERC20.Opcodes.ALWAYS), op(EmissionsERC20.Opcodes.VAL, 3), op(EmissionsERC20.Opcodes.EAGER_IF), op(EmissionsERC20.Opcodes.EAGER_IF)])];
    this.stackLength = 20;
    this.argumentsLength = 0;
  } else {
    this.constants = [ownerAddress, parseUnits(mintUnits.toString(), 18)];
    this.sources = [concat([op(EmissionsERC20.Opcodes.VAL, 0), op(EmissionsERC20.Opcodes.CLAIMANT_ACCOUNT), op(EmissionsERC20.Opcodes.EQUAL_TO), op(EmissionsERC20.Opcodes.VAL, 1), op(EmissionsERC20.Opcodes.ALWAYS), op(EmissionsERC20.Opcodes.EAGER_IF)])];
    this.stackLength = 6;
    this.argumentsLength = 0;
  }
};

/**
 * @public
 *
 * A generic ERC1155 interface to get connected to any ERC1155 address and make transactions.
 *
 * @remarks
 * The interface only have and provide generic and common methods calls. Remember that any specific
 * method implemented in the contract will NOT be available in this interface.
 */

var ERC1155 =
/**
 * Constructs a new ERC1155 from a known address.
 *
 * @param address - The address of the ERC1155 contract
 * @param signer - An ethers.js Signer
 * @returns A new ERC1155 instance
 *
 */
function ERC1155(_address, _signer) {
  var _this = this;

  this.signer = void 0;
  this.address = void 0;

  this.connect = function (signer) {
    return new ERC1155(_this.address, signer);
  };

  this.attach = function (address) {
    return new ERC1155(address, _this.signer);
  };

  this.balanceOf = void 0;
  this.balanceOfBatch = void 0;
  this.burn = void 0;
  this.burnBatch = void 0;
  this.isApprovedForAll = void 0;
  this.safeBatchTransferFrom = void 0;
  this.safeTransferFrom = void 0;
  this.setApprovalForAll = void 0;
  this.supportsInterface = void 0;
  this.uri = void 0;
  this.address = _address;
  this.signer = _signer;

  var _erc1155 = ERC1155Burnable__factory.connect(_address, _signer);

  this.balanceOf = _erc1155.balanceOf;
  this.balanceOfBatch = _erc1155.balanceOfBatch;
  this.burn = _erc1155.burn;
  this.burnBatch = _erc1155.burnBatch;
  this.isApprovedForAll = _erc1155.isApprovedForAll;
  this.safeBatchTransferFrom = _erc1155.safeBatchTransferFrom;
  this.safeTransferFrom = _erc1155.safeTransferFrom;
  this.setApprovalForAll = _erc1155.setApprovalForAll;
  this.supportsInterface = _erc1155.supportsInterface;
  this.uri = _erc1155.uri;
}
/**
 * Check if the address is an IERC1155.
 *
 * @remarks
 * A valid IERC1155 are those contracts that support and have integrate the ERC1155 interface in their code.
 *
 * @param address - Address to check if is the IERC1155
 * @param signer - Signer necessary to valid the IERC1155
 * @returns True if the address is a valid IERC1155
 */
;

ERC1155.isERC1155 = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(address, signer) {
    var erc1155;
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            erc1155 = new ERC1155(address, signer);
            _context.prev = 1;
            _context.next = 4;
            return erc1155.supportsInterface('0xd9b67a26');

          case 4:
            return _context.abrupt("return", _context.sent);

          case 7:
            _context.prev = 7;
            _context.t0 = _context["catch"](1);
            return _context.abrupt("return", Promise.resolve(false));

          case 10:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 7]]);
  }));

  return function (_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @public
 *
 * A generic ERC721 interface to get connected to any ERC721 address and make transactions.
 *
 * @remarks
 * The interface only have and provide generic and common methods calls. Remember that any specific
 * method implemented in the contract will NOT be available in this interface.
 */

var ERC721 =
/**
 * Constructs a new ERC721 from a known address.
 *
 * @param address - The address of the ERC721 contract
 * @param signer - An ethers.js Signer
 * @returns A new ERC721 instance
 *
 */
function ERC721(_address, _signer) {
  var _this = this;

  this.signer = void 0;
  this.address = void 0;

  this.connect = function (signer) {
    return new ERC721(_this.address, signer);
  };

  this.attach = function (address) {
    return new ERC721(address, _this.signer);
  };

  this.approve = void 0;
  this.balanceOf = void 0;
  this.burn = void 0;
  this.getApproved = void 0;
  this.isApprovedForAll = void 0;
  this.name = void 0;
  this.ownerOf = void 0;
  this.safeTransferFrom = void 0;
  this.safeTransferFromWithData = void 0;
  this.setApprovalForAll = void 0;
  this.supportsInterface = void 0;
  this.symbol = void 0;
  this.tokenURI = void 0;
  this.transferFrom = void 0;
  this.address = _address;
  this.signer = _signer;

  var _erc721 = ERC721Burnable__factory.connect(_address, _signer); //


  this.approve = _erc721.approve;
  this.balanceOf = _erc721.balanceOf;
  this.burn = _erc721.burn;
  this.getApproved = _erc721.getApproved;
  this.isApprovedForAll = _erc721.isApprovedForAll;
  this.name = _erc721.name;
  this.ownerOf = _erc721.ownerOf;
  this.safeTransferFrom = _erc721['safeTransferFrom(address,address,uint256)'];
  this.safeTransferFromWithData = _erc721['safeTransferFrom(address,address,uint256,bytes)'];
  this.setApprovalForAll = _erc721.setApprovalForAll;
  this.supportsInterface = _erc721.supportsInterface;
  this.symbol = _erc721.symbol;
  this.tokenURI = _erc721.tokenURI;
  this.transferFrom = _erc721.transferFrom;
}
/**
 * Check if the address is an IERC721.
 *
 * @remarks
 * A valid IERC721 are those contracts that support and have integrate the ERC721 interface in their code
 *
 * @param address - Address to check if is the IERC721
 * @param signer - Signer necessary to valid the IERC721
 * @returns True if the address is a valid IERC721
 */
;

ERC721.isERC721 = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(address, signer) {
    var erc721;
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            erc721 = new ERC721(address, signer);
            _context.prev = 1;
            _context.next = 4;
            return erc721.supportsInterface('0x80ac58cd');

          case 4:
            return _context.abrupt("return", _context.sent);

          case 7:
            _context.prev = 7;
            _context.t0 = _context["catch"](1);
            return _context.abrupt("return", Promise.resolve(false));

          case 10:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 7]]);
  }));

  return function (_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @public
 * Class to interact with ITier contracts
 *
 * @remarks
 * Generic class to interact with any ITier contract in chain with the basic methods and functions.
 *
 * `ITier` is a simple interface that contracts can implement to provide membership lists for other contracts.
 *
 * This class can be used to interact with any contract that implement the ITier interface in their code, but
 * does not know if the contract has implemented the code. E.g: if a contract does not implementent `setTier` as
 * a BalanceTier Contract, the call will be reverted.
 */

var ITier = /*#__PURE__*/function (_TierContract) {
  _inheritsLoose(ITier, _TierContract);

  function ITier(_address, _signer) {
    var _this;

    _this = _TierContract.call(this, _address, _signer) || this;

    _this.connect = function (signer) {
      return new ITier(_this.address, signer);
    };

    return _this;
  }

  return ITier;
}(TierContract);

/**
 * @public - The javascript version of the RainVM, basically does the same job RainVM does
 * but off-chain.
 * @see RainVM in RainVM.sol
 *
 */

var RainJS = /*#__PURE__*/function () {
  /**
   * All of RainVM standard opcodes, i.e. AllStandardOps
   */

  /**
   * The result state of the executed Rainjs.
   */

  /**
   * The property of type StateJS which that RainJS will run based on.
   */

  /**
   * It is a property for overriding the opcodes. Need to ba passed at the time of construction
   * because the RainJS opcode functions should not change after an instance has be created.
   */

  /**
   * An ethers Signer.
   */

  /**
   * An ethers provider.
   */

  /**
   * An ethers Contract address
   */

  /**
   * The constructor of RainJS which initiates the RainJS and also a StateJS for a RainVM script.
   *
   * @param state - A regular StateConfig used to new a StateJS object to be used in RainJS.
   * @param options - (optional) used for initiating the optional properties (signer, provider, contract and applyOpFn)
   *
   */
  function RainJS(_state, options) {
    var _this = this,
        _this$_OPCODE_,
        _options$provider,
        _options$signer;

    this.lastState = [];
    this.state = void 0;
    this.applyOpFn = void 0;
    this.signer = void 0;
    this.provider = void 0;
    this.contract = void 0;
    this._OPCODE_ = (_this$_OPCODE_ = {}, _this$_OPCODE_[RainJS.Opcodes.SKIP] = function (state, operand, data) {
      throw new Error("SKIP is no longer available");
    }, _this$_OPCODE_[RainJS.Opcodes.VAL] = function (state, operand, data) {
      if (!(operand >> 7)) {
        if (state.constants[operand] != undefined) {
          state.stack.push(state.constants[operand]);
        } else throw new Error("out-of-bound constants");
      } else {
        if (state.argumentsStack[operand & 127] != undefined) {
          state.stack.push(state.argumentsStack[operand & 127]);
        } else throw new Error("out-of-bound arguments");
      }
    }, _this$_OPCODE_[RainJS.Opcodes.DUP] = function (state, operand, data) {
      state.stack.push(state.stack[operand]);
    }, _this$_OPCODE_[RainJS.Opcodes.ZIPMAP] = function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(state, operand, data) {
        var sourceIndex_, numberOfVals_, a_, valSize_, loopSize_, items_, _startIndex, _endIndex, i, j, _i;

        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                sourceIndex_ = operand & 7;
                numberOfVals_ = (operand & 224) >> 5;
                a_ = (operand & 24) >> 3;
                valSize_ = a_ == 3 ? 8 : a_ == 2 ? 16 : a_ == 1 ? 32 : 64;
                loopSize_ = 64 / valSize_;
                items_ = state.stack.splice(-(numberOfVals_ + 1));
                _startIndex = 64 - valSize_;
                _endIndex = 64;

                if (!(items_.length == numberOfVals_ + 1)) {
                  _context.next = 37;
                  break;
                }

                i = 0;

              case 10:
                if (!(i < loopSize_)) {
                  _context.next = 35;
                  break;
                }

                state.argumentsStack.splice(-(numberOfVals_ + 1));

                for (j = 0; j < items_.length; j++) {
                  state.argumentsStack.push(BigNumber.from("0x" + items_[j].toHexString().substring(2).padStart(64, "0").slice(_startIndex, _endIndex)));
                }

                _i = 0;

              case 14:
                if (!(_i < _this.state.sources[sourceIndex_].length)) {
                  _context.next = 30;
                  break;
                }

                if (!(_this.applyOpFn != undefined)) {
                  _context.next = 24;
                  break;
                }

                if (!Object.keys(_this.applyOpFn).includes(_this.state.sources[sourceIndex_][_i].toString())) {
                  _context.next = 20;
                  break;
                }

                _this.applyOpFn[_this.state.sources[sourceIndex_][_i]](_this.state, _this.state.sources[sourceIndex_][_i + 1], data);

                _context.next = 22;
                break;

              case 20:
                _context.next = 22;
                return _this.dispatch(_this.state, _this.state.sources[sourceIndex_][_i], _this.state.sources[sourceIndex_][_i + 1], data);

              case 22:
                _context.next = 26;
                break;

              case 24:
                _context.next = 26;
                return _this.dispatch(_this.state, _this.state.sources[sourceIndex_][_i], _this.state.sources[sourceIndex_][_i + 1], data);

              case 26:
                _i++;

              case 27:
                _i++;
                _context.next = 14;
                break;

              case 30:
                _startIndex -= valSize_;
                _endIndex -= valSize_;

              case 32:
                i++;
                _context.next = 10;
                break;

              case 35:
                _context.next = 38;
                break;

              case 37:
                throw new Error("Undefined stack variables");

              case 38:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3) {
        return _ref.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.DEBUG] = function (state, operand, data) {
      console.log(state.stack);
    }, _this$_OPCODE_[RainJS.Opcodes.BLOCK_NUMBER] = function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(state, operand, data) {
        return runtime_1.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (!(_this.provider != undefined)) {
                  _context2.next = 10;
                  break;
                }

                _context2.t0 = state.stack;
                _context2.t1 = BigNumber;
                _context2.next = 5;
                return _this.provider.getBlockNumber();

              case 5:
                _context2.t2 = _context2.sent;
                _context2.t3 = _context2.t1.from.call(_context2.t1, _context2.t2);

                _context2.t0.push.call(_context2.t0, _context2.t3);

                _context2.next = 11;
                break;

              case 10:
                throw new Error("Undefined Provider");

              case 11:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      return function (_x4, _x5, _x6) {
        return _ref2.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.BLOCK_TIMESTAMP] = function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(state, operand, data) {
        return runtime_1.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                if (!(_this.provider != undefined)) {
                  _context3.next = 14;
                  break;
                }

                _context3.t0 = state.stack;
                _context3.t1 = BigNumber;
                _context3.t2 = _this.provider;
                _context3.next = 6;
                return _this.provider.getBlockNumber();

              case 6:
                _context3.t3 = _context3.sent;
                _context3.next = 9;
                return _context3.t2.getBlock.call(_context3.t2, _context3.t3);

              case 9:
                _context3.t4 = _context3.sent.timestamp;
                _context3.t5 = _context3.t1.from.call(_context3.t1, _context3.t4);

                _context3.t0.push.call(_context3.t0, _context3.t5);

                _context3.next = 15;
                break;

              case 14:
                throw new Error("Undefined Provider");

              case 15:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }));

      return function (_x7, _x8, _x9) {
        return _ref3.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.SENDER] = function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(state, operand, data) {
        return runtime_1.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                if (!(_this.signer != undefined)) {
                  _context4.next = 10;
                  break;
                }

                _context4.t0 = state.stack;
                _context4.t1 = BigNumber;
                _context4.next = 5;
                return _this.signer.getAddress();

              case 5:
                _context4.t2 = _context4.sent;
                _context4.t3 = _context4.t1.from.call(_context4.t1, _context4.t2);

                _context4.t0.push.call(_context4.t0, _context4.t3);

                _context4.next = 11;
                break;

              case 10:
                throw new Error("undefined signer");

              case 11:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }));

      return function (_x10, _x11, _x12) {
        return _ref4.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.THIS_ADDRESS] = function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee5(state, operand, data) {
        return runtime_1.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!(_this.contract != undefined)) {
                  _context5.next = 4;
                  break;
                }

                state.stack.push(BigNumber.from(_this.contract));
                _context5.next = 5;
                break;

              case 4:
                throw new Error("Undefined contract");

              case 5:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }));

      return function (_x13, _x14, _x15) {
        return _ref5.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.SCALE18_MUL] = function (state, operand, data) {
      var item2_ = state.stack.pop();
      var item1_ = state.stack.pop();

      if (item1_ && item2_ != undefined) {
        state.stack.push(item2_.mul(operand <= 18 ? item1_.mul(Math.pow(10, 18 - operand).toString()) : item1_.div(Math.pow(10, operand - 18).toString())));
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.SCALE18_DIV] = function (state, operand, data) {
      var item2_ = state.stack.pop();
      var item1_ = state.stack.pop();

      if (item1_ && item2_ != undefined) {
        state.stack.push((operand <= 18 ? item1_.mul(Math.pow(10, 18 - operand).toString()) : item1_.div(Math.pow(10, operand - 18).toString())).div(item2_));
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.SCALE18] = function (state, operand, data) {
      var item_ = state.stack.pop();

      if (item_ != undefined) {
        state.stack.push(operand <= 18 ? item_.mul(Math.pow(10, 18 - operand).toString()) : item_.div(Math.pow(10, operand - 18).toString()));
      } else throw new Error("Undefined stack variable");
    }, _this$_OPCODE_[RainJS.Opcodes.SCALEN] = function (state, operand, data) {
      var item_ = state.stack.pop();

      if (item_ != undefined) {
        state.stack.push(operand <= 18 ? item_.div(Math.pow(10, 18 - operand).toString()) : item_.mul(Math.pow(10, operand - 18).toString()));
      } else throw new Error("Undefined stack variable");
    }, _this$_OPCODE_[RainJS.Opcodes.SCALE_BY] = function (state, operand, data) {
      var item_ = state.stack.pop();

      if (item_ != undefined) {
        if (operand > 127) {
          operand = 256 - operand;
          state.stack.push(item_.div(Math.pow(10, operand).toString()));
        } else {
          state.stack.push(item_.mul(Math.pow(10, operand).toString()));
        }
      } else throw new Error("Undefined stack variable");
    }, _this$_OPCODE_[RainJS.Opcodes.SCALE18_ONE] = function (state, operand, data) {
      state.stack.push(BigNumber.from("1000000000000000000"));
    }, _this$_OPCODE_[RainJS.Opcodes.SCALE18_DECIMALS] = function (state, operand, data) {
      state.stack.push(BigNumber.from(18));
    }, _this$_OPCODE_[RainJS.Opcodes.ADD] = function (state, operand, data) {
      var _item;

      var _accumulator = ethers.constants.Zero;

      for (var i = 0; i < operand; i++) {
        _item = state.stack.pop();

        if (_item != undefined) {
          _accumulator = _accumulator.add(_item);

          if (_accumulator.gt(ethers.constants.MaxUint256)) {
            throw new Error("max numeric range overflow");
          }
        } else throw new Error("Undefined stack variables");
      }

      state.stack.push(_accumulator);
    }, _this$_OPCODE_[RainJS.Opcodes.SATURATING_ADD] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);
      var _accumulator = ethers.constants.Zero;

      var _item;

      for (var i = 0; i < operand; i++) {
        _item = items_.shift();

        if (_item != undefined) {
          _accumulator = _accumulator.add(_item);
          _accumulator = _accumulator.gt(ethers.constants.MaxUint256) ? ethers.constants.MaxUint256 : _accumulator;
        } else throw new Error("Undefined stack variables");
      }

      state.stack.push(_accumulator);
    }, _this$_OPCODE_[RainJS.Opcodes.SUB] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);

      var _accumulator = items_.shift();

      var _item;

      if (_accumulator != undefined) {
        for (var i = 1; i < operand; i++) {
          _item = items_.shift();

          if (_item != undefined) {
            _accumulator = _accumulator.sub(_item);
          } else throw new Error("Undefined stack variables");
        }

        if (_accumulator.isNegative()) {
          throw new Error("Invalid value (negative value not allowed)");
        } else state.stack.push(_accumulator);
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.SATURATING_SUB] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);

      var _accumulator = items_.shift();

      var _item;

      if (_accumulator != undefined) {
        for (var i = 1; i < operand; i++) {
          _item = items_.shift();

          if (_item != undefined) {
            var _accumulator2;

            _accumulator = (_accumulator2 = _accumulator) == null ? void 0 : _accumulator2.sub(_item);
            _accumulator = _accumulator.gt(0) ? _accumulator : ethers.constants.Zero;
          } else throw new Error("Undefined stack variabble");
        }

        state.stack.push(_accumulator);
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.MUL] = function (state, operand, data) {
      var _accumulator = ethers.constants.One;

      var _item;

      for (var i = 0; i < operand; i++) {
        _item = state.stack.pop();

        if (_item != undefined) {
          _accumulator = _accumulator.mul(_item);

          if (_accumulator.gt(ethers.constants.MaxUint256)) {
            throw new Error("max numeric range overflow");
          }
        } else throw new Error("Undefined stack variables");
      }

      state.stack.push(_accumulator);
    }, _this$_OPCODE_[RainJS.Opcodes.SATURATING_MUL] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);
      var _accumulator = ethers.constants.One;

      var _item;

      for (var i = 0; i < operand; i++) {
        _item = items_.shift();

        if (_item != undefined) {
          _accumulator = _accumulator.mul(_item);
          _accumulator = _accumulator.gt(ethers.constants.MaxUint256) ? ethers.constants.MaxUint256 : _accumulator;
        } else throw new Error("Undefined stack variables");
      }

      state.stack.push(_accumulator);
    }, _this$_OPCODE_[RainJS.Opcodes.DIV] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);

      var _accumulator = items_.shift();

      var _item;

      if (_accumulator != undefined) {
        for (var i = 1; i < operand; i++) {
          _item = items_.shift();

          if (_item != undefined) {
            _accumulator = _accumulator.div(_item);
          } else throw new Error("Undefined stack variables");
        }

        state.stack.push(_accumulator);
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.MOD] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);

      var _accumulator = items_.shift();

      var _item;

      if (_accumulator != undefined) {
        for (var i = 1; i < operand; i++) {
          _item = items_.shift();

          if (_item != undefined) {
            _accumulator = _accumulator.mod(_item);
          } else throw new Error("Undefined stack variables");
        }

        state.stack.push(_accumulator);
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.EXP] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);

      var _accumulator = items_.shift();

      var _item;

      if (_accumulator != undefined) {
        for (var i = 1; i < operand; i++) {
          _item = items_.shift();

          if (_item != undefined) {
            _accumulator = _accumulator.pow(_item);

            if (_accumulator.gt(ethers.constants.MaxUint256)) {
              throw new Error("max numeric range overflow");
            }
          } else throw new Error("Undefined stack variables");
        }

        state.stack.push(_accumulator);
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.MIN] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);

      if (items_.length == operand) {
        state.stack.push(items_.reduce(function (e, m) {
          return e.lt(m) ? e : m;
        }));
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.MAX] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);

      if (items_.length = operand) {
        state.stack.push(items_.reduce(function (e, m) {
          return e.gt(m) ? e : m;
        }));
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.ISZERO] = function (state, operand, data) {
      var item_ = state.stack.pop();

      if (item_ != undefined) {
        state.stack.push(item_.isZero() ? ethers.constants.One : ethers.constants.Zero);
      } else throw new Error("Undefined stack variable");
    }, _this$_OPCODE_[RainJS.Opcodes.EAGER_IF] = function (state, operand, data) {
      var false_ = state.stack.pop();
      var true_ = state.stack.pop();
      var condition_ = state.stack.pop();

      if (false_ && true_ && condition_ != undefined) {
        state.stack.push(condition_.gt(0) ? true_ : false_);
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.EQUAL_TO] = function (state, operand, data) {
      var item2_ = state.stack.pop();
      var item1_ = state.stack.pop();

      if (item1_ && item2_ != undefined) {
        state.stack.push(item2_.eq(item1_) ? ethers.constants.One : ethers.constants.Zero);
      } else throw new Error("Underfined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.LESS_THAN] = function (state, operand, data) {
      var item2_ = state.stack.pop();
      var item1_ = state.stack.pop();

      if (item1_ && item2_ != undefined) {
        state.stack.push(item2_.gt(item1_) ? ethers.constants.One : ethers.constants.Zero);
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.GREATER_THAN] = function (state, operand, data) {
      var item2_ = state.stack.pop();
      var item1_ = state.stack.pop();

      if (item1_ && item2_ != undefined) {
        state.stack.push(item2_.lt(item1_) ? ethers.constants.One : ethers.constants.Zero);
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.EVERY] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);

      var _check;

      var _item;

      for (var i = 0; i < operand; i++) {
        _item = items_.shift();

        if (_item != undefined) {
          if (_item.isZero()) {
            _check = ethers.constants.Zero;
            break;
          } else _check = ethers.constants.One;
        } else throw new Error("Undefined stack variables");
      }

      if (_check != undefined) {
        state.stack.push(_check);
      } else throw new Error("Undefined stack variable");
    }, _this$_OPCODE_[RainJS.Opcodes.ANY] = function (state, operand, data) {
      var items_ = state.stack.splice(-operand);

      var _check;

      var _item;

      for (var i = 0; i < operand; i++) {
        _item = items_.shift();

        if (_item != undefined) {
          if (_item.gt(0)) {
            _check = ethers.constants.One;
            break;
          } else _check = ethers.constants.Zero;
        } else throw new Error("Undefined stack variables");
      }

      if (_check != undefined) {
        state.stack.push(_check);
      } else throw new Error("Undefined stack variable");
    }, _this$_OPCODE_[RainJS.Opcodes.REPORT] = function () {
      var _ref6 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee6(state, operand, data) {
        var item2_, item1_, account_, iTierContract;
        return runtime_1.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                item2_ = state.stack.pop();
                item1_ = state.stack.pop();

                if (!(item1_ && item2_ && _this.signer != undefined)) {
                  _context6.next = 12;
                  break;
                }

                account_ = "0x" + item2_.toHexString().substring(2).padStart(40, "0");
                iTierContract = new ITier("0x" + item1_.toHexString().substring(2).padStart(40, "0"), _this.signer);
                _context6.t0 = state.stack;
                _context6.next = 8;
                return iTierContract.report(account_);

              case 8:
                _context6.t1 = _context6.sent;

                _context6.t0.push.call(_context6.t0, _context6.t1);

                _context6.next = 13;
                break;

              case 12:
                throw new Error("Undefined stack variables");

              case 13:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }));

      return function (_x16, _x17, _x18) {
        return _ref6.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.NEVER] = function (state, operand, data) {
      state.stack.push(ethers.constants.MaxUint256);
    }, _this$_OPCODE_[RainJS.Opcodes.ALWAYS] = function (state, operand, data) {
      state.stack.push(ethers.constants.Zero);
    }, _this$_OPCODE_[RainJS.Opcodes.SATURATING_DIFF] = function (state, operand, data) {
      var _state$stack$pop, _state$stack$pop2;

      var report2_ = (_state$stack$pop = state.stack.pop()) == null ? void 0 : _state$stack$pop.toHexString().substring(2).padStart(64, "0");
      var report1_ = (_state$stack$pop2 = state.stack.pop()) == null ? void 0 : _state$stack$pop2.toHexString().substring(2).padStart(64, "0");

      if (report1_ && report2_ != undefined) {
        var _startIndex = 0;
        var _endIndex = 8;
        var _result = "";

        var _tierRep1;

        var _tierRep2;

        for (var i = 0; i < 8; i++) {
          _tierRep1 = BigNumber.from("0x" + report1_.slice(_startIndex, _endIndex));
          _tierRep2 = BigNumber.from("0x" + report2_.slice(_startIndex, _endIndex));
          _result += _tierRep1.gt(_tierRep2) ? _tierRep1.sub(_tierRep2).toHexString().substring(2).padStart(8, "0") : "00000000";
          _startIndex += 8;
          _endIndex += 8;
        }

        state.stack.push(BigNumber.from("0x" + _result));
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.UPDATE_BLOCKS_FOR_TIER_RANGE] = function (state, operand, data) {
      var _state$stack$pop3, _state$stack$pop4;

      var endTier_ = operand >> 4;
      var startTier_ = operand & 15;
      var blockNumber_ = (_state$stack$pop3 = state.stack.pop()) == null ? void 0 : _state$stack$pop3.toHexString().substring(2).padStart(8, "0");
      var report_ = (_state$stack$pop4 = state.stack.pop()) == null ? void 0 : _state$stack$pop4.toHexString().substring(2).padStart(64, "0");

      if (report_ && blockNumber_ != undefined) {
        var _startIndex = (8 - endTier_) * 8;

        var _endIndex = _startIndex + 8;

        var _result = report_.slice(0, _startIndex);

        var resultLow_ = report_.slice(_startIndex + (endTier_ - startTier_) * 8);

        for (var i = 0; i < endTier_ - startTier_; i++) {
          _result += report_.slice(_startIndex, _endIndex) > blockNumber_ ? blockNumber_ : report_.slice(_startIndex, _endIndex);
          _startIndex += 8;
          _endIndex += 8;
        }

        state.stack.push(BigNumber.from("0x" + _result + resultLow_));
      } else throw new Error("Undefined stack variables");
    }, _this$_OPCODE_[RainJS.Opcodes.SELECT_LTE] = function (state, operand, data) {
      var _state$stack$pop5;

      var length_ = operand & 31;
      var mode_ = (operand & 96) >> 5;
      var logic_ = (operand & 128) >> 7;
      var blockNumber_ = (_state$stack$pop5 = state.stack.pop()) == null ? void 0 : _state$stack$pop5.toHexString().substring(2).padStart(8, "0");

      var _item; // array of raw reports


      var _reports = []; // array of array of each tier's lte report

      var _reportsAtTier = [[], [], [], [], [], [], [], []];
      var _result = ""; //building an array of each tier's report against blockNumber_
      //tiers greater than blockNumber_ will get "ffffffff"

      if (blockNumber_ != undefined) {
        for (var i = 0; i < length_; i++) {
          var _startIndex = 0;
          var _endIndex = 8;
          _item = state.stack.pop();

          if (_item != undefined) {
            _reports[i] = _item.toHexString().substring(2).padStart(64, "0");

            for (var j = 0; j < 8; j++) {
              _reportsAtTier[j].push(blockNumber_ < _reports[i].slice(_startIndex, _endIndex) ? "ffffffff" : _reports[i].slice(_startIndex, _endIndex));

              _startIndex += 8;
              _endIndex += 8;
            }
          } else throw new Error("Undefined stack variables");
        }
      } else throw new Error("Undefined stack variable"); // logic_ and mode_ selections


      if (logic_) {
        for (var _i2 = 0; _i2 < 8; _i2++) {
          if (mode_ == 0) {
            _reportsAtTier[_i2] = [_reportsAtTier[_i2].reduce(function (e, m) {
              return e < m ? e : m;
            })];
          } else if (mode_ == 1) {
            //filter out "ffffffff"
            _reportsAtTier[_i2] = _reportsAtTier[_i2].filter(function (e) {
              return e != "ffffffff";
            });
            _reportsAtTier[_i2] = _reportsAtTier[_i2].length > 0 ? [_reportsAtTier[_i2].reduce(function (e, m) {
              return e > m ? e : m;
            })] : ["ffffffff"];
          } else if (mode_ == 2) {
            //filter out "ffffffff"
            _reportsAtTier[_i2] = _reportsAtTier[_i2].filter(function (e) {
              return e != "ffffffff";
            });
            _reportsAtTier[_i2] = _reportsAtTier[_i2].length > 0 ? [_reportsAtTier[_i2][_reportsAtTier[_i2].length - 1]] : ["ffffffff"];
          }
        }
      } else {
        for (var _i3 = 0; _i3 < 8; _i3++) {
          if (mode_ == 0) {
            //check if "ffffffff" exists within the tier's array
            _reportsAtTier[_i3] = _reportsAtTier[_i3].includes("ffffffff") ? ["ffffffff"] : [_reportsAtTier[_i3].reduce(function (e, m) {
              return e < m ? e : m;
            })];
          } else if (mode_ == 1) {
            //check if "ffffffff" exists within the tier's array
            _reportsAtTier[_i3] = _reportsAtTier[_i3].includes("ffffffff") ? ["ffffffff"] : [_reportsAtTier[_i3].reduce(function (e, m) {
              return e > m ? e : m;
            })];
          } else if (mode_ == 2) {
            //check if "ffffffff" exists within the tier's array
            _reportsAtTier[_i3] = _reportsAtTier[_i3].includes("ffffffff") ? ["ffffffff"] : [_reportsAtTier[_i3][_reportsAtTier[_i3].length - 1]];
          }
        }
      } //building the final report


      for (var _i4 = 0; _i4 < 8; _i4++) {
        _result += _reportsAtTier[_i4][0];
      }

      state.stack.push(BigNumber.from("0x" + _result));
    }, _this$_OPCODE_[RainJS.Opcodes.IERC20_BALANCE_OF] = function () {
      var _ref7 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee7(state, operand, data) {
        var item2_, item1_, account_, erc20Address_, erc20Contract_;
        return runtime_1.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                item2_ = state.stack.pop();
                item1_ = state.stack.pop();

                if (!(item1_ && item2_ && _this.signer != undefined)) {
                  _context7.next = 13;
                  break;
                }

                account_ = "0x" + item2_.toHexString().substring(2).padStart(40, "0");
                erc20Address_ = "0x" + item1_.toHexString().substring(2).padStart(40, "0");
                erc20Contract_ = new ERC20(erc20Address_, _this.signer);
                _context7.t0 = state.stack;
                _context7.next = 9;
                return erc20Contract_.balanceOf(account_);

              case 9:
                _context7.t1 = _context7.sent;

                _context7.t0.push.call(_context7.t0, _context7.t1);

                _context7.next = 14;
                break;

              case 13:
                throw new Error("Undefined stack variables");

              case 14:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }));

      return function (_x19, _x20, _x21) {
        return _ref7.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.IERC20_TOTAL_SUPPLY] = function () {
      var _ref8 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee8(state, operand, data) {
        var item_, erc20Address_, erc20Contract_;
        return runtime_1.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                item_ = state.stack.pop();

                if (!(item_ && _this.signer != undefined)) {
                  _context8.next = 11;
                  break;
                }

                erc20Address_ = "0x" + item_.toHexString().substring(2).padStart(40, "0");
                erc20Contract_ = new ERC20(erc20Address_, _this.signer);
                _context8.t0 = state.stack;
                _context8.next = 7;
                return erc20Contract_.totalSupply();

              case 7:
                _context8.t1 = _context8.sent;

                _context8.t0.push.call(_context8.t0, _context8.t1);

                _context8.next = 12;
                break;

              case 11:
                throw new Error("Undefined stack variable");

              case 12:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }));

      return function (_x22, _x23, _x24) {
        return _ref8.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.IERC721_BALANCE_OF] = function () {
      var _ref9 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee9(state, operand, data) {
        var item2_, item1_, account_, erc721Address_, erc721Contract_;
        return runtime_1.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                item2_ = state.stack.pop();
                item1_ = state.stack.pop();

                if (!(item1_ && item2_ && _this.signer != undefined)) {
                  _context9.next = 13;
                  break;
                }

                account_ = "0x" + item2_.toHexString().substring(2).padStart(40, "0");
                erc721Address_ = "0x" + item1_.toHexString().substring(2).padStart(40, "0");
                erc721Contract_ = new ERC721(erc721Address_, _this.signer);
                _context9.t0 = state.stack;
                _context9.next = 9;
                return erc721Contract_.balanceOf(account_);

              case 9:
                _context9.t1 = _context9.sent;

                _context9.t0.push.call(_context9.t0, _context9.t1);

                _context9.next = 14;
                break;

              case 13:
                throw new Error("Undefined stack variables");

              case 14:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }));

      return function (_x25, _x26, _x27) {
        return _ref9.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.IERC721_OWNER_OF] = function () {
      var _ref10 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee10(state, operand, data) {
        var item2_, item1_, tokenId_, erc721Address_, erc721Contract_;
        return runtime_1.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                item2_ = state.stack.pop();
                item1_ = state.stack.pop();

                if (!(item1_ && item2_ && _this.signer != undefined)) {
                  _context10.next = 15;
                  break;
                }

                tokenId_ = BigNumber.from(item2_);
                erc721Address_ = "0x" + item1_.toHexString().substring(2).padStart(40, "0");
                erc721Contract_ = new ERC721(erc721Address_, _this.signer);
                _context10.t0 = state.stack;
                _context10.t1 = BigNumber;
                _context10.next = 10;
                return erc721Contract_.ownerOf(tokenId_);

              case 10:
                _context10.t2 = _context10.sent;
                _context10.t3 = _context10.t1.from.call(_context10.t1, _context10.t2);

                _context10.t0.push.call(_context10.t0, _context10.t3);

                _context10.next = 16;
                break;

              case 15:
                throw new Error("Undefined stack variable");

              case 16:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }));

      return function (_x28, _x29, _x30) {
        return _ref10.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.IERC1155_BALANCE_OF] = function () {
      var _ref11 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee11(state, operand, data) {
        var item3_, item2_, item1_, id_, account_, erc1155Address_, erc1155Contract_;
        return runtime_1.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                item3_ = state.stack.pop();
                item2_ = state.stack.pop();
                item1_ = state.stack.pop();

                if (!(item1_ && item2_ && item3_ && _this.signer != undefined)) {
                  _context11.next = 15;
                  break;
                }

                id_ = BigNumber.from(item3_);
                account_ = "0x" + item2_.toHexString().substring(2).padStart(40, "0");
                erc1155Address_ = "0x" + item1_.toHexString().substring(2).padStart(40, "0");
                erc1155Contract_ = new ERC1155(erc1155Address_, _this.signer);
                _context11.t0 = state.stack;
                _context11.next = 11;
                return erc1155Contract_.balanceOf(account_, id_);

              case 11:
                _context11.t1 = _context11.sent;

                _context11.t0.push.call(_context11.t0, _context11.t1);

                _context11.next = 16;
                break;

              case 15:
                throw new Error("Undefined stack variables");

              case 16:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }));

      return function (_x31, _x32, _x33) {
        return _ref11.apply(this, arguments);
      };
    }(), _this$_OPCODE_[RainJS.Opcodes.IERC1155_BALANCE_OF_BATCH] = function () {
      var _ref12 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee12(state, operand, data) {
        var item3_, item2_, item1_, _state$stack, tokenIds_, i, accounts_, _i5, erc1155Address_, erc1155Contract_;

        return runtime_1.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
                item3_ = state.stack.splice(-(operand + 1));
                item2_ = state.stack.splice(-(operand + 1));
                item1_ = state.stack.pop();

                if (!(item1_ && item2_ && item3_ && _this.signer != undefined && item2_.length == item3_.length)) {
                  _context12.next = 20;
                  break;
                }

                tokenIds_ = [];

                for (i = 0; i < item3_.length; i++) {
                  tokenIds_.push(BigNumber.from(item3_[i]));
                }
                accounts_ = [];

                for (_i5 = 0; _i5 < item2_.length; _i5++) {
                  accounts_.push("0x" + item2_[_i5].toHexString().substring(2).padStart(40, "0"));
                }
                erc1155Address_ = "0x" + item1_.toHexString().substring(2).padStart(40, "0");
                erc1155Contract_ = new ERC1155(erc1155Address_, _this.signer);
                _context12.t0 = (_state$stack = state.stack).push;
                _context12.t1 = _state$stack;
                _context12.next = 16;
                return erc1155Contract_.balanceOfBatch(accounts_, tokenIds_);

              case 16:
                _context12.t2 = _context12.sent;

                _context12.t0.apply.call(_context12.t0, _context12.t1, _context12.t2);

                _context12.next = 21;
                break;

              case 20:
                throw new Error("Undefined stack variable");

              case 21:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12);
      }));

      return function (_x34, _x35, _x36) {
        return _ref12.apply(this, arguments);
      };
    }(), _this$_OPCODE_);
    var stack = [];
    var argumentsStack = [];
    var constants = [];
    var sources = [];

    for (var i = 0; i < _state.constants.length; i++) {
      constants.push(BigNumber.from(_state.constants[i]));
    }

    for (var _i6 = 0; _i6 < _state.sources.length; _i6++) {
      sources.push(arrayify(_state.sources[_i6], {
        allowMissingPrefix: true
      }));
    }
    this.state = {
      stack: stack,
      argumentsStack: argumentsStack,
      constants: constants,
      sources: sources
    };
    this.applyOpFn = options == null ? void 0 : options.applyOpFn;
    this.signer = options == null ? void 0 : options.signer;
    this.contract = options == null ? void 0 : options.contract;
    this.provider = (_options$provider = options == null ? void 0 : options.provider) != null ? _options$provider : options == null ? void 0 : (_options$signer = options.signer) == null ? void 0 : _options$signer.provider;
  }
  /**
   * The main workhorse of RainJS, basically the javascript version of 'eval' method in RainVM.sol.
   * It executes the RainVM script based on each Opcode or the custom opcodes i.e. applyOpFn that
   * has been passed at the time of cinstruction of a RainJS object.
   * @see eval method in RainVM.sol
   *
   * @param data - (optional) An object which is used to provide additional values for "applyOpFn" if there
   * are custom opcodes passed at the time of construction ot to pass in some user input value to the script.
   * @param index - used internally for indicating which item in state sources array to execute for zipmap function.
   *
   */


  var _proto = RainJS.prototype;

  _proto.eval =
  /*#__PURE__*/
  function () {
    var _eval2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee13(data, index) {
      var sourcesIndex, i;
      return runtime_1.wrap(function _callee13$(_context13) {
        while (1) {
          switch (_context13.prev = _context13.next) {
            case 0:
              sourcesIndex = index ? index : 0;
              i = 0;

            case 2:
              if (!(i < this.state.sources[sourcesIndex].length)) {
                _context13.next = 19;
                break;
              }

              if (!(this.applyOpFn != undefined)) {
                _context13.next = 13;
                break;
              }

              if (!Object.keys(this.applyOpFn).includes(this.state.sources[sourcesIndex][i].toString())) {
                _context13.next = 9;
                break;
              }

              _context13.next = 7;
              return this.applyOpFn[this.state.sources[sourcesIndex][i]](this.state, this.state.sources[sourcesIndex][i + 1], data);

            case 7:
              _context13.next = 11;
              break;

            case 9:
              _context13.next = 11;
              return this.dispatch(this.state, this.state.sources[sourcesIndex][i], this.state.sources[sourcesIndex][i + 1], data);

            case 11:
              _context13.next = 15;
              break;

            case 13:
              _context13.next = 15;
              return this.dispatch(this.state, this.state.sources[sourcesIndex][i], this.state.sources[sourcesIndex][i + 1], data);

            case 15:
              i++;

            case 16:
              i++;
              _context13.next = 2;
              break;

            case 19:
            case "end":
              return _context13.stop();
          }
        }
      }, _callee13, this);
    }));

    function _eval(_x37, _x38) {
      return _eval2.apply(this, arguments);
    }

    return _eval;
  }()
  /**
   * It is a protected method used by eval to run the correct function for each opcode in the script.
   * For each opcode please @see AllStandardOps
   *
   * @param state - StateJS property used in each opcode function to either read or write data into stack.
   * @param opcode - the opcode to dispatch and run the function of that opcode
   * @param operand - the addtional info for each opcode to run based on.
   * @param data - (optional) used only for zipmap opcode in order to be able to run custom function i.e. applyOpFn
   * for zipmap function source.
   *
   */
  ;

  _proto.dispatch =
  /*#__PURE__*/
  function () {
    var _dispatch = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee14(state, opcode, operand, data) {
      return runtime_1.wrap(function _callee14$(_context14) {
        while (1) {
          switch (_context14.prev = _context14.next) {
            case 0:
              _context14.next = 2;
              return this._OPCODE_[opcode](state, operand, data);

            case 2:
            case "end":
              return _context14.stop();
          }
        }
      }, _callee14, this);
    }));

    function dispatch(_x39, _x40, _x41, _x42) {
      return _dispatch.apply(this, arguments);
    }

    return dispatch;
  }()
  /**
   * Method to execute the RainJS.
   *
   * @param data - (optional) Used as additional info for some local opcodes
   * or custom opcode functions i.e. applyOpFn.
   *
   * @returns - An array represting the final state of the RainJS stack.
   */
  ;

  _proto.run =
  /*#__PURE__*/
  function () {
    var _run = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee15(data) {
      var _this$lastState;

      var result_;
      return runtime_1.wrap(function _callee15$(_context15) {
        while (1) {
          switch (_context15.prev = _context15.next) {
            case 0:
              _context15.next = 2;
              return this.eval(data);

            case 2:
              (_this$lastState = this.lastState).push.apply(_this$lastState, this.state.stack.splice(-this.state.stack.length));

              result_ = this.lastState[this.lastState.length - 1];
              return _context15.abrupt("return", result_);

            case 5:
            case "end":
              return _context15.stop();
          }
        }
      }, _callee15, this);
    }));

    function run(_x43) {
      return _run.apply(this, arguments);
    }

    return run;
  }()
  /**
   * key/value pair of opcodes and their functions for all standard opcodes
   */
  ;

  return RainJS;
}();
RainJS.Opcodes = VM.Opcodes;

/**
 * @public - The javascript version of CombineTierVM which inherits RainJS with local CombineTier opcodes.
 * @see CombineTier.sol in contracts
 *
 */

var CombineTierJS = /*#__PURE__*/function (_RainJS) {
  _inheritsLoose(CombineTierJS, _RainJS);

  /**
   * Local CombineTier Opcodes + AllstandardOps
   */

  /**
   * Constructor of CombineTierJS to create a instance of this class with CombineTier's local opcodes.
   * @see RainJS
   *
   * @param state - A regular StateConfig
   * @param options - (optional) additional arguments for instantiating this class
   * (a Signer, a CombineTier Contract and custom opcode functions)
   *
   */
  function CombineTierJS(_state, options) {
    var _extends2;

    var _this;

    _this = _RainJS.call(this, _state, {
      signer: options == null ? void 0 : options.signer,
      contract: options == null ? void 0 : options.contract,
      applyOpFn: options == null ? void 0 : options.applyOpFn
    }) || this;
    _this._OPCODE_ = _extends({}, _this._OPCODE_, (_extends2 = {}, _extends2[CombineTierJS.Opcodes.ACCOUNT] = function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(state, operand, data) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!(data && data.account != undefined)) {
                  _context.next = 4;
                  break;
                }

                state.stack.push(BigNumber.from(data.account));
                _context.next = 5;
                break;

              case 4:
                throw new Error("Undefined account address");

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3) {
        return _ref.apply(this, arguments);
      };
    }(), _extends2));
    return _this;
  }
  /**
   * key/value pair of opcodes and their functions for all standard opcodes + EmissionsERC20 local opcodes
   */


  return CombineTierJS;
}(RainJS);
CombineTierJS.Opcodes = CombineTier.Opcodes;

/**
 * @public - The javascript version of EmissionsERC20JS which inherits RainJS with local EmissionsERC20JS opcodes.
 * @see EmissionsERC20.sol in contracts
 *
 */

var EmissionsERC20JS = /*#__PURE__*/function (_RainJS) {
  _inheritsLoose(EmissionsERC20JS, _RainJS);

  /**
   * Local EmissionsERC20 Opcodes + AllstandardOps
   */

  /**
   * Constructor of SaleJS to create a instance of this class with EmissionsERC20's local opcodes.
   * @see RainJS
   *
   * @param state - A regular StateConfig
   * @param options - (optional) additional arguments for instantiating this class
   * (a Signer, a EmissionsERC20JS Contract and custom opcode functions)
   *
   */
  function EmissionsERC20JS(_state, options) {
    var _extends2;

    var _this;

    _this = _RainJS.call(this, _state, {
      signer: options == null ? void 0 : options.signer,
      contract: options == null ? void 0 : options.contract,
      applyOpFn: options == null ? void 0 : options.applyOpFn
    }) || this;
    _this._OPCODE_ = _extends({}, _this._OPCODE_, (_extends2 = {}, _extends2[EmissionsERC20JS.Opcodes.CLAIMANT_ACCOUNT] = function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(state, operand, data) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!(data && data.claimant_account != undefined)) {
                  _context.next = 4;
                  break;
                }

                state.stack.push(BigNumber.from(data.claimant_account));
                _context.next = 5;
                break;

              case 4:
                throw new Error("Undefined claimant account address");

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3) {
        return _ref.apply(this, arguments);
      };
    }(), _extends2));
    return _this;
  }
  /**
   * key/value pair of opcodes and their functions for all standard opcodes + EmissionsERC20 local opcodes
   */


  return EmissionsERC20JS;
}(RainJS);
EmissionsERC20JS.Opcodes = EmissionsERC20.Opcodes;

/**
 * @public - The javascript version of SaleVM which inherits RainJS with local Sale opcodes.
 * @see Sale.sol in contracts
 *
 */

var SaleJS = /*#__PURE__*/function (_RainJS) {
  _inheritsLoose(SaleJS, _RainJS);

  /**
   * Local Sale's opcodes + AllstandardOps
   */

  /**
   * Constructor of SaleJS to create a instance of this class with Sale's local opcodes.
   * @see RainJS
   *
   * @param state - A regular StateConfig
   * @param options - (optional) additional arguments for instantiating this class
   * (a Signer, a Sale Contract and custom opcode functions)
   *
   */
  function SaleJS(_state, options) {
    var _extends2;

    var _this;

    _this = _RainJS.call(this, _state, {
      signer: options == null ? void 0 : options.signer,
      contract: options == null ? void 0 : options.contract,
      applyOpFn: options == null ? void 0 : options.applyOpFn
    }) || this;
    _this._OPCODE_ = _extends({}, _this._OPCODE_, (_extends2 = {}, _extends2[SaleJS.Opcodes.REMAINING_UNITS] = function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(state, operand, data) {
        var salecontract_, rTKNAddress_, rTKNContract_;
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!(_this.signer && _this.contract != undefined)) {
                  _context.next = 15;
                  break;
                }

                salecontract_ = new Sale(_this.contract, _this.signer);
                _context.next = 4;
                return salecontract_.token();

              case 4:
                rTKNAddress_ = _context.sent;
                rTKNContract_ = new ERC20(rTKNAddress_, _this.signer);
                _context.t0 = state.stack;
                _context.t1 = BigNumber;
                _context.next = 10;
                return rTKNContract_.balanceOf(_this.contract);

              case 10:
                _context.t2 = _context.sent;
                _context.t3 = _context.t1.from.call(_context.t1, _context.t2);

                _context.t0.push.call(_context.t0, _context.t3);

                _context.next = 16;
                break;

              case 15:
                throw new Error("Undefined Signer or Sale Contract");

              case 16:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3) {
        return _ref.apply(this, arguments);
      };
    }(), _extends2[SaleJS.Opcodes.TOTAL_RESERVE_IN] = function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(state, operand, data) {
        var salecontract_, reserveAddress_, reserveContract_;
        return runtime_1.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (!(_this.signer && _this.contract != undefined)) {
                  _context2.next = 15;
                  break;
                }

                salecontract_ = new Sale(_this.contract, _this.signer);
                _context2.next = 4;
                return salecontract_.reserve();

              case 4:
                reserveAddress_ = _context2.sent;
                reserveContract_ = new ERC20(reserveAddress_, _this.signer);
                _context2.t0 = state.stack;
                _context2.t1 = BigNumber;
                _context2.next = 10;
                return reserveContract_.balanceOf(_this.contract);

              case 10:
                _context2.t2 = _context2.sent;
                _context2.t3 = _context2.t1.from.call(_context2.t1, _context2.t2);

                _context2.t0.push.call(_context2.t0, _context2.t3);

                _context2.next = 16;
                break;

              case 15:
                throw new Error("Undefined Signer or Sale Contract");

              case 16:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      return function (_x4, _x5, _x6) {
        return _ref2.apply(this, arguments);
      };
    }(), _extends2[SaleJS.Opcodes.CURRENT_BUY_UNITS] = function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(state, operand, data) {
        return runtime_1.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                if (!(data && data.current_buy_units != undefined)) {
                  _context3.next = 4;
                  break;
                }

                state.stack.push(BigNumber.from(data.current_buy_units));
                _context3.next = 5;
                break;

              case 4:
                throw new Error("Undefined buy units");

              case 5:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }));

      return function (_x7, _x8, _x9) {
        return _ref3.apply(this, arguments);
      };
    }(), _extends2[SaleJS.Opcodes.TOKEN_ADDRESS] = function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(state, operand, data) {
        var salecontract_;
        return runtime_1.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                if (!(_this.signer && _this.contract != undefined)) {
                  _context4.next = 11;
                  break;
                }

                salecontract_ = new Sale(_this.contract, _this.signer);
                _context4.t0 = state.stack;
                _context4.t1 = BigNumber;
                _context4.next = 6;
                return salecontract_.token();

              case 6:
                _context4.t2 = _context4.sent;
                _context4.t3 = _context4.t1.from.call(_context4.t1, _context4.t2);

                _context4.t0.push.call(_context4.t0, _context4.t3);

                _context4.next = 12;
                break;

              case 11:
                throw new Error("Undefined Signer or Sale Contract");

              case 12:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }));

      return function (_x10, _x11, _x12) {
        return _ref4.apply(this, arguments);
      };
    }(), _extends2[SaleJS.Opcodes.RESERVE_ADDRESS] = function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee5(state, operand, data) {
        var salecontract_;
        return runtime_1.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!(_this.signer && _this.contract != undefined)) {
                  _context5.next = 11;
                  break;
                }

                salecontract_ = new Sale(_this.contract, _this.signer);
                _context5.t0 = state.stack;
                _context5.t1 = BigNumber;
                _context5.next = 6;
                return salecontract_.reserve();

              case 6:
                _context5.t2 = _context5.sent;
                _context5.t3 = _context5.t1.from.call(_context5.t1, _context5.t2);

                _context5.t0.push.call(_context5.t0, _context5.t3);

                _context5.next = 12;
                break;

              case 11:
                throw new Error("Undefined Signer or Sale Contract");

              case 12:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }));

      return function (_x13, _x14, _x15) {
        return _ref5.apply(this, arguments);
      };
    }(), _extends2));
    return _this;
  }
  /**
   * key/value pair of opcodes and their functions for all standard opcodes + Sale local opcodes
   */


  return SaleJS;
}(RainJS);
SaleJS.Opcodes = Sale.Opcodes;

/**
 * @public
 * A class for deploying and calling methods on a ERC20BalanceTier.
 *
 * @remarks
 *  This class provides an easy way to deploy ERC20BalanceTiers using Rain's canonical factories,
 * and methods for interacting with an already deployed ERC20BalanceTier.
 *
 * The `ERC20BalanceTier` simply checks the current balance of an erc20 against tier values.
 * As the current balance is always read from the erc20 contract directly there is no historical
 * block data.
 *
 * @example
 * ```typescript
 * import { ERC20BalanceTier } from 'rain-sdk'
 *
 * // To deploy a new ERC20BalanceTier, pass an ethers.js Signer and the config for the ERC20BalanceTier.
 * const newTier = await ERC20BalanceTier.deploy(signer, ERC20BalanceTierArgs);
 *
 * // To connect to an existing ERC20BalanceTier just pass the tier address, token address and an ethers.js Signer.
 * const existingTier = new ERC20BalanceTier(address, signer[, tokenAddress]);
 *
 * // Once you have a ERC20BalanceTier, you can call the smart contract methods:
 * const tierValues = await existingTier.tierValues();
 * ```
 *
 */

var ERC20BalanceTier = /*#__PURE__*/function (_TierContract) {
  _inheritsLoose(ERC20BalanceTier, _TierContract);

  /**
   * Constructs a new ERC20BalanceTier from a known address.
   *
   * @param address - The address of the ERC20BalanceTier contract
   * @param signer - An ethers.js Signer
   * @returns A new ERC20BalanceTier instance
   *
   */
  function ERC20BalanceTier(address, _signer, _tokenAddress) {
    var _this;

    if (_tokenAddress === void 0) {
      _tokenAddress = '';
    }

    ERC20BalanceTier.checkAddress(address);
    _this = _TierContract.call(this, address, _signer) || this;
    _this.token = void 0;

    _this.connect = function (signer) {
      return new ERC20BalanceTier(_this.address, signer, _this.token);
    };

    _this.addTokenAddress = function (tokenAddress) {
      return new ERC20BalanceTier(_this.address, _this.signer, tokenAddress);
    };

    _this.setTier = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(account, endTier, data, overrides) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                throw new Error('SET TIER: NOT IMPLEMENTED');

              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3, _x4) {
        return _ref.apply(this, arguments);
      };
    }();

    _this.tierValues = void 0;

    var _erc20balanceTier = ERC20BalanceTier__factory.connect(address, _signer);

    _this.tierValues = _erc20balanceTier.tierValues;
    _this.token = _tokenAddress;
    return _this;
  }
  /**
   * ERC20 Token address that track the Tier
   */


  var _proto = ERC20BalanceTier.prototype;

  /**
   * Calculate how much amount of the token needed transfer to or transfer out of the account to reach a `desiredLevel`.
   *
   * Take in mind:
   *  - If the `desired level` is higher than the current level, the amount returned will be the amount
   * needed to obtain or transfer to the `account`.
   *  - If the `desired level` is lower than the current level, the amount returned will be the amount
   * needed to remove or transfer out of the `account`.
   * - If already have the `desired` tier, will return 0
   *
   * @param desiredLevel - the tier level desired to get
   * @param account - (optional) the account address to calculate. If not provided will use the signer of
   * the instance
   * @returns The amount t
   */
  _proto.amountToTier =
  /*#__PURE__*/
  function () {
    var _amountToTier = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(account, desiredLevel) {
      var token, values, amountDestiny, amountOrigin;
      return runtime_1.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              if (this.token) {
                _context2.next = 2;
                break;
              }

              throw new Error('Missing ERC20 Token address in the instance. Please, use addTokenAddress');

            case 2:
              token = IERC20__factory.connect(this.token, this.signer);
              _context2.next = 5;
              return this.tierValues();

            case 5:
              values = _context2.sent;
              amountDestiny = desiredLevel ? values[desiredLevel - 1] : BigNumber.from(0);
              _context2.t0 = token;
              _context2.t1 = account;

              if (_context2.t1) {
                _context2.next = 13;
                break;
              }

              _context2.next = 12;
              return this.signer.getAddress();

            case 12:
              _context2.t1 = _context2.sent;

            case 13:
              _context2.t2 = _context2.t1;
              _context2.next = 16;
              return _context2.t0.balanceOf.call(_context2.t0, _context2.t2);

            case 16:
              amountOrigin = _context2.sent;
              return _context2.abrupt("return", amountDestiny.sub(amountOrigin).abs());

            case 18:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, this);
    }));

    function amountToTier(_x5, _x6) {
      return _amountToTier.apply(this, arguments);
    }

    return amountToTier;
  }()
  /**
   * It is NOT implemented in BalanceTiers. Always will throw an error
   */
  ;

  return ERC20BalanceTier;
}(TierContract);
ERC20BalanceTier.nameBookReference = 'erc20BalanceTierFactory';

ERC20BalanceTier.deploy = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(signer, args, overrides) {
    var erc20BalanceTierFactory, tx, receipt, address;
    return runtime_1.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            if (overrides === void 0) {
              overrides = {};
            }

            _context3.t0 = ERC20BalanceTierFactory__factory;
            _context3.t1 = ERC20BalanceTier;
            _context3.next = 5;
            return ERC20BalanceTier.getChainId(signer);

          case 5:
            _context3.t2 = _context3.sent;
            _context3.t3 = _context3.t1.getBookAddress.call(_context3.t1, _context3.t2);
            _context3.t4 = signer;
            erc20BalanceTierFactory = _context3.t0.connect.call(_context3.t0, _context3.t3, _context3.t4);
            _context3.next = 11;
            return erc20BalanceTierFactory.createChildTyped(args, overrides);

          case 11:
            tx = _context3.sent;
            _context3.next = 14;
            return tx.wait();

          case 14:
            receipt = _context3.sent;
            address = ERC20BalanceTier.getNewChildFromReceipt(receipt, erc20BalanceTierFactory);
            return _context3.abrupt("return", new ERC20BalanceTier(address, signer, args.erc20));

          case 17:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function (_x7, _x8, _x9) {
    return _ref2.apply(this, arguments);
  };
}();

ERC20BalanceTier.isChild = /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(signer, maybeChild) {
    return runtime_1.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            _context4.next = 2;
            return ERC20BalanceTier._isChild(signer, maybeChild);

          case 2:
            return _context4.abrupt("return", _context4.sent);

          case 3:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));

  return function (_x10, _x11) {
    return _ref3.apply(this, arguments);
  };
}();

/**
 * @public
 * A class for deploying and calling methods on a ERC20TransferTier.
 *
 * @remarks
 * This class provides an easy way to deploy ERC20TransferTiers using Rain's canonical factories, and methods for
 * interacting with an already deployed ERC20TransferTier.
 *
 * The `ERC20TransferTier` takes ownership of an erc20 balance by transferring erc20 token to itself. The
 * `msg.sender` must pay the difference on upgrade; the tiered address receives refunds on downgrade. This
 * allows users to "gift" tiers to each other. As the transfer is a state changing event we can track
 * historical block times.
 *
 * As the tiered address moves up/down tiers it sends/receives the value difference between its current tier
 * only.The user is required to preapprove enough erc20 to cover the tier change or they will fail and lose gas.
 *
 * In addition to the standard accounting it requires that users transfer erc20 tokens to achieve a tier.
 * Data is ignored, the only requirement is that the user has approved sufficient balance to gain the next tier.
 * The 8 values for gainable tiers and erc20 contract must be set upon construction and are immutable.
 *
 * `⚠️ WARNING:` If a user sends erc20 tokens directly to the contract without calling `setTier` the FUNDS ARE LOST. ⚠️
 *
 * @example
 * ```typescript
 * import { ERC20TransferTier } from 'rain-sdk'
 *
 * // To deploy a new ERC20TransferTier, pass an ethers.js Signer and the config for the ERC20TransferTier.
 * const newTier = await ERC20TransferTier.deploy(signer, ERC20TransferTierConfigArgs);
 *
 * // To connect to an existing ERC20TransferTier just pass the tier address, token address and an ethers.js Signer.
 * const existingTier = new ERC20TransferTier(address, tokenAddress, signer);
 *
 * // Once you have a ERC20TransferTier, you can call the smart contract methods:
 * const tierValues = await existingTier.tierValues();
 * ```
 *
 */

var ERC20TransferTier = /*#__PURE__*/function (_TierContract) {
  _inheritsLoose(ERC20TransferTier, _TierContract);

  /**
   * Constructs a new ERC20TransferTier from a known address.
   *
   * @param address - The address of the ERC20TransferTier contract
   * @param signer - An ethers.js Signer
   * @returns A new ERC20TransferTier instance
   *
   */
  function ERC20TransferTier(address, _signer, _tokenAddress) {
    var _this;

    if (_tokenAddress === void 0) {
      _tokenAddress = '';
    }

    ERC20TransferTier.checkAddress(address);
    _this = _TierContract.call(this, address, _signer) || this;
    _this.token = void 0;

    _this.connect = function (signer) {
      return new ERC20TransferTier(_this.address, signer, _this.token);
    };

    _this.addTokenAddress = function (tokenAddress) {
      return new ERC20TransferTier(_this.address, _this.signer, tokenAddress);
    };

    _this.tierValues = void 0;

    var _erc20TransferTier = ERC20TransferTier__factory.connect(address, _signer);

    _this.tierValues = _erc20TransferTier.tierValues;
    _this.token = _tokenAddress;
    return _this;
  }
  /**
   * The ERC20 Token address that holds the Tier.
   */


  /**
   * Deploys a new ERC20TransferTier.
   *
   * @param signer - An ethers.js Signer
   * @param args - Arguments for deploying a ERC20TransferTier @see ERC20TransferTierDeployArgs
   * @param overrides - @see TxOverrides
   * @returns A new ERC20TransferTier instance
   *
   */
  ERC20TransferTier.deploy =
  /*#__PURE__*/
  function () {
    var _deploy = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(signer, args, overrides) {
      var erc20TransferTierFactory, tx, receipt, address;
      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (overrides === void 0) {
                overrides = {};
              }

              _context.t0 = ERC20TransferTierFactory__factory;
              _context.t1 = this;
              _context.next = 5;
              return this.getChainId(signer);

            case 5:
              _context.t2 = _context.sent;
              _context.t3 = _context.t1.getBookAddress.call(_context.t1, _context.t2);
              _context.t4 = signer;
              erc20TransferTierFactory = _context.t0.connect.call(_context.t0, _context.t3, _context.t4);
              _context.next = 11;
              return erc20TransferTierFactory.createChildTyped(args, overrides);

            case 11:
              tx = _context.sent;
              _context.next = 14;
              return tx.wait();

            case 14:
              receipt = _context.sent;
              address = this.getNewChildFromReceipt(receipt, erc20TransferTierFactory);
              return _context.abrupt("return", new ERC20TransferTier(address, signer, args.erc20));

            case 17:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    }));

    function deploy(_x, _x2, _x3) {
      return _deploy.apply(this, arguments);
    }

    return deploy;
  }();

  var _proto = ERC20TransferTier.prototype;

  /**
   * Calculate how much amount of the token needed transfer to the tier contract or how much tokens the
   * `account` will get back to reach the `desiredLevel`.
   *
   * @remarks
   * Take in mind:
   * - If the `account` directly send tokens to the ERC20TransferTier contract, those tokens are lost. All
   * the calcualtions are make it with the tokens transfered by the account with setTier method.
   * - If the `desired level` is higher than the current level, the amount returned will be the amount
   * needed to obtain tier.
   * - If the `desired level` is lower than the current level, the amount returned will be the amount
   * that will get back.
   * - If already have the `desired` tier, will return 0.
   *
   * @param desiredLevel - the tier level desired to get
   * @param account - (optional) the account address to calculate. If not provided will use the signer of
   * the instance
   * @returns The amount t
   */
  _proto.amountToTier =
  /*#__PURE__*/
  function () {
    var _amountToTier = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(account, desiredLevel) {
      var values, currentTier, amountDestiny, amountOrigin;
      return runtime_1.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              if (this.token) {
                _context2.next = 2;
                break;
              }

              throw new Error('Missing ERC20 Token address in the instance. Please, use addTokenAddress');

            case 2:
              _context2.next = 4;
              return this.tierValues();

            case 4:
              values = _context2.sent;
              _context2.t0 = this;
              _context2.t1 = account;

              if (_context2.t1) {
                _context2.next = 11;
                break;
              }

              _context2.next = 10;
              return this.signer.getAddress();

            case 10:
              _context2.t1 = _context2.sent;

            case 11:
              _context2.t2 = _context2.t1;
              _context2.next = 14;
              return _context2.t0.currentTier.call(_context2.t0, _context2.t2);

            case 14:
              currentTier = _context2.sent;
              amountDestiny = desiredLevel ? values[desiredLevel - 1] : BigNumber.from(0);
              amountOrigin = currentTier ? values[currentTier - 1] : BigNumber.from(0);
              return _context2.abrupt("return", amountDestiny.sub(amountOrigin).abs());

            case 18:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, this);
    }));

    function amountToTier(_x4, _x5) {
      return _amountToTier.apply(this, arguments);
    }

    return amountToTier;
  }();

  _proto.approveTokenForTier = /*#__PURE__*/function () {
    var _approveTokenForTier = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(amount, overrides) {
      var token;
      return runtime_1.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              if (overrides === void 0) {
                overrides = {};
              }

              token = IERC20__factory.connect(this.token, this.signer);
              _context3.next = 4;
              return token.approve(this.address, amount, overrides);

            case 4:
              return _context3.abrupt("return", _context3.sent);

            case 5:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3, this);
    }));

    function approveTokenForTier(_x6, _x7) {
      return _approveTokenForTier.apply(this, arguments);
    }

    return approveTokenForTier;
  }()
  /**
   * Complements the default solidity accessor for `tierValues`. Returns all the values in a
   * listrather than requiring an index be specified.
   *
   * @param overrides - @see ReadTxOverrides
   * @returns The immutable `tierValues[8]`.
   */
  ;

  return ERC20TransferTier;
}(TierContract);
ERC20TransferTier.nameBookReference = 'erc20TransferTierFactory';

ERC20TransferTier.isChild = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(signer, maybeChild) {
    return runtime_1.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            _context4.next = 2;
            return ERC20TransferTier._isChild(signer, maybeChild);

          case 2:
            return _context4.abrupt("return", _context4.sent);

          case 3:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));

  return function (_x8, _x9) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @public
 *   A class for deploying and calling methods on a ERC721BalanceTier.
 * The `ERC721BalanceTier` simply checks the current balance of an erc721 against tier values.
 * As the current balance is always read from the erc721contract directly there is no historical
 * block data.
 *
 * @remarks
 *   This class provides an easy way to deploy ERC721BalanceTiers using Rain's canonical factories,
 * and methods for interacting with an already deployed ERC721BalanceTier.
 *
 * @example
 * ```typescript
 * import { ERC721BalanceTier } from 'rain-sdk'
 *
 * // To deploy a new ERC721BalanceTier, pass an ethers.js Signer and the config for the ERC721BalanceTier.
 * const newTier = await ERC721BalanceTier.deploy(signer, ERC721BalanceTierConfigArgs);
 *
 * // To connect to an existing ERC721BalanceTier just pass the address and an ethers.js Signer.
 * const existingTier = new ERC721BalanceTier(address, signer);
 *
 * // Once you have a ERC721BalanceTier, you can call the smart contract methods:
 * const tierValues = await existingTier.tierValues();
 * ```
 *
 */

var ERC721BalanceTier = /*#__PURE__*/function (_TierContract) {
  _inheritsLoose(ERC721BalanceTier, _TierContract);

  /**
   * Constructs a new ERC721BalanceTier from a known address.
   *
   * @param address - The address of the ERC721BalanceTier contract
   * @param signer - An ethers.js Signer
   * @returns A new ERC721BalanceTier instance
   *
   */
  function ERC721BalanceTier(address, _signer, _tokenAddress) {
    var _this;

    if (_tokenAddress === void 0) {
      _tokenAddress = '';
    }

    ERC721BalanceTier.checkAddress(address);
    _this = _TierContract.call(this, address, _signer) || this;
    _this.token = void 0;

    _this.connect = function (signer) {
      return new ERC721BalanceTier(_this.address, signer, _this.token);
    };

    _this.addTokenAddress = function (tokenAddress) {
      return new ERC721BalanceTier(_this.address, _this.signer, tokenAddress);
    };

    _this.setTier = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(account, endTier, data, overrides) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                throw new Error('SET TIER: NOT IMPLEMENTED');

              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3, _x4) {
        return _ref.apply(this, arguments);
      };
    }();

    _this.tierValues = void 0;

    var _erc721BalanceTier = ERC721BalanceTier__factory.connect(address, _signer);

    _this.tierValues = _erc721BalanceTier.tierValues;
    _this.token = _tokenAddress;
    return _this;
  }
  /**
   * ERC721 Token address that track the Tier
   */


  var _proto = ERC721BalanceTier.prototype;

  /**
   * Calculate how much amount of the token needed transfer to or transfer out of the account to reach a `desiredLevel`.
   *
   * Take in mind:
   *  - If the `desired level` is higher than the current level, the amount returned will be the amount
   * needed to obtain or transfer to the `account`.
   *  - If the `desired level` is lower than the current level, the amount returned will be the amount
   * needed to remove or transfer out of the `account`.
   * - If already have the `desired` tier, will return 0
   *
   * @param desiredLevel - the tier level desired to get
   * @param account - (optional) the account address to calculate. If not provided will use the signer of
   * the instance
   * @returns The amount t
   */
  _proto.amountToTier =
  /*#__PURE__*/
  function () {
    var _amountToTier = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(account, desiredLevel) {
      var token, values, amountDestiny, amountOrigin;
      return runtime_1.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              if (this.token) {
                _context2.next = 2;
                break;
              }

              throw new Error('Missing ER721 Token address in the instance. Please, use addTokenAddress');

            case 2:
              token = IERC721__factory.connect(this.token, this.signer);
              _context2.next = 5;
              return this.tierValues();

            case 5:
              values = _context2.sent;
              amountDestiny = desiredLevel ? values[desiredLevel - 1] : BigNumber.from(0);
              _context2.t0 = token;
              _context2.t1 = account;

              if (_context2.t1) {
                _context2.next = 13;
                break;
              }

              _context2.next = 12;
              return this.signer.getAddress();

            case 12:
              _context2.t1 = _context2.sent;

            case 13:
              _context2.t2 = _context2.t1;
              _context2.next = 16;
              return _context2.t0.balanceOf.call(_context2.t0, _context2.t2);

            case 16:
              amountOrigin = _context2.sent;
              return _context2.abrupt("return", amountDestiny.sub(amountOrigin).abs());

            case 18:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, this);
    }));

    function amountToTier(_x5, _x6) {
      return _amountToTier.apply(this, arguments);
    }

    return amountToTier;
  }()
  /**
   * It is NOT implemented in BalanceTiers. Always will throw an error
   */
  ;

  return ERC721BalanceTier;
}(TierContract);
ERC721BalanceTier.nameBookReference = 'erc721BalanceTierFactory';

ERC721BalanceTier.deploy = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(signer, args, overrides) {
    var erc721BalanceTierFactory, tx, receipt, address;
    return runtime_1.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            if (overrides === void 0) {
              overrides = {};
            }

            _context3.t0 = ERC721BalanceTierFactory__factory;
            _context3.t1 = ERC721BalanceTier;
            _context3.next = 5;
            return ERC721BalanceTier.getChainId(signer);

          case 5:
            _context3.t2 = _context3.sent;
            _context3.t3 = _context3.t1.getBookAddress.call(_context3.t1, _context3.t2);
            _context3.t4 = signer;
            erc721BalanceTierFactory = _context3.t0.connect.call(_context3.t0, _context3.t3, _context3.t4);
            _context3.next = 11;
            return erc721BalanceTierFactory.createChildTyped(args, overrides);

          case 11:
            tx = _context3.sent;
            _context3.next = 14;
            return tx.wait();

          case 14:
            receipt = _context3.sent;
            address = ERC721BalanceTier.getNewChildFromReceipt(receipt, erc721BalanceTierFactory);
            return _context3.abrupt("return", new ERC721BalanceTier(address, signer, args.erc721));

          case 17:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function (_x7, _x8, _x9) {
    return _ref2.apply(this, arguments);
  };
}();

ERC721BalanceTier.isChild = /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(signer, maybeChild) {
    return runtime_1.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            _context4.next = 2;
            return ERC721BalanceTier._isChild(signer, maybeChild);

          case 2:
            return _context4.abrupt("return", _context4.sent);

          case 3:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));

  return function (_x10, _x11) {
    return _ref3.apply(this, arguments);
  };
}();

/**
 * @public
 * A class for deploying and calling methods on a VerifyTier.
 *
 *   A contract that is `VerifyTier` expects to derive tiers from the time the account was
 * approved by the underlying `Verify` contract. The approval block numbers defer to `State.since`
 * returned from `Verify.state`.
 *
 * @remarks
 *   This class provides an easy way to deploy VerifyTiers using Rain's canonical factories,
 * and methods for interacting with an already deployed VerifyTier.
 *
 * @example
 * ```typescript
 * import { VerifyTier } from 'rain-sdk'
 * // To deploy a new VerifyTier, pass an ethers.js Signer and the config for the VerifyTier.
 * const newTier = await VerifyTier.deploy(signer, VerifyTierConfigArgs);
 *
 * // To connect to an existing VerifyTier just pass the address and an ethers.js Signer.
 * const existingTier = new VerifyTier(address, signer);
 *
 * // Once you have a VerifyTier, you can call the smart contract methods:
 * const report = await existingTier.report(address);
 * ```
 *
 */

var VerifyTier = /*#__PURE__*/function (_TierContract) {
  _inheritsLoose(VerifyTier, _TierContract);

  /**
   * Constructs a new VerifyTier from a known address.
   *
   * @param address - The address of the VerifyTier contract
   * @param signer - An ethers.js Signer
   * @returns A new VerifyTier instance
   *
   */
  function VerifyTier(address, _signer) {
    var _this;

    VerifyTier.checkAddress(address);
    _this = _TierContract.call(this, address, _signer) || this;

    _this.connect = function (signer) {
      return new VerifyTier(_this.address, signer);
    };

    _this.setTier = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(account, endTier, data, overrides) {
        return runtime_1.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                throw new Error('SET TIER: NOT IMPLEMENTED');

              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3, _x4) {
        return _ref.apply(this, arguments);
      };
    }();

    return _this;
  }
  /**
   * Deploys a new VerifyTier.
   *
   * @param signer - An ethers.js Signer
   * @param verifyAddress - The contract to check to produce reports.
   * @param overrides - Specific transaction values to send it (e.g gasLimit, nonce or gasPrice)
   * @returns A new VerifyTier instance
   *
   */


  return VerifyTier;
}(TierContract);
VerifyTier.nameBookReference = 'verifyTierFactory';

VerifyTier.deploy = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(signer, verifyAddress, overrides) {
    var verifyTierFactory, tx, receipt, address;
    return runtime_1.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            if (overrides === void 0) {
              overrides = {};
            }

            _context2.t0 = VerifyTierFactory__factory;
            _context2.t1 = VerifyTier;
            _context2.next = 5;
            return VerifyTier.getChainId(signer);

          case 5:
            _context2.t2 = _context2.sent;
            _context2.t3 = _context2.t1.getBookAddress.call(_context2.t1, _context2.t2);
            _context2.t4 = signer;
            verifyTierFactory = _context2.t0.connect.call(_context2.t0, _context2.t3, _context2.t4);
            _context2.next = 11;
            return verifyTierFactory.createChildTyped(verifyAddress, overrides);

          case 11:
            tx = _context2.sent;
            _context2.next = 14;
            return tx.wait();

          case 14:
            receipt = _context2.sent;
            address = VerifyTier.getNewChildFromReceipt(receipt, verifyTierFactory);
            return _context2.abrupt("return", new VerifyTier(address, signer));

          case 17:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function (_x5, _x6, _x7) {
    return _ref2.apply(this, arguments);
  };
}();

VerifyTier.isChild = /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(signer, maybeChild) {
    return runtime_1.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _context3.next = 2;
            return VerifyTier._isChild(signer, maybeChild);

          case 2:
            return _context3.abrupt("return", _context3.sent);

          case 3:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function (_x8, _x9) {
    return _ref3.apply(this, arguments);
  };
}();

/**
 * Game assets opcodes
 */

var GameAssetsOpcode;

(function (GameAssetsOpcode) {
  GameAssetsOpcode[GameAssetsOpcode["TIER_REPORT_AT_BLOCK"] = 46] = "TIER_REPORT_AT_BLOCK";
  GameAssetsOpcode[GameAssetsOpcode["ACCOUNT"] = 47] = "ACCOUNT";
  GameAssetsOpcode[GameAssetsOpcode["CURRENT_UNITS"] = 48] = "CURRENT_UNITS";
})(GameAssetsOpcode || (GameAssetsOpcode = {}));

var newOpMeta = [{
  opcode: AllStandardOps.SKIP,
  name: 'SKIP',
  input: ''
}, {
  opcode: AllStandardOps.VAL,
  name: 'VAL',
  input: 'constantIndex'
}, {
  opcode: AllStandardOps.DUP,
  name: 'DUP',
  input: 'DUP'
}, {
  opcode: AllStandardOps.ZIPMAP,
  name: 'ZIPMAP',
  input: 'zipmap'
}, {
  opcode: AllStandardOps.DEBUG,
  name: 'DEBUG',
  input: ''
}, {
  opcode: AllStandardOps.BLOCK_NUMBER,
  name: 'BLOCK_NUMBER',
  input: 'blockNumber'
}, {
  opcode: AllStandardOps.BLOCK_TIMESTAMP,
  name: 'BLOCK_TIMESTAMP',
  input: 'blockTimestamp'
}, {
  opcode: AllStandardOps.SENDER,
  name: 'SENDER',
  input: 'msgSender'
}, {
  opcode: AllStandardOps.THIS_ADDRESS,
  name: 'THIS_ADDRESS',
  input: 'thisAddress'
}, {
  opcode: AllStandardOps.SCALE18_MUL,
  name: 'SCALE18_MUL',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SCALE18_DIV,
  name: 'SCALE18_DIV',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SCALE18,
  name: 'SCALE18',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SCALEN,
  name: 'SCALEN',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SCALE_BY,
  name: 'SCALE_BY',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SCALE18_ONE,
  name: 'SCALE18_ONE',
  input: 'SCALE18_ONE'
}, {
  opcode: AllStandardOps.SCALE18_DECIMALS,
  name: 'SCALE18_DECIMALS',
  input: 'SCALE18_DECIMALS'
}, {
  opcode: AllStandardOps.ADD,
  name: 'ADD',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SATURATING_ADD,
  name: 'SATURATING_ADD',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SUB,
  name: 'SUB',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SATURATING_SUB,
  name: 'SATURATING_SUB',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.MUL,
  name: 'MUL',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SATURATING_MUL,
  name: 'SATURATING_MUL',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.DIV,
  name: 'DIV',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.MOD,
  name: 'MOD',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.EXP,
  name: 'EXP',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.MIN,
  name: 'MIN',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.MAX,
  name: 'MAX',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.ISZERO,
  name: 'ISZERO',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.EAGER_IF,
  name: 'EAGER_IF',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.EQUAL_TO,
  name: 'EQUAL_TO',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.LESS_THAN,
  name: 'LESS_THAN',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.GREATER_THAN,
  name: 'GREATER_THAN',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.EVERY,
  name: 'EVERY',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.ANY,
  name: 'ANY',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.REPORT,
  name: 'REPORT',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.NEVER,
  name: 'NEVER',
  input: 'NEVER'
}, {
  opcode: AllStandardOps.ALWAYS,
  name: 'ALWAYS',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SATURATING_DIFF,
  name: 'SATURATING_DIFF',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.UPDATE_BLOCKS_FOR_TIER_RANGE,
  name: 'UPDATE_BLOCKS_FOR_TIER_RANGE',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.SELECT_LTE,
  name: 'SELECT_LTE',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.IERC20_BALANCE_OF,
  name: 'IERC20_BALANCE_OF',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.IERC20_TOTAL_SUPPLY,
  name: 'IERC20_TOTAL_SUPPLY',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.IERC721_BALANCE_OF,
  name: 'IERC721_BALANCE_OF',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.IERC721_OWNER_OF,
  name: 'IERC721_OWNER_OF',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.IERC1155_BALANCE_OF,
  name: 'IERC1155_BALANCE_OF',
  input: 'takeFromStack'
}, {
  opcode: AllStandardOps.IERC1155_BALANCE_OF_BATCH,
  name: 'IERC1155_BALANCE_OF_BATCH',
  input: 'takeFromStack'
}];
var opSaleMeta = [{
  opcode: Sale.Opcodes.REMAINING_UNITS,
  name: 'REMAINING_UNITS',
  input: 'REMAINING_UNITS'
}, {
  opcode: Sale.Opcodes.TOTAL_RESERVE_IN,
  name: 'TOTAL_RESERVE_IN',
  input: 'TOTAL_RESERVE_IN'
}, {
  opcode: Sale.Opcodes.CURRENT_BUY_UNITS,
  name: 'CURRENT_BUY_UNITS',
  input: 'CURRENT_BUY_UNITS'
}, {
  opcode: Sale.Opcodes.TOKEN_ADDRESS,
  name: 'TOKEN_ADDRESS',
  input: 'TOKEN_ADDRESS'
}, {
  opcode: Sale.Opcodes.RESERVE_ADDRESS,
  name: 'RESERVE_ADDRESS',
  input: 'RESERVE_ADDRESS'
}];
var opCombineTierMeta = [{
  opcode: CombineTier.Opcodes.ACCOUNT,
  name: 'ACCOUNT',
  input: 'ACCOUNT'
}];
var opEmissionsMeta = [{
  opcode: EmissionsERC20.Opcodes.CLAIMANT_ACCOUNT,
  name: 'CLAIMANT_ACCOUNT',
  input: 'CLAIMANT_ACCOUNT'
}];
/**
 * @public
 * The generator of friendly human readable source.
 *
 * @remarks
 * Parse an State/Script to a more human readable form, making easier to understand. This form allow to the users read exactly
 * what the Script is made for, like the conditions, values used, etc. Also, anyone can learn to write their own scripts
 * if use the Human Form to see the output for each combination that they made.
 *
 * If you find an issue or you want to propose a better way to show a specific script or opcodes, please
 * feel to do it on: https://github.com/beehive-innovation/rain-sdk/issues
 */

var HumanFriendlySource = /*#__PURE__*/function () {
  function HumanFriendlySource() {}

  /**
   * Obtain the friendly output from an script.
   * @param _state - The state or script to generate the friendly version @see StateConfig
   * @param _config - The configuration that will run the generator. @see Config
   * @returns
   */
  HumanFriendlySource.get = function get(_state, _config) {
    var _config2, _config2$contract;

    if (_config === void 0) {
      _config = {
        contract: '',
        pretty: false
      };
    }

    this._context = (_config2 = _config) == null ? void 0 : (_config2$contract = _config2.contract) == null ? void 0 : _config2$contract.toLowerCase();
    this._pretty = _config.pretty ? true : false;
    var state = {
      stackIndex: 0,
      stack: [],
      sources: _state.sources,
      constants: _state.constants
    };

    var _result = this._eval(state, 0);

    return this._pretty ? this.prettify(_result) : _result;
  }
  /**
   * Make more readable the output from the HumanFriendly Source adding indenting following the parenthesis
   *
   * @remarks
   * If the string is already indentend, the method will wrongly generate the string
   *
   * @param _text - The output from the HumanFriendlySource
   * @param _config - The configuration of the prettify method (experimental)
   * @returns The pretty output
   */
  ;

  HumanFriendlySource.prettify = function prettify(_text, _config) {
    if (_config === void 0) {
      _config = {};
    }

    var _config3 = _config,
        n = _config3.n,
        length = _config3.length;
    if (!n) n = 2;
    if (!length) length = 20;
    _text = _text.replace(/\s/g, '');
    var space = ' ';
    var counter = 0;
    var skip = 0;

    for (var i = 0; i < _text.length; i++) {
      if (_text[i] === ',' && skip > 0) {
        _text = _text.slice(0, i + 1) + space + _text.slice(i + 1);
      }

      if (_text[i] === '(' || _text[i] === '[' || _text[i] === ',' && skip === 0) {
        if (_text[i] === ',' || this.needIndent(_text, i, length - counter * n)) {
          if (_text[i] !== ',') counter++;
          _text = _text.slice(0, i + 1) + '\n' + space.repeat(counter * n) + _text.slice(i + 1);
        } else {
          skip++;
        }
      }

      if (_text[i] === ')' || _text[i] === ']') {
        if (skip === 0) {
          counter--;
          _text = _text.slice(0, i) + '\n' + space.repeat(counter * n) + _text.slice(i);
          i = i + counter * n + 1;
        } else {
          skip--;
        }
      }
    }

    return _text;
  };

  HumanFriendlySource.flagOp = function flagOp(_a) {
    var spcOpces = [AllStandardOps.SCALE_BY, AllStandardOps.SCALEN];
    return spcOpces.includes(_a);
  };

  HumanFriendlySource.getSigned8 = function getSigned8(_value) {
    if ((_value & 0x80) > 0) {
      _value = _value - 0x100;
    }

    return _value;
  };

  HumanFriendlySource.tierRangeFromOp = function tierRangeFromOp(_op) {
    var startTier_ = _op & 0x0f;
    var endTier_ = _op >> 4 & 0x0f;
    return [startTier_, endTier_];
  };

  HumanFriendlySource.needIndent = function needIndent(text, index, max) {
    var openRef = text[index];
    var closeRef = openRef === '(' ? ')' : ']';
    text = text.slice(index + 1);
    var counter = 0;

    for (var i = 0; i < text.length && i < max + 1; i++) {
      if (text[i] === openRef) counter++;

      if (text[i] === closeRef) {
        if (counter === 0) return false;
        counter--;
      }
    }

    return true;
  };

  HumanFriendlySource.cleanStack = function cleanStack(_stack, length) {
    var j = 0;
    var k = 0;
    var _newStack = [];

    while (j < length) {
      if (!_stack[k].consumed || _stack[k].consumed && _stack[k].wasDup) {
        _newStack[j] = _stack[k];
        j++;
      }

      k++;
    }

    return _newStack;
  };

  HumanFriendlySource.identifyZipmap = function identifyZipmap(_stack, stackToRead) {
    if (stackToRead <= 0) {
      return -1;
    }

    var zipmapCounter = 0;
    var _stackReturn = stackToRead;
    var i = _stack.length - 1;

    while (i >= 0 && _stack.length - i <= _stackReturn) {
      var _text = _stack[i].val.toString();

      if (_text.slice(0, 6) === 'ZIPMAP') {
        zipmapCounter++;

        var _zipmapStack = parseInt(_text.slice(7, 8));

        _stackReturn -= _zipmapStack;
      }

      i--;
    }

    return _stackReturn === stackToRead ? -1 : _stackReturn + zipmapCounter;
  };

  return HumanFriendlySource;
}();
HumanFriendlySource.opMeta = newOpMeta;
HumanFriendlySource._context = void 0;
HumanFriendlySource._pretty = void 0;
HumanFriendlySource._isZipmap = false;

HumanFriendlySource._eval = function (_state, sourceIndex) {
  var i = 0;
  var op;
  var state = {
    stackIndex: 0,
    stack: [],
    sources: _state.sources,
    constants: _state.constants
  };
  var ops = HumanFriendlySource.pairs(state.sources[sourceIndex]).map(function (pair) {
    var opmeta = HumanFriendlySource.opMeta.find(function (opmeta) {
      return opmeta.opcode === pair[0];
    });

    if (typeof opmeta === 'undefined') {
      // Search with local opcodes if context provided
      if (HumanFriendlySource._context) {
        var _contract = HumanFriendlySource._context;

        if (_contract === 'sale') {
          opmeta = opSaleMeta.find(function (opmeta) {
            return opmeta.opcode === pair[0];
          });
        } else if (_contract === 'combinetier') {
          opmeta = opCombineTierMeta.find(function (opmeta) {
            return opmeta.opcode === pair[0];
          });
        } else if (_contract === 'emissions') {
          opmeta = opEmissionsMeta.find(function (opmeta) {
            return opmeta.opcode === pair[0];
          });
        } else if (_contract === 'gameassets') {
          opmeta = opEmissionsMeta.find(function (opmeta) {
            return opmeta.opcode === pair[0];
          });
        } else {
          throw Error("Unknown opcode: " + pair[0] + " using context " + _contract);
        }
      } // still undefined


      if (typeof opmeta === 'undefined') {
        throw Error("Unknown opcode: " + pair[0]);
      }
    }

    return _extends({
      operand: pair[1]
    }, opmeta);
  });

  while (i < ops.length) {
    var _stackIndex = BigNumber.from(state.stackIndex).toNumber();

    op = ops[i];
    i++;

    if (op.input === 'constantIndex') {
      if (op.operand < 128) {
        state.stack[_stackIndex] = {
          val: state.constants[op.operand],
          consumed: false
        };
      } else {
        state.stack[_stackIndex] = {
          val: '^' + (op.operand - 128),
          consumed: false
        };
      }

      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'blockNumber') {
      state.stack[_stackIndex] = {
        val: 'BLOCK_NUMBER()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'DUP') {
      state.stack[op.operand].consumed = true;
      state.stack[op.operand].wasDup = true;
      var valueDup = {
        val: state.stack[op.operand].val,
        consumed: false
      };
      state.stack.push(valueDup);
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'msgSender') {
      state.stack[_stackIndex] = {
        val: 'SENDER()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'thisAddress') {
      state.stack[_stackIndex] = {
        val: 'THIS_ADDRESS()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'blockTimestamp') {
      state.stack[_stackIndex] = {
        val: 'BLOCK_TIMESTAMP()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'SCALE18_DECIMALS') {
      state.stack[_stackIndex] = {
        val: 'SCALE18_DECIMALS()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'SCALE18_ONE') {
      state.stack[_stackIndex] = {
        val: 'SCALE18_ONE()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'NEVER') {
      state.stack[_stackIndex] = {
        val: 'NEVER()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'REMAINING_UNITS') {
      state.stack[_stackIndex] = {
        val: 'REMAINING_UNITS()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'TOTAL_RESERVE_IN') {
      state.stack[_stackIndex] = {
        val: 'TOTAL_RESERVE_IN()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'CURRENT_BUY_UNITS') {
      state.stack[_stackIndex] = {
        val: 'CURRENT_BUY_UNITS()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'TOKEN_ADDRESS') {
      state.stack[_stackIndex] = {
        val: 'TOKEN_ADDRESS()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'RESERVE_ADDRESS') {
      state.stack[_stackIndex] = {
        val: 'RESERVE_ADDRESS()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'ACCOUNT') {
      state.stack[_stackIndex] = {
        val: 'ACCOUNT()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'CLAIMANT_ACCOUNT') {
      state.stack[_stackIndex] = {
        val: 'CLAIMANT_ACCOUNT()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'CURRENT_UNITS') {
      state.stack[_stackIndex] = {
        val: 'CURRENT_UNITS()',
        consumed: false
      };
      state.stackIndex = BigNumber.from(_stackIndex).add(1).toNumber();
    } else if (op.input === 'takeFromStack') {
      HumanFriendlySource.applyOp(state, op);
    } else if (op.input === 'zipmap') {
      HumanFriendlySource._isZipmap = true;
      HumanFriendlySource.zipmap(state, op.operand);
      HumanFriendlySource._isZipmap = false;
    }
  }

  return state.stack.filter(function (item) {
    return item.consumed === false;
  }).map(function (item) {
    item.consumed = true;
    return item.val;
  }).join(' ');
};

HumanFriendlySource.zipmap = function (state, operand) {
  var sourceIndex = operand & 0x07;
  var stepSize = operand >> 3 & 0x03;
  var valLength = operand >> 5 & 0x07;
  var amountValues = "" + Math.pow(2, stepSize);
  var tempString = "ZIPMAP_" + amountValues + "(\n";
  var tempArr = [];
  var leng = state.stack.length - 1;

  for (var i = leng - valLength; i <= leng; i++) {
    if (stepSize > 0) {
      var divided = HumanFriendlySource.divideArray(state.stack[i].val, stepSize);

      var _value = typeof divided === 'string' ? divided : JSON.stringify(Array.from(divided)).replace(/,/g, ', ');

      tempArr.push(_value);
    } else {
      tempArr.push(state.stack[i].val);
    }

    state.stack[i].consumed = true;
  }

  tempArr.push(HumanFriendlySource._eval(state, sourceIndex));
  tempString += tempArr.map(function (entry) {
    return '    ' + entry;
  }).join(",\n");
  tempString += "\n)";
  state.stackIndex = BigNumber.from(state.stackIndex).sub(valLength); // Minus 1 becuase the zipmap is not added yet

  var baseIndex = BigNumber.from(state.stackIndex).toNumber() - 1; // Cleaning the stack

  state.stack = HumanFriendlySource.cleanStack(state.stack, baseIndex);
  state.stack[baseIndex] = {
    val: tempString,
    consumed: false
  };
};

HumanFriendlySource.applyOp = function (state, op) {
  var _stackIndex = BigNumber.from(state.stackIndex).toNumber();

  var baseIndex = _stackIndex - op.operand;
  var top = _stackIndex - 1;
  var tempString = op.name + '(';
  var cursor = baseIndex;
  var tempArr;
  var operandFlag = false;
  var tierRangeFlag = false;
  var selectLteFlag = false;

  if (op.opcode === AllStandardOps.IERC1155_BALANCE_OF_BATCH || op.opcode === AllStandardOps.IERC1155_BALANCE_OF) {
    baseIndex = _stackIndex - 1 - (op.operand + 1) * 2;
    cursor = baseIndex;
    tempArr = [state.stack[cursor].val]; //
  } else if (op.opcode === AllStandardOps.UPDATE_BLOCKS_FOR_TIER_RANGE) {
    tierRangeFlag = true;
    baseIndex = _stackIndex - 2;
    cursor = baseIndex;
    tempArr = [state.stack[cursor].val]; //
  } else if (op.opcode === AllStandardOps.SCALE18_DIV || op.opcode === AllStandardOps.SCALE18_MUL) {
    var _stackLength = HumanFriendlySource.identifyZipmap(state.stack, 2); // At least one zipmap was found to fll all the required stack from MIN


    if (_stackLength !== -1) {
      baseIndex = _stackIndex - _stackLength;
    } // Since no zipmap was found, the stack could fill the the stack required
    else {
      baseIndex = _stackIndex - 2;
    }

    cursor = baseIndex;
    tempArr = [state.stack[cursor].val + '*10**18']; //
  } else if (op.opcode === AllStandardOps.EAGER_IF) {
    var _stackLength2 = HumanFriendlySource.identifyZipmap(state.stack, 3); // At least one zipmap was found to fll all the required stack from MIN


    if (_stackLength2 !== -1) {
      baseIndex = _stackIndex - _stackLength2;
    } // Since no zipmap was found, the stack could fill the the stack required
    else {
      baseIndex = _stackIndex - 3;
    }

    cursor = baseIndex;
    tempArr = [state.stack[cursor].val]; //
  } else if (op.opcode === AllStandardOps.SCALE18 || op.opcode === AllStandardOps.ISZERO || op.opcode === AllStandardOps.IERC20_TOTAL_SUPPLY || HumanFriendlySource.flagOp(op.opcode)) {
    var _stackLength3 = HumanFriendlySource.identifyZipmap(state.stack, 1); // At least one zipmap was found to fll all the required stack from MIN


    if (_stackLength3 !== -1) {
      baseIndex = _stackIndex - _stackLength3;
    } // Since no zipmap was found, the stack could fill the the stack required
    else {
      baseIndex = _stackIndex - 1;
    }

    operandFlag = HumanFriendlySource.flagOp(op.opcode);
    cursor = baseIndex;
    tempArr = [state.stack[cursor].val]; //
  } else if (op.opcode === AllStandardOps.GREATER_THAN || op.opcode === AllStandardOps.LESS_THAN || op.opcode === AllStandardOps.EQUAL_TO || op.opcode === AllStandardOps.SATURATING_DIFF || op.opcode === AllStandardOps.IERC721_OWNER_OF || op.opcode === AllStandardOps.IERC721_BALANCE_OF || op.opcode === AllStandardOps.IERC20_BALANCE_OF || op.opcode === AllStandardOps.REPORT || op.opcode === GameAssetsOpcode.TIER_REPORT_AT_BLOCK && op.name === 'TIER_REPORT_AT_BLOCK') {
    var _stackLength4 = HumanFriendlySource.identifyZipmap(state.stack, 2); // At least one zipmap was found to fll all the required stack from MIN


    if (_stackLength4 !== -1) {
      baseIndex = _stackIndex - _stackLength4;
    } // Since no zipmap was found, the stack could fill the the stack required
    else {
      baseIndex = _stackIndex - 2;
    }

    cursor = baseIndex;
    tempArr = [state.stack[cursor].val]; //
  } else if (op.opcode === AllStandardOps.SELECT_LTE) {
    var _stackLength5 = HumanFriendlySource.identifyZipmap(state.stack, 3); // At least one zipmap was found to fll all the required stack from MIN


    if (_stackLength5 !== -1) {
      baseIndex = _stackIndex - _stackLength5;
    } // Since no zipmap was found, the stack could fill the the stack required
    else {
      baseIndex = _stackIndex - 3;
    }

    selectLteFlag = true;
    cursor = baseIndex;
    tempArr = [];
  } else if (op.opcode === AllStandardOps.MIN || op.opcode === AllStandardOps.MAX || op.opcode === AllStandardOps.ADD || op.opcode === AllStandardOps.SUB || op.opcode === AllStandardOps.MUL || op.opcode === AllStandardOps.DIV || op.opcode === AllStandardOps.SATURATING_ADD || op.opcode === AllStandardOps.SATURATING_SUB || op.opcode === AllStandardOps.SATURATING_MUL) {
    var operand = HumanFriendlySource.identifyZipmap(state.stack, op.operand); // At least one zipmap was found to fll all the required stack from MIN

    if (operand !== -1) {
      baseIndex = _stackIndex - operand;
    } // Since no zipmap was found, the stack could fill the the stack required
    else if (_stackIndex >= op.operand) {
      baseIndex = _stackIndex - op.operand;
    } // All the stack should fill the requiremnte (?)
    else {
      baseIndex = 0;
    }

    cursor = baseIndex;
    tempArr = [state.stack[cursor].val];
  } else {
    tempArr = [state.stack[cursor].val];
  }

  state.stack[cursor].consumed = true;

  while (cursor < top || operandFlag || tierRangeFlag || selectLteFlag) {
    cursor++;

    if (selectLteFlag) {
      var logic_ = op.operand >> 7;
      var mode_ = op.operand >> 5 & 0x3;
      var reportsLength_ = op.operand & 0x1f;
      var selectLteText = 'Invalid selectLte';

      if ((logic_ === 0 || logic_ === 1) && mode_ >= 0 && mode_ <= 2) {
        selectLteText = selectLteLogic[logic_] + ", " + selectLteMode[mode_] + ", " + reportsLength_;
      }

      tempArr.push(selectLteText);
      selectLteFlag = false;
      cursor -= 2;
    } else if (operandFlag) {
      tempArr.push(HumanFriendlySource.getSigned8(op.operand));
      operandFlag = false;
    } else if (tierRangeFlag) {
      var _HumanFriendlySource$ = HumanFriendlySource.tierRangeFromOp(op.operand),
          _start = _HumanFriendlySource$[0],
          _end = _HumanFriendlySource$[1];

      var range = 'Invalid Range';

      if (_end > _start) {
        range = "(" + _start + ", " + _end + ")";
      } else {
        range = "(" + _end + ")";
      }

      tempArr.push(range);
      tierRangeFlag = false;
      cursor--;
    } else {
      tempArr.push(state.stack[cursor].val);
      state.stack[cursor].consumed = true;
    }
  }

  tempString += tempArr.join(', ');
  tempString += ')';
  state.stack[baseIndex] = {
    val: tempString,
    consumed: false
  };
  state.stackIndex = baseIndex + 1; // Cleaning the stack

  state.stack = HumanFriendlySource.cleanStack(state.stack, state.stackIndex);
};

HumanFriendlySource.pairs = function (arr) {
  var _arr = Array.from(arrayify(arr));

  var pairs = [];

  for (var i = 0; i < arr.length; i += 2) {
    pairs.push(_arr.slice(i, i + 2));
  }

  return pairs;
};

HumanFriendlySource.divideArray = function (arr, times) {
  if (arr.constructor === Uint8Array) {
    var n = arr.length;

    for (var i = 0; i < times; i++) {
      n = n / 2;
    }

    return Array.from(arr.filter(function (_, i) {
      return i % n === n - 1;
    }));
  } else {
    try {
      /**
       * If it's not an Uint8Array, then it's an possible HexString or BigNumber comming from constants.
       * Mostly these cases should come from reports. Raise an issue on repo if you see any error.
       */
      BigNumber.from(arr);

      var _eachLength = 32 / Math.pow(2, times) * 2;

      var regex = new RegExp(".{1," + _eachLength + "}", 'g');
      var value = paddedUInt256(BigNumber.from(arr));
      return value.slice(2).match(regex);
    } catch (e) {
      // If fail, we just return the value normally as string
      return arr.toString();
    }
  }
};

export { AddressBook, AllStandardOps, BuildReport, CombineTier, CombineTierGenerator, CombineTierJS, CreateERC20, ERC1155, ERC20, ERC20BalanceTier, ERC20TransferTier, ERC721, ERC721BalanceTier, EmissionsERC20, EmissionsERC20JS, FactoryContract, FixedPrice, GatedNFT, HumanFriendlySource, ITier, IncreasingPrice, LinearEmissions, NoticeBoard, PriceCurve, RainContract, RainJS, RedeemableERC20, RedeemableERC20ClaimEscrow, Sale, SaleDurationInBlocks, SaleDurationInTimestamp, SaleJS, SequentialEmissions, Tier, TierContract, Transferrable, VM, Verify, VerifyStatus, VerifyTier, WalletCapMode, utils, vLBP };
//# sourceMappingURL=rain-sdk.esm.js.map
